import React, { useState, useEffect, useRef, useMemo } from "react";
import hwBg from "./hw-bg.webp?url";
import hwSnd from "./hw-sound.mp3?url";
import hwBg2 from "./hw-bg2.webp?url";
import {
  AVATAR_COLORS,
  AttachmentPreview,
  AudioWithFallback,
  RecordingWave,
  ChatScrollFab,
  MessageTicks,
  COMPENSATION_REQUESTS_KEY,
  COUNTED_STATUSES,
  ChatDateSeparator,
  EMOJI_LIST,
  MessageReactions,
  QUICK_REACTIONS,
  RANKING_START_DATE,
  TASK_COMPLETE_POINTS,
  TopBar,
  URL_REGEX,
  UploadProgressBanner,
  avatarColorFor,
  bonusDateLabel,
  bonusStatus,
  byPeriodDesc,
  chatDateLabel,
  compressImageFile,
  driverCar,
  formatMessageTime,
  formatPLN,
  formatSeenBy,
  groupDisplayName,
  handleImagePaste,
  highlightSearchMatch,
  initials,
  isAnyRecordingActive,
  isGroupedWithAdjacent,
  useCountUp,
  linkifyText,
  liveCompAdjustment,
  payoutDecisionKey,
  periodKey,
  periodLabel,
  playNotificationPing,
  recordKey,
  sameName,
  sendNtfyNotification,
  statusBadge,
  todayStr,
  uploadAttachment,
  uploadFiles,
  uploadVoiceBlob,
  useNewMessageSound,
  useSmartAutoScroll,
  useVoiceRecorder,
} from "./shared.jsx";
import { ChatShell, useMsgWindow, ComposerField, fieldVal, fieldSet, fieldInsert, withReaction } from "./chatkit.jsx";
import { useDriverPresence, OnlinePill, OnlineDot } from "./presence.jsx";

// Panel admina ładowany LENIWIE — kod trafia do przeglądarki dopiero gdy
// ktoś faktycznie wejdzie do panelu, nie przy każdym otwarciu appki.
// Dzięki temu kierowca ściąga dużo mniej kodu przy zwykłym logowaniu.
const AdminScreen = React.lazy(() => import("./AdminScreen.jsx"));
// Doładowywane części appki mają limit czasu (słabe łącze potrafiło zawiesić ich pobieranie na zawsze) i własną obsługę błędu.
function lazyTimeout(factory) {
  return () =>
    new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("lazy-timeout")), 25000);
      factory().then(
        (m) => {
          clearTimeout(t);
          resolve(m);
        },
        (e) => {
          clearTimeout(t);
          reject(e);
        }
      );
    });
}
class LazyBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  componentDidCatch(e) {
    try {
      if (window.__patexMark) window.__patexMark("lazy-fail", (this.props.name || "") + ":" + (e && e.message));
    } catch (x) {
      /* nic */
    }
  }
  render() {
    if (!this.state.err) return this.props.children;
    if (this.props.quiet) return null;
    return (
      <div className="ec">
        <div className="ec-ic">📡</div>
        <b>Nie udało się wczytać tej części aplikacji</b>
        <span>To zwykle słaby zasięg. Odśwież aplikację, gdy zasięg wróci — reszta appki działa normalnie.</span>
        <button type="button" onClick={() => window.location.reload()}>
          Odśwież aplikację
        </button>
      </div>
    );
  }
}
function SlowFallback({ name, label }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setSlow(true);
      try {
        if (window.__patexMark) window.__patexMark("lazy-slow", name);
      } catch (e) {
        /* nic */
      }
    }, 6000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ minHeight: 240, marginTop: 14, borderRadius: 18, background: "var(--surface)", opacity: 0.75, display: "grid", placeItems: "center", padding: 18, textAlign: "center", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
      {slow ? "Wolne połączenie — wczytuję " + label + "…" : ""}
    </div>
  );
}
const FortuneTab = React.lazy(lazyTimeout(() => import("./fortune.jsx")));
const WhatsNewLazy = React.lazy(lazyTimeout(() => import("./whatsnew.jsx")));
const PushHelpLazy = React.lazy(lazyTimeout(() => import("./extras.jsx").then((m) => ({ default: m.PushHelp }))));
const DebtRingLazy = React.lazy(lazyTimeout(() => import("./extras.jsx").then((m) => ({ default: m.DebtRing }))));
const WeekStarLazy = React.lazy(lazyTimeout(() => import("./weekstar.jsx")));
const GirlIntroLazy = React.lazy(lazyTimeout(() => import("./girl.jsx").then((m) => ({ default: m.GirlIntro }))));
const GirlChipLazy = React.lazy(lazyTimeout(() => import("./girl.jsx").then((m) => ({ default: m.GirlChip }))));
const GirlPeekLazy = React.lazy(lazyTimeout(() => import("./girl.jsx").then((m) => ({ default: m.GirlPeek }))));
const GirlRevealLazy = React.lazy(lazyTimeout(() => import("./girl.jsx").then((m) => ({ default: m.GirlReveal }))));
const SurveyPromoLazy = React.lazy(lazyTimeout(() => import("./survey.jsx").then((m) => ({ default: m.SurveyPromo }))));
const SatisfactionCardLazy = React.lazy(lazyTimeout(() => import("./survey.jsx").then((m) => ({ default: m.SatisfactionCard }))));
const SurveySheetLazy = React.lazy(lazyTimeout(() => import("./survey.jsx").then((m) => ({ default: m.SurveySheet }))));
const LevelCardLazy = React.lazy(lazyTimeout(() => import("./xp.jsx").then((m) => ({ default: m.LevelCard }))));
const ScratchStackLazy = React.lazy(lazyTimeout(() => import("./scratch.jsx").then((m) => ({ default: m.ScratchStack }))));
import { createPortal } from "react-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  KeyRound,
  ChevronLeft,
  LogOut,
  Delete,
  Check,
  FileSpreadsheet,
  Trash2,
  AlertCircle,
  Eye,
  EyeOff,
  Users,
  UserPlus,
  RefreshCw,
  Car,
  Trophy,
  Receipt,
  StickyNote,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
  Sparkles,
  XCircle,
  HelpCircle,
  Gift,
  Edit3,
  Lock,
  Pencil,
  Phone,
  Search,
  PlusCircle,
  MinusCircle,
  Wallet,
  MessageCircle,
  MessageSquare,
  Settings,
  Mic,
  TrendingUp,
  Zap,
  Award,
  X,
  VolumeX,
  Pin,
  Sun,
  Moon,
  Volume2,
  CalendarClock,
  FlaskConical,
  TrendingDown,
  Minus,
  Send,
  ArrowLeft,
  Megaphone,
  Percent,
  Download,
  Paperclip,
  Smile,
  Reply,
  Bell,
  LayoutDashboard,
  ArrowUp,
  ArrowDown,
  Camera,
  ChevronDown,
  MoreHorizontal,
  Flame,
  Crown,
  Archive,
  Target,
  Ticket,
} from "lucide-react";

const PATEX_ICON = "data:image/png;base64,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";

/* ---------------------------------------------------------------------- */
/* Storage                                                                 */
/* ---------------------------------------------------------------------- */
const DRIVERS_KEY = "fleet-drivers-v4";
const ADMIN_PASSWORD = "Mrlzbnv6";
const AVATARS_KEY = "fleet-avatars-v1";
const SESSION_KEY = "patex-session";
const PAYOUT_DECISIONS_KEY = "fleet-payout-decisions-v1";
const CHAT_THREADS_KEY = "fleet-chat-threads-v1";
const NEWS_KEY = "fleet-news-v1";
const TASKS_KEY = "fleet-tasks-v1";
const POINT_ADJUSTMENTS_KEY = "fleet-point-adjustments-v1";
const CARS_KEY = "fleet-cars-v1";
const WEEKLY_REVENUE_KEY = "fleet-weekly-revenue-v1";
const PROFIT_LOSS_KEY = "fleet-profit-loss-v1";
const CAR_REPAIRS_KEY = "fleet-car-repairs-v1";
const COMPANY_EXPENSES_KEY = "fleet-company-expenses-v1";
const CAR_INFO_KEY = "fleet-car-info-v1";
const COMPANY_ADJUSTMENTS_KEY = "fleet-company-adjustments-v1";
const CRYPTO_PORTFOLIO_KEY = "fleet-crypto-paper-portfolio-v1";
const CRYPTO_STRATEGY_PARAMS_KEY = "fleet-crypto-strategy-params-v1";
const CRYPTO_INTRADAY_PORTFOLIO_KEY = "fleet-crypto-intraday-portfolio-v1";
const CRYPTO_INTRADAY_STRATEGY_PARAMS_KEY = "fleet-crypto-intraday-strategy-params-v1";
const DEBT_REPAYMENTS_KEY = "fleet-debt-repayments-v1";
const DRIVER_DEBTS_KEY = "fleet-driver-debts-v1";
const GROUP_CHAT_KEY = "fleet-group-chat-v1";
// Prywatne wiadomości MIĘDZY kierowcami (nie z adminem) — osobna kolekcja,
// żeby nie mieszać się z czatem kierowca-admin (CHAT_THREADS_KEY).
const DM_THREADS_KEY = "fleet-dm-threads-v1";
const EARNINGS_KEY = "fleet-earnings-v4";
const SEED_KEY = "fleet-seed-v4";
const GLOBAL_BONUSES_KEY = "fleet-global-bonuses-v1";

/* ---------------------------------------------------------------------- */
/* Column matching — accepts common Polish header spellings from the      */
/* real rozliczenia spreadsheet                                          */
/* ---------------------------------------------------------------------- */

// Driver names in the source sheet carry platform tags e.g. "Jakub Bedla (U,F)"
// or "Andrzej Adach (B) Własne Auto" — strip those for matching/display.
// Some rows tag the driver's assigned fleet car in parentheses, e.g.
// "Tomasz Wrona (Skoda)" or "Mateusz Koszykowski (Aveo2)" — pull that out
// against the known fleet before the name gets stripped down above.
// A handful of legacy short codes get expanded to a nicer full name; any
// other car name is used exactly as written in the sheet (fleets change —
// this must not depend on a fixed whitelist of models).
// Skraca imię i nazwisko do samego imienia — a jeśli kilku kierowców ma to
// samo imię (np. kilku "Patryk"), dokleja pierwszą literę nazwiska, żeby dało
// się ich rozróżnić na czacie grupowym. "Patryk Fidor" -> "Patryk F." (tylko
// gdy jest więcej niż jeden Patryk), w przeciwnym razie po prostu "Patryk".
// Stały, ale różny kolor tła dla każdego imienia — żeby awatary na czacie
// grupowym łatwo dało się rozróżnić na pierwszy rzut oka.
const AVATAR_EMOJIS = [
  "🚗", "🏎️", "🚕", "🚙", "🚓", "😎", "🔥", "⭐",
  "🎯", "🚀", "💪", "🦁", "🐺", "🦊", "🐯", "⚡",
];




function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function subscribeToPushNotifications(driverName, allowPrompt = true) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { status: "unsupported" };
  }
  // Prośba o zgodę bez dotknięcia ekranu (np. przy samym uruchomieniu) jest przez Chrome wyciszana i kończy się "odmową" —
  // dlatego przy starcie pytamy tylko, gdy zgoda już jest; o zgodę prosimy przyciskiem w appce lub przy logowaniu PIN-em.
  let permission = typeof Notification !== "undefined" ? Notification.permission : "denied";
  if (permission === "default") {
    if (!allowPrompt) return { status: "not-asked" };
    try {
      permission = await Notification.requestPermission();
    } catch (e) {
      return { status: "unsupported" };
    }
  }
  if (permission !== "granted") {
    return { status: "denied" };
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const keyRes = await fetch("/api/vapid-public-key");
    const { publicKey } = await keyRes.json();
    if (!publicKey) return { status: "no-vapid-key" };

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const saveRes = await fetch("/api/push-subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ driverName, subscription }),
    });
    if (!saveRes.ok) return { status: "save-failed" };
    return { status: "success" };
  } catch (e) {
    return { status: "subscribe-failed", detail: e && e.message ? e.message : String(e) };
  }
}

async function subscribeAndReportPushStatus(driverName, allowPrompt = true) {
  const result = await subscribeToPushNotifications(driverName, allowPrompt);
  try {
    await fetch("/api/push-status", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        driverName,
        status: result.status,
        detail: result.detail || "",
        userAgent: navigator.userAgent,
      }),
    });
  } catch (e) {
    /* diagnostyka jest dodatkiem — nie blokuj niczego, jeśli się nie uda */
  }
}

// Suma zatwierdzonych rekompensat dla danego kierowcy i dokładnego okresu —
// używana wszędzie tam, gdzie appka pokazuje kwotę do wypłaty na żywo,
// zamiast surowej wartości zapisanej w rozliczeniu.


// Telefony robią zdjęcia w rozdzielczości nadającej się do druku (często
// 3-8 MB), a w czacie i tak pokazujemy je jako miniaturkę ~220px szeroką —
// wysyłanie i ładowanie pełnego oryginału za każdym razem było główną
// przyczyną, że zdjęcia długo się ładowały. Ta funkcja pomniejsza i lekko
// kompresuje zdjęcie W PRZEGLĄDARCE, zanim w ogóle poleci na serwer —
// zwykle 5-10x mniejszy plik, praktycznie bez widocznej różnicy jakości na
// telefonie. GIF-y pomijamy (przepisanie przez canvas zabiłoby animację),
// a jeśli coś pójdzie nie tak (stary telefon, brak wsparcia), po prostu
// wysyłamy oryginał — nigdy nie blokujemy wysyłki z tego powodu.
// Pokazuje "Dziś" / "Wczoraj" / pełną datę nad pierwszą wiadomością danego
// dnia — jak w każdym porządnym komunikatorze, żeby łatwo zorientować się
// w czasie przy przewijaniu starszej historii.
// Pasek postępu wysyłania — bez tego duży filmik po prostu "wisiał" bez
// żadnej informacji, ile jeszcze zostało, co wyglądało jakby appka się
// zawiesiła. Teraz widać dokładny procent, w czasie rzeczywistym.
// Pozwala wkleić zdjęcie ze schowka (np. skopiowany zrzut ekranu) prosto do
// pola wiadomości, Ctrl+V / Cmd+V, zamiast zawsze przechodzić przez wybór
// pliku. Jeśli w schowku nie ma obrazka, nic się nie dzieje (normalne
// wklejanie tekstu leci dalej, bez przechwytywania).

// Przewija czat na sam dół tylko wtedy, gdy użytkownik i tak już tam był
// (albo to pierwsze wczytanie) — jeśli ktoś przewinął w górę, żeby
// przeczytać starsze wiadomości, appka mu w tym nie przeszkadza, nawet gdy
// w tle przyjdzie nowa wiadomość albo appka odświeży dane.
// Krótki, delikatny "ping" generowany na żywo (bez pliku audio) plus
// wibracja telefonu — używane, kiedy w appce (na pierwszym planie) przyjdzie
// nowa wiadomość, żeby było to zauważalne nawet bez powiadomienia push.
// Odtwarza dźwięk/wibrację, kiedy liczba wiadomości rośnie — ale tylko jeśli
// appka nie jest w trakcie pierwszego wczytywania i przyrost NIE pochodzi od
// wiadomości właśnie wysłanej przez samego użytkownika (rozpoznawane po tym,
// że appka i tak przewija w dół tylko przy nowych wiadomościach kogoś innego
// — tu upraszczamy: dźwięk gra przy każdym przyroście spoza pierwszego renderu).

// Gest "pociągnij w dół, żeby odświeżyć" — typowy dla telefonu. Działa na
// całej stronie: jeśli użytkownik jest już na samej górze i przeciągnie
// palcem w dół wystarczająco daleko, appka sama odświeży dane po puszczeniu.
function usePullToRefresh(onRefresh) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(null);
  const pullingRef = useRef(false);
  const pullDistanceRef = useRef(0);

  useEffect(() => {
    function onTouchStart(e) {
      if (window.scrollY <= 0) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
      }
    }
    function onTouchMove(e) {
      if (!pullingRef.current || startYRef.current === null) return;
      const diff = e.touches[0].clientY - startYRef.current;
      if (diff > 0 && window.scrollY <= 0) {
        const d = Math.min(diff * 0.5, 90);
        pullDistanceRef.current = d;
        setPullDistance(d);
      } else {
        pullingRef.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    }
    async function onTouchEnd() {
      if (pullingRef.current && pullDistanceRef.current > 60) {
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
        }
      }
      pullDistanceRef.current = 0;
      setPullDistance(0);
      pullingRef.current = false;
      startYRef.current = null;
    }
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
    // eslint-disable-next-line
  }, [onRefresh]);

  return { pullDistance, refreshing };
}


// Prosta, dzielona (poza komponentami) flaga — appka sama sprawdza ją przed
// każdym cichym odświeżeniem danych w tle, żeby nigdy nie przeszkadzać
// aktywnemu nagrywaniu głosówki.



// Jeden uniwersalny komponent listy + formularza dla wszystkich pięciu
// kolekcji finansowych (Utargi, Zyski/Straty, Naprawy, Opłaty, Info o
// autach) — różnią się tylko zestawem pól (schema), więc zamiast pisać pięć
// prawie identycznych interfejsów, ten jeden renderuje dowolny z nich.
// Stare, zaimportowane dane mają czasem daty w formacie DD.MM.RRRR (np.
// "05.08.2026" = 5 sierpnia), a nowy datownik zapisuje ISO (RRRR-MM-DD).
// Zwykłe new Date("05.08.2026") odczytuje to jako amerykański zapis
// MM.DD.RRRR (8 maja!) — stąd błędne "przeterminowane". Ta funkcja
// rozpoznaje oba formaty poprawnie.
// Liczy "Zarobki Firmy": ile firma faktycznie zarabia po odjęciu napraw,
// rekompensat (netto) i opłat firmowych od sumy potrąceń za auto.
// Celowo POMIJA rekompensaty "przeniesiono na kolejny tydzień" — to
// przesunięcie tej samej kwoty w czasie, a nie nowy koszt/przychód, więc
// liczenie jej tutaj podwoiłoby zarobek.
// Generuje eleganckie podsumowanie miesiąca i otwiera systemowe okno
// drukowania przeglądarki, w którym można zapisać jako PDF — bez potrzeby
// doinstalowywania żadnej dodatkowej biblioteki do appki.
// Krótka etykieta miesiąca do wykresu (mało miejsca) — "Sie" zamiast
// "sierpień 2026".
// Elegancki, animowany wykres słupkowy do porównań miesiąc-do-miesiąca i
// rok-do-roku na Dashboardzie — ten sam, sprawdzony, bez-bibliotekowy
// wzorzec co EarningsChart wyżej, tylko z gradientowymi słupkami i
// czytelnymi etykietami pod każdym.
// Elegancki, "informacyjny" widok trendu zarobków — zamiast samych słupków,
// od razu widać: dużą liczbę bieżącego okresu, plakietkę "o ile % lepiej/
// gorzej niż poprzedni okres", zdanie tłumaczące to słowami, i delikatny
// wykres liniowy z gradientowym wypełnieniem pod spodem, pokazujący cały
// trend na raz.
// Agreguje wszystkie tygodnie po samym AUCIE (niezależnie od tego, ilu
// różnych kierowców nim jeździło w międzyczasie) — daje pełny, całościowy
// obraz tego, które auto realnie zarabia, a które generuje straty.
// Agreguje wszystkie tygodnie po samym AUCIE (niezależnie od tego, ilu
// różnych kierowców nim jeździło w międzyczasie) — daje pełny, całościowy
// obraz tego, które auto realnie zarabia, a które generuje straty.
// WAŻNE: utarg i potrącenia są "na kierowcę" więc poprawnie się sumują,
// kiedy kilku kierowców dzieliło auto w tym samym tygodniu — ale naprawy są
// właściwością AUTA W DANYM TYGODNIU, nie każdego kierowcy z osobna, więc
// liczą się TYLKO RAZ na auto na tydzień (nie raz na każdego kierowcę,
// który je tego tygodnia miał) — inaczej ten sam koszt naprawy dublowałby
// się przy każdym dodatkowym kierowcy dzielącym to auto.


// ---------------------------------------------------------------------------
// Krypto Lab — testowanie strategii bota na PRAWDZIWYCH danych rynkowych
// (pobieranych przez własny serwer, żeby ominąć ograniczenia sieciowe
// przeglądarki), z w pełni wirtualnym portfelem — żadne prawdziwe środki
// nigdy nie są w grze. Stan portfela zapisuje się trwale, więc przetrwa
// między sesjami.
// ---------------------------------------------------------------------------
// Typowa prowizja giełdy krypto (np. Binance ma 0,1% od transakcji) —
// naliczana przy KAŻDEJ transakcji (kupno i sprzedaż osobno), żeby wynik
// symulacji był realistyczny. Bez tego kupno i natychmiastowa sprzedaż po
// tej samej cenie wyszłyby "na zero", co w prawdziwym handlu nigdy się nie
// zdarza — zawsze płacisz prowizję i tracisz trochę na spreadzie.

// Liczy wynik zamknięcia pozycji, poprawnie dla OBU kierunków:
// - "long" (zarabiasz na wzroście): zysk = (cena teraz - cena wejścia) × ilość
// - "short" (zarabiasz na SPADKU): zysk = (cena wejścia - cena teraz) × ilość
// Zwraca proceeds (ile wraca do wirtualnej gotówki) i pnl (sam zysk/strata).
// Sprawdza, czy AKTUALNA cena trafiła w stop-loss/take-profit — kierunek
// zależny od tego, czy to pozycja "long" czy "short" (dla short są
// dosłownie odwrócone: stop jest WYŻEJ, cel NIŻEJ od ceny wejścia).


// Zamienia surowy sygnał (BUY/SELL/HOLD) na jasny, jednoznaczny komunikat
// po polsku — "Warto kupić X teraz" zamiast samego słowa "BUY".
// Zawsze widoczna, duża karta otwartej pozycji — niezależnie od tego, którą
// monetę akurat oglądasz. To naprawia realny problem: wcześniej przycisk
// sprzedaży bywał ukryty, jeśli patrzyłeś na inną zakładkę niż ta, na
// której masz otwartą pozycję.


// Skanuje już wczytaną historię cen i pokazuje, kiedy w PRZESZŁOŚCI
// pojawiłby się sygnał BUY/SELL — żeby było widać, jak często (a nie tylko
// "czy teraz"), bez pobierania czegokolwiek dodatkowo.

// --- Intraday: dane godzinowe, szybsze wskaźniki (EMA9/21 zamiast SMA50,
// RSI liczony na świecach godzinowych zamiast dziennych) — dobrane pod
// pozycje trzymane godzinami, nie dniami/tygodniami.


// Pokazuje prawdziwy stan konta Binance obok wirtualnego portfela — tylko
// podgląd (odczyt), appka nigdy nie handluje ani nie wypłaca stąd niczego.









// Zamienia zwykły tekst wiadomości na tablicę elementów React — zwykły
// tekst zostaje tekstem, a wszystko, co wygląda na link, staje się klikalnym
// odnośnikiem otwierającym się w nowej karcie, zamiast trzeba było go
// kopiować ręcznie.
// Podświetla dopasowany fragment tekstu przy wyszukiwaniu w czacie —
// prosty, bezpieczny podział na kawałki (bez ryzyka złego dopasowania przy
// znakach specjalnych w zapytaniu, bo nie budujemy z niego regexa).


function slugifyName(name) {
  return String(name)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
// Tri-state: b.active === false -> forced ended; b.active === true -> forced
// active (overrides the dates); undefined -> automatic, driven by dateFrom/dateTo.
function bonusCountsTowardTotal(b) {
  const s = bonusStatus(b);
  return s === "active" || s === "completed";
}

/* ---------------------------------------------------------------------- */
/* Refresh — re-reads the shared storage so this session picks up         */
/* anything an admin imported from another device/tab. (A direct browser  */
/* fetch to Google Sheets was tried and is blocked by the app sandbox —   */
/* the reliable path for new data stays: export from Sheets → import in   */
/* the admin panel below, or send Claude a fresh export to re-generate.)  */
/* ---------------------------------------------------------------------- */
// Scala listę wiadomości "po staremu" (lokalnych, mogących mieć coś
// świeżego, czego serwer jeszcze nie zdążył zwrócić) z listą "świeżą"
// (właśnie odczytaną z serwera) — bez utraty ANI JEDNEJ wiadomości.
// Deduplikacja po id, kolejność po czasie wysłania.
//
// WAŻNE zastrzeżenie: "ratujemy" z poprzedniego stanu TYLKO wiadomości
// wysłane w ciągu ostatnich kilkunastu sekund. Appka pozwala usuwać
// wiadomości — gdyby scalanie przywracało WSZYSTKO, czego nie ma w
// świeżej odpowiedzi, świadomie usunięta wiadomość wróciłaby na ekran
// przy pierwszym kolejnym odpytaniu serwera. Prawdziwy wyścig (wiadomość
// wysłana tuż przed tym jak serwer zdążył ją "zobaczyć") dotyczy zawsze
// czegoś świeżo wysłanego — więc próg czasowy odróżnia jedno od drugiego.
const CHAT_MERGE_RESCUE_WINDOW_MS = 15000;
function mergeMessagesById(prevMessages, freshMessages, now = Date.now()) {
  if (!prevMessages || prevMessages.length === 0) return freshMessages || [];
  if (!freshMessages) return prevMessages;
  const freshIds = new Set(freshMessages.map((m) => m.id));
  const onlyInPrev = prevMessages.filter(
    (m) => !freshIds.has(m.id) && now - (m.timestamp || 0) < CHAT_MERGE_RESCUE_WINDOW_MS
  );
  if (onlyInPrev.length === 0) return freshMessages; // szybka ścieżka — nic do dołożenia
  return [...freshMessages, ...onlyInPrev].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
}

// To samo, ale dla wątków czatu 1-na-1 (każdy wątek ma swoją własną
// listę wiadomości do scalenia) — i dodatkowo zachowuje CAŁE wątki,
// które istnieją tylko lokalnie (np. świeżo utworzony wątek przy
// pierwszej wiadomości, którego ten konkretny odczyt z serwera jeszcze
// nie "widział").
function mergeChatThreads(prevThreads, freshThreads) {
  if (!prevThreads || prevThreads.length === 0) return freshThreads || [];
  if (!freshThreads) return prevThreads;
  const freshById = new Map(freshThreads.map((t) => [t.id, t]));
  const merged = freshThreads.map((freshThread) => {
    const prevThread = prevThreads.find((t) => t.id === freshThread.id);
    if (!prevThread) return freshThread;
    return { ...freshThread, messages: mergeMessagesById(prevThread.messages, freshThread.messages) };
  });
  const onlyLocalThreads = prevThreads.filter((t) => !freshById.has(t.id));
  return [...merged, ...onlyLocalThreads];
}

// Odpowiedź serwera, która NIE zmieniła się od poprzedniego odczytu, zwraca TEN
// SAM obiekt (a nie jego świeżą kopię). Dzięki temu odświeżanie w tle co 4 s
// nie wywołuje przerysowania całej appki i całej listy wiadomości, kiedy nic
// się nie zmieniło — wcześniej każde odświeżenie podmieniało ~12 zestawów
// danych na nowe obiekty, co na telefonie dawało zauważalne "szarpnięcia".
const parsedResponseCache = new Map();
function parseCached(key, raw) {
  const hit = parsedResponseCache.get(key);
  if (hit && hit.raw === raw) return hit.data;
  const data = JSON.parse(raw);
  parsedResponseCache.set(key, { raw, data });
  return data;
}

// Lekki odczyt czatu dla kierowcy: tylko jego rozmowy + ostatnie wiadomości grupy (usługa /call-api/chat).
// Zwraca null przy jakimkolwiek problemie (wtedy appka robi pełny odczyt jak dawniej).
// Odczyt czatu jest WARUNKOWY: serwer odsyła ETag, a przy niezmienionej treści odpowiada pustym "304" zamiast ~16 KB
// (ok. 100 KB do przetworzenia) co kilka sekund. Najpierw była tu opcja no-store, która to wyłączała.
let chatSliceMemo = { name: null, etag: null, value: null };
async function fetchChatSlice(name, n) {
  const memoKey = name + "|" + n;
  const slow = typeof window !== "undefined" && window.__patexSlowLink && window.__patexSlowLink();
  const ctl = new AbortController();
  let timer = setTimeout(() => ctl.abort(), slow ? 40000 : 15000);
  try {
    const headers = {};
    if (chatSliceMemo.name === memoKey && chatSliceMemo.etag && chatSliceMemo.value) headers["If-None-Match"] = chatSliceMemo.etag;
    const res = await fetch(`/call-api/chat?name=${encodeURIComponent(name)}&n=${n}`, { signal: ctl.signal, headers });
    if (res.status === 304 && chatSliceMemo.value && chatSliceMemo.name === memoKey) return chatSliceMemo.value;
    if (!res.ok) return null;
    clearTimeout(timer);
    timer = setTimeout(() => ctl.abort(), slow ? 60000 : 30000);
    const text = await res.text();
    const data = JSON.parse(text);
    if (!data || !data.ok || !Array.isArray(data.chat) || !Array.isArray(data.group) || !Array.isArray(data.dm)) return null;
    const value = { text, data };
    chatSliceMemo = { name: memoKey, etag: res.headers.get("ETag"), value };
    return value;
  } catch (e) {
    // Przerwane/nieudane z powodu SŁABEGO ŁĄCZA (a nie błędu usługi) — nie wolno wtedy przechodzić na 10x cięższy odczyt pełny.
    if (window.__patexNoteAbort) window.__patexNoteAbort();
    return "net";
  } finally {
    clearTimeout(timer);
  }
}

async function reloadFromStorage(skipChat) {
  // Nieudany odczyt (słaby zasięg) = BŁĄD, który przerywa całe odświeżanie i
  // zostawia na ekranie to, co już jest. Wcześniej taki odczyt po cichu dawał
  // "pustą listę" — a to np. na chwilę kasowało z ekranu całą historię czatu
  // grupowego i rozliczenia. Odczyty lecą równolegle (nie jeden po drugim).
  const strictGet = (key) => window.storage.get(key, true, { strict: true });
  const [
    driversRes,
    earningsRes,
    bonusesRes,
    avatarsRes,
    payoutDecisionsRes,
    compensationRequestsRes,
    chatThreadsRes,
    newsRes,
    tasksRes,
    pointAdjustmentsRes,
    groupChatRes,
    dmThreadsRes,
  ] = await Promise.all([
    strictGet(DRIVERS_KEY),
    strictGet(EARNINGS_KEY),
    strictGet(GLOBAL_BONUSES_KEY),
    strictGet(AVATARS_KEY),
    strictGet(PAYOUT_DECISIONS_KEY),
    strictGet(COMPENSATION_REQUESTS_KEY),
    skipChat ? null : strictGet(CHAT_THREADS_KEY),
    strictGet(NEWS_KEY),
    strictGet(TASKS_KEY),
    strictGet(POINT_ADJUSTMENTS_KEY),
    skipChat ? null : strictGet(GROUP_CHAT_KEY),
    skipChat ? null : strictGet(DM_THREADS_KEY),
  ]);
  const drivers = driversRes && driversRes.value ? parseCached(DRIVERS_KEY, driversRes.value) : [];
  const earnings = earningsRes && earningsRes.value ? parseCached(EARNINGS_KEY, earningsRes.value) : [];
  const globalBonuses = bonusesRes && bonusesRes.value ? parseCached(GLOBAL_BONUSES_KEY, bonusesRes.value) : [];
  const avatars = avatarsRes && avatarsRes.value ? parseCached(AVATARS_KEY, avatarsRes.value) : {};
  const payoutDecisions = payoutDecisionsRes && payoutDecisionsRes.value ? parseCached(PAYOUT_DECISIONS_KEY, payoutDecisionsRes.value) : {};
  const compensationRequests =
    compensationRequestsRes && compensationRequestsRes.value ? parseCached(COMPENSATION_REQUESTS_KEY, compensationRequestsRes.value) : [];
  const chatThreads = chatThreadsRes && chatThreadsRes.value ? parseCached(CHAT_THREADS_KEY, chatThreadsRes.value) : [];
  const news = newsRes && newsRes.value ? parseCached(NEWS_KEY, newsRes.value) : [];
  const tasks = tasksRes && tasksRes.value ? parseCached(TASKS_KEY, tasksRes.value) : [];
  const pointAdjustments = pointAdjustmentsRes && pointAdjustmentsRes.value ? parseCached(POINT_ADJUSTMENTS_KEY, pointAdjustmentsRes.value) : [];
  const groupChatMessages = groupChatRes && groupChatRes.value ? parseCached(GROUP_CHAT_KEY, groupChatRes.value) : [];
  const dmThreads = dmThreadsRes && dmThreadsRes.value ? parseCached(DM_THREADS_KEY, dmThreadsRes.value) : [];
  return { drivers, earnings, globalBonuses, avatars, payoutDecisions, compensationRequests, chatThreads, groupChatMessages, dmThreads, news, tasks, pointAdjustments };
}

/* ---------------------------------------------------------------------- */
/* Signature element — odometer-style animated readout                    */
/* ---------------------------------------------------------------------- */
function DigitDisplay({ value, tone = "amber", size = "lg" }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setDisplay(value);
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    const to = value;
    const duration = 750;
    const start = performance.now();
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = to;
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return <span className={`digit digit-${size} digit-${tone}`}>{formatPLN(display)}</span>;
}

/* ---------------------------------------------------------------------- */
/* Ticket-style row (evokes a paper trip / fuel slip)                     */
/* ---------------------------------------------------------------------- */
// Klucz decyzji o wypłacie oparty na imieniu + okresie, NIE na r.id — id z
// arkusza (sync_N) jest tymczasowe i zmienia się po każdej synchronizacji,
// więc nie nadaje się jako trwały identyfikator.
function latestRecord(rows) {
  if (rows.length === 0) return null;
  return [...rows].sort(byPeriodDesc)[0];
}
// Top 3 by "Zarobki netto" for whichever week is the most recent across the
// whole dataset — recomputed fresh from `earnings` every render, so a new
// import (with a newer or corrected week) updates this automatically.
// Punkty rankingowe: liczy tylko dwa najnowsze tygodnie obecne w danych
// (czyli "poprzedni tydzień" + bieżący/najnowszy) — starsze rozliczenia się
// nie liczą. Za każdy taki tydzień, dla każdego kierowcy: suma pól
// "aplikacja" + "gotówka" >= progu = tyle punktów (nie kumulacyjnie, tylko
// najwyższy osiągnięty próg w danym tygodniu).
function pointsForWeekRevenue(revenue) {
  if (revenue >= 3000) return 400;
  if (revenue >= 2500) return 250;
  if (revenue >= 2000) return 200;
  if (revenue >= 1500) return 150;
  if (revenue >= 1000) return 100;
  if (revenue >= 500) return 40;
  return 0;
}
// Ranking i osobiste statystyki kierowcy (rekord tygodnia, seria dobrych
// tygodni) liczą się TYLKO z rozliczeń "Wypłacone"/"Przeniesiono" — to o
// WYNIKU kierowcy, który nie jest ostateczny, dopóki tydzień nie jest
// zamknięty (np. "Zaległość" oznacza, że coś jeszcze wisi w powietrzu).
// Zarobki Firmy to co innego — to zarobek FIRMY (Suma potrąceń: auto +
// ryczałt + VAT), nie kierowcy. Ten zarobek jest realny, NIEZALEŻNIE od
// tego, czy akurat kierowca wyszedł na tym tygodniu na plus czy na minus —
// "Zaległość" znaczy tylko, że kierowca jest Ci winien pieniądze, a nie że
// firma nic nie zarobiła. Dlatego to osobny zestaw statusów, świadomie
// szerszy niż ten do rankingu.
function computeDriverRanking(earnings, drivers, tasks, pointAdjustments) {
  const countedRecords = earnings.filter(
    (r) => r.periodFrom && r.periodFrom >= RANKING_START_DATE && COUNTED_STATUSES.includes(r.status)
  );
  const countedPeriods = [...new Set(countedRecords.map((r) => periodKey(r)).filter(Boolean))].sort();

  const totals = {}; // name -> { points, revenue, weeks }
  for (const r of countedRecords) {
    // Wyjątek na jeden, pierwszy liczony tydzień (20.07–26.07) — ten był
    // wpisywany jeszcze pod starym rozumieniem pola "Aplikacja" (przed
    // zmianą na "Utarg"), więc dla NIEGO liczymy tak jak dawniej: Aplikacja
    // + Gotówka razem. Każdy kolejny tydzień liczy się już normalnie, samym
    // Utargiem (appEarnings) — bez dodawania gotówki.
    const revenue = r.periodFrom === "2026-07-20" ? (r.appEarnings || 0) + (r.cashEarnings || 0) : r.appEarnings || 0;
    const pts = pointsForWeekRevenue(revenue);
    if (!totals[r.name]) totals[r.name] = { points: 0, revenue: 0, weeks: 0, taskPoints: 0 };
    totals[r.name].points += pts;
    totals[r.name].revenue += revenue;
    totals[r.name].weeks += 1;
  }
  for (const t of tasks || []) {
    const taskPoints = t.points != null ? t.points : TASK_COMPLETE_POINTS;
    for (const [driverName, status] of Object.entries(t.completions || {})) {
      if (status !== "completed") continue;
      if (!totals[driverName]) totals[driverName] = { points: 0, revenue: 0, weeks: 0, taskPoints: 0 };
      totals[driverName].points += taskPoints;
      totals[driverName].taskPoints += taskPoints;
    }
  }
  for (const a of pointAdjustments || []) {
    if (!totals[a.driverName]) totals[a.driverName] = { points: 0, revenue: 0, weeks: 0, taskPoints: 0 };
    totals[a.driverName].points += a.points;
  }

  const rows = drivers.map((d) => ({
    id: d.id,
    name: d.name,
    car: driverCar(earnings.filter((r) => sameName(r.name, d.name)), d.car),
    points: totals[d.name] ? totals[d.name].points : 0,
    revenue: totals[d.name] ? totals[d.name].revenue : 0,
    taskPoints: totals[d.name] ? totals[d.name].taskPoints : 0,
  }));
  rows.sort((a, b) => b.points - a.points || b.revenue - a.revenue || a.name.localeCompare(b.name));
  return { rows, countedPeriods };
}

function weeklyLeaderboard(earnings) {
  if (earnings.length === 0) return { period: null, top: [] };
  let latestKey = "";
  for (const r of earnings) {
    const k = periodKey(r);
    if (k > latestKey) latestKey = k;
  }
  const weekRows = earnings.filter((r) => periodKey(r) === latestKey);
  const top = [...weekRows].sort((a, b) => b.netEarnings - a.netEarnings).slice(0, 3);
  return { period: weekRows[0] ? periodLabel(weekRows[0]) : null, top };
}
// Liczy wypłatę "na żywo" z surowych pól rozliczenia — używane wszędzie,
// gdzie appka musi wiedzieć DOKŁADNIE ile kierowca ma dostać/jest winny w
// TYM konkretnym tygodniu, żeby nie polegać na polu "payout", które mogło
// nigdy nie zostać przeliczone (np. stare wpisy z importu, gdzie ktoś
// wpisał dług tylko słownie w notatce, a nie jako liczbę).
function computeRecordPayout(record, liveCompensationSum) {
  const utarg = record.appEarnings || 0;
  const cash = record.cashEarnings || 0;
  const zus = record.zus || 0;
  const appSettlement = record.appSettlement || 0;
  const carDeduction = record.carDeduction || 0;
  const ryczaltAmount = record.ryczaltAmount || 0;
  const vatAmount = record.vatAmount || 0;
  // WAŻNE: pole record.compensations to osobna, ręcznie wpisywana liczba —
  // NIE jest tym samym, co prawdziwe zgłoszenia rekompensat (np. "Kasa za
  // poprzedni tydzień"), które żyją w OSOBNEJ liście (compensationRequests)
  // i nigdy automatycznie nie aktualizują tego pola. Jeśli podano
  // liveCompensationSum (policzoną na żywo z tamtej listy), używamy JEJ —
  // inaczej drugie przeniesienie "gubiło" kwotę przeniesioną tydzień wcześniej.
  const compensations = liveCompensationSum != null ? liveCompensationSum : record.compensations || 0;
  const afterDeductions = utarg + zus - appSettlement - carDeduction;
  return Math.round((afterDeductions - ryczaltAmount - vatAmount + compensations - cash) * 100) / 100;
}
// Re-importing a file re-sends every row, including ones already in the
// database — upsert by driver+period so those become updates, not duplicates.
function upsertRecords(existing, incoming) {
  const byKey = new Map(existing.map((r) => [recordKey(r), r]));
  for (const r of incoming) {
    byKey.set(recordKey(r), r);
  }
  return [...byKey.values()];
}


const CONFETTI_COLORS = ["#ffa630", "#ffb84d", "#6ee7b7", "#60a5fa", "#f472b6", "#ffffff"];
function CelebrationOverlay({ message, onDone }) {
  useEffect(() => {
    buzz([60, 40, 60, 40, 120]);
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);
  const pieces = Array.from({ length: 26 });
  return createPortal(
    <div className="celebration-overlay" onClick={onDone}>
      {pieces.map((_, i) => (
        <div
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${Math.random() * 0.4}s`,
            animationDuration: `${1.8 + Math.random() * 1.2}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
      <div className="celebration-card">
        <div className="celebration-emoji">🎉</div>
        <div className="celebration-msg">{message}</div>
      </div>
    </div>,
    document.body
  );
}


// ====================== v38: pomoc z powiadomieniami, oś czasu wypłaty, koło spłaty, puste stany, wibracje ======================
function buzz(p) {
  try {
    if (navigator.vibrate) navigator.vibrate(p);
  } catch (e) {
    /* brak wibracji */
  }
}

// Ładniejszy "pusty stan" (zamiast szarego napisu): emoji, tytuł, podpowiedź i opcjonalny przycisk
function EmptyCard({ emoji, title, hint, cta, onCta }) {
  return (
    <div className="ec">
      <div className="ec-ic">{emoji}</div>
      <b>{title}</b>
      {hint && <span>{hint}</span>}
      {cta && (
        <button type="button" onClick={onCta}>
          {cta}
        </button>
      )}
    </div>
  );
}

// Rozliczenie jako oś czasu: Tydzień trwa → Rozliczanie → Wypłata
// Oś etapów rozliczenia: Tydzień trwa → Rozliczanie → Wypłata.
//  * "Tydzień trwa" — do końca tygodnia (niedziela); pasek i samochód pokazują, ile dni już minęło
//  * "Rozliczanie"   — zmienia się AUTOMATYCZNIE po północy z niedzieli na poniedziałek (godzina warszawska)
//  * "Wypłata"       — dopiero gdy szef wrzuci rozliczenie do akceptacji (status "W trakcie"); potem akceptacja → wypłata
const warsawDay = () => {
  try {
    return new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Warsaw" });
  } catch (e) {
    return new Date().toISOString().slice(0, 10);
  }
};
const dayDiff = (a, b) => Math.round((Date.parse(b + "T00:00:00Z") - Date.parse(a + "T00:00:00Z")) / 86400000);
const TL_DAYS = ["P", "W", "Ś", "C", "P", "S", "N"];
// ===================== Panel 3.0: orbita, dolny pasek, profil =====================
// Wyłącznik awaryjny: ?ui=classic albo localStorage patex-ui=classic przywraca stary wygląd (np. gdy czyjś telefon sobie nie radzi).
let COUNTED = false;
const uiClassic = () => {
  try {
    return /[?&]ui=classic/.test(location.search) || localStorage.getItem("patex-ui") === "classic";
  } catch (e) {
    return false;
  }
};
// Słaby telefon (mało rdzeni/pamięci): bez rozmycia i bez nieskończonych animacji
const lowEnd = () => {
  try {
    return (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 2;
  } catch (e) {
    return false;
  }
};
function weekInfo() {
  const day = warsawDay();
  const idx = (new Date(day + "T12:00:00Z").getUTCDay() + 6) % 7;
  return { idx, frac: (idx + 1) / 7, left: 6 - idx };
}
function splitPLN(v) {
  const n = Number(v) || 0;
  const neg = n < 0;
  const [i, d] = Math.abs(n).toFixed(2).split(".");
  return { neg, i: i.replace(/\B(?=(\d{3})+(?!\d))/g, " "), d };
}
function cachedTickets(name) {
  try {
    const c = JSON.parse(localStorage.getItem("patex-fz-state-" + name) || "null");
    const b = c && (c.balance != null ? c.balance : c.data && c.data.balance);
    return typeof b === "number" ? b : null;
  } catch (e) {
    return null;
  }
}
function useCountUp3(target, ms) {
  const [v, setV] = useState(() => (COUNTED || !target ? target : 0));
  useEffect(() => {
    if (COUNTED || !target) {
      setV(target);
      return undefined;
    }
    let reduce = false;
    try {
      reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      /* nic */
    }
    if (reduce) {
      COUNTED = true;
      setV(target);
      return undefined;
    }
    let raf = 0;
    let last = 0;
    const t0 = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      if (t - last > 33 || k === 1) {
        last = t;
        setV(target * (1 - Math.pow(1 - k, 3)));
      }
      if (k < 1) raf = requestAnimationFrame(step);
      else COUNTED = true;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return v;
}
function AmountBig({ value, tone = "green", sm, plus }) {
  const v = useCountUp3(Number(value) || 0, 1300);
  const a = splitPLN(v);
  return (
    <div className={`am3 ${sm ? "sm" : ""} t-${tone}`}>
      {a.neg && <span className="m">−</span>}
      {plus && !a.neg && Number(value) > 0 && <span className="m">+</span>}
      <span className="i">{a.i}</span>
      <span className="d">,{a.d}</span>
      <span className="c">zł</span>
    </div>
  );
}
function OrbHero({ pct, gid, c1, c2, children }) {
  const r = 112;
  const c = 2 * Math.PI * r;
  const [on, setOn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setOn(true), 90);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="orb3">
      <svg className="orb3-rg" viewBox="0 0 250 250" aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={c1} />
            <stop offset="1" stopColor={c2} />
          </linearGradient>
        </defs>
        <circle cx="125" cy="125" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="8" />
        <circle className="val" cx="125" cy="125" r={r} fill="none" stroke={"url(#" + gid + ")"} strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={on ? c * (1 - Math.max(0, Math.min(1, pct))) : c} />
      </svg>
      <div className="orb3-orbit" aria-hidden="true">
        <i />
      </div>
      <div className="orb3-core">{children}</div>
      <i className="orb3-sp a" />
      <i className="orb3-sp b" />
      <i className="orb3-sp c" />
    </div>
  );
}
function MiniRing({ pct }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 58 58" aria-hidden="true">
      <defs>
        <linearGradient id="hdg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6ee7b7" />
          <stop offset="1" stopColor="#ffd166" />
        </linearGradient>
      </defs>
      <circle cx="29" cy="29" r={r} fill="none" stroke="rgba(255,255,255,.16)" strokeWidth="4" />
      <circle cx="29" cy="29" r={r} fill="none" stroke="url(#hdg)" strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
    </svg>
  );
}
function Dock3({ tab, setTab, canSee, dots, moreOpen, setMoreOpen }) {
  const host = document.querySelector(".app-root") || document.body;
  const moreActive = ["zadluzenie", "rekompensaty", "grupa", "ranking"].includes(tab);
  const item = (k, Icon, label, dot) => (
    <button
      key={k}
      className={"dk3" + (tab === k && !moreOpen ? " on" : "")}
      onClick={() => {
        setMoreOpen(false);
        setTab(k);
      }}
    >
      <Icon size={22} />
      {dot && <em className="dot" />}
      <span>{label}</span>
    </button>
  );
  return createPortal(
    <div className="dock3" role="navigation" aria-label="Menu">
      {item("rozliczenia", Receipt, "Rozliczenia", false)}
      {canSee("bonusy") && item("bonusy", Gift, "Bonusy", dots.bonusy)}
      {canSee("fortuna") && (
        <button
          className={"coin3" + (tab === "fortuna" && !moreOpen ? " on" : "")}
          aria-label="Fortuna"
          onClick={() => {
            setMoreOpen(false);
            setTab("fortuna");
          }}
          onTouchStart={preloadFortune}
        >
          <Ticket size={28} />
        </button>
      )}
      {canSee("czat") && item("czat", MessageCircle, "Czat", dots.czat)}
      <button className={"dk3" + (moreActive || moreOpen ? " on" : "")} onClick={() => setMoreOpen(!moreOpen)}>
        <MoreHorizontal size={22} />
        {dots.more && <em className="dot" />}
        <span>Więcej</span>
      </button>
    </div>,
    host
  );
}
function MoreSheet3({ setTab, canSee, dots, onClose }) {
  const host = document.querySelector(".app-root") || document.body;
  const row = (k, Icon, label, sub, grad, dot) => (
    <button
      key={k}
      className="sh3-row"
      onClick={() => {
        onClose();
        setTab(k);
      }}
    >
      <span className="ic" style={{ background: grad }}>
        <Icon size={21} />
      </span>
      <span className="tx">
        <b>{label}</b>
        <small>{sub}</small>
      </span>
      {dot && <em className="bd" />}
    </button>
  );
  return createPortal(
    <>
      <div className="sh3-dim" onClick={onClose} />
      <div className="sh3" role="dialog" aria-label="Więcej">
        <div className="sh3-grab" />
        <div className="sh3-k">WIĘCEJ</div>
        {row("zadluzenie", AlertTriangle, "Zadłużenie", "Spłaty i saldo", "linear-gradient(150deg,#fb7185,#e11d48)", dots.zadluzenie)}
        {canSee("rekompensaty") && row("rekompensaty", Wallet, "Rekompensaty", "Dodaj lub odejmij", "linear-gradient(150deg,#60a5fa,#2563eb)", dots.rekompensaty)}
        {canSee("grupa") && row("grupa", Users, "Grupa", "Czat całej ekipy", "linear-gradient(150deg,#f472b6,#db2777)", dots.grupa)}
        {canSee("ranking") && row("ranking", Trophy, "Ranking", "Kierowca tygodnia, zadania, osiągnięcia", "linear-gradient(150deg,#ffd166,#f59e0b)", false)}
      </div>
    </>,
    host
  );
}
function ProfileSheet3({ driver, avatar, onSetAvatar, theme, onToggleTheme, onLogout, car, count, onClose }) {
  const host = document.querySelector(".app-root") || document.body;
  return createPortal(
    <>
      <div className="sh3-dim" onClick={onClose} />
      <div className="sh3" role="dialog" aria-label="Profil">
        <div className="sh3-grab" />
        <div className="sh3-me">
          <span className="av">{avatar || initials(driver.name)}</span>
          <div>
            <b>{driver.name}</b>
            <small>{count === 0 ? "brak rozliczeń w historii" : `${count} rozliczeń w historii`}</small>
          </div>
        </div>
        {car && (
          <div className="sh3-row static">
            <span className="ic" style={{ background: "linear-gradient(150deg,#ffd166,#f59e0b)" }}>
              <Car size={21} />
            </span>
            <span className="tx">
              <b>Samochód</b>
              <small>{car}</small>
            </span>
          </div>
        )}
        <div className="sh3-k" style={{ marginTop: 14 }}>
          AWATAR
        </div>
        <div className="sh3-av">
          {AVATAR_EMOJIS.map((e) => (
            <button key={e} className={avatar === e ? "on" : ""} onClick={() => onSetAvatar(e)}>
              {e}
            </button>
          ))}
          {avatar && (
            <button className="rst" onClick={() => onSetAvatar(null)}>
              Aa
            </button>
          )}
        </div>
        <button className="sh3-row" onClick={onToggleTheme}>
          <span className="ic" style={{ background: "linear-gradient(150deg,#818cf8,#4f46e5)" }}>
            {theme === "light" ? <Moon size={21} /> : <Sun size={21} />}
          </span>
          <span className="tx">
            <b>Motyw</b>
            <small>{theme === "light" ? "Jasny, dotknij by zmienić na ciemny" : "Ciemny, dotknij by zmienić na jasny"}</small>
          </span>
        </button>
        <button
          className="sh3-row"
          onClick={() => {
            onClose();
            onLogout();
          }}
        >
          <span className="ic" style={{ background: "linear-gradient(150deg,#fb7185,#e11d48)" }}>
            <LogOut size={21} />
          </span>
          <span className="tx">
            <b>Wyloguj</b>
            <small>Zakończ sesję na tym telefonie</small>
          </span>
        </button>
      </div>
    </>,
    host
  );
}

// ===================== Panel 3.0 / etap 2: komponenty zakładek (droga, orbity, osie czasu) =====================
const firstWord = (n) => String(n || "").split(" ")[0];

// Kropki na osi czasu: kolor zależy od stanu
function Rank3({ rows, driverName, star, prev, avatars, online, onOpen, hasMe }) {
  const idx = rows.findIndex((r) => sameName(r.name, driverName));
  const me = idx >= 0 ? rows[idx] : null;
  const lead = star ? { name: star.name, points: star.points, wk: true } : rows[0] ? { name: rows[0].name, points: rows[0].points, wk: false } : null;
  const pts = useCountUp3(lead ? Math.round(lead.points) : 0, 1200);
  if (!lead) return null;
  const ringPct = me && lead.points > 0 ? Math.min(1, me.points / lead.points) : 0.75;
  const av = (avatars && avatars[lead.name]) || initials(lead.name);
  const above = idx > 0 ? rows[idx - 1] : null;
  const diff = above && me ? Math.max(0, above.points - me.points + 1) : 0;
  const mv = me && prev && prev[me.name] ? prev[me.name] - (idx + 1) : null;
  const barPct = above && above.points > 0 && me ? Math.max(4, Math.min(100, Math.round((me.points / above.points) * 100))) : 100;
  const cols = ["#ffd166", "#e5e7eb", "#fdba74"];
  return (
    <>
      <OrbHero pct={ringPct} gid="oh5" c1="#ffd166" c2="#fb923c">
        <span className="rk3-crown">👑</span>
        <span className="rk3-av">{av}</span>
        <b className="rk3-nm">{firstWord(lead.name)}</b>
        <span className="rk3-k">{lead.wk ? "KIEROWCA TYGODNIA" : "LIDER RANKINGU"}</span>
        <span className="rk3-pt">{pts} pkt</span>
      </OrbHero>
      {me && (
        <div className="chips3">
          <span>Ty: {idx + 1}. miejsce</span>
          <span>{me.points} pkt</span>
          {mv != null && mv !== 0 && <span className={mv > 0 ? "up" : "dn"}>{mv > 0 ? "▲" : "▼"} {Math.abs(mv)}</span>}
        </div>
      )}
      {me && above && (
        <div className="rk3-prog">
          <div>
            Do {idx}. miejsca brakuje <b>{diff} pkt</b>
          </div>
          <div className="rk3-bar">
            <i style={{ width: barPct + "%" }} />
          </div>
        </div>
      )}
      {me && !above && rows.length > 1 && (
        <div className="rk3-prog lead">
          <Crown size={15} /> Jesteś liderem — tak trzymaj!
        </div>
      )}
      {rows.length >= 3 && (
        <>
          <div className="section-label">Top 3</div>
          <Podium3 rows={rows} driverName={driverName} avatars={avatars} online={online} onOpen={onOpen} />
        </>
      )}
      {rows.length > 3 && <div className="section-label">Pozostali</div>}
      <div className="tl3 rk3-list">
        {rows.map((r, i) => {
          if (rows.length >= 3 && i < 3) return null;
          const place = i + 1;
          const isMe = sameName(r.name, driverName);
          const m = prev && prev[r.name] ? prev[r.name] - place : null;
          return (
            <button key={r.id || r.name} type="button" className={"tn3 rk3-row" + (isMe ? " me" : "")} style={{ "--nc": cols[i] || "#8b95a7", animationDelay: Math.min(i, 12) * 55 + "ms" }} onClick={() => onOpen(r)}>
              <span className="tn3-t">
                <b>
                  {place}. {r.name}
                  {isMe ? " (Ty)" : ""}
                </b>
                <small>
                  {r.points} pkt{online(r.name) ? <i className="rk3-on" aria-label="online" /> : null}
                </small>
              </span>
              {m != null && m !== 0 && <span className={"mv " + (m > 0 ? "up" : "dn")}>{m > 0 ? "▲" : "▼"} {Math.abs(m)}</span>}
            </button>
          );
        })}
      </div>
    </>
  );
}
function BonusRow3({ r, i }) {
  const done = r.myStatus === "completed";
  const tone = done ? "#4fdc92" : r.status === "upcoming" ? "#94a3b8" : r.status === "ended" ? "#fb7185" : "#c084fc";
  return (
    <div className={"tn3 bn3" + (done ? " done" : "")} style={{ "--nc": tone, animationDelay: Math.min(i, 10) * 70 + "ms" }}>
      <span className="tn3-t">
        <b>{r.label}</b>
        <small>
          {r.status === "upcoming" ? "wkrótce" : r.status === "ended" && !done ? "zakończony" : r.myStatus === "not_completed" ? "nie zrealizowane" : r.dates || "aktywny"}
          {done ? " · ODEBRANY" : ""}
        </small>
        {r.note && <small className="bn3-n">{r.note}</small>}
      </span>
      <b className="bn3-a">+{formatPLN(r.amount)}</b>
    </div>
  );
}
function Con3({ drivers, presence, avatars }) {
  const list = (drivers || []).filter((d) => presence.isOnline(d.name)).slice(0, 5);
  const N = list.length;
  return (
    <span className="con3" aria-hidden="true">
      <span className="con3-core">👥</span>
      {list.map((d, i) => {
        const a = (i / Math.max(N, 1)) * 2 * Math.PI - Math.PI / 2;
        return (
          <i key={d.name} style={{ left: "calc(50% + " + (Math.cos(a) * 19).toFixed(1) + "px)", top: "calc(50% + " + (Math.sin(a) * 19).toFixed(1) + "px)" }}>
            {(avatars && avatars[d.name]) || initials(d.name).slice(0, 1)}
          </i>
        );
      })}
    </span>
  );
}
function Badges3({ items }) {
  return (
    <div className="bw3">
      {items.map((b, i) => (
        <div key={i} className={"bd3 " + (b.on ? "on" : "off")} style={{ animationDelay: i * 60 + "ms" }}>
          <span>{b.on ? b.ic : "🔒"}</span>
          {b.l}
        </div>
      ))}
    </div>
  );
}

// ===================== etap 3: okno decyzji o wypłacie + wykres ostatnich tygodni =====================
const CF3 = [[-70, -58, 200, "#6ee7b7"], [-48, -78, -120, "#ffd166"], [-24, -66, 260, "#fff"], [0, -84, 90, "#fb923c"], [22, -70, -200, "#6ee7b7"], [46, -80, 150, "#ffd166"], [70, -56, -90, "#fff"], [-84, -30, 120, "#fb923c"], [84, -34, -160, "#ffd166"], [-58, -92, 300, "#6ee7b7"], [60, -94, -240, "#fff"], [12, -96, 180, "#ffd166"]];
// ===================== etap 5: zadania 3.0, podium TOP 3 =====================
const dmShort3 = (iso) => (iso ? String(iso).slice(8, 10) + "." + String(iso).slice(5, 7) : "");
function TaskCard3({ row, index, todayStr }) {
  const { t, myStatus, points, daysLeft } = row;
  const state = myStatus === "completed" ? "done" : myStatus === "not_completed" ? "missed" : "waiting";
  const urgent = state === "waiting" && daysLeft != null && daysLeft <= 1;
  const frac = state === "waiting" ? taskTimeFraction(t, todayStr) : null;
  const tk = ticketsOfTask(t);
  let sub = (t.dateFrom ? dmShort3(t.dateFrom) : "?") + (t.dateTo ? " – " + dmShort3(t.dateTo) : "");
  if (state === "done") sub = "Zrealizowane · +" + points + " pkt";
  else if (state === "missed") sub = "Nie zrealizowane";
  else if (daysLeft != null) sub += " · " + (daysLeft <= 0 ? "dziś ostatni dzień!" : daysLeft === 1 ? "został 1 dzień" : daysLeft <= 14 ? "zostało " + daysLeft + " dni" : "do " + dmShort3(t.dateTo));
  else sub += " · bez terminu";
  return (
    <div className={"tc3 " + state + (urgent ? " urgent" : "") + (tk ? " tk" : "") + (t.priority ? " prio" : "")} style={{ animationDelay: Math.min(index, 8) * 70 + "ms" }}>
      <span className="tc3-ic">{state === "done" ? <CheckCircle2 size={20} /> : state === "missed" ? <XCircle size={20} /> : tk ? <Ticket size={20} /> : <Target size={20} />}</span>
      <div className="tc3-m">
        <b className="tc3-t">
          {t.priority && <Flame size={13} className="tc3-fl" />}
          {t.title}
        </b>
        <small className="tc3-s">{sub}</small>
        {frac != null && (
          <div className={"tc3-bar" + (frac > 0.8 ? " late" : "")}>
            <i style={{ transform: "scaleX(" + Math.max(0.04, Math.min(1, frac)) + ")" }} />
          </div>
        )}
      </div>
      <div className="tc3-r">
        <span className="tc3-pts">
          +{points}
          <small> pkt</small>
        </span>
        {tk > 0 && <span className="tc3-tk">🎟 +{tk}</span>}
      </div>
    </div>
  );
}
function TaskList3({ rows, todayStr }) {
  const [f, setF] = useState("all");
  const isUrgent = (r) => !r.myStatus && r.daysLeft != null && r.daysLeft <= 3;
  const nTk = rows.filter((r) => ticketsOfTask(r.t) > 0).length;
  const nUr = rows.filter(isUrgent).length;
  const list = rows.filter((r) => (f === "tk" ? ticketsOfTask(r.t) > 0 : f === "ur" ? isUrgent(r) : true));
  return (
    <>
      {(nTk > 0 || nUr > 0) && (
        <div className="tf3">
          <button type="button" className={f === "all" ? "on" : ""} onClick={() => setF("all")}>
            Wszystkie {rows.length}
          </button>
          {nTk > 0 && (
            <button type="button" className={f === "tk" ? "on" : ""} onClick={() => setF("tk")}>
              🎟 Z biletem {nTk}
            </button>
          )}
          {nUr > 0 && (
            <button type="button" className={f === "ur" ? "on" : ""} onClick={() => setF("ur")}>
              ⏰ Pilne {nUr}
            </button>
          )}
        </div>
      )}
      <div className="tl3 tl3r">
        {list.map((row, i) => (
          <div key={row.t.id} className="tlr" style={{ "--nc": row.myStatus === "completed" ? "#4fdc92" : isUrgent(row) ? "#f43f5e" : "#ffb02e" }}>
            <TaskCard3 row={row} index={i} todayStr={todayStr} />
          </div>
        ))}
        {list.length === 0 && <div className="tf3-empty">Brak zadań w tym filtrze</div>}
      </div>
    </>
  );
}
function Pod3Slot({ r, place, isMe, av, on, onOpen, delay }) {
  const pts = useCountUp3(Math.round(r.points), 1200);
  const col = place === 1 ? "g" : place === 2 ? "s" : "b";
  return (
    <button type="button" className={"pd3-s " + col + (isMe ? " me" : "")} style={{ animationDelay: delay + "ms" }} onClick={() => onOpen(r)}>
      {place === 1 && <span className="pd3-crown">👑</span>}
      <span className="pd3-av">
        {av}
        {on && <i className="pd3-on" />}
      </span>
      <b className="pd3-n">
        {firstWord(r.name)}
        {isMe ? " (Ty)" : ""}
      </b>
      <span className="pd3-p">{pts} pkt</span>
      <span className="pd3-base">
        <em>{place}</em>
        <i className="pd3-sh" />
      </span>
    </button>
  );
}
function Podium3({ rows, driverName, avatars, online, onOpen }) {
  const meIdx = rows.findIndex((r) => sameName(r.name, driverName));
  const order = [[1, 150], [0, 0], [2, 300]];
  return (
    <div className="pd3">
      <span className="pd3-spot" aria-hidden="true" />
      <i className="pd3-sp a" />
      <i className="pd3-sp b" />
      <i className="pd3-sp c" />
      <i className="pd3-sp d" />
      <div className="pd3-row">
        {order.map(([i, d]) =>
          rows[i] ? (
            <Pod3Slot key={rows[i].id || rows[i].name} r={rows[i]} place={i + 1} isMe={sameName(rows[i].name, driverName)} av={(avatars && avatars[rows[i].name]) || initials(rows[i].name)} on={online(rows[i].name)} onOpen={onOpen} delay={d} />
          ) : null
        )}
      </div>
      {meIdx > -1 && meIdx < 3 && <div className="pd3-me">🎉 Jesteś na podium!</div>}
    </div>
  );
}

function PayoutActions3({ amount, onChoose }) {
  const [boom, setBoom] = useState(0);
  const busy = useRef(false);
  const a = splitPLN(amount);
  const pick = (act) => {
    if (busy.current) return;
    try {
      if (navigator.vibrate) navigator.vibrate(act === "approved" ? [25, 40, 25] : 15);
    } catch (e) {
      /* nic */
    }
    if (act === "approved") {
      busy.current = true;
      setBoom((n) => n + 1);
      setTimeout(() => {
        busy.current = false;
        onChoose(act);
      }, 560);
    } else {
      onChoose(act);
    }
  };
  return (
    <div className="pa3">
      <span className="pa3-glow" aria-hidden="true" />
      <div className="pa3-h">
        <span>
          <Sparkles size={14} /> Co robimy z tą wypłatą?
        </span>
        <b>
          {a.neg ? "−" : ""}
          {a.i}
          <i>,{a.d}</i> zł
        </b>
      </div>
      <div className="pa3-b">
        <button type="button" className="pa3-t ok" onClick={() => pick("approved")}>
          <span className="pa3-ic">
            <CheckCircle2 size={24} />
          </span>
          <span className="pa3-tx">
            <b>Zaakceptuj wypłatę</b>
            <small>Potwierdzam kwotę z rozliczenia</small>
          </span>
          <i className="pa3-sh" aria-hidden="true" />
          {boom > 0 && (
            <span key={boom} className="pa3-cf" aria-hidden="true">
              {CF3.map((c, i) => (
                <i key={i} style={{ "--x": c[0] + "px", "--y": c[1] + "px", "--r": c[2] + "deg", background: c[3] }} />
              ))}
            </span>
          )}
        </button>
        <div className="pa3-r">
          <button type="button" className="pa3-t no" onClick={() => pick("rejected")}>
            <XCircle size={20} />
            <span className="pa3-tx">
              <b>Odrzuć</b>
              <small>coś się nie zgadza</small>
            </span>
          </button>
          <button type="button" className="pa3-t nx" aria-label="Przenieś na kolejny tydzień" onClick={() => pick("next_week")}>
            <CalendarClock size={20} />
            <span className="pa3-tx">
              <b>Przenieś</b>
              <small>na kolejny tydzień</small>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

function PayoutTimeline({ record, decision, ui3 }) {
  const [today, setToday] = useState(warsawDay);
  const [on, setOn] = useState(false);
  const [px, setPx] = useState(0);
  const lineRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => {
      if (lineRef.current) setPx(lineRef.current.offsetWidth); // jednorazowy pomiar: samochód jedzie transformacją (bez przeliczania układu)
      setOn(true);
    }, 120);
    const iv = setInterval(() => setToday((d) => (warsawDay() !== d ? warsawDay() : d)), 60000); // przełączenie po północy bez odświeżania appki
    return () => {
      clearTimeout(t);
      clearInterval(iv);
    };
  }, []);
  const st = record.status;
  const paid = st === "paid" || st === "settled" || st === "transferred" || !!(decision && decision.paid);
  const closed = paid || st === "debt";
  const weekOver = !!record.periodTo && today > record.periodTo;
  const step = closed || st === "inprogress" ? 2 : weekOver ? 1 : 0;
  const total = record.periodFrom && record.periodTo ? Math.max(1, dayDiff(record.periodFrom, record.periodTo) + 1) : 7;
  const idx = record.periodFrom ? Math.max(0, Math.min(total - 1, dayDiff(record.periodFrom, today))) : 0;
  // 0..1 wzdłuż osi: pierwszy odcinek = postęp tygodnia, kolejne stacje przy 0,5 i 1
  const p = step === 0 ? ((idx + 0.5) / total) * 0.5 : step === 1 ? 0.5 : 1;
  const decided = decision && decision.action;
  const accepted = decided === "approved";
  let l2 = "Wypłata";
  let title;
  let sub;
  if (step === 0) {
    const left = record.periodTo ? dayDiff(today, record.periodTo) : null;
    title = "Tydzień trwa";
    sub = left == null ? "Tydzień w toku." : left === 0 ? "Dziś ostatni dzień tygodnia." : `Do końca tygodnia: ${left} ${left === 1 ? "dzień" : "dni"}.`;
  } else if (step === 1) {
    title = "Rozliczanie";
    sub = "Tydzień zakończony — szef przygotowuje Twoje rozliczenie.";
  } else if (closed) {
    l2 = st === "transferred" ? "Przeniesione" : st === "debt" ? "Zaległość" : st === "settled" ? "Spłacone" : "Wypłacone";
    title = l2;
    sub = st === "debt" ? "Rozliczenie z zaległością — szczegóły w zakładce Zadłużenie." : st === "transferred" ? "Kwota przeniesiona na kolejny tydzień." : "Wypłata za ten tydzień została wysłana.";
  } else {
    l2 = decided ? (accepted ? "Czeka na wypłatę" : "Do sprawdzenia") : "Do akceptacji";
    title = l2;
    sub = !decided ? "Rozliczenie gotowe — wybierz poniżej, co robimy z wypłatą." : accepted ? "Zaakceptowałeś — czekamy na wypłatę." : "Szef sprawdza Twoją decyzję.";
  }
  if (ui3) {
    const pos = [0, 50, 100];
    const labs = ["Tydzień", step === 1 ? "Rozliczanie" : "Rozliczenie", l2];
    const fin = (i) => i < step || (i === 2 && step === 2 && closed);
    return (
      <div className={`rd3 s${step}${on ? " on" : ""}${st === "debt" ? " debt" : ""}`}>
        <div className="rd3-road">
          <div className="rd3-ln" aria-hidden="true" />
          <div className="rd3-dn" aria-hidden="true" style={{ width: on ? p * 100 + "%" : "0%" }} />
          {pos.map((x, i) => (
            <span key={i} className={"rd3-st" + (fin(i) ? " done" : i === step ? " cur" : "")} style={{ left: x + "%" }}>
              
            </span>
          ))}
          <span className="rd3-car" style={{ left: on ? p * 100 + "%" : "0%" }} aria-hidden="true">
            <i>🚗</i>
          </span>
          {labs.map((l, i) => (
            <span key={i} className={"rd3-lb" + (i === step ? " cur" : "")} style={{ left: pos[i] + "%" }}>
              {l}
            </span>
          ))}
        </div>
      </div>
    );
  }
  const labels = ["Tydzień trwa", "Rozliczanie", l2];
  const icons = [<CalendarClock size={21} />, <Receipt size={21} />, <Wallet size={21} />];
  return (
    <div className={`tl tl-s${step} ${st === "debt" ? "tl-debt" : ""} ${on ? "on" : ""}`}>
      <div className="tl-glow" aria-hidden="true" />
      <div className="tl-track" style={{ "--p": p }}>
        <div className="tl-line" aria-hidden="true" ref={lineRef}>
          <i />
          <span className="tl-car" style={{ transform: `translateX(${on ? Math.round(px * p) : 0}px)` }}>
            <Car size={22} />
          </span>
        </div>
        {labels.map((l, i) => (
          <div key={i} className={`tl-st ${i < step ? "done" : i === step ? "cur" : ""}`} style={{ "--k": i }}>
            <span className="tl-node">
              <b>{i < step || (i === 2 && step === 2 && closed) ? <Check size={22} strokeWidth={3.2} /> : icons[i]}</b>
            </span>
            <small>{l}</small>
          </div>
        ))}
      </div>
      {step === 0 && record.periodFrom && (
        <div className="tl-days" aria-hidden="true">
          {Array.from({ length: total }, (_, d) => (
            <span key={d} className={d < idx ? "past" : d === idx ? "now" : ""}>
              {TL_DAYS[d % 7]}
            </span>
          ))}
        </div>
      )}
      <div className="tl-hint">
        <b>{title}</b>
        <span>{sub}</span>
      </div>
    </div>
  );
}



// Karta rozliczenia na osi czasu: krótka data + status + kwota, po kliknięciu rozwija się skład wypłaty
function TicketRow({ record, index = 0, prev, onAsk }) {
  const [open, setOpen] = useState(false);
  const badge = statusBadge(record.status);
  const tone = badge.cls === "badge-green" ? "green" : badge.cls === "badge-red" ? "red" : "amber";
  const vatRy = (record.ryczaltAmount || 0) + (record.vatAmount || 0);
  const pot = (record.carDeduction || 0) + vatRy;
  const rows = [
    ["Utarg", record.appEarnings || 0, "", true],
    ["Ryczałt i VAT", vatRy, "cut", vatRy !== 0],
    ["Odliczenie za auto", record.carDeduction || 0, "cut", (record.carDeduction || 0) !== 0],
    ["Gotówka", record.cashEarnings || 0, "", (record.cashEarnings || 0) !== 0],
    ["Rekompensaty", record.compensations || 0, "sg", (record.compensations || 0) !== 0],
    ["ZUS", record.zus || 0, "cut", (record.zus || 0) !== 0],
  ].filter((c) => c[3]);
  const fmtRow = (v, kind) => (kind === "cut" ? "− " + formatPLN(Math.abs(v)) : kind === "sg" && v > 0 ? "+ " + formatPLN(v) : formatPLN(v));
  // pasek składu: utarg / dodatki / potrącenia
  const sUtarg = Math.max(0, record.appEarnings || 0);
  const sAdd = Math.max(0, record.cashEarnings || 0) + Math.max(0, record.compensations || 0);
  const sCut = Math.abs(pot) + Math.abs(record.zus || 0) + Math.abs(Math.min(0, record.compensations || 0));
  const sTot = sUtarg + sAdd + sCut;
  const a = splitPLN(record.payout);
  const delta = prev && (prev.payout || 0) > 0 && (record.payout || 0) > 0 ? Math.round(((record.payout - prev.payout) / prev.payout) * 100) : null;
  return (
    <div className={`tk4 ${open ? "open" : ""}`} data-tone={tone} style={{ animationDelay: `${Math.min(index, 10) * 55}ms` }}>
      <button className="tk4-h" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="tk4-d">
          <b>{shortPeriod(record)}</b>
          <small className={"tk4-st " + tone}>{badge.text}</small>
        </span>
        <span className={`tk4-amt ${(record.payout || 0) < 0 ? "neg" : ""}`}>
          {a.neg && "−"}
          {a.i}
          <i>,{a.d}</i>
          <em>zł</em>
        </span>
        <ChevronDown size={16} className="tk4-chev" />
      </button>
      <div className="tk4-body">
        <div className="tk4-in">
          {sTot > 0 && (
            <div className="tk4-bar" aria-hidden="true">
              <i style={{ width: (sUtarg / sTot) * 100 + "%", background: "linear-gradient(90deg,#ffd166,#ff9a1f)" }} />
              {sAdd > 0 && <i style={{ width: (sAdd / sTot) * 100 + "%", background: "#6ee7b7" }} />}
              {sCut > 0 && <i style={{ width: (sCut / sTot) * 100 + "%", background: "#fb7185" }} />}
            </div>
          )}
          <div className="tk5-rows">
            {rows.map(([label, v, kind]) => (
              <div key={label} className={"tk5-r" + (kind === "cut" || v < 0 ? " neg" : "")}>
                <span>{label}</span>
                <b>{fmtRow(v, kind)}</b>
              </div>
            ))}
            <div className="tk5-r tot">
              <span>Do wypłaty</span>
              <b className={(record.payout || 0) < 0 ? "neg" : ""}>{formatPLN(record.payout || 0)}</b>
            </div>
          </div>
          {delta != null && delta !== 0 && (
            <div className="tk5-ch">
              <span className={delta > 0 ? "up" : "dn"}>
                {delta > 0 ? "▲ +" : "▼ "}
                {delta}% vs poprzedni tydzień
              </span>
            </div>
          )}
          {record.notatka && (
            <div className="tk3-note">
              <StickyNote size={12} />
              <span>{record.notatka}</span>
            </div>
          )}
          {onAsk && (
            <button type="button" className="tk5-btn" onClick={onAsk}>
              <MessageCircle size={15} /> Zapytaj szefa
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Screens                                                                 */
/* ---------------------------------------------------------------------- */

function HomeScreen({ onDriver, onAdmin, earnings, drivers, news }) {
  // No visible admin entry point — this screen is driver-only.
  // Administrators open the panel via 5 quick taps on the truck icon.
  const tapTimes = useRef([]);
  function handleHeroTap() {
    const now = Date.now();
    tapTimes.current = [...tapTimes.current.filter((t) => now - t < 2200), now];
    if (tapTimes.current.length >= 5) {
      tapTimes.current = [];
      onAdmin();
    }
  }
  return (
    <div className="screen home">
      <div className="home-blob home-blob-1" aria-hidden="true" />
      <div className="home-blob home-blob-2" aria-hidden="true" />
      <div className="home-bg-glow" aria-hidden="true" />

      <div className="home-hero home-anim" style={{ animationDelay: "0ms" }} onClick={handleHeroTap}>
        <div className="hero-icon-ring">
          <img src={PATEX_ICON} alt="PateX" className="hero-icon-img" />
        </div>
        <div className="hero-eyebrow">Rozliczenia floty</div>
        <div className="brand-name">
          Pate<span className="brand-accent">X</span>
        </div>
        <div className="road-strip" aria-hidden="true" />
        <div className="brand-tagline">Partner Flotowy</div>
      </div>

      <div className="partners-strip home-anim" style={{ animationDelay: "70ms" }}>
        <div className="partner-logo-box">
          <img src="/logos/bolt.png" alt="Bolt" className="partner-logo" />
        </div>
        <div className="partner-logo-box">
          <img src="/logos/uber.png" alt="Uber" className="partner-logo" />
        </div>
        <div className="partner-logo-box">
          <img src="/logos/freenow.png" alt="FreeNow" className="partner-logo" />
        </div>
      </div>

      {news && news.length > 0 && (
        <div className="news-widget home-anim" style={{ animationDelay: "140ms" }}>
          <div className="news-widget-header">
            <Megaphone size={14} /> Powiadomienia
          </div>
          <div className="news-widget-list">
            {news.slice(0, 3).map((n) => (
              <div className="news-item" key={n.id}>
                <div className="news-item-text">{n.text}</div>
                <div className="news-item-date">
                  {new Date(n.createdAt).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button className="big-card home-anim" style={{ animationDelay: "210ms" }} onClick={onDriver}>
        <span className="big-card-shine" aria-hidden="true" />
        <span className="big-card-icon">
          <KeyRound size={20} />
        </span>
        <div className="big-card-text">
          <div className="big-card-title">Jestem kierowcą</div>
          <div className="big-card-sub">Wpisz swój kod PIN</div>
        </div>
      </button>
    </div>
  );
}


function PinScreen({ pin, setPin, error, onSubmit, onBack }) {
  const [show, setShow] = useState(false);
  const [shake, setShake] = useState(false);
  const maxLen = 8;

  useEffect(() => {
    if (!error) return;
    setShake(true);
    const t = setTimeout(() => setShake(false), 420);
    return () => clearTimeout(t);
  }, [error]);

  function press(d) {
    if (pin.length < maxLen) setPin(pin + d);
  }
  function backspace() {
    setPin(pin.slice(0, -1));
  }

  return (
    <div className="screen">
      <TopBar title="Logowanie kierowcy" onBack={onBack} />
      <div className={`pin-wrap ${shake ? "shake" : ""}`}>
        <div className="pin-display">
          {pin.length === 0 && <span className="pin-placeholder">Wpisz kod PIN</span>}
          {pin.length > 0 && (show ? pin : "•".repeat(pin.length))}
        </div>
        <button className="eye-btn" onClick={() => setShow((s) => !s)} aria-label="Pokaż/ukryj PIN">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error && (
        <div className="error-line">
          <AlertCircle size={14} />
          {error}
        </div>
      )}
      <div className="keypad">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} className="key" onClick={() => press(d)}>
            {d}
          </button>
        ))}
        <button className="key key-action" onClick={backspace} aria-label="Usuń cyfrę">
          <Delete size={18} />
        </button>
        <button className="key" onClick={() => press("0")}>
          0
        </button>
        <button className="key key-action key-confirm" onClick={onSubmit} aria-label="Zatwierdź">
          <Check size={18} />
        </button>
      </div>
    </div>
  );
}

function AdminLoginScreen({ onSuccess, onBack }) {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (!error) return;
    setShake(true);
    const t = setTimeout(() => setShake(false), 420);
    return () => clearTimeout(t);
  }, [error]);

  function submit() {
    if (password === ADMIN_PASSWORD) {
      onSuccess();
    } else {
      setError("Nieprawidłowe hasło.");
      setPassword("");
    }
  }

  return (
    <div className="screen">
      <TopBar title="Panel administratora" onBack={onBack} />
      <div className="admin-lock-icon">
        <Lock size={22} />
      </div>
      <div className={`pin-wrap admin-pw-wrap ${shake ? "shake" : ""}`}>
        <input
          type={show ? "text" : "password"}
          className="admin-pw-input"
          placeholder="Wpisz hasło"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          autoFocus
        />
        <button className="eye-btn" onClick={() => setShow((s) => !s)} aria-label="Pokaż/ukryj hasło">
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error && (
        <div className="error-line">
          <AlertCircle size={14} />
          {error}
        </div>
      )}
      <button className="btn btn-primary" style={{ marginTop: 18 }} onClick={submit}>
        Zatwierdź
      </button>
    </div>
  );
}

// Progi utargu, przy których wyskakuje "ludzik z gratulacjami" — im
// wyższy próg, tym mocniejsze emocje. Sortowane malejąco, żeby
// ".find()" zawsze złapał NAJWYŻSZY przebity próg, nie pierwszy z brzegu.
const EARNINGS_CELEBRATION_TIERS = [
  {
    tier: 4,
    threshold: 3000,
    emoji: "🤯",
    title: "W SZOKU!!!",
    subtitle: "3000 zł?! To już nie utarg, to legenda tego tygodnia 👑",
    accent: "#a78bfa",
    confettiCount: 40,
    shake: true,
  },
  {
    tier: 3,
    threshold: 2000,
    emoji: "🚀",
    title: "NIESAMOWITE!",
    subtitle: "2000 zł — rakieta poszła w kosmos, tak trzymaj!",
    accent: "#ff9d2e",
    confettiCount: 28,
    shake: false,
  },
  {
    tier: 2,
    threshold: 1500,
    emoji: "🔥",
    title: "Wow, w to mi graj!",
    subtitle: "1500 zł na koncie — jedziesz na pełnych obrotach.",
    accent: "#ff6b6b",
    confettiCount: 18,
    shake: false,
  },
  {
    tier: 1,
    threshold: 1000,
    emoji: "💪",
    title: "Świetna robota!",
    subtitle: "Przebiłeś 1000 zł utargu w tym tygodniu.",
    accent: "#4ade90",
    confettiCount: 10,
    shake: false,
  },
];

// Sam "ludzik" — animowana emotka w pulsującej bańce, z konfetti i
// komunikatem, którego intensywność rośnie razem z progiem.
// Prosty, rozpoznawalny "ludzik" (SVG) zamiast emotki — czarne włosy, duże
// brązowe oczy, lekki 3-4-dniowy zarost. Twarz jest ta sama na każdym
// poziomie, zmienia się tylko mina (brwi, oczy, usta) — im wyższy próg,
// tym większe emocje.
const CELEB_FACE_BY_TIER = {
  1: {
    // Zadowolony, spokojny uśmiech — brwi lekko uniesione.
    eyebrowL: "M38,47 Q45,42 53,46",
    eyebrowR: "M67,46 Q75,42 82,47",
    eyeRy: 8,
    mouth: "M46,86 Q60,96 74,86 Q60,92 46,86 Z",
  },
  2: {
    // Szeroki, wesoły uśmiech — brwi mocniej uniesione.
    eyebrowL: "M37,45 Q45,39 54,44",
    eyebrowR: "M66,44 Q75,39 83,45",
    eyeRy: 9,
    mouth: "M44,84 Q60,102 76,84 Q60,98 44,84 Z",
  },
  3: {
    // Głośny okrzyk radości — usta szeroko otwarte, brwi wysoko.
    eyebrowL: "M36,44 Q45,36 55,42",
    eyebrowR: "M65,42 Q75,36 84,44",
    eyeRy: 10,
    mouth: "M43,83 Q60,108 77,83 Q60,86 43,83 Z",
  },
  4: {
    // W szoku — oczy i usta szeroko otwarte, brwi uniesione maksymalnie.
    eyebrowL: "M35,42 Q45,32 56,40",
    eyebrowR: "M64,40 Q75,32 85,42",
    eyeRy: 12,
    mouth: "M52,84 a10,12 0 1,0 16,0 a10,12 0 1,0 -16,0 Z",
  },
};

function CelebCharacterSVG({ tier, accent }) {
  const face = CELEB_FACE_BY_TIER[tier] || CELEB_FACE_BY_TIER[1];
  return (
    <svg viewBox="0 0 120 150" width="104" height="130" className="earn-celeb-face-svg">
      <defs>
        <pattern id={`stubble-${tier}`} width="4" height="4" patternUnits="userSpaceOnUse">
          <circle cx="1.2" cy="1.2" r="0.65" fill="#4a3a2e" opacity="0.55" />
          <circle cx="3" cy="3" r="0.5" fill="#4a3a2e" opacity="0.4" />
        </pattern>
      </defs>
      {/* Ramiona / koszulka w kolorze progu */}
      <path d="M22,150 Q60,124 98,150 L98,150 L22,150 Z" fill={accent} />
      {/* Szyja */}
      <rect x="50" y="98" width="20" height="18" rx="6" fill="#dba679" />
      {/* Uszy */}
      <ellipse cx="29" cy="63" rx="5" ry="8" fill="#dba679" />
      <ellipse cx="91" cy="63" rx="5" ry="8" fill="#dba679" />
      {/* Twarz */}
      <ellipse cx="60" cy="62" rx="31" ry="36" fill="#dba679" />
      {/* Zarost — dolna część twarzy + wąsik, POD spodem, usta i tak są rysowane później na wierzchu */}
      <path
        d="M31,68 Q30,92 45,105 Q60,112 75,105 Q90,92 89,68 Q89,84 60,90 Q31,84 31,68 Z"
        fill={`url(#stubble-${tier})`}
      />
      {/* Włosy — krótkie, czarne, lekko potargane */}
      <path
        d="M27,55 Q22,20 60,16 Q98,20 93,55 Q93,42 85,44 Q88,28 60,26 Q32,28 35,44 Q27,42 27,55 Z"
        fill="#161616"
      />
      <path d="M27,55 Q25,46 30,40 Q28,50 33,58 Q29,58 27,55 Z" fill="#161616" />
      <path d="M93,55 Q95,46 90,40 Q92,50 87,58 Q91,58 93,55 Z" fill="#161616" />
      {/* Brwi */}
      <path d={face.eyebrowL} stroke="#161616" strokeWidth="3.4" strokeLinecap="round" fill="none" />
      <path d={face.eyebrowR} stroke="#161616" strokeWidth="3.4" strokeLinecap="round" fill="none" />
      {/* Oczy — duże, brązowe */}
      <ellipse cx="46" cy="60" rx="8" ry={face.eyeRy} fill="#fff" />
      <ellipse cx="74" cy="60" rx="8" ry={face.eyeRy} fill="#fff" />
      <circle cx="46" cy="61" r="5" fill="#5b3a24" />
      <circle cx="74" cy="61" r="5" fill="#5b3a24" />
      <circle cx="46" cy="61" r="2.4" fill="#161616" />
      <circle cx="74" cy="61" r="2.4" fill="#161616" />
      <circle cx="44" cy="58.5" r="1.3" fill="#fff" />
      <circle cx="72" cy="58.5" r="1.3" fill="#fff" />
      {/* Nos */}
      <path d="M59,64 Q56,72 59,75 Q61,76 63,74" stroke="#b8875c" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Usta */}
      <path d={face.mouth} fill="#7a3b3b" />
    </svg>
  );
}

function EarningsCelebration({ tier, onClose }) {
  const config = EARNINGS_CELEBRATION_TIERS.find((t) => t.tier === tier);
  useEffect(() => {
    const timer = setTimeout(onClose, 5200);
    return () => clearTimeout(timer);
  }, [onClose]);
  if (!config) return null;
  return createPortal(
    <div className="earn-celeb-overlay" onClick={onClose}>
      <div className={`earn-celeb-card ${config.shake ? "earn-celeb-shake" : ""}`} style={{ "--celeb-accent": config.accent }}>
        <div className="earn-celeb-confetti">
          {Array.from({ length: config.confettiCount }).map((_, i) => (
            <span
              key={i}
              className={`earn-celeb-confetti-piece confetti-${i % 5}`}
              style={{ left: `${(i * 37) % 100}%`, animationDelay: `${(i % 9) * 0.09}s` }}
            />
          ))}
        </div>
        <div className="earn-celeb-mascot">
          <CelebCharacterSVG tier={tier} accent={config.accent} />
        </div>
        <div className="earn-celeb-title">{config.title}</div>
        <div className="earn-celeb-subtitle">{config.subtitle}</div>
        <button className="earn-celeb-close" onClick={onClose}>
          Dzięki! 🙌
        </button>
      </div>
    </div>,
    document.body
  );
}

// Pojedyncze miejsce na podium (1-3) — osobny komponent (nie inline w
// .map()), żeby móc bezpiecznie animować liczbę punktów "od zera w górę"
// (useCountUp to hak Reacta, a haków nie wolno wywoływać bezpośrednio
// wewnątrz .map()). Miejsce 1 dostaje dodatkowo pulsującą poświatę za
// medalem — żeby zwycięzca tygodnia naprawdę "świecił".
// ====================== Ranking i zadania 2.0 — komponenty ======================

// Pierścień postępu (SVG) — wypełnia się płynnie po wejściu na ekran.
function ProgressRing({ value, size = 88, stroke = 7, tone = "amber", children }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setShown(Math.max(0, Math.min(100, value || 0))), 150);
    return () => clearTimeout(t);
  }, [value]);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className={`rk2-ring rk2-ring-${tone}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="rk2-ring-track" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          className="rk2-ring-fill"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - shown / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="rk2-ring-inner">{children}</div>
    </div>
  );
}

// Karta "Twoja pozycja" — miejsce, punkty i ile brakuje do miejsca wyżej.
function RankHero({ rows, driverName }) {
  const idx = rows.findIndex((r) => sameName(r.name, driverName));
  const me = idx >= 0 ? rows[idx] : null;
  const animatedPoints = useCountUp(me ? me.points : 0, 1300);
  if (!me) return null;
  const place = idx + 1;
  const above = idx > 0 ? rows[idx - 1] : null;
  const ringPct = above && above.points > 0 ? Math.round((me.points / above.points) * 100) : 100;
  const diff = above ? Math.max(0, above.points - me.points + 1) : 0;
  const tier = place <= 3 ? "gold" : place <= 6 ? "silver" : "bronze";
  const tierText = place <= 3 ? "Strefa bonusu 100–200 zł" : place <= 6 ? "Strefa bonusu 50–100 zł" : "Strefa bonusu 0–50 zł";
  const hint =
    rows.length < 2
      ? "Jesteś pierwszy w rankingu"
      : place === 1
        ? "Jesteś liderem — tak trzymaj!"
        : `Do miejsca #${place - 1} brakuje ${diff} pkt`;
  return (
    <div className={`rk2-hero rk2-hero-${tier}`}>
      <ProgressRing value={ringPct} size={96} stroke={8} tone={place === 1 ? "green" : "amber"}>
        <div className="rk2-hero-place">
          <small>#</small>
          {place}
        </div>
      </ProgressRing>
      <div className="rk2-hero-info">
        <div className="rk2-hero-label">Twoja pozycja · {rows.length} kierowców</div>
        <div className="rk2-hero-points">
          {animatedPoints}
          <span> pkt</span>
        </div>
        <div className="rk2-hero-hint">
          {place === 1 ? <Crown size={13} /> : <Zap size={13} />} {hint}
        </div>
        <div className={`rk2-tier tier-${tier === "gold" ? "gold" : tier}`}>{tierText}</div>
      </div>
    </div>
  );
}

// Miejsca 1–3 na podium (reflektor, korona, rosnące podesty).
function WeekPlaque() {
  return (
    <span className="rk2-wkp" title="Kierowca tygodnia">
      <i className="rk2-wkp-ic">
        <Crown size={13} strokeWidth={2.6} />
      </i>
      <span className="rk2-wkp-tx">
        <b>Kierowca</b>
        <em>tygodnia</em>
      </span>
      <u className="sp4 rk2-wkp-sp" aria-hidden="true" />
    </span>
  );
}
function MoveChip({ move }) {
  if (move == null) return null;
  return move > 0 ? <span className="rk2-mv up">▲{move}</span> : move < 0 ? <span className="rk2-mv down">▼{-move}</span> : <span className="rk2-mv eq">–</span>;
}
function PodiumSlot({ r, place, isMe, avatars, bonusRange, onOpenProfile, online, move, star }) {
  const animatedPoints = useCountUp(r.points, 1400);
  return (
    <div
      className={`rk2-slot rk2-place-${place} ${isMe ? "rk2-me" : ""}`}
      onClick={() => onOpenProfile(r)}
      role="button"
      tabIndex={0}
    >
      <div className={`rk2-avatar-ring ${online ? "is-online" : ""}`}>
        {place === 1 && <Crown size={30} className="rk2-crown" />}
        <div className="rk2-avatar">{(avatars && avatars[r.name]) || initials(r.name)}</div>
        <span className="rk2-place-chip">{place}</span>
        {online && <OnlineDot />}
      </div>
      <div className="rk2-slot-name" title={r.name}>
        {r.name}
      </div>
      {move != null && (
        <div className="rk2-slot-mv">
          <MoveChip move={move} />
        </div>
      )}
      {star && (
        <div className="rk2-slot-wk">
          <WeekPlaque />
        </div>
      )}
      {isMe && <span className="rk2-me-tag">Ty</span>}
      <div className="rk2-slot-points">
        {animatedPoints}
        <small> pkt</small>
      </div>
      <div className="rk2-slot-bonus">{bonusRange}</div>
      <div className="rk2-pedestal">
        <span className="rk2-pedestal-num">{place}</span>
      </div>
    </div>
  );
}

// Miejsca 4+ — karta z paskiem postępu względem lidera.
function RankingListRow({ r, place, isMe, avatars, barPct, animDelay, pointsToNextTier, onOpenProfile, online, move, star }) {
  const animatedPoints = useCountUp(r.points, 1000);
  const rankingTier = place <= 6 ? "silver" : "bronze";
  const rankingTierRange = place <= 6 ? "50–100 zł" : "0–50 zł";
  return (
    <div
      className={`rk2-row rk2-row-${rankingTier} ${isMe ? "rk2-row-me" : ""} ${star ? "rk2-row-star" : ""}`}
      style={{ animationDelay: `${animDelay}ms` }}
      onClick={() => onOpenProfile(r)}
      role="button"
      tabIndex={0}
    >
      <div className="rk2-row-rank">{place}</div>
      <div className={`rk2-row-avatar ${online ? "is-online" : ""}`}>
        {(avatars && avatars[r.name]) || initials(r.name)}
        {online && <OnlineDot />}
      </div>
      <div className="rk2-row-main">
        <div className="rk2-row-name">
          {r.name}
          {isMe && <span className="rk2-me-tag">Ty</span>}
          <MoveChip move={move} />
        </div>
        {star && (
          <div className="rk2-row-wk">
            <WeekPlaque />
          </div>
        )}
        <div className="rk2-row-bar">
          <div className="rk2-row-bar-fill" style={{ width: `${barPct}%`, animationDelay: `${animDelay + 200}ms` }} />
        </div>
        <div className="rk2-row-meta">
          {online && (
            <span className="pr-chip">
              <i /> online
            </span>
          )}
          {r.car && (
            <span>
              <Car size={10} /> {r.car}
            </span>
          )}
          {isMe && pointsToNextTier != null && pointsToNextTier > 0 && (
            <span className="rk2-row-hint">
              <Zap size={10} /> {pointsToNextTier} pkt do wyższego progu
            </span>
          )}
        </div>
      </div>
      <div className="rk2-row-pts">
        <div className="rk2-row-pts-val">{animatedPoints}</div>
        <div className="rk2-row-pts-lbl">pkt</div>
        <div className={`rk2-tier tier-${rankingTier}`}>{rankingTierRange}</div>
      </div>
    </div>
  );
}

// ---------------------------- Zadania ----------------------------

// Ile dni zostało do końca zadania (0 = dziś ostatni dzień, ujemne = po terminie, null = bez terminu).
function taskDaysLeft(t, todayStr) {
  if (!t.dateTo) return null;
  const a = Date.parse(`${todayStr}T00:00:00Z`);
  const b = Date.parse(`${t.dateTo}T00:00:00Z`);
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
}
function taskTimeFraction(t, todayStr) {
  if (!t.dateFrom || !t.dateTo) return null;
  const a = Date.parse(`${t.dateFrom}T00:00:00Z`);
  const b = Date.parse(`${t.dateTo}T00:00:00Z`);
  const n = Date.parse(`${todayStr}T00:00:00Z`);
  if (isNaN(a) || isNaN(b) || isNaN(n) || b <= a) return null;
  return Math.max(0.04, Math.min(1, (n - a) / (b - a)));
}

// Karta aktywnego zadania.
function TaskCard({ row, index, todayStr }) {
  const { t, myStatus, points, daysLeft } = row;
  const state = myStatus === "completed" ? "done" : myStatus === "not_completed" ? "missed" : "waiting";
  const urgent = state === "waiting" && daysLeft != null && daysLeft <= 1;
  const frac = state === "waiting" ? taskTimeFraction(t, todayStr) : null;
  let countdown = null;
  if (state === "waiting" && daysLeft != null) {
    countdown =
      daysLeft <= 0
        ? "Dziś ostatni dzień!"
        : daysLeft === 1
          ? "Został 1 dzień"
          : daysLeft <= 14
            ? `Zostało ${daysLeft} dni`
            : `Do ${t.dateTo}`;
  } else if (state === "waiting") {
    countdown = "Bez terminu";
  }
  if (ticketsOfTask(t) > 0) {
    return <GoldenTicket t={t} state={state} points={points} urgent={urgent} frac={frac} countdown={countdown} index={index} />;
  }
  return (
    <div
      className={`tk2-card tk2-${state} ${t.priority ? "tk2-priority" : ""} ${urgent ? "tk2-urgent" : ""} ${ticketsOfTask(t) ? "tk2-ms" : ""}`}
      style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
    >
      <div className="tk2-card-top">
        <div className={`tk2-badge tk2-badge-${state}`}>
          {state === "done" ? <CheckCircle2 size={19} /> : state === "missed" ? <XCircle size={19} /> : <Target size={19} />}
        </div>
        <div className="tk2-main">
          <div className="tk2-title">{t.title}</div>
          <div className="tk2-sub">
            {t.dateFrom || "?"}
            {t.dateTo ? ` – ${t.dateTo}` : ""}
          </div>
        </div>
        <div className="tk2-points">
          <Zap size={11} />+{points}
          <small> pkt</small>
        </div>
      </div>
      {frac != null && (
        <div className={`tk2-time ${frac > 0.8 ? "late" : ""}`}>
          <i style={{ width: `${Math.round(frac * 100)}%` }} />
        </div>
      )}
      <div className="tk2-footer">
        {state === "done" ? (
          <span className="tk2-result tk2-result-done">
            <CheckCircle2 size={13} /> Zrealizowane · +{points} pkt
          </span>
        ) : state === "missed" ? (
          <span className="tk2-result tk2-result-missed">
            <XCircle size={13} /> Nie zrealizowane
          </span>
        ) : (
          <span className="tk2-result tk2-result-waiting">
            <Clock size={13} /> Czeka na ocenę
          </span>
        )}
        {countdown && <span className={`tk2-chip ${urgent ? "urgent" : ""}`}>{countdown}</span>}
        {t.priority && (
          <span className="tk2-prio">
            <Flame size={11} /> Priorytet
          </span>
        )}
      </div>
    </div>
  );
}

// Zwięzły wiersz zadania w archiwum.
function TaskArchiveRow({ row }) {
  const { t, myStatus, points } = row;
  const state = myStatus === "completed" ? "done" : myStatus === "not_completed" ? "missed" : "none";
  return (
    <div className={`tk2-arow tk2-arow-${state}`}>
      <span className={`tk2-arow-ic tk2-arow-ic-${state}`}>
        {state === "done" ? <CheckCircle2 size={14} /> : state === "missed" ? <XCircle size={14} /> : <Minus size={14} />}
      </span>
      <div className="tk2-arow-main">
        <div className="tk2-arow-title">
          {t.title}
          {ticketsOfTask(t) > 0 && <span className="gt-mini-tag">🎟 +{ticketsOfTask(t)}</span>}
        </div>
        <div className="tk2-arow-sub">
          {t.dateFrom || "?"}
          {t.dateTo ? ` – ${t.dateTo}` : ""}
        </div>
      </div>
      <span className={`tk2-arow-res tk2-arow-res-${state}`}>
        {state === "done" ? `+${points} pkt` : state === "missed" ? "Nie zrealizowane" : "Bez oceny"}
      </span>
    </div>
  );
}

// Spadające jesienne listki (sama dekoracja). Pojawiają się dopiero 2,5 s po wejściu do panelu (start aplikacji
// nie jest obciążany), animowane wyłącznie transformacją/przezroczystością; na słabszych telefonach jest ich mniej,
// a przy włączonym "ogranicz ruch" nie ma ich wcale. Nie reagują na dotyk (pointer-events: none).
// W czacie (hidden) są całkowicie odmontowane, a po powrocie startują od razu.
let leavesStarted = false;
const LEAF_DEFS = [
  { x: 6, s: 29, d: 13, dl: -3, sd: 2.6, dir: 1, sw: 3, c: 1 },
  { x: 17, s: 21, d: 10, dl: -8, sd: 2.1, dir: 2, sw: 1, c: 3 },
  { x: 28, s: 34, d: 16, dl: -11, sd: 3.2, dir: 1, sw: 3, c: 2 },
  { x: 39, s: 20, d: 9.5, dl: -2, sd: 1.9, dir: 2, sw: 1, c: 5 },
  { x: 48, s: 27, d: 14, dl: -6, sd: 2.8, dir: 1, sw: 2, c: 4 },
  { x: 58, s: 23, d: 11, dl: -9, sd: 2.3, dir: 2, sw: 2, c: 1 },
  { x: 68, s: 35, d: 17, dl: -14, sd: 3.4, dir: 1, sw: 3, c: 3 },
  { x: 77, s: 18, d: 9, dl: -4, sd: 1.8, dir: 2, sw: 1, c: 2 },
  { x: 86, s: 30, d: 15, dl: -12, sd: 3, dir: 1, sw: 3, c: 5 },
  { x: 93, s: 22, d: 12, dl: -7, sd: 2.4, dir: 2, sw: 2, c: 4 },
  { x: 33, s: 25, d: 13.5, dl: -1, sd: 2.7, dir: 2, sw: 2, c: 2 },
  { x: 73, s: 26, d: 12.5, dl: -10, sd: 2.5, dir: 1, sw: 2, c: 1 },
];
// Duży komunikat "Szef woła wszystkich" — pokazuje się kierowcy, który ma otwartą appkę, gdy na grupie pojawi się zawołanie.
function CallAllOverlay({ msg, onOpen, onClose }) {
  useEffect(() => {
    try {
      if (navigator.vibrate) navigator.vibrate([400, 150, 400, 150, 800]);
    } catch (e) {
      /* brak wibracji */
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return undefined;
      const ctx = new Ctx();
      [0, 0.32, 0.64].forEach((d) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "triangle";
        o.frequency.value = 880;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + d);
        g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + d + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + d + 0.26);
        o.connect(g);
        g.connect(ctx.destination);
        o.start(ctx.currentTime + d);
        o.stop(ctx.currentTime + d + 0.3);
      });
      const t = setTimeout(() => ctx.close().catch(() => {}), 1500);
      return () => clearTimeout(t);
    } catch (e) {
      return undefined; // dźwięk bywa zablokowany przez telefon — komunikat i tak jest widoczny
    }
  }, []);
  const text = String(msg.text || "").replace(/^📣\s*SZEF WOŁA WSZYSTKICH:\s*/i, "");
  // do .app-root (jak czat) — wewnątrz ekranu kierowcy "fixed" liczyłoby się względem całej, długiej strony
  return createPortal(
    <div className="ca-overlay" role="alertdialog" aria-label="Szef woła wszystkich">
      <div className="ca-card">
        <div className="ca-rings" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <div className="ca-icon">📣</div>
        <div className="ca-title">Szef woła wszystkich!</div>
        <div className="ca-text">{text || "Wejdź na czat grupowy."}</div>
        <button className="ca-go" onClick={onOpen}>
          Wchodzę na czat
        </button>
        <button className="ca-close" onClick={onClose}>
          Zamknij
        </button>
      </div>
    </div>,
    document.querySelector(".app-root") || document.body
  );
}


function shortPeriod(r) {
  const f = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}` : "");
  return r.periodTo ? `${f(r.periodFrom)} – ${f(r.periodTo)}` : f(r.periodFrom);
}
// ====================== Tickety (Koło fortuny) ======================
// Admin oznacza zadanie jako "ticket" (milestone: true + tickets: N). Za wykonanie kierowca dostaje N ticketów,
// a 1 ticket = 1 zakręcenie Kołem fortuny (zakładka "Fortuna" — doładowywana osobnym plikiem; tickety liczy serwer).
function ticketsOfTask(t) {
  return t && (t.milestone === true || Number(t.tickets) > 0) ? Math.max(1, Math.min(20, Math.round(Number(t.tickets)) || 1)) : 0;
}
const plTickets = (n) => (n === 1 ? "ticket" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? "tickety" : "ticketów");
const preloadFortune = () => {
  import("./fortune.jsx").catch(() => {});
};
const TicketEarnedLazy = React.lazy(lazyTimeout(() => import("./fortune.jsx").then((m) => ({ default: m.TicketEarned }))));

// Złoty bilet: karta zadania, za którego wykonanie dostaje się tickety (perforacja + odcinek z liczbą ticketów)
function GoldenTicket({ t, state, points, urgent, frac, countdown, index }) {
  const n = ticketsOfTask(t);
  return (
    <div className={`gt gt-${state} ${urgent ? "gt-urgent" : ""}`} style={{ animationDelay: `${Math.min(index, 8) * 90}ms` }}>
      <div className="gt-body">
        <div className="gt-eyebrow">
          <span className="gt-star sp4" /> Zadanie z ticketem{t.priority ? <em> · priorytet</em> : null}
        </div>
        <div className="gt-title">{t.title}</div>
        <div className="gt-dates">
          {t.dateFrom || "?"}
          {t.dateTo ? ` – ${t.dateTo}` : ""}
        </div>
        {frac != null && (
          <div className={`gt-time ${frac > 0.8 ? "late" : ""}`}>
            <i style={{ width: `${Math.round(frac * 100)}%` }} />
          </div>
        )}
        <div className="gt-foot">
          {state === "done" ? (
            <span className="gt-pill done">Zrealizowane</span>
          ) : state === "missed" ? (
            <span className="gt-pill missed">Nie zrealizowane</span>
          ) : (
            <span className="gt-pill">Czeka na ocenę</span>
          )}
          {countdown && state === "waiting" && <span className={`gt-chip ${urgent ? "urgent" : ""}`}>{countdown}</span>}
          <span className={`gt-pts ${state}`}>+{points} pkt</span>
        </div>
      </div>
      <div className="gt-perf" aria-hidden="true">
        <b className="t" />
        <b className="b" />
      </div>
      <div className="gt-stub">
        <div className="gt-stub-top">TICKET</div>
        <div className="gt-stub-n">+{n}</div>
        <div className="gt-mini" aria-hidden="true" />
        <div className="gt-stub-sub">{plTickets(n)}</div>
        {state === "done" && <div className="gt-stamp">ZDOBYTE</div>}
        {state === "missed" && <div className="gt-stamp miss">UTRACONE</div>}
      </div>
      <i className="gt-sheen" aria-hidden="true" />
      <i className="gt-sp gt-sp1 sp4" aria-hidden="true" />
      <i className="gt-sp gt-sp2 sp4" aria-hidden="true" />
      <i className="gt-sp gt-sp3 sp4" aria-hidden="true" />
    </div>
  );
}

// Pasek nad listą zadań: ile ticketów leży do zdobycia
function TicketStrip({ rows, onOpen }) {
  const open = (rows || []).filter((r) => !r.myStatus && ticketsOfTask(r.t) > 0);
  const sum = open.reduce((s, r) => s + ticketsOfTask(r.t), 0);
  if (!sum) return null;
  return (
    <button type="button" className="gts" onClick={onOpen} onTouchStart={preloadFortune}>
      <span className="gts-ic">🎟️</span>
      <span className="gts-tx">
        <b>
          +{sum} {plTickets(sum)} do zdobycia
        </b>
        <small>
          {open.length} {open.length === 1 ? "zadanie" : open.length < 5 ? "zadania" : "zadań"} z biletem · 1 ticket = 1 zakręcenie koła
        </small>
      </span>
      <span className="gts-go">Do koła ›</span>
    </button>
  );
}

// Osiągnięcia: baner Koła fortuny
function TicketPromo({ onOpen }) {
  return (
    <button type="button" className="tkp2" onClick={onOpen} onTouchStart={preloadFortune}>
      <span className="tkp2-wheel" aria-hidden="true" />
      <span className="tkp2-tx">
        <em>
          <i className="sp4" /> Koło fortuny
        </em>
        <b>Kręć i wygrywaj!</b>
        <small>Tickety dostajesz za zadania i utarg. 1 ticket to 1 zakręcenie.</small>
      </span>
      <span className="tkp2-go">Kręć ›</span>
    </button>
  );
}

// ====================== Czat 3.0 — powłoka pełnoekranowa ======================
// Czat otwiera się na CAŁY ekran (więcej miejsca na wiadomości), dopasowuje się do klawiatury
// (visualViewport) i blokuje przewijanie strony pod spodem. Renderowany do .app-root (portal),
// żeby motyw jasny/ciemny działał tak samo jak w reszcie aplikacji.
// Dodatki: przesunięcie wiadomości w bok = odpowiedź (gest), wskaźnik braku internetu, wibracja przy wysyłaniu.
// Zielona słuchawka: okienko wyboru "Zadzwoń w aplikacji" (rozmowa trafia do panelu Szefa) albo zwykły telefon.
// Numer jest tylko w linku tel:, nigdzie nie jest wyświetlany.
const BOSS_TEL = "tel:+48573188202";
const DriverCallScreenLazy = React.lazy(() => import("./calls.jsx").then((m) => ({ default: m.DriverCallScreen })));
const AdminCallListenerLazy = React.lazy(() => import("./calls.jsx").then((m) => ({ default: m.AdminCallListener })));
function CallButton({ driverName, blocked }) {
  const [mode, setMode] = useState(null); // null | "choose" | "app"
  const host = document.querySelector(".app-root") || document.body;
  if (blocked) {
    return (
      <button className="cx-call" disabled style={{ opacity: 0.35, filter: "grayscale(1)" }} aria-label="Połączenia zablokowane przez administratora" title="Połączenia zablokowane przez administratora">
        <Phone size={19} strokeWidth={2.4} />
      </button>
    );
  }
  return (
    <>
      <button className="cx-call" onClick={() => setMode("choose")} aria-label="Zadzwoń do Szefa" title="Zadzwoń do Szefa">
        <span className="cx-call-pulse" />
        <Phone size={19} strokeWidth={2.4} />
      </button>
      {mode === "choose" &&
        createPortal(
          <div className="cx-call-overlay" onClick={() => setMode(null)}>
            <div className="cx-call-sheet" onClick={(e) => e.stopPropagation()}>
              <div className="cx-call-avatar">
                <i />
                <i />
                <i />
                <span>👔</span>
              </div>
              <div className="cx-call-title">Zadzwonić do Szefa?</div>
              <div className="cx-call-sub">Wybierz rodzaj połączenia</div>
              <button className="cx-call-go" onClick={() => setMode("app")}>
                <Phone size={19} strokeWidth={2.4} /> Zadzwoń w aplikacji
              </button>
              <a className="cx-call-alt" href={BOSS_TEL} onClick={() => setTimeout(() => setMode(null), 400)}>
                Zwykły telefon
              </a>
              <button className="cx-call-cancel" onClick={() => setMode(null)}>
                Anuluj
              </button>
            </div>
          </div>,
          host
        )}
      {mode === "app" && (
        <React.Suspense fallback={null}>
          <DriverCallScreenLazy driverName={driverName} telHref={BOSS_TEL} onClose={() => setMode(null)} />
        </React.Suspense>
      )}
    </>
  );
}

// Pasek nad polem pisania: wysyłanie głosówki w toku / głosówka, której nie udało się wysłać (można ponowić albo usunąć).
// Informacja po wysłaniu głosówki (np. "była bardzo cicha"); sama oczekująca głosówka jest pokazana jako dymek w rozmowie (PendingVoiceBubble).
function VoicePendingBanner({ rec }) {
  if (!rec || !rec.notice) return null;
  return (
    <div className="cx-voice-banner notice">
      <span>🔈 {rec.notice}</span>
    </div>
  );
}
// Dymek wysyłanej głosówki — pojawia się w rozmowie od razu po nagraniu; po wysłaniu zastępuje go prawdziwa wiadomość.
function PendingVoiceBubble({ rec }) {
  const p = rec && rec.pending;
  if (!p) return null;
  const t = Math.floor((p.seconds || 0) / 60) + ":" + String((p.seconds || 0) % 60).padStart(2, "0");
  const sending = p.stage === "sending";
  return (
    <div className="vpb-row">
      <div className={"vpb " + (sending ? "sending" : "wait")}>
        <span className="vpb-mic">🎙️</span>
        <span className="vpb-bars" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => (
            <i key={i} style={{ height: 6 + ((i * 7) % 5) * 3 + "px" }} />
          ))}
        </span>
        <span className="vpb-time">{t}</span>
      </div>
      <div className="vpb-state">
        {sending ? (
          <>
            <span className="cx-spinner" /> Wysyłanie…
          </>
        ) : (
          <>
            Czeka na połączenie — wyślę automatycznie
            <button onClick={rec.retry}>Wyślij teraz</button>
            <button className="ghost" onClick={rec.discard}>
              Usuń
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Szybkie odpowiedzi w czacie z Szefem — wstawiają gotowy tekst do pola (kierowca może go jeszcze zmienić).
const QUICK_REPLIES = ["Jestem w drodze 🚗", "Mam problem z autem 🔧", "Oddzwoń proszę 📞", "Dziękuję 🙏", "Wszystko ok ✅"];

// ====================== Rekompensaty 2.0 ======================
// Widok zgłoszeń: aktualny tydzień na wierzchu (bilans + efektowne karty), starsze tygodnie w zwijanym archiwum.
// Archiwizacja jest AUTOMATYCZNA i oparta tylko na dacie (nic nie jest zmieniane w danych): w każdy wtorek tygodnie
// zakończone przed tym wtorkiem przechodzą do archiwum. Zgłoszenia "oczekuje" zostają na wierzchu zawsze.
function compLocalDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
// ostatni wtorek (dziś, jeśli dziś wtorek) jako "RRRR-MM-DD"
function compArchiveCutoff(now) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - ((d.getDay() - 2 + 7) % 7));
  return compLocalDate(d);
}
function isCompArchived(r, cutoff) {
  if (r.status === "pending") return false;
  if (!r.periodTo) return true;
  return r.periodTo < cutoff;
}
const compShortDay = (s) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}` : "");
const compWeekLabel = (r) => (r.periodFrom ? `${compShortDay(r.periodFrom)} – ${compShortDay(r.periodTo)}` : "Bez okresu");
const compSigned = (r) => (r.status === "approved" ? (r.type === "add" ? r.amount : -r.amount) : 0);
const compValidTs = (t) => typeof t === "number" && t > 946684800000;
const compDayText = (ts) => new Date(ts).toLocaleDateString("pl-PL", { day: "numeric", month: "short" });

// płynne "dojeżdżanie" kwoty (z groszami)
function useMoneyCount(target, ms) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / ms);
      setV(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

// Karta podsumowania tygodnia: bilans zatwierdzonych rekompensat + pierścień "ile zatwierdzono".
function CompHero({ rows }) {
  const approved = rows.filter((r) => r.status === "approved").length;
  const pending = rows.filter((r) => r.status === "pending").length;
  const rejected = rows.filter((r) => r.status === "rejected").length;
  const net = Math.round(rows.reduce((s, r) => s + compSigned(r), 0) * 100) / 100;
  const shown = useMoneyCount(net, 1100);
  const tone = net > 0 ? "green" : net < 0 ? "red" : "amber";
  return (
    <div className={`cm2-hero cm2-hero-${tone}`}>
      <ProgressRing value={rows.length ? Math.round((approved / rows.length) * 100) : 0} size={84} stroke={8} tone={net < 0 ? "amber" : "green"}>
        <div className="cm2-ring-num">
          {approved}
          <small>/{rows.length}</small>
        </div>
      </ProgressRing>
      <div className="cm2-hero-info">
        <div className="cm2-hero-label">Bilans rekompensat · ten tydzień</div>
        <div className={`cm2-hero-amount ${net < 0 ? "neg" : net > 0 ? "pos" : ""}`}>
          {net > 0 ? "+" : ""}
          {formatPLN(shown)}
        </div>
        <div className="cm2-hero-chips">
          {pending > 0 && (
            <span className="cm2-chip cm2-chip-pending">
              <Clock size={11} /> {pending} oczekuje
            </span>
          )}
          {approved > 0 && (
            <span className="cm2-chip cm2-chip-ok">
              <CheckCircle2 size={11} /> {approved} zatwierdz.
            </span>
          )}
          {rejected > 0 && (
            <span className="cm2-chip cm2-chip-no">
              <XCircle size={11} /> {rejected} odrzuc.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const COMP_STATUS = {
  pending: { text: "Oczekuje na decyzję", Icon: Clock },
  approved: { text: "Zatwierdzone", Icon: CheckCircle2 },
  rejected: { text: "Odrzucone", Icon: XCircle },
};

// Karta pojedynczego zgłoszenia.
function CompCard({ r, index }) {
  const st = COMP_STATUS[r.status] || COMP_STATUS.pending;
  const add = r.type === "add";
  return (
    <div className={`cm2-card cm2-${add ? "add" : "sub"} cm2-st-${r.status}`} style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}>
      <div className="cm2-card-top">
        <div className="cm2-badge">{add ? <PlusCircle size={22} /> : <MinusCircle size={22} />}</div>
        <div className="cm2-main">
          <div className="cm2-kind">{add ? "Dopłata" : "Odjęcie"}</div>
          <div className="cm2-when">
            {compValidTs(r.requestedAt) ? `Zgłoszono ${compDayText(r.requestedAt)}` : "Zgłoszenie"}
          </div>
        </div>
        <div className="cm2-amount">
          {add ? "+" : "−"}
          {formatPLN(r.amount)}
        </div>
      </div>
      {r.note && <div className="cm2-note">{r.note}</div>}
      <div className="cm2-footer">
        <span className={`cm2-status cm2-status-${r.status}`}>
          <st.Icon size={13} />
          {st.text}
          {r.status === "pending" && (
            <i className="cm2-dots" aria-hidden="true">
              <b />
              <b />
              <b />
            </i>
          )}
        </span>
        <span className="cm2-period">
          <CalendarClock size={11} /> {compWeekLabel(r)}
        </span>
        {r.status !== "pending" && compValidTs(r.decidedAt) && <span className="cm2-decided">decyzja {compDayText(r.decidedAt)}</span>}
      </div>
    </div>
  );
}

// Zwięzły wiersz w archiwum.
function CompArchiveRow({ r }) {
  const st = COMP_STATUS[r.status] || COMP_STATUS.pending;
  const add = r.type === "add";
  return (
    <div className={`cm2-arow cm2-arow-${add ? "pos" : "neg"} cm2-arow-${r.status}`}>
      <span className="cm2-arow-ic">{add ? <PlusCircle size={14} /> : <MinusCircle size={14} />}</span>
      <div className="cm2-arow-main">
        <div className="cm2-arow-note">{r.note || (add ? "Dopłata" : "Odjęcie")}</div>
        <div className="cm2-arow-sub">
          <st.Icon size={10} /> {st.text.replace(" na decyzję", "")}
          {compValidTs(r.requestedAt) ? ` · ${compDayText(r.requestedAt)}` : ""}
        </div>
      </div>
      <span className="cm2-arow-amount">
        {add ? "+" : "−"}
        {formatPLN(r.amount)}
      </span>
    </div>
  );
}

function DashboardScreen({
  driver,
  drivers,
  earnings,
  onSaveEarnings,
  onEnsureNextWeekRecord,
  globalBonuses,
  avatar,
  avatars,
  onSetAvatar,
  payoutDecisions,
  onSetPayoutDecision,
  compensationRequests,
  onAddCompensationRequest,
  debtRepayments,
  onAddDebtRepayment,
  driverDebts,
  chatThreads,
  onCreateChatThread,
  onSendChatMessage,
  onMarkThreadRead,
  dmThreads,
  onSendDm,
  onMarkDmThreadRead,
  onResolveThread,
  groupChatMessages,
  onSendGroupMessage,
  onMarkGroupMessagesSeen,
  onToggleGroupReaction,
  onToggleThreadReaction,
  onToggleDmReaction,
  chatsLoaded,
  chatsError,
  trackPresence,
  onGirlFreed,
  tasks,
  pointAdjustments,
  onRefresh,
  onLogout,
  theme,
  onToggleTheme,
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState("");
  const [tab, setTab] = useState("rozliczenia");
  const [rankingSubTab, setRankingSubTab] = useState("ranking");
  const presence = useDriverPresence(driver.name, trackPresence !== false); // kto jest teraz online (podgląd admina tylko czyta)
  const [tasksArchiveOpen, setTasksArchiveOpen] = useState(false); // archiwum zadań domyślnie zwinięte
  const [tasksArchiveShown, setTasksArchiveShown] = useState(8);
  const [rankRulesOpen, setRankRulesOpen] = useState(false); // zasady punktacji domyślnie zwinięte
  const [compArchiveOpen, setCompArchiveOpen] = useState(false); // archiwum rekompensat domyślnie zwinięte
  const [debtHistoryOpen, setDebtHistoryOpen] = useState(false); // historia zgłoszeń spłaty domyślnie zwinięta
  const [compArchiveShown, setCompArchiveShown] = useState(10);
  const [bonusFilterTab, setBonusFilterTab] = useState("active");
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [compModalOpen, setCompModalOpen] = useState(false);
  const [compModalType, setCompModalType] = useState("add");
  const [compAmount, setCompAmount] = useState("");
  const [compNote, setCompNote] = useState("");
  const [compSaving, setCompSaving] = useState(false);
  const [compErr, setCompErr] = useState("");
  const compReqRef = useRef(null); // to samo zgłoszenie (to samo id) przy ponawianiu — bez duplikatów
  const [debtSaving, setDebtSaving] = useState(false);
  const [debtErr, setDebtErr] = useState("");
  const debtReqRef = useRef(null);
  const [payErr, setPayErr] = useState("");
  const [activeThreadId, setActiveThreadId] = useState(null);
  const chatField = useRef(null); // tekst trzymany w polu (ComposerField), nie w stanie ekranu
  const [chatEmpty, setChatEmpty] = useState(true);
  // Pokazuje się, kiedy wiadomość NAPRAWDĘ nie dało się zapisać (po 3
  // próbach ponowienia w mutateArrayCollection) — appka wtedy przywraca
  // treść wiadomości z powrotem do pola, żeby nic nie zginęło.
  const [chatSendError, setChatSendError] = useState(null);
  // Profil kierowcy (klikalny z rankingu) — TYLKO informacyjny (awatar,
  // imię, auto). "activeDmPartner" (imię kierowcy albo null) to osobny,
  // globalny stan mówiący, którą prywatną rozmowę appka aktualnie pokazuje
  // w zakładce Czat — dzięki temu "Napisz wiadomość" w profilu po prostu
  // przenosi tam appkę, zamiast otwierać osobne okienko czatu.
  const [dmProfileDriver, setDmProfileDriver] = useState(null);
  const [activeDmPartner, setActiveDmPartner] = useState(null);
  const dmField = useRef(null);
  const [dmReplyTo, setDmReplyTo] = useState(null);
  const [dmSendError, setDmSendError] = useState(null);
  const [dmUploadProgress, setDmUploadProgress] = useState(null);
  const [uploadingDmAttachment, setUploadingDmAttachment] = useState(false);
  const dmMessagesRef = useRef(null);
  const [debtRepayFormOpen, setDebtRepayFormOpen] = useState(false);
  const [debtRepayAmount, setDebtRepayAmount] = useState("");
  const [debtRepayNote, setDebtRepayNote] = useState("");
  const [debtRepayMethod, setDebtRepayMethod] = useState("blik");
  const [priorityMode, setPriorityMode] = useState(false);
  const [chatReplyTo, setChatReplyTo] = useState(null);
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const chatMessagesRef = useRef(null);
  const chatVoiceRec = useVoiceRecorder((attachment) => sendChatMessageAsDriver(attachment), `chat:${driver.name}`);
  const chatFileInputRef = useRef(null);
  const dmFileInputRef = useRef(null);
  const [uploadingChatAttachment, setUploadingChatAttachment] = useState(false);
  const [chatUploadProgress, setChatUploadProgress] = useState(null);
  async function handleChatFileSelected(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingChatAttachment(true);
    setChatUploadProgress(0);
    try {
      const attachment = await uploadFiles(files, setChatUploadProgress);
      if (!attachment) throw Object.assign(new Error("Anulowano"), { aborted: true });
      sendChatMessageAsDriver(attachment);
    } catch (err) {
      if (!(err && err.aborted)) alert(`Nie udało się wysłać załącznika: ${err && err.message ? err.message : "nieznany błąd"}`);
    }
    setUploadingChatAttachment(false);
    setChatUploadProgress(null);
  }
  async function handleDmFileSelected(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingDmAttachment(true);
    setDmUploadProgress(0);
    try {
      const attachment = await uploadFiles(files, setDmUploadProgress);
      if (!attachment) throw Object.assign(new Error("Anulowano"), { aborted: true });
      sendDmMessageAsDriver(attachment);
    } catch (err) {
      if (!(err && err.aborted)) alert(`Nie udało się wysłać załącznika: ${err && err.message ? err.message : "nieznany błąd"}`);
    }
    setUploadingDmAttachment(false);
    setDmUploadProgress(null);
  }
  const groupMessagesRef = useRef(null);
  // Wspólny sposób wysyłania wiadomości na grupę z poziomu appki kierowcy —
  // jeśli zapis się NAPRAWDĘ nie uda (po próbach ponowienia w
  // mutateArrayCollection), przywraca tekst z powrotem do pola (żeby nic
  // nie zginęło) i pokazuje czytelny komunikat, zamiast cicho gubić
  // wiadomość.
  function sendGroupMessageAsDriver(text, attachment, replyTo, restoreTo) {
    onSendGroupMessage(text, attachment, replyTo).catch(() => {
      if (restoreTo !== undefined) fieldSet(groupField, restoreTo);
      alert("Nie udało się wysłać wiadomości na grupę — sprawdź połączenie z internetem i spróbuj ponownie.");
    });
  }
  const groupVoiceRec = useVoiceRecorder(async (attachment) => {
    try {
      await onSendGroupMessage("", attachment, groupReplyTo);
      setGroupReplyTo(null);
      return true;
    } catch (e) {
      return false;
    }
  }, `group:${driver.name}`);
  const groupField = useRef(null);
  function submitGroupText() {
    const textSent = fieldVal(groupField).trim();
    if (!textSent) return;
    fieldSet(groupField, "");
    setGroupReplyTo(null);
    sendGroupMessageAsDriver(textSent, undefined, groupReplyTo, textSent);
  }
  const [groupReplyTo, setGroupReplyTo] = useState(null);
  const [showGroupEmoji, setShowGroupEmoji] = useState(false);
  const [uploadingGroupAttachment, setUploadingGroupAttachment] = useState(false);
  const [groupUploadProgress, setGroupUploadProgress] = useState(null);
  const groupFileInputRef = useRef(null);

  async function handleGroupFileSelected(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingGroupAttachment(true);
    setGroupUploadProgress(0);
    try {
      const attachment = await uploadFiles(files, setGroupUploadProgress);
      if (!attachment) throw Object.assign(new Error("Anulowano"), { aborted: true });
      const textSent = fieldVal(groupField).trim();
      fieldSet(groupField, "");
      sendGroupMessageAsDriver(textSent, attachment, undefined, textSent);
    } catch (err) {
      if (!(err && err.aborted)) alert(`Nie udało się wysłać załącznika: ${err && err.message ? err.message : "nieznany błąd"}`);
    }
    setUploadingGroupAttachment(false);
    setGroupUploadProgress(null);
  }
  const [groupLastSeen, setGroupLastSeen] = useState(0);
  // "Zawołanie wszystkich" od Szefa: duży komunikat (gdy appka jest otwarta) + otwarcie grupy z powiadomienia
  const [callAllMsg, setCallAllMsg] = useState(null);
  useEffect(() => {
    const onMsg = (e) => {
      const d = e && e.data;
      if (d && d.type === "open-tab" && d.tab) setTab(d.tab);
    };
    try {
      if (navigator.serviceWorker) navigator.serviceWorker.addEventListener("message", onMsg);
      if (window.location.hash === "#grupa") {
        setTab("grupa");
        window.history.replaceState(null, "", window.location.pathname);
      }
    } catch (e) {
      /* bez tej funkcji appka działa jak dotąd */
    }
    return () => {
      try {
        if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener("message", onMsg);
      } catch (e) {
        /* ignore */
      }
    };
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const list = groupChatMessages || [];
    let latest = null;
    for (let i = list.length - 1; i >= 0 && i >= list.length - 15; i--) {
      const m = list[i];
      if (m && m.callAll && Date.now() - (m.timestamp || 0) < 5 * 60 * 1000 && m.driverName !== driver.name) {
        latest = m;
        break;
      }
    }
    if (!latest) return;
    let seen = [];
    try {
      seen = JSON.parse(localStorage.getItem("patex-callall-seen") || "[]");
    } catch (e) {
      /* ignore */
    }
    if (seen.includes(latest.id)) return;
    try {
      localStorage.setItem("patex-callall-seen", JSON.stringify([...seen, latest.id].slice(-30)));
    } catch (e) {
      /* ignore */
    }
    if (tab === "grupa") return; // już jest na grupie — widzi wiadomość
    setCallAllMsg(latest);
    // eslint-disable-next-line
  }, [groupChatMessages]);
  const [showResolvedThreads, setShowResolvedThreads] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(`patex-group-seen-${driver.name}`);
      if (saved) setGroupLastSeen(parseInt(saved, 10));
    } catch (e) {
      /* ignore */
    }
  }, [driver.name]);

  useEffect(() => {
    if (tab === "grupa") {
      const now = Date.now();
      setGroupLastSeen(now);
      try {
        localStorage.setItem(`patex-group-seen-${driver.name}`, String(now));
      } catch (e) {
        /* ignore */
      }
      onMarkGroupMessagesSeen();
    }
  }, [tab]);

  const latestGroupMsg = (groupChatMessages || [])[groupChatMessages.length - 1];
  const hasUnreadGroup =
    !!latestGroupMsg && !sameName(latestGroupMsg.driverName, driver.name) && latestGroupMsg.timestamp > groupLastSeen;
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalAction, setReasonModalAction] = useState(null);
  const [reasonNote, setReasonNote] = useState("");

  const mine = earnings
    .filter((r) => sameName(r.name, driver.name))
    .sort(byPeriodDesc);
  const inProgressOnes = mine.filter((r) => r.status === "inprogress");
  const latest = inProgressOnes[0] || mine[0] || null;

  // "Ludzik z gratulacjami" za dobry utarg — cztery progi, coraz większa
  // ekscytacja. Pokazuje się TYLKO RAZ na próg, na dany tydzień (zapamiętane
  // w localStorage) — jeśli kierowca odświeży appkę, appka nie zasypie go
  // tym samym powiadomieniem od nowa. Jeśli w tym samym tygodniu przebije
  // KOLEJNY, wyższy próg, dostanie nowe, mocniejsze gratulacje.
  const [celebrationTier, setCelebrationTier] = useState(null);
  useEffect(() => {
    if (!latest || !latest.periodFrom) return;
    // Utarg = pole "aplikacja" (gotówka jest w nim już zawarta — tak samo liczy ranking i seria dobrych tygodni).
    const utarg = latest.periodFrom === "2026-07-20" ? (latest.appEarnings || 0) + (latest.cashEarnings || 0) : latest.appEarnings || 0;
    const crossedTier = EARNINGS_CELEBRATION_TIERS.find((t) => utarg >= t.threshold);
    if (!crossedTier) return;
    const storageKey = `patex_celebration_${driver.name}_${latest.periodFrom}`;
    const alreadyShownTier = parseInt(localStorage.getItem(storageKey) || "0", 10);
    if (crossedTier.tier > alreadyShownTier) {
      localStorage.setItem(storageKey, String(crossedTier.tier));
      setCelebrationTier(crossedTier.tier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latest?.periodFrom, latest?.appEarnings, latest?.cashEarnings, driver.name]);
  const latestResolved = !!latest && (latest.status === "paid" || latest.status === "settled" || latest.status === "transferred" || latest.status === "debt");
  const totalDue = latest && !latestResolved ? latest.payout : 0;
  const ACTION_LABELS = {
    approved: "Zaakceptowana",
    rejected: "Odrzucona",
    next_week: "Przeniesiona na kolejny tydzień",
    checking: "Administrator sprawdza",
  };

  function payoutMessageText(action, note) {
    const period = latest ? periodLabel(latest) : "";
    const amount = latest ? formatPLN(latest.payout) : "";
    let line = `${driver.name} — ${ACTION_LABELS[action]} wypłata za ${period}, kwota ${amount}.`;
    if (note) line += ` Powód: ${note}`;
    return line;
  }
  async function finalizeDecision(action, note) {
    setPayErr("");
    try {
      await onSetPayoutDecision(payoutDecisionKey(latest), {
        action,
        note,
        decidedAt: Date.now(),
        driverName: driver.name,
        periodFrom: latest.periodFrom,
        periodTo: latest.periodTo,
        amount: latest.payout,
      });
    } catch (e) {
      setPayErr("Nie udało się zapisać Twojej decyzji (słaby zasięg). Spróbuj jeszcze raz.");
      return;
    }
    sendNtfyNotification("PateX — decyzja o wypłacie", payoutMessageText(action, note));
    if (action === "next_week") {
      try {
        // Rozliczenie samo w sobie kończy się statusem "przeniesiono" —
        // dzięki temu appka wszędzie indziej (Kierowcy, karta "Do wypłaty"
        // itd.) poprawnie traktuje je jako ZAKOŃCZONE, tak samo jak
        // "wypłacone". WAŻNE: to musi się w pełni zakończyć (await), zanim
        // zaczniemy dopisywać kolejny tydzień do TEJ SAMEJ kolekcji —
        // inaczej dwa równoległe zapisy potrafią się wzajemnie nadpisać.
        await onSaveEarnings([{ ...latest, status: "transferred", debtCarried: true }]);
        // I od razu zakłada kolejny tydzień, żeby było gdzie doliczyć tę
        // przeniesioną kwotę — inaczej czekałaby "w zawieszeniu" aż do
        // następnej, niepowiązanej wypłaty. Kwota liczona na żywo (z
        // rekompensatami wliczonymi), nie z pola "payout", które mogło nie
        // być przeliczone.
        if (onEnsureNextWeekRecord) {
          // WAŻNE: pobieramy świeże dane wprost z serwera, nie z lokalnego
          // stanu appki — appka kierowcy mogła wczytać listę rekompensat
          // ZANIM admin zatwierdził najnowszą spłatę zadłużenia (np. w
          // zupełnie innej sesji przeglądarki), więc lokalna kopia mogłaby
          // nie zawierać tej najświeższej rekompensaty.
          let freshCompensations;
          try {
            const res = await window.storage.get(COMPENSATION_REQUESTS_KEY, true);
            freshCompensations = res && res.value ? JSON.parse(res.value) : compensationRequests || [];
          } catch (e) {
            freshCompensations = compensationRequests || [];
          }
          const matchingComps = freshCompensations.filter(
            (c) =>
              c.status === "approved" &&
              sameName(c.driverName, driver.name) &&
              c.periodFrom === latest.periodFrom &&
              c.periodTo === latest.periodTo
          );
          const liveCompensationSum = matchingComps.reduce((s, c) => s + (c.type === "add" ? c.amount : -c.amount), 0);
          // WAŻNE: przenosimy DOKŁADNIE to, co appka pokazuje jako "Do
          // wypłaty" na ekranie — czyli zapisaną wypłatę PLUS rekompensaty
          // na żywo — a NIE świeże przeliczenie od zera z surowych pól.
          // Te dwie rzeczy mogą się różnić (np. jeśli ryczałt/VAT w
          // rozliczeniu nie zostały nigdy przeliczone przez formularz), a
          // appka ma pokazywać kierowcy spójną liczbę wszędzie.
          const carryAmount = Math.round(((latest.payout || 0) + liveCompensationSum) * 100) / 100;
          // WAŻNE: pomijamy notatki, które same już są wcześniejszym
          // przeniesieniem ("Kasa za poprzedni tydzień...") — inaczej każde
          // kolejne przeniesienie doklejało CAŁĄ treść poprzedniego, tydzień
          // po tygodniu, aż notatka rosła w nieskończoność. Realne notatki o
          // konkretnych potrąceniach (np. "-400zł bankomat") nadal się
          // przenoszą normalnie — tylko "przeniesienie przeniesienia" nie.
          const compNotes = matchingComps
            .map((c) => c.note)
            .filter((n) => n && !n.startsWith("Kasa za poprzedni tydzień"))
            .join("; ");
          const carryNote = `Kasa za poprzedni tydzień (${latest.periodFrom} – ${latest.periodTo})${compNotes ? `: ${compNotes}` : ""}`;
          await onEnsureNextWeekRecord(driver.name, latest.periodTo, { amount: carryAmount, note: carryNote });
        }
      } catch (e) {
        alert(
          `Coś poszło nie tak przy zakładaniu kolejnego tygodnia: ${e && e.message ? e.message : e}. Odśwież stronę i spróbuj ponownie.`
        );
      }
    }
  }
  function chooseAction(action) {
    if (action === "rejected") {
      setReasonModalAction(action);
      setReasonModalOpen(true);
      return;
    }
    finalizeDecision(action, "");
  }
  function submitReasonModal() {
    if (!reasonNote.trim()) return;
    const note = reasonNote.trim();
    const action = reasonModalAction;
    setReasonModalOpen(false);
    setReasonNote("");
    setReasonModalAction(null);
    finalizeDecision(action, note);
  }

  const myCompRequests = (compensationRequests || [])
    .filter((r) => sameName(r.driverName, driver.name))
    .sort((a, b) => b.requestedAt - a.requestedAt);

  const approvedForLatest = latest
    ? myCompRequests.filter(
        (r) => r.status === "approved" && r.periodFrom === latest.periodFrom && r.periodTo === latest.periodTo
      )
    : [];
  const rawCompensationAdjustment = approvedForLatest.reduce(
    (sum, r) => sum + (r.type === "add" ? r.amount : -r.amount),
    0
  );
  // Karta "Do wypłaty" ma pokazywać 0, kiedy najnowszy tydzień jest już
  // rozliczony (wypłacony/przeniesiony) — inaczej rekompensata doliczona do
  // TEGO tygodnia zostawałaby widoczna jako "wciąż do wypłaty" na zawsze,
  // mimo że w rzeczywistości już dawno wyszła w tej wypłacie.
  const compensationAdjustment = latestResolved ? 0 : rawCompensationAdjustment;

  function openCompModal(type) {
    setCompModalType(type);
    setCompModalOpen(true);
  }
  async function submitCompensation() {
    if (compSaving) return;
    if (!latest || latest.status !== "notstarted") return;
    const amount = parseFloat(compAmount.replace(",", "."));
    if (!amount || amount <= 0 || !compNote.trim()) return;
    const base = compReqRef.current || { id: `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, requestedAt: Date.now() };
    const req = {
      ...base,
      driverName: driver.name,
      periodFrom: latest ? latest.periodFrom : "",
      periodTo: latest ? latest.periodTo : "",
      type: compModalType,
      amount,
      note: compNote.trim(),
      status: "pending",
    };
    compReqRef.current = req;
    setCompErr("");
    setCompSaving(true);
    try {
      await onAddCompensationRequest(req);
    } catch (e) {
      setCompSaving(false);
      setCompErr("Nie udało się wysłać zgłoszenia (słaby zasięg). Dotknij „Wyślij” jeszcze raz — Twoje dane zostały w oknie.");
      return;
    }
    compReqRef.current = null;
    setCompSaving(false);
    const label = compModalType === "add" ? "dodanie" : "odjęcie";
    sendNtfyNotification(
      "PateX — zlecenie rekompensaty",
      `${driver.name} — zlecono ${label} ${formatPLN(amount)} ${
        latest ? `(tydzień ${periodLabel(latest)})` : ""
      }. Powód: ${req.note}`
    );
    setCompModalOpen(false);
    setCompAmount("");
    setCompNote("");
  }

  async function submitDebtRepayment() {
    if (debtSaving) return;
    const amount = parseFloat(String(debtRepayAmount).replace(",", "."));
    if (!amount || amount <= 0) return;
    if (debtRepayMethod === "deduct_next_week" && !hasNotStartedWeek) return;
    const base = debtReqRef.current || { id: `debtrepay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, requestedAt: Date.now() };
    const req = {
      ...base,
      driverName: driver.name,
      amount,
      note: "Spłata zadłużenia",
      method: debtRepayMethod,
      status: "pending",
    };
    debtReqRef.current = req;
    setDebtErr("");
    setDebtSaving(true);
    try {
      await onAddDebtRepayment(req);
    } catch (e) {
      setDebtSaving(false);
      setDebtErr("Nie udało się wysłać zgłoszenia (słaby zasięg). Dotknij „Zgłoś spłatę” jeszcze raz — dane zostały w oknie.");
      return;
    }
    debtReqRef.current = null;
    setDebtSaving(false);
    sendNtfyNotification(
      "PateX — zgłoszenie spłaty zadłużenia",
      `${driver.name} zgłasza spłatę ${formatPLN(amount)} (${debtRepayMethod === "blik" ? "BLIK" : "odjęcie od najbliższego rozliczenia"})`
    );
    setDebtRepayFormOpen(false);
    setDebtRepayAmount("");
    setDebtRepayNote("");
    setDebtRepayMethod("blik");
  }

  const myThreads = (chatThreads || [])
    .filter((t) => sameName(t.driverName, driver.name))
    .sort((a, b) => {
      const aLast = a.messages.length ? a.messages[a.messages.length - 1].timestamp : a.createdAt;
      const bLast = b.messages.length ? b.messages[b.messages.length - 1].timestamp : b.createdAt;
      return bLast - aLast;
    });
  const activeThread = myThreads.find((t) => t.id === activeThreadId) || null;
  // Jeden stały czat z Szefem: zawsze ten sam (najnowszy) wątek kierowcy — bez kończenia i zakładania kolejnych.
  const currentThread = myThreads[0] || null;
  const resolvedThreads = [];

  // Wszystkie prywatne rozmowy Z INNYMI KIEROWCAMI, w których uczestniczy
  // ten kierowca — posortowane od najświeższej wiadomości, do pokazania
  // jako lista "skrzynki" w zakładce Czat.
  const myDmThreads = (dmThreads || [])
    .filter((t) => (t.participants || []).some((p) => sameName(p, driver.name)))
    .map((t) => ({
      ...t,
      partnerName: (t.participants || []).find((p) => !sameName(p, driver.name)) || "?",
    }))
    .sort((a, b) => {
      const aLast = a.messages.length ? a.messages[a.messages.length - 1].timestamp : a.createdAt;
      const bLast = b.messages.length ? b.messages[b.messages.length - 1].timestamp : b.createdAt;
      return bLast - aLast;
    });
  // Wątek AKTUALNIE otwartej prywatnej rozmowy (jeśli w ogóle jakiś
  // istnieje — ID jest deterministyczne, więc appka zawsze trafi na TEN
  // SAM wątek dla tej pary osób, niezależnie kto pierwszy napisał).
  const currentDmThread = activeDmPartner
    ? (dmThreads || []).find((t) => t.id === `dm_${[driver.name, activeDmPartner].sort().join("__")}`) || null
    : null;

  useEffect(() => {
    if (tab === "czat" && currentThread && currentThread.unreadForDriver) {
      onMarkThreadRead(currentThread.id, "driver");
    }
  }, [tab, currentThread]);

  useEffect(() => {
    if (tab === "czat" && activeDmPartner && currentDmThread && (currentDmThread.unreadFor || []).includes(driver.name)) {
      onMarkDmThreadRead(currentDmThread.id, driver.name);
    }
  }, [tab, activeDmPartner, currentDmThread]);

  useSmartAutoScroll(
    chatMessagesRef,
    currentThread ? currentThread.messages.length : 0,
    // "activeDmPartner" musi być tu też — inaczej po przełączeniu na
    // rozmowę prywatną i z powrotem na czat z adminem, ten div (chat-
    // messages) faktycznie ODMONTOWUJE się i montuje na nowo (bo to inna
    // gałąź tego samego warunku w JSX), więc traci swoją poprzednią
    // pozycję przewijania i wraca na sam GÓRĘ — a bez zmiany w tym kluczu
    // "przewiń na dół" się nie uruchamiało ponownie, żeby to naprawić.
    `${tab === "czat"}-${activeDmPartner ? "dm" : "admin"}-${currentThread ? currentThread.id : "none"}`
  );
  useSmartAutoScroll(groupMessagesRef, (groupChatMessages || []).length, tab === "grupa");
  const chatWin = useMsgWindow(`${tab === "czat"}|${currentThread ? currentThread.id : "none"}`, currentThread ? currentThread.messages.length : 0, chatMessagesRef);
  const groupWin = useMsgWindow(`${tab === "grupa"}`, (groupChatMessages || []).length, groupMessagesRef);
  useSmartAutoScroll(
    dmMessagesRef,
    currentDmThread ? currentDmThread.messages.length : 0,
    `${tab === "czat"}-${activeDmPartner || "none"}`
  );
  useNewMessageSound(currentThread ? currentThread.messages.length : 0);
  useNewMessageSound((groupChatMessages || []).length);
  useNewMessageSound(currentDmThread ? currentDmThread.messages.length : 0);

  async function sendChatMessageAsDriver(attachment) {
    if (isChatBlocked) {
      setChatSendError("Czat z Szefem został zablokowany przez administratora.");
      return false;
    }
    if (!fieldVal(chatField).trim() && !attachment) return;
    if (!currentThread && !chatsLoaded) {
      setChatSendError("Trwa ładowanie rozmowy — spróbuj za chwilę.");
      return false;
    }
    const wasPriority = priorityMode;
    const wasReplyTo = chatReplyTo;
    const textToSend = fieldVal(chatField).trim();
    setChatSendError(null);
    if (currentThread) {
      // Czyścimy pole OD RAZU (dla płynności) — jeśli zapis się jednak nie
      // uda, przywracamy treść z powrotem niżej, żeby nic nie zginęło.
      fieldSet(chatField, "");
      setPriorityMode(false);
      setChatReplyTo(null);
      try {
        await onSendChatMessage(currentThread.id, "driver", textToSend, attachment, wasPriority, wasReplyTo);
        if (currentThread.resolved) {
          // stary, zakończony wątek wznawiamy (już PO wysłaniu — nie opóźnia wiadomości) zamiast zakładać nowy
          Promise.resolve(onResolveThread(currentThread.id, false)).catch(() => {});
        }
        sendNtfyNotification(
          wasPriority ? "🚨 PILNA wiadomość" : "PateX — nowa wiadomość",
          `${driver.name}: ${textToSend || (attachment ? `📎 ${attachment.filename}` : "")}`
        );
      } catch (e) {
        fieldSet(chatField, textToSend);
        setPriorityMode(wasPriority);
        setChatReplyTo(wasReplyTo);
        setChatSendError("Nie udało się wysłać wiadomości — sprawdź połączenie z internetem i spróbuj ponownie.");
        return false;
      }
      return true;
    }
    // Brak jeszcze żadnej rozmowy — appka tworzy ją teraz, przy pierwszej wiadomości.
    // stałe ID wątku kierowcy — dwa równoczesne "pierwsze" zapisy trafiają do TEGO SAMEGO wątku (nie powstają duplikaty)
    const threadId = `thread_${String(driver.name).toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${[...String(driver.name)].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7).toString(36)}`;
    const firstMsg = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      sender: "driver",
      text: textToSend,
      timestamp: Date.now(),
      ...(attachment ? { attachment } : {}),
      ...(wasPriority ? { priority: true } : {}),
    };
    setActiveThreadId(threadId);
    setPriorityMode(false);
    fieldSet(chatField, "");
    try {
      await onCreateChatThread({
        id: threadId,
        driverName: driver.name,
        topic: "Wiadomość",
        createdAt: Date.now(),
        messages: [firstMsg],
        unreadForDriver: false,
        unreadForAdmin: true,
      });
      sendNtfyNotification(
        wasPriority ? "🚨 PILNA wiadomość" : "PateX — nowa wiadomość",
        `${driver.name}: ${firstMsg.text || (attachment ? `📎 ${attachment.filename}` : "")}`
      );
    } catch (e) {
      setActiveThreadId(null);
      fieldSet(chatField, textToSend);
      setPriorityMode(wasPriority);
      setChatSendError("Nie udało się wysłać wiadomości — sprawdź połączenie z internetem i spróbuj ponownie.");
      return false;
    }
    return true;
  }

  // Prywatna wiadomość do INNEGO kierowcy (nie admina) — ten sam wzorzec
  // co przy czacie z adminem: czyścimy pole od razu, a jeśli zapis się
  // jednak nie uda, przywracamy treść z powrotem.
  async function sendDmMessageAsDriver(attachment) {
    if ((!fieldVal(dmField).trim() && !attachment) || !activeDmPartner) return;
    const textToSend = fieldVal(dmField).trim();
    const wasReplyTo = dmReplyTo;
    setDmSendError(null);
    fieldSet(dmField, "");
    setDmReplyTo(null);
    try {
      await onSendDm(driver.name, activeDmPartner, textToSend, attachment, wasReplyTo);
    } catch (e) {
      fieldSet(dmField, textToSend);
      setDmReplyTo(wasReplyTo);
      setDmSendError("Nie udało się wysłać wiadomości — sprawdź połączenie z internetem i spróbuj ponownie.");
    }
  }
  function openDriverProfile(r) {
    setDmProfileDriver({ name: r.name, car: r.car || "" });
  }
  // Przycisk "Napisz wiadomość" w profilu kierowcy — zamyka profil,
  // przełącza appkę na zakładkę Czat i otwiera tam tę konkretną rozmowę.
  // Cała obsługa czatu żyje TYLKO w jednym miejscu (zakładka Czat), więc
  // nie ma dwóch kopii tego samego kodu do utrzymania.
  function goToDmWith(name) {
    setDmProfileDriver(null);
    setActiveDmPartner(name);
    setTab("czat");
  }

  function openThread(threadId) {
    setActiveThreadId(threadId);
    onMarkThreadRead(threadId, "driver");
  }

  const { rows: rankingRows, countedPeriods: rankingPeriods } = computeDriverRanking(earnings, drivers || [], tasks, pointAdjustments);
  // Ranking: ruch pozycji względem stanu sprzed ostatniego liczonego tygodnia + "Kierowca tygodnia" (liczone tylko gdy zakładka jest otwarta)
  const rkExtra = useMemo(() => {
    if (tab !== "ranking" || rankingSubTab !== "ranking" || rankingPeriods.length < 1) return null;
    const lastKey = rankingPeriods[rankingPeriods.length - 1];
    // "Kierowca tygodnia" pokazujemy, gdy KAŻDY kierowca w danym tygodniu ma rozliczenie co najmniej "W trakcie" (nikt nie został na "Nie rozpoczęto");
    // do tego czasu zostaje ostatni taki tydzień. Do wyniku wliczamy rozliczenia "W trakcie" oraz liczone (Wypłacone/Przeniesiono).
    const starOk = (r) => r.status === "inprogress" || COUNTED_STATUSES.includes(r.status);
    const starKeys = [...new Set((earnings || []).filter((r) => r.periodFrom && r.periodFrom >= RANKING_START_DATE && starOk(r)).map((r) => periodKey(r)))].sort();
    const weekDone = (k) => (earnings || []).every((r) => periodKey(r) !== k || r.status !== "notstarted");
    let starKey = null;
    for (let i = starKeys.length - 1; i >= 0; i--) {
      if (weekDone(starKeys[i])) {
        starKey = starKeys[i];
        break;
      }
    }
    // Wynik tygodnia = punkty za utarg + punkty za zadania wykonane w tym tygodniu (zadanie liczy się w tygodniu swojego terminu).
    const recs = (earnings || []).filter((r) => r.periodFrom && r.periodFrom >= RANKING_START_DATE && starOk(r) && starKey != null && periodKey(r) === starKey);
    const pf = recs[0] ? recs[0].periodFrom : null;
    const pt = recs[0] ? recs[0].periodTo : null;
    const inWeek = (t) => {
      const d = t.dateTo || t.dateFrom;
      return !!d && !!pf && d >= pf && d <= pt;
    };
    const cand = recs.map((r) => {
      const revenue = r.periodFrom === "2026-07-20" ? (r.appEarnings || 0) + (r.cashEarnings || 0) : r.appEarnings || 0;
      let tasksN = 0;
      let taskPts = 0;
      for (const t of tasks || []) {
        if ((t.completions || {})[r.name] === "completed" && inWeek(t)) {
          tasksN++;
          taskPts += t.points != null ? t.points : TASK_COMPLETE_POINTS;
        }
      }
      const revPts = pointsForWeekRevenue(revenue);
      return { name: r.name, revenue, tasksN, taskPts, revPts, points: revPts + taskPts };
    });
    cand.sort((a, b) => b.points - a.points || b.revenue - a.revenue);
    let star = cand[0] && cand[0].points > 0 ? { ...cand[0], periodFrom: pf, periodTo: pt, runners: cand.slice(1, 3).filter((c) => c.points > 0) } : null;
    let prev = null;
    if (rankingPeriods.length >= 2) {
      const pr = computeDriverRanking((earnings || []).filter((r) => periodKey(r) !== lastKey), drivers || [], tasks, pointAdjustments).rows;
      prev = {};
      pr.forEach((r, i) => (prev[r.name] = i + 1));
    }
    return { star, prev };
  }, [tab, rankingSubTab, earnings, drivers, tasks, pointAdjustments]);
  // Najlepszy pojedynczy tydzień CAŁEJ FIRMY — jeden kierowca, jeden
  // tydzień, najwyższy utarg — licząc z tych samych rozliczeń, co ranking
  // (od RANKING_START_DATE, tylko "Wypłacone"/"Przeniesiono").
  const companyBestWeek = (earnings || [])
    .filter((r) => r.periodFrom && r.periodFrom >= RANKING_START_DATE && COUNTED_STATUSES.includes(r.status))
    .reduce((best, r) => {
      const revenue = r.periodFrom === "2026-07-20" ? (r.appEarnings || 0) + (r.cashEarnings || 0) : r.appEarnings || 0;
      return !best || revenue > best.revenue ? { name: r.name, revenue, periodFrom: r.periodFrom, periodTo: r.periodTo } : best;
    }, null);
  const countedMine = mine
    .filter((r) => r.periodFrom && r.periodFrom >= RANKING_START_DATE && COUNTED_STATUSES.includes(r.status)) // tak samo jak w rankingu: Wypłacone + Przeniesiono (+ pozostałe liczone statusy)
    .map((r) => ({ ...r, _revenue: r.appEarnings || 0 }));
  // Seria i rekord liczą się z CAŁEJ historii rozliczeń (nie tylko z tych
  // "liczonych do rankingu") — to osobista statystyka kierowcy, niezależna
  // od tego, od kiedy działa ranking.
  const allMineWithRevenue = mine
    .filter((r) => COUNTED_STATUSES.includes(r.status))
    .map((r) => ({ ...r, _revenue: r.appEarnings || 0 }));
  const GOOD_WEEK_THRESHOLD = 1000;
  let currentStreak = 0;
  for (const r of allMineWithRevenue) {
    if (r._revenue >= GOOD_WEEK_THRESHOLD) currentStreak++;
    else break;
  }
  const bestWeek = allMineWithRevenue.reduce((best, r) => (!best || r._revenue > best._revenue ? r : best), null);
  const completedTasksCount = (tasks || []).filter(
    (t) => ((t.completions || {})[driver.name] || "pending") === "completed"
  ).length;
  // Zadania: AKTYWNE na wierzchu (najpierw te bez oceny, priorytetowe i z najbliższym terminem),
  // wszystko nieaktywne (po terminie / zamknięte) trafia do zwijanego archiwum pod spodem.
  const taskTodayStr = new Date().toISOString().slice(0, 10);
  const taskRows = (tasks || []).map((t) => {
    const myStatus = (t.completions || {})[driver.name]; // undefined = jeszcze nieocenione
    const expired = !!(t.dateTo && t.dateTo < taskTodayStr);
    const isActive = t.status !== "completed" && !expired;
    return {
      t,
      myStatus,
      isActive,
      points: t.points != null ? t.points : TASK_COMPLETE_POINTS,
      daysLeft: taskDaysLeft(t, taskTodayStr),
    };
  });
  const activeTaskRows = taskRows
    .filter((r) => r.isActive)
    .sort((a, b) => {
      const ap = a.myStatus ? 1 : 0;
      const bp = b.myStatus ? 1 : 0;
      if (ap !== bp) return ap - bp;
      if (!!a.t.priority !== !!b.t.priority) return a.t.priority ? -1 : 1;
      const ad = a.daysLeft == null ? 1e9 : a.daysLeft;
      const bd = b.daysLeft == null ? 1e9 : b.daysLeft;
      if (ad !== bd) return ad - bd;
      return (b.t.createdAt || 0) - (a.t.createdAt || 0);
    });
  const archiveTaskRows = taskRows.filter((r) => !r.isActive).sort((a, b) => (b.t.createdAt || 0) - (a.t.createdAt || 0));
  const taskStats = {
    total: taskRows.length,
    done: taskRows.filter((r) => r.myStatus === "completed").length,
    points: taskRows.reduce((s, r) => s + (r.myStatus === "completed" ? r.points : 0), 0),
    activeCount: activeTaskRows.length,
    archDone: archiveTaskRows.filter((r) => r.myStatus === "completed").length,
    archMissed: archiveTaskRows.filter((r) => r.myStatus === "not_completed").length,
  };

  const fullDriverRecord = (drivers || []).find((d) => sameName(d.name, driver.name)) || {};
  const isGroupMuted =
    fullDriverRecord.groupMutedUntil &&
    (fullDriverRecord.groupMutedUntil === "forever" || fullDriverRecord.groupMutedUntil > Date.now());
  // Blokada czatu z Szefem + połączeń (jak wyciszenie na grupie): chatBlockedUntil = null | "forever" | znacznik czasu (ms)
  const isChatBlocked =
    fullDriverRecord.chatBlockedUntil &&
    (fullDriverRecord.chatBlockedUntil === "forever" || fullDriverRecord.chatBlockedUntil > Date.now());
  function canSee(key) {
    return !fullDriverRecord.permissions || fullDriverRecord.permissions[key] !== false;
  }
  useEffect(() => {
    const gatedTabs = ["bonusy", "rekompensaty", "czat", "grupa", "ranking", "fortuna"];
    if (gatedTabs.includes(tab) && !canSee(tab)) setTab("rozliczenia");
  }, [tab, fullDriverRecord.permissions]);

  // Zadłużenie to teraz osobna, niezależna lista — admin dodaje/edytuje/
  // usuwa konkretne, nazwane długi (np. "-300 pożyczka") ręcznie w panelu.
  // NIE wynika już z wyliczonej wypłaty za dany tydzień — to były dwie
  // różne rzeczy, które wcześniej się myliły.
  const myDebts = (driverDebts || []).filter((d) => sameName(d.driverName, driver.name) && d.status !== "resolved");
  const debtEntries = myDebts; // zachowana nazwa dla reszty JSX poniżej
  const totalDebt = -myDebts.reduce((sum, d) => sum + (d.amount || 0), 0); // ujemne
  const myDebtRepayments = (debtRepayments || []).filter((r) => sameName(r.driverName, driver.name));
  const totalRepaid = myDebtRepayments.filter((r) => r.status === "approved").reduce((sum, r) => sum + r.amount, 0);
  // WAŻNE: "ile aktualnie zostało do spłaty" to PO PROSTU suma długów ze
  // statusem innym niż "resolved" (totalDebt) — status na każdym długu
  // JUŻ JEST źródłem prawdy o tym, co jest spłacone. totalRepaid to
  // historyczna, ŁĄCZNA suma WSZYSTKICH zgłoszeń spłaty tego kierowcy
  // KIEDYKOLWIEK (do samego pokazania "Spłacone dotąd: X" niżej) — kiedyś
  // była też odejmowana tutaj, ale to błędnie odejmowało STARE, już
  // rozliczone spłaty od zupełnie NOWYCH, niepowiązanych długów, więc
  // pozostałe zadłużenie wychodziło zaniżone.
  const netDebt = Math.round(totalDebt * 100) / 100; // wciąż ujemne, jeśli coś zostało
  const hasNotStartedWeek = mine.some((r) => r.status === "notstarted");
  const bonusEntries = (globalBonuses || [])
    .filter((b) => !b.recipientNames || b.recipientNames.some((n) => sameName(n, driver.name)))
    .map((b) => ({
      id: b.id,
      label: b.title,
      amount: b.amount,
      note: b.note,
      dates: bonusDateLabel(b),
      status: bonusStatus(b), // "active" / "ended" / "upcoming" — czy bonus w ogóle jeszcze obowiązuje
      // Stare bonusy sprzed tej zmiany miały jeden wspólny status dla
      // wszystkich — jeśli ten bonus nie ma jeszcze osobnych danych, a
      // kiedyś był oznaczony jako ukończony w całości, traktujemy to jako
      // zrealizowane, żeby nic nie wyglądało na "cofnięte".
      myStatus:
        (b.completions || {})[driver.name] !== undefined
          ? (b.completions || {})[driver.name]
          : b.status === "completed"
            ? "completed"
            : undefined,
      active: bonusCountsTowardTotal(b),
      sortKey: b.dateFrom || b.createdAt || "",
    }))
    .sort((a, b) => (b.sortKey > a.sortKey ? 1 : -1));
  const totalBonus = bonusEntries.filter((r) => r.myStatus === "completed").reduce((s, r) => s + r.amount, 0);
  const car = driverCar(mine, fullDriverRecord.car);

  const completedBonusCount = bonusEntries.filter((b) => b.myStatus === "completed").length;
  const [celebration, setCelebration] = useState(null);
  // Zdobyte tickety za zadania: świętowanie (złoty bilet na pełnym ekranie) tylko dla NOWO wykonanych zadań z ticketem
  const tkDone = useMemo(() => (tasks || []).filter((t) => ticketsOfTask(t) > 0 && (t.completions || {})[driver.name] === "completed"), [tasks, driver.name]);
  const tkDoneSig = tkDone.map((t) => t.id).join(",");
  const [tkWow, setTkWow] = useState(null);
  // "Co nowego": przewodnik pokazywany RAZ na telefonie (po chwili od wejścia; doładowywany osobnym plikiem tylko wtedy)
  const [showNew, setShowNew] = useState(false);
  useEffect(() => {
    if (trackPresence === false) return undefined;
    try {
      if (localStorage.getItem("patex-whatsnew-v1")) return undefined;
    } catch (e) {
      return undefined;
    }
    const t = setTimeout(() => setShowNew(true), 3500);
    return () => clearTimeout(t);
  }, []);
  // "Uwolnij dziewczynkę" (halloween): instrukcja po zalogowaniu, ukryta dziewczynka w Bonusy → Archiwalne, ticket za uwolnienie.
  const [girlOn] = useState(() => trackPresence !== false && halloweenOn());
  const gKey = girlKey(driver.name);
  const [girlFreed, setGirlFreed] = useState(() => girlOn && girlGet(gKey) != null);
  const [girlPhase, setGirlPhase] = useState(null); // null | "intro" | "chip" | "reveal"
  const [girlClaim, setGirlClaim] = useState("pending"); // pending | ok | fail
  const showNewRef = useRef(false);
  showNewRef.current = showNew;
  useEffect(() => {
    if (!girlOn) return undefined;
    let dead = false;
    if (girlGet(gKey) != null) {
      girlSet("patex_girl_device", "1");
      if (onGirlFreed) onGirlFreed(true);
    }
    // 4 s po wejściu: dosłanie niepotwierdzonego ticketu albo sprawdzenie, czy kierowca uwolnił ją już na innym telefonie
    const t = setTimeout(async () => {
      const local = girlGet(gKey);
      if (local === "pending") {
        const r = await girlCall(driver, "free");
        if (!dead && r && r.ok && r.freed) girlSet(gKey, "1");
      } else if (local == null) {
        const r = await girlCall(driver, "check");
        if (!dead && r && r.ok && r.freed) {
          girlSet(gKey, "1");
          girlSet("patex_girl_device", "1");
          setGirlFreed(true);
          hwStop(1200);
          if (onGirlFreed) onGirlFreed(true);
        }
      }
    }, 4000);
    return () => {
      dead = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (!girlOn || girlFreed) return undefined;
    const unseen = !girlGet("patex-whatsnew-v1");
    let t = 0;
    const tick = () => {
      if (showNewRef.current) {
        t = setTimeout(tick, 1500); // najpierw "Co nowego", dopiero potem dziewczynka
        return;
      }
      if (GIRL_SHOWN.v) return; // pełna instrukcja przy każdym uruchomieniu appki (raz na uruchomienie), dopóki dziewczynka nie zostanie uwolniona
      GIRL_SHOWN.v = true;
      setGirlPhase((cur) => cur || "intro");
    };
    t = setTimeout(tick, unseen ? 4600 : 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [girlOn, girlFreed]);
  function girlFind() {
    if (girlFreed) return;
    girlSet(gKey, "pending");
    girlSet("patex_girl_device", "1");
    setGirlFreed(true);
    setGirlClaim("pending");
    setGirlPhase("reveal");
    hwStop(1600);
    if (onGirlFreed) onGirlFreed();
    girlCall(driver, "free").then((r) => {
      if (r && r.ok && r.freed) {
        girlSet(gKey, "1");
        setGirlClaim("ok");
      } else {
        setGirlClaim("fail");
      }
    });
  }
  // Ankieta zadowolenia (jednorazowa): stan z serwera zapamiętany w telefonie, żeby karta pokazywała się od razu
  const svKey = "patex-survey-" + driver.name;
  const svOn = trackPresence !== false;
  const [sv, setSv] = useState(() => {
    try {
      const c = localStorage.getItem(svKey);
      return c ? JSON.parse(c) : null;
    } catch (e) {
      return null;
    }
  });
  const [svOpen, setSvOpen] = useState(false);
  // Panel 3.0 (nowy wygląd): tylko prawdziwy kierowca (nie podgląd admina) i bez wyłącznika awaryjnego
  const ui3 = trackPresence !== false && !uiClassic();
  const wk = ui3 ? weekInfo() : null;
  const [profileOpen, setProfileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => {
    if (!ui3) return undefined;
    const el = document.documentElement;
    el.classList.add("ui3");
    if (lowEnd()) el.classList.add("lite");
    return () => {
      el.classList.remove("ui3");
      el.classList.remove("lite");
    };
  }, [ui3]);
  function svSave(r) {
    const st = { enabled: r.enabled !== false, answered: !!r.answered, total: r.total || 0, count: r.count || 0, stats: r.stats || null };
    setSv(st);
    try {
      localStorage.setItem(svKey, JSON.stringify(st));
    } catch (e) {
      /* nic */
    }
  }
  useEffect(() => {
    if (!svOn) return undefined;
    let dead = false;
    const t = setTimeout(async () => {
      const r = await svPost(driver, "state");
      if (!dead && r && r.ok) svSave(r);
    }, 3500);
    return () => {
      dead = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line
  }, []);
  // Zdrapki od admina (zakładka Bonusy): lista z serwera zapamiętana w telefonie, odświeżana po wejściu w Bonusy
  const scKey = "patex-sc-" + driver.name;
  const [scList, setScList] = useState(() => {
    try {
      const c = localStorage.getItem(scKey);
      return c ? JSON.parse(c) : [];
    } catch (e) {
      return [];
    }
  });
  async function scRefresh() {
    const r = await fzPostJson("/scratch/list", driver);
    if (r && r.ok && Array.isArray(r.cards)) {
      setScList(r.cards);
      try {
        localStorage.setItem(scKey, JSON.stringify(r.cards));
      } catch (e) {
        /* nic */
      }
    }
  }
  useEffect(() => {
    if (!svOn) return undefined;
    const t = setTimeout(scRefresh, 5200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (svOn && tab === "bonusy") scRefresh();
    // eslint-disable-next-line
  }, [tab]);
  function scClaimed(id, result) {
    setScList((l) => {
      const n = l.map((c) => (c.id === id ? { ...c, claimed: true, result } : c));
      try {
        localStorage.setItem(scKey, JSON.stringify(n));
      } catch (e) {
        /* nic */
      }
      return n;
    });
  }
  useEffect(() => {
    if (trackPresence === false || !(tasks || []).length) return;
    const key = "patex-tk-done-" + driver.name;
    let seen = null;
    try {
      seen = JSON.parse(localStorage.getItem(key) || "null");
    } catch (e) {
      seen = null;
    }
    const ids = tkDone.map((t) => t.id);
    const save = () => {
      try {
        localStorage.setItem(key, JSON.stringify(ids));
      } catch (e) {
        /* ignore */
      }
    };
    if (!Array.isArray(seen)) {
      save(); // pierwsze uruchomienie na tym telefonie: bez świętowania wstecz
      return;
    }
    const fresh = tkDone.filter((t) => !seen.includes(t.id));
    if (fresh.length) {
      save();
      setTkWow({ n: fresh.reduce((s, t) => s + ticketsOfTask(t), 0), title: fresh[0].title, count: fresh.length });
    } else if (ids.length !== seen.length) save();
  }, [tkDoneSig]);
  const prevMilestonesRef = useRef({
    latestKey: latest ? latest.periodFrom : null,
    latestPaid: !!(latest && latest.status === "paid"),
    tasks: completedTasksCount,
    bonuses: completedBonusCount,
    bestWeekPeriod: bestWeek ? bestWeek.periodFrom : null,
    initialized: false,
  });
  useEffect(() => {
    const prev = prevMilestonesRef.current;
    if (prev.initialized && latest && prev.latestKey && prev.latestKey !== latest.periodFrom) buzz(40); // nowy tydzień
    if (prev.initialized) {
      if (latest && prev.latestKey === latest.periodFrom && !prev.latestPaid && latest.status === "paid") {
        setCelebration(`Wypłata za ${periodLabel(latest)} została wysłana! 💸`);
      } else if (completedTasksCount > prev.tasks) {
        setCelebration("Wykonałeś zadanie! +30 punktów do rankingu 🏆");
      } else if (completedBonusCount > prev.bonuses) {
        setCelebration("Zdobyłeś bonus! Sprawdź swoje rekompensaty 💰");
      } else if (
        bestWeek &&
        bestWeek.periodFrom !== prev.bestWeekPeriod &&
        prev.bestWeekPeriod !== null &&
        bestWeek.periodFrom > prev.bestWeekPeriod
      ) {
        setCelebration(`Nowy rekord tygodnia! ${formatPLN(bestWeek._revenue)} 🔥`);
      }
    }
    prevMilestonesRef.current = {
      latestKey: latest ? latest.periodFrom : null,
      latestPaid: !!(latest && latest.status === "paid"),
      tasks: completedTasksCount,
      bonuses: completedBonusCount,
      bestWeekPeriod: bestWeek ? bestWeek.periodFrom : null,
      initialized: true,
    };
    // eslint-disable-next-line
  }, [completedTasksCount, completedBonusCount, bestWeek && bestWeek.periodFrom, latest && latest.periodFrom, latest && latest.status]);

  async function handleRefresh() {
    setRefreshing(true);
    setRefreshMsg("");
    const result = await onRefresh();
    setRefreshing(false);
    if (!result.ok) {
      setRefreshMsg("Nie udało się odświeżyć danych. Spróbuj ponownie.");
    } else if (result.count > 0) {
      setRefreshMsg("Dane zaktualizowane.");
    } else {
      setRefreshMsg("Dane są już aktualne.");
    }
  }
  const { pullDistance, refreshing: pullRefreshing } = usePullToRefresh(handleRefresh);

  return (
    <div className={ui3 ? "screen dash3" : "screen"}>
      {callAllMsg && (
        <CallAllOverlay
          msg={callAllMsg}
          onOpen={() => {
            setCallAllMsg(null);
            setTab("grupa");
          }}
          onClose={() => setCallAllMsg(null)}
        />
      )}
      {(pullDistance > 0 || pullRefreshing) && (
        <div
          className="pull-refresh-indicator"
          style={{
            height: pullRefreshing ? 44 : pullDistance,
            opacity: pullRefreshing ? 1 : Math.min(pullDistance / 60, 1),
          }}
        >
          <RefreshCw
            size={18}
            className={pullRefreshing ? "spin" : ""}
            style={pullRefreshing ? {} : { transform: `rotate(${pullDistance * 3}deg)` }}
          />
        </div>
      )}
      {ui3 && (
        <div className="hd3">
          <button className="hd3-av" onClick={() => setProfileOpen(true)} aria-label="Profil">
            <MiniRing pct={wk.frac} />
            <b>{avatar || initials(driver.name)}</b>
            <i>{wk.idx + 1}/7</i>
          </button>
          <div className="t">
            <b>
              {(() => {
                const h = new Date().getHours();
                const firstName = driver.name.split(" ")[0];
                return `Cześć, ${firstName} ${h < 5 || h >= 18 ? "🌙" : h < 12 ? "☀️" : "🌤️"}`;
              })()}
            </b>
            <small>{wk.left === 0 ? "Dziś ostatni dzień tygodnia" : `Tydzień trwa · rozliczenie za ${wk.left} ${wk.left === 1 ? "dzień" : "dni"}`}</small>
          </div>
          <button className="hd3-btn" onClick={onToggleTheme} aria-label="Zmień motyw">
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>
        </div>
      )}
      {!ui3 && (
      <>
      <TopBar
        title="Twoje rozliczenia"
        right={
          <>
            <button className="icon-btn" onClick={onToggleTheme} aria-label="Zmień motyw">
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button className="icon-btn" onClick={onLogout} aria-label="Wyloguj">
              <LogOut size={18} />
            </button>
          </>
        }
      />
      <div className="dash-header-row">
        <div className="avatar-wrap">
          <button className="avatar avatar-btn" onClick={() => setAvatarPickerOpen((o) => !o)} aria-label="Zmień awatar">
            {avatar || initials(driver.name)}
          </button>
          <span className="avatar-edit-badge">
            <Pencil size={9} />
          </span>
          {avatarPickerOpen && (
            <div className="avatar-picker">
              <div className="avatar-picker-title">Wybierz awatar</div>
              <div className="avatar-picker-grid">
                {AVATAR_EMOJIS.map((e) => (
                  <button
                    key={e}
                    className={`avatar-emoji-btn ${avatar === e ? "selected" : ""}`}
                    onClick={() => {
                      onSetAvatar(e);
                      setAvatarPickerOpen(false);
                    }}
                  >
                    {e}
                  </button>
                ))}
              </div>
              {avatar && (
                <button
                  className="avatar-reset-btn"
                  onClick={() => {
                    onSetAvatar(null);
                    setAvatarPickerOpen(false);
                  }}
                >
                  Wróć do inicjałów
                </button>
              )}
            </div>
          )}
        </div>
        <div>
          <div className="dash-greeting">
            {(() => {
              const h = new Date().getHours();
              const firstName = driver.name.split(" ")[0];
              if (h < 5) return `Dobrej nocy, ${firstName} 🌙`;
              if (h < 12) return `Dzień dobry, ${firstName} ☀️`;
              if (h < 18) return `Miłego popołudnia, ${firstName} 🌤️`;
              return `Dobry wieczór, ${firstName} 🌙`;
            })()}
          </div>
          <div className="dash-subtitle">
            {mine.length === 0 ? "brak rozliczeń w historii" : `${mine.length} rozliczeń w historii`}
          </div>
        </div>
        {car && (
          <div className="car-badge">
            <Car size={14} />
            <span>{car}</span>
          </div>
        )}
      </div>
      </>
      )}

      {trackPresence !== false && typeof Notification !== "undefined" && "PushManager" in window && Notification.permission !== "granted" && (
        <LazyBoundary quiet name="push">
          <React.Suspense fallback={null}>
            <PushHelpLazy onEnable={() => subscribeAndReportPushStatus(driver.name, true)} />
          </React.Suspense>
        </LazyBoundary>
      )}
      {!ui3 && (
      <div className="tab-bar">
        <button className={`tab-btn ${tab === "rozliczenia" ? "active" : ""}`} onClick={() => setTab("rozliczenia")}>
          <Receipt size={14} /> Rozliczenia
        </button>
        <button className={`tab-btn ${tab === "zadluzenie" ? "active" : ""}`} onClick={() => setTab("zadluzenie")}>
          <AlertTriangle size={14} /> Zadłużenie
          {debtEntries.length > 0 && <span className="tab-dot" />}
        </button>
        {canSee("bonusy") && (
          <button className={`tab-btn ${tab === "bonusy" ? "active" : ""}`} onClick={() => setTab("bonusy")}>
            <Gift size={14} /> Bonusy
            {(bonusEntries.some((r) => r.active) || scList.some((c) => !c.claimed)) && <span className="tab-dot bonus" />}
          </button>
        )}
        {canSee("rekompensaty") && (
          <button
            className={`tab-btn ${tab === "rekompensaty" ? "active" : ""}`}
            onClick={() => setTab("rekompensaty")}
          >
            <Wallet size={14} /> Rekompensaty
            {myCompRequests.some((r) => r.status === "pending") && <span className="tab-dot" />}
          </button>
        )}
        {canSee("czat") && (
          <button className={`tab-btn ${tab === "czat" ? "active" : ""}`} onClick={() => setTab("czat")}>
            <MessageCircle size={14} /> Czat
            {(myThreads.some((t) => t.unreadForDriver) || myDmThreads.some((t) => (t.unreadFor || []).includes(driver.name))) && (
              <span className="tab-dot" />
            )}
          </button>
        )}
        {canSee("grupa") && (
          <button className={`tab-btn ${tab === "grupa" ? "active" : ""}`} onClick={() => setTab("grupa")}>
            <Users size={14} /> Grupa
            {hasUnreadGroup && <span className="tab-dot" />}
          </button>
        )}
        {canSee("ranking") && (
          <button className={`tab-btn ${tab === "ranking" ? "active" : ""}`} onClick={() => setTab("ranking")}>
            <Trophy size={14} /> Ranking
          </button>
        )}
        {canSee("fortuna") && (
          <button className={`tab-btn tab-fz ${tab === "fortuna" ? "active" : ""}`} onClick={() => setTab("fortuna")} onTouchStart={preloadFortune} onMouseEnter={preloadFortune}>
            <Ticket size={14} /> Fortuna
          </button>
        )}
      </div>
      )}

      {tab === "rozliczenia" && (
        <>
          {ui3 && (
            <>
              <OrbHero pct={wk.frac} gid="oh1" c1="#6ee7b7" c2="#ffd166">
                <div className="orb3-lbl">DO WYPŁATY</div>
                <AmountBig value={totalDue + compensationAdjustment} tone={totalDue + compensationAdjustment < 0 ? "red" : "green"} sm />
                {latest ? (
                  (() => {
                    const bd = statusBadge(latest.status);
                    const BdI = bd.Icon;
                    return (
                      <>
                        <span className={`badge ${bd.cls} orb3-pill`}>
                          <BdI size={12} className={bd.spin ? "spin" : ""} />
                          {bd.text}
                        </span>
                        <span className="orb3-sub">{periodLabel(latest)}</span>
                      </>
                    );
                  })()
                ) : (
                  <span className="orb3-sub">brak rozliczeń</span>
                )}
              </OrbHero>
              <div className="chips3">
                {bestWeek && <span>🏆 {formatPLN(bestWeek._revenue)}</span>}
                {cachedTickets(driver.name) != null && <span>🎟️ {cachedTickets(driver.name)}</span>}
                {currentStreak > 0 && <span>🔥 {currentStreak} tyg.</span>}
              </div>
            </>
          )}
          {!ui3 && (
          <>
          {currentStreak > 0 && (
            <div className="streak-mini-chip">
              <span className="flame">🔥</span>
              {currentStreak} {currentStreak === 1 ? "dobry tydzień" : currentStreak < 5 ? "dobre tygodnie" : "dobrych tygodni"} z rzędu
            </div>
          )}
          <div
            className={`dash-hero dash-hero-${totalDue + compensationAdjustment < 0 ? "red" : latestResolved ? "green" : "amber"}`}
          >
            <div className="dash-hero-glow" />
            <Sparkles size={14} className="hero-sparkle" />
            <div className="dash-hero-label">Do wypłaty</div>
            <DigitDisplay
              value={totalDue + compensationAdjustment}
              tone={totalDue + compensationAdjustment < 0 ? "red" : latestResolved ? "green" : "amber"}
              size="lg"
            />
            <div className="dash-hero-sub">
              {latest
                ? latestResolved
                  ? `rozliczone za ${periodLabel(latest)}`
                  : `za okres ${periodLabel(latest)}`
                : "brak rozliczeń"}
            </div>
          </div>
          </>
          )}

          {(() => {
            // oś pokazuje bieżący cykl: najstarsze jeszcze niezamknięte rozliczenie (nie "pusty" tydzień z przodu), a gdy wszystko zamknięte — ostatnie
            const cut = Date.now() - 28 * 86400000;
            const cyc = mine.filter((r) => (r.status === "notstarted" || r.status === "inprogress") && (!r.periodTo || Date.parse(r.periodTo) > cut)).slice(-1)[0] || latest;
            return cyc ? <PayoutTimeline key={cyc.periodFrom} record={cyc} decision={payoutDecisions[payoutDecisionKey(cyc)]} ui3={ui3} /> : null;
          })()}
          {svOn && sv && sv.enabled && !sv.answered && (
            <LazyBoundary quiet name="svpromo">
            <React.Suspense fallback={null}>
              <SurveyPromoLazy count={sv.count} total={sv.total} onOpen={() => setSvOpen(true)} />
            </React.Suspense>
          </LazyBoundary>
          )}
          {svOn && sv && sv.answered && sv.stats && (
            <LazyBoundary quiet name="svres">
            <React.Suspense fallback={null}>
              <SatisfactionCardLazy stats={sv.stats} total={sv.total} compact />
            </React.Suspense>
          </LazyBoundary>
          )}

          {payErr && latest && latest.status === "inprogress" && !payoutDecisions[payoutDecisionKey(latest)] && <div className="save-err">{payErr}</div>}
          {ui3 && latest && latest.status === "inprogress" && !payoutDecisions[payoutDecisionKey(latest)] && (
            <PayoutActions3 amount={totalDue + compensationAdjustment} onChoose={chooseAction} />
          )}
          {!ui3 && latest && latest.status === "inprogress" && !payoutDecisions[payoutDecisionKey(latest)] && (
            <div className="payout-action-card">
              <div className="payout-action-label">Co robimy z tą wypłatą?</div>
              <div className="payout-action-btns">
                <button className="payout-btn approve" onClick={() => chooseAction("approved")}>
                  <CheckCircle2 size={15} /> Zaakceptuj
                </button>
                <button className="payout-btn reject" onClick={() => chooseAction("rejected")}>
                  <XCircle size={15} /> Odrzuć
                </button>
                <button className="payout-btn review" onClick={() => chooseAction("next_week")}>
                  <CalendarClock size={15} /> Przenieś na kolejny tydzień
                </button>
              </div>
            </div>
          )}

          {latest && latest.status === "inprogress" && payoutDecisions[payoutDecisionKey(latest)] && (
            <div
              className={`payout-status-card ${
                payoutDecisions[payoutDecisionKey(latest)].paid ? "paid" : payoutDecisions[payoutDecisionKey(latest)].action
              }`}
            >
              {payoutDecisions[payoutDecisionKey(latest)].paid ? (
                <CheckCircle2 size={14} />
              ) : (
                <>
                  {payoutDecisions[payoutDecisionKey(latest)].action === "approved" && <CheckCircle2 size={14} />}
                  {payoutDecisions[payoutDecisionKey(latest)].action === "rejected" && <XCircle size={14} />}
                  {payoutDecisions[payoutDecisionKey(latest)].action === "next_week" && <CalendarClock size={14} />}
                  {payoutDecisions[payoutDecisionKey(latest)].action === "checking" && <Search size={14} />}
                </>
              )}
              <span>
                {payoutDecisions[payoutDecisionKey(latest)].paid
                  ? "Wypłacone"
                  : payoutDecisions[payoutDecisionKey(latest)].action === "approved"
                  ? "Oczekuje na wypłatę"
                  : payoutDecisions[payoutDecisionKey(latest)].action === "rejected"
                  ? "Wypłata odrzucona"
                  : payoutDecisions[payoutDecisionKey(latest)].action === "next_week"
                  ? "Przeniesiona na kolejny tydzień"
                  : payoutDecisions[payoutDecisionKey(latest)].action === "checking"
                  ? "Administrator sprawdza Twoją wypłatę"
                  : ""}
              </span>
            </div>
          )}

          {reasonModalOpen &&
            createPortal(
              <div className="modal-overlay" onClick={() => setReasonModalOpen(false)}>
                <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-title">Dlaczego odrzucasz tę wypłatę?</div>
                  <textarea
                    className="modal-textarea"
                    placeholder="Opisz krótko powód..."
                    value={reasonNote}
                    onChange={(e) => setReasonNote(e.target.value)}
                    autoFocus
                  />
                  <div className="modal-btns">
                    <button className="btn btn-ghost" onClick={() => setReasonModalOpen(false)}>
                      Anuluj
                    </button>
                    <button className="btn btn-primary" onClick={submitReasonModal}>
                      Wyślij
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

          <div className="refresh-row">
            <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={13} className={refreshing ? "spin" : ""} />
              {refreshing ? "Odświeżanie…" : "Odśwież dane"}
            </button>
            {refreshMsg && <span className="refresh-msg">{refreshMsg}</span>}
          </div>
          <div className="section-label">Historia rozliczeń</div>
          {refreshing ? (
            <div className="ticket-list">
              {[0, 1, 2].map((i) => (
                <div className="skeleton-row" key={i} style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="skeleton-line skeleton-line-title" />
                  <div className="skeleton-line skeleton-line-sub" />
                </div>
              ))}
            </div>
          ) : mine.length === 0 ? (
            <EmptyCard emoji="🧾" title="Brak rozliczeń" hint="Pierwsze rozliczenie pojawi się tutaj po zakończeniu tygodnia." />
          ) : (
            <div className={ui3 ? "ticket-list tl3" : "ticket-list"}>
              {mine.map((r, i) => {
                const approvedForThis = myCompRequests.filter(
                  (cr) => cr.status === "approved" && cr.periodFrom === r.periodFrom && cr.periodTo === r.periodTo
                );
                let displayRecord = r;
                if (approvedForThis.length > 0) {
                  const adj = approvedForThis.reduce(
                    (sum, cr) => sum + (cr.type === "add" ? cr.amount : -cr.amount),
                    0
                  );
                  const compNotes = approvedForThis
                    .map((cr) => `${cr.type === "add" ? "+" : "-"}${formatPLN(cr.amount)} ${cr.note}`)
                    .join(" | ");
                  displayRecord = {
                    ...r,
                    compensations: r.compensations + adj,
                    payout: r.payout + adj,
                    notatka: [r.notatka, compNotes].filter(Boolean).join(" | "),
                  };
                }
                return <TicketRow key={r.id} record={displayRecord} index={i} prev={mine[i + 1]} onAsk={canSee("czat") ? () => setTab("czat") : null} />;
              })}
            </div>
          )}
        </>
      )}

      {tab === "zadluzenie" && (
        <>
          {ui3 && (() => {
            const pos = myDebts.reduce((x, d) => x + ((d.amount || 0) > 0 ? d.amount : 0), 0);
            const cred = myDebts.reduce((x, d) => x + ((d.amount || 0) < 0 ? -d.amount : 0), 0);
            const left = Math.max(0, -netDebt);
            const pct = left <= 0.005 ? 1 : pos > 0 ? Math.max(0, Math.min(1, cred / pos)) : 0;
            const firstTs = myDebts.filter((d) => (d.amount || 0) > 0).reduce((m, d) => Math.min(m, d.createdAt || Date.now()), Date.now());
            const days = Math.max(1, (Date.now() - firstTs) / 86400000);
            let eta = "";
            if (left > 0.5 && days >= 10 && cred > 0) {
              const dd = left / (cred / days);
              eta = dd > 365 ? "tempo: ponad rok" : dd < 8 ? "tempo: poniżej tygodnia" : `tempo: ok. ${Math.round(dd / 7)} tyg.`;
            }
            return (
              <>
                <OrbHero pct={pct} gid="oh8" c1="#6ee7b7" c2="#ffd166">
                  <div className="orb3-lbl">{left <= 0.005 ? "ZADŁUŻENIE" : "SPŁACONO"}</div>
                  <div className="big3 mint">{left <= 0.005 ? "0 zł" : Math.round(pct * 100) + "%"}</div>
                  <span className="orb3-sub">{left <= 0.005 ? "nic nie wisi 🎉" : "zostało " + formatPLN(left).replace(",00", "")}</span>
                </OrbHero>
                {(() => {
                  const okRep = myDebtRepayments.filter((r) => r.status === "approved").sort((x, y) => (y.requestedAt || 0) - (x.requestedAt || 0));
                  const dLeft = left > 0.5 && cred > 0 ? Math.ceil(left / (cred / days)) : null;
                  const endD = dLeft && dLeft < 3650 ? new Date(Date.now() + dLeft * 86400000) : null;
                  const filled = Math.floor(pct * 10);
                  return (
                    <>
                      <div className="chips3">
                        {okRep[0] && <span>💳 ostatnia {formatPLN(okRep[0].amount).replace(",00", "")}</span>}
                        <span>🧾 {okRep.length} {okRep.length === 1 ? "spłata" : okRep.length < 5 && okRep.length > 1 ? "spłaty" : "spłat"}</span>
                        {endD && <span>🏁 ok. {String(endD.getDate()).padStart(2, "0") + "." + String(endD.getMonth() + 1).padStart(2, "0")}</span>}
                      </div>
                      {dLeft != null && dLeft < 3650 && (
                        <div className="free3">
                          <b>{dLeft}</b>
                          <div>
                            <small>DNI DO WOLNOŚCI</small>
                            <div className="dots3">
                              {Array.from({ length: 10 }, (_, i) => (
                                <i key={i} className={i < filled ? "on" : i === filled ? "nx" : ""} />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            );
          })()}
          <div className="section-label debt-label" style={{ marginTop: 18 }}>
            <AlertTriangle size={13} /> Zadłużenie
          </div>
          {debtEntries.length === 0 ? (
            <EmptyCard emoji="🎉" title="Zero zadłużenia" hint="Nic nie wisi — tak trzymaj!" />
          ) : (
            <>
              <div className={ui3 ? "debt-hero-card dh-old" : "debt-hero-card"}>
                <div className="debt-hero-glow" />
                <div className="debt-hero-label">Pozostałe zadłużenie</div>
                <div className="debt-hero-value">{formatPLN(netDebt)}</div>
                {(() => {
                  const pos = myDebts.reduce((s, d) => s + ((d.amount || 0) > 0 ? d.amount : 0), 0);
                  const cred = myDebts.reduce((s, d) => s + ((d.amount || 0) < 0 ? -d.amount : 0), 0);
                  if (pos <= 0 || cred <= 0) return null;
                  const firstTs = myDebts.filter((d) => (d.amount || 0) > 0).reduce((m, d) => Math.min(m, d.createdAt || Date.now()), Date.now());
                  return (
                    <LazyBoundary quiet name="debt">
                      <React.Suspense fallback={null}>
                        <DebtRingLazy pos={pos} cred={cred} firstTs={firstTs} />
                      </React.Suspense>
                    </LazyBoundary>
                  );
                })()}
                {totalRepaid > 0 && (
                  <div className="debt-hero-repaid">
                    <CheckCircle2 size={13} /> Spłacone dotąd: {formatPLN(totalRepaid)}
                  </div>
                )}
              </div>

              {netDebt < -0.01 && (
                <>
                  {!debtRepayFormOpen ? (
                    <button className="debt-repay-cta" onClick={() => setDebtRepayFormOpen(true)}>
                      <PlusCircle size={17} /> Spłać zadłużenie
                    </button>
                  ) : (
                    <div className="debt-repay-form">
                      <label className="form-label">Kwota spłaty</label>
                      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                        <input
                          className="comp-amount-input"
                          style={{ flex: 1 }}
                          type="text"
                          inputMode="decimal"
                          placeholder="np. 200"
                          value={debtRepayAmount}
                          onChange={(e) => setDebtRepayAmount(e.target.value)}
                        />
                        <button
                          type="button"
                          className="debt-full-btn"
                          onClick={() => setDebtRepayAmount(String(Math.abs(netDebt)))}
                        >
                          Spłać całość ({formatPLN(-netDebt)})
                        </button>
                      </div>

                      <label className="form-label">Sposób spłaty</label>
                      <div className="crypto-direction-toggle" style={{ marginBottom: 14 }}>
                        <button
                          type="button"
                          className={`crypto-direction-btn long ${debtRepayMethod === "blik" ? "active" : ""}`}
                          onClick={() => setDebtRepayMethod("blik")}
                        >
                          <Wallet size={14} /> BLIK
                        </button>
                        <button
                          type="button"
                          className={`crypto-direction-btn short ${debtRepayMethod === "deduct_next_week" ? "active" : ""}`}
                          disabled={!hasNotStartedWeek}
                          onClick={() => hasNotStartedWeek && setDebtRepayMethod("deduct_next_week")}
                          style={!hasNotStartedWeek ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                        >
                          <Minus size={14} /> Odejmij od rozliczenia
                        </button>
                      </div>

                      {debtRepayMethod === "blik" && (
                        <div className="debt-info-note">
                          <Wallet size={15} />
                          <div>
                            <b>BLIK na numer: 573 188 202</b>
                            <div>Wyślij, potem zgłoś tutaj — administrator potwierdzi po zaksięgowaniu wpłaty.</div>
                          </div>
                        </div>
                      )}
                      {debtRepayMethod === "deduct_next_week" && !hasNotStartedWeek && (
                        <div className="debt-info-note warn">
                          <Lock size={15} />
                          <div>
                            <b>Brak dostępnego tygodnia</b>
                            <div>Nie masz teraz tygodnia "Nie rozpoczęto" — poczekaj na nowy, albo wybierz BLIK.</div>
                          </div>
                        </div>
                      )}

                      {debtErr && <div className="save-err">{debtErr}</div>}
                      <div className="comp-action-row" style={{ marginTop: 4 }}>
                        <button
                          className="btn btn-ghost"
                          disabled={debtSaving}
                          onClick={() => {
                            debtReqRef.current = null;
                            setDebtErr("");
                            setDebtRepayFormOpen(false);
                          }}
                        >
                          Anuluj
                        </button>
                        <button className="btn btn-primary" disabled={debtSaving} onClick={submitDebtRepayment}>
                          {debtSaving ? "Wysyłam…" : "Zgłoś spłatę"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {ui3 && myDebtRepayments.length > 0 && (
                <>
                  <div className="section-label">Spłaty</div>
                  <div className="tl3">
                    {myDebtRepayments
                      .slice()
                      .sort((x, y) => (y.requestedAt || 0) - (x.requestedAt || 0))
                      .map((r, i) => (
                        <div key={r.id} className="tn3" style={{ "--nc": r.status === "approved" ? "#4fdc92" : r.status === "rejected" ? "#fb7185" : "#ffb02e", animationDelay: Math.min(i, 10) * 60 + "ms" }}>
                          <span className="tn3-t">
                            <b>Spłata {formatPLN(r.amount)}</b>
                            <small>
                              {new Date(r.requestedAt).toLocaleDateString("pl-PL")} · {r.method === "blik" ? "BLIK" : "odjęcie od rozliczenia"}
                            </small>
                          </span>
                          <span className={"stt3 " + r.status}>{r.status === "approved" ? "zatwierdzona" : r.status === "rejected" ? "odrzucona" : "oczekuje"}</span>
                        </div>
                      ))}
                  </div>
                </>
              )}
              {!ui3 && myDebtRepayments.length > 0 && (
                <div className={`tk2-archive ${debtHistoryOpen ? "open" : ""}`} style={{ marginBottom: 14 }}>
                  <button className="tk2-archive-toggle" onClick={() => setDebtHistoryOpen((v) => !v)} aria-expanded={debtHistoryOpen}>
                    <Archive size={17} />
                    <span className="tk2-archive-title">Historia zgłoszeń spłaty</span>
                    <span className="tk2-count">{myDebtRepayments.length}</span>
                    <span className="tk2-archive-sum">
                      <span className="tk2-mini done">
                        <CheckCircle2 size={11} /> {myDebtRepayments.filter((r) => r.status === "approved").length}
                      </span>
                      <span className="tk2-mini missed">
                        <XCircle size={11} /> {myDebtRepayments.filter((r) => r.status === "rejected").length}
                      </span>
                    </span>
                    <ChevronDown size={18} className="tk2-chev" />
                  </button>
                  <div className="tk2-archive-body">
                    <div className="tk2-archive-inner">
                      <div className="debt-history-list">
                        {myDebtRepayments
                          .slice()
                          .sort((a, b) => b.requestedAt - a.requestedAt)
                          .map((r) => (
                            <div className="debt-history-row" key={r.id}>
                              <div className={`debt-history-icon ${r.status}`}>
                                {r.method === "blik" ? <Wallet size={15} /> : <Minus size={15} />}
                              </div>
                              <div className="debt-history-mid">
                                <div className="debt-history-amount">{formatPLN(r.amount)}</div>
                                <div className="debt-history-method">{r.method === "blik" ? "BLIK" : "Odjęcie od rozliczenia"}</div>
                              </div>
                              <span className={`comp-tag ${r.status === "approved" ? "comp-tag-approved" : r.status === "rejected" ? "comp-tag-rejected" : "comp-tag-pending"}`}>
                                {r.status === "approved" ? "Zatwierdzona" : r.status === "rejected" ? "Odrzucona" : "Oczekuje"}
                              </span>
                            </div>
                          ))}
                      </div>

                    </div>
                  </div>
                </div>
              )}

              <div className="section-label">Szczegóły zadłużenia</div>
              <div className={ui3 ? "tl3 tl3d" : "ticket-list"}>
                {debtEntries
                  .filter((d) => (d.amount || 0) > 0)
                  .map((d, i) => (
                  <div className="debt-card debt-card-v2" key={d.id} style={{ animationDelay: `${Math.min(i, 10) * 55}ms` }}>
                    <div className="debt-card-icon">
                      <AlertTriangle size={16} />
                    </div>
                    <div className="debt-card-body">
                      <div className="debt-card-note">{d.note || "Zaległość"}</div>
                      <div className="debt-card-date">{new Date(d.createdAt).toLocaleDateString("pl-PL")}</div>
                    </div>
                    <div className="debt-card-amount">{formatPLN(-d.amount)}</div>
                  </div>
                ))}
                {(() => {
                  // spłaty/potrącenia (wpisy ujemne) nie są "długami" — zbieramy je w jednym wierszu, żeby suma się zgadzała
                  const paid = -debtEntries.filter((d) => (d.amount || 0) < 0).reduce((sum, d) => sum + d.amount, 0);
                  if (paid <= 0.005) return null;
                  return (
                    <div className="debt-card debt-card-v2 debt-card-paid" style={{ opacity: 0.92 }}>
                      <div className="debt-card-icon" style={{ background: "rgba(52,211,153,0.16)", color: "#34d399" }}>
                        <CheckCircle2 size={16} />
                      </div>
                      <div className="debt-card-body">
                        <div className="debt-card-note">Spłaty i potrącenia</div>
                        <div className="debt-card-date">odliczone od pozycji wyżej</div>
                      </div>
                      <div className="debt-card-amount" style={{ color: "#34d399" }}>+{formatPLN(paid)}</div>
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </>
      )}

      {tab === "bonusy" && (
        <>
          {ui3 && (
            <>
              <OrbHero pct={(() => { const n = bonusEntries.length; return n ? bonusEntries.filter((r) => r.myStatus === "completed").length / n : 0; })()} gid="oh3" c1="#c084fc" c2="#ffd166">
                <div className="orb3-lbl">BONUS</div>
                <AmountBig value={totalBonus} tone="gold" sm />
                {(() => {
                  const completedCount = bonusEntries.filter((r) => r.myStatus === "completed").length;
                  return completedCount > 0 ? (
                    <span className="badge badge-green orb3-pill">
                      <Gift size={12} /> {completedCount} {completedCount === 1 ? "odebrany" : "odebranych"}
                    </span>
                  ) : (
                    <span className="orb3-sub">jeszcze żadnego odebranego</span>
                  );
                })()}
              </OrbHero>
              <div className="chips3">
                <span>🎁 {bonusEntries.filter((r) => r.active).length} {bonusEntries.filter((r) => r.active).length === 1 ? "aktywny" : "aktywne"}</span>
                <span>✅ {bonusEntries.filter((r) => r.myStatus === "completed").length} odebrane</span>
                {bonusEntries.some((r) => r.active && r.myStatus !== "completed") && (
                  <span>+{formatPLN(bonusEntries.filter((r) => r.active && r.myStatus !== "completed").reduce((x, r) => x + (r.amount || 0), 0)).replace(",00", "")}</span>
                )}
              </div>
            </>
          )}
          {!ui3 && (
          <>
          <div className="bonus-hero">
            <div className="bonus-shine" />
            <Gift size={20} className="bonus-hero-icon" />
            <div className="dash-hero-label">Łącznie zarobione w bonusach</div>
            <DigitDisplay value={totalBonus} tone="green" size="lg" />
            <div className="dash-hero-sub">
              {(() => {
                const completedCount = bonusEntries.filter((r) => r.myStatus === "completed").length;
                if (completedCount === 0) return "jeszcze żadnego zrealizowanego bonusu";
                return `${completedCount} ${completedCount === 1 ? "zrealizowany bonus" : "zrealizowanych bonusów"}`;
              })()}
            </div>
          </div>
          </>
          )}
          {svOn && scList.length > 0 && (
            <LazyBoundary quiet name="scratch">
              <React.Suspense fallback={null}>
                <ScratchStackLazy cards={scList} driver={driver} onClaimed={scClaimed} />
              </React.Suspense>
            </LazyBoundary>
          )}
          <div className="section-label bonus-label" style={{ marginTop: 18 }}>
            {ui3 ? "Kupony" : (<><Gift size={13} /> Historia bonusów</>)}
          </div>
          <div className="ranking-subnav" style={{ marginBottom: 14 }}>
            <button
              className={`ranking-subnav-btn ${bonusFilterTab === "active" ? "active" : ""}`}
              onClick={() => setBonusFilterTab("active")}
            >
              Aktywne
            </button>
            <button
              className={`ranking-subnav-btn ${bonusFilterTab === "upcoming" ? "active" : ""}`}
              onClick={() => setBonusFilterTab("upcoming")}
            >
              Nadchodzące
            </button>
            <button
              className={`ranking-subnav-btn ${bonusFilterTab === "archived" ? "active" : ""}`}
              onClick={() => setBonusFilterTab("archived")}
            >
              Archiwalne
            </button>
          </div>
          {(() => {
            const filtered = bonusEntries.filter((r) =>
              bonusFilterTab === "upcoming" ? r.status === "upcoming" : bonusFilterTab === "archived" ? r.status === "ended" : r.status === "active"
            );
            if (refreshing) {
              return (
                <div className="ticket-list">
                  {[0, 1, 2].map((i) => (
                    <div className="skeleton-row" key={i} style={{ animationDelay: `${i * 80}ms` }}>
                      <div className="skeleton-line skeleton-line-title" />
                      <div className="skeleton-line skeleton-line-sub" />
                    </div>
                  ))}
                </div>
              );
            }
            if (filtered.length === 0) {
              return bonusFilterTab === "upcoming" ? (
                <EmptyCard emoji="⏳" title="Brak nadchodzących bonusów" hint="Gdy szef doda nowy bonus, zobaczysz go tutaj z wyprzedzeniem." />
              ) : bonusFilterTab === "archived" ? (
                <EmptyCard emoji="🗂️" title="Brak zakończonych bonusów" hint="Tu trafią bonusy po ich zakończeniu." />
              ) : (
                <EmptyCard
                  emoji="🎁"
                  title="Brak aktywnych bonusów"
                  hint="Pojawią się tutaj, gdy tylko dostaniesz pierwszy. Tymczasem zajrzyj do zadań z ticketem — to szansa na nagrody w Kole fortuny."
                  cta="Zobacz zadania"
                  onCta={() => {
                    setRankingSubTab("zadania");
                    setTab("ranking");
                  }}
                />
              );
            }
            return (
              <div className={ui3 ? "tl3" : "ticket-list"}>
                {filtered.map((r, i) => ui3 ? (<BonusRow3 key={r.id} r={r} i={i} />) : (
                  <div
                    className={`bonus-card bn-coupon ${r.status === "ended" && r.myStatus !== "completed" ? "bonus-card-expired" : ""} ${r.status === "upcoming" ? "bonus-card-upcoming" : ""} ${r.myStatus === "completed" ? "bonus-card-completed" : ""}`}
                    key={r.id}
                    style={{ animationDelay: `${Math.min(i, 10) * 70}ms` }}
                  >
                    <div className="bonus-icon-badge">
                      {r.status === "upcoming" ? <Clock size={16} /> : <Gift size={16} />}
                    </div>
                    <div className="bonus-info">
                      <div className="bonus-period">
                        {r.label}
                        {r.status === "ended" && <span className="bonus-tag bonus-tag-expired">zakończony</span>}
                        {r.status === "upcoming" && (
                          <span className="bonus-tag bonus-tag-upcoming bonus-tag-upcoming-strong">
                            <Clock size={9} /> wkrótce
                          </span>
                        )}
                        {r.myStatus === "completed" && <span className="bonus-tag bonus-tag-completed">zrealizowane</span>}
                        {r.myStatus === "not_completed" && (
                          <span className="bonus-tag bonus-tag-expired">nie zrealizowane</span>
                        )}
                      </div>
                      {r.dates && <div className="bonus-dates">{r.dates}</div>}
                      {r.note && <div className="bonus-note">{r.note}</div>}
                    </div>
                    <span className="bn-perf" aria-hidden="true">
                      <b className="t" />
                      <b className="b" />
                    </span>
                    <div className="bonus-amount">
                      +{formatPLN(r.amount)}
                      {r.myStatus === "completed" && <span className="bn-stamp">ODEBRANY</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
          {girlOn && !girlFreed && bonusFilterTab === "archived" && (
            <LazyBoundary quiet name="girlpeek">
              <React.Suspense fallback={null}>
                <GirlPeekLazy onFind={girlFind} />
              </React.Suspense>
            </LazyBoundary>
          )}
        </>
      )}

      {tab === "rekompensaty" && (
        <>
          {ui3 && (
            <>
              <OrbHero pct={myCompRequests.length ? myCompRequests.filter((r) => r.status === "approved").length / myCompRequests.length : 0} gid="oh9" c1="#60a5fa" c2="#6ee7b7">
                <div className="orb3-lbl">SALDO W TYGODNIU</div>
                <AmountBig value={rawCompensationAdjustment} tone={rawCompensationAdjustment < 0 ? "red" : "green"} plus sm />
                {myCompRequests.some((r) => r.status === "pending") ? (
                  <span className="badge badge-amber orb3-pill">
                    <Clock size={12} /> {myCompRequests.filter((r) => r.status === "pending").length} oczekuje
                  </span>
                ) : (
                  <span className="orb3-sub">brak oczekujących</span>
                )}
              </OrbHero>
            </>
          )}
          {!latest || latest.status !== "notstarted" ? (
            <div className="comp-blocked-note">
              <Lock size={16} />
              <div>
                <div className="comp-blocked-title">
                  {latest && latest.status === "inprogress"
                    ? "Rozliczenie za ten tydzień jest już w trakcie"
                    : "Rozliczenie za ten tydzień jest już zakończone"}
                </div>
                <div className="comp-blocked-text">
                  Rekompensaty można dodawać tylko do tygodnia, który jeszcze się nie rozpoczął. Nowe rekompensaty
                  będą możliwe po dodaniu kolejnego rozliczenia (zwykle w poniedziałek).
                </div>
              </div>
            </div>
          ) : (
            <div className="comp-action-row">
              <button className="comp-btn comp-btn-add" onClick={() => openCompModal("add")}>
                <PlusCircle size={20} />
                Dodaj
              </button>
              <button className="comp-btn comp-btn-subtract" onClick={() => openCompModal("subtract")}>
                <MinusCircle size={20} />
                Odejmij
              </button>
            </div>
          )}

          {approvedForLatest.length > 0 && (
            <div className="comp-adjustment-note">
              <Wallet size={14} />
              <span>
                Do tego tygodnia doliczono {rawCompensationAdjustment >= 0 ? "+" : ""}
                {formatPLN(rawCompensationAdjustment)} —{" "}
                {approvedForLatest.map((r) => r.note).join("; ")}
              </span>
            </div>
          )}

          {(() => {
            // Aktualne zgłoszenia na wierzchu; tygodnie zakończone przed ostatnim wtorkiem — w zwijanym archiwum.
            const cutoff = compArchiveCutoff(new Date());
            const current = myCompRequests.filter((r) => !isCompArchived(r, cutoff));
            const archived = myCompRequests
              .filter((r) => isCompArchived(r, cutoff))
              .sort((a, b) => String(b.periodFrom || "").localeCompare(String(a.periodFrom || "")) || (b.requestedAt || 0) - (a.requestedAt || 0));
            const weekNet = {};
            archived.forEach((r) => {
              const k = r.periodFrom || "none";
              weekNet[k] = (weekNet[k] || 0) + compSigned(r);
            });
            const archShown = archived.slice(0, compArchiveShown);
            return myCompRequests.length === 0 ? (
              <>
                <div className="section-label" style={{ marginTop: 18 }}>
                  <Wallet size={13} /> Historia zgłoszeń
                </div>
                <div className="empty-state">
                  <Wallet size={22} />
                  <span>Nie masz jeszcze żadnych zgłoszeń — użyj przycisków powyżej.</span>
                </div>
              </>
            ) : (
              <>
                <div className="tk2-section-head" style={{ marginTop: 18 }}>
                  <span>
                    <Sparkles size={14} /> Ten tydzień
                  </span>
                  <span className="tk2-count">{current.length}</span>
                </div>
                {current.length === 0 ? (
                  <div className="cm2-allclear">
                    <CheckCircle2 size={26} />
                    <div>
                      <b>Brak zgłoszeń w tym tygodniu</b>
                      <span>Starsze znajdziesz w archiwum poniżej.</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <CompHero rows={current} />
                    {ui3 ? (
                      <div className="tl3 tl3r">
                        {current.map((r, i) => (
                          <div key={r.id} className="tlr" style={{ "--nc": r.status === "approved" ? "#4fdc92" : r.status === "rejected" ? "#fb7185" : "#ffb02e" }}>
                            <CompCard r={r} index={i} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      current.map((r, i) => <CompCard key={r.id} r={r} index={i} />)
                    )}
                  </>
                )}
                {archived.length > 0 && (
                  <div className={`tk2-archive ${compArchiveOpen ? "open" : ""}`}>
                    <button className="tk2-archive-toggle" onClick={() => setCompArchiveOpen((v) => !v)} aria-expanded={compArchiveOpen}>
                      <Archive size={17} />
                      <span className="tk2-archive-title">Archiwum rekompensat</span>
                      <span className="tk2-count">{archived.length}</span>
                      <span className="tk2-archive-sum">
                        <span className="tk2-mini done">
                          <CheckCircle2 size={11} /> {archived.filter((r) => r.status === "approved").length}
                        </span>
                        <span className="tk2-mini missed">
                          <XCircle size={11} /> {archived.filter((r) => r.status === "rejected").length}
                        </span>
                      </span>
                      <ChevronDown size={18} className="tk2-chev" />
                    </button>
                    <div className="tk2-archive-body">
                      <div className="tk2-archive-inner">
                        {archShown.map((r, i) => {
                          const k = r.periodFrom || "none";
                          const newWeek = i === 0 || (archShown[i - 1].periodFrom || "none") !== k;
                          const net = Math.round(weekNet[k] * 100) / 100;
                          return (
                            <React.Fragment key={r.id}>
                              {newWeek && (
                                <div className="cm2-awk">
                                  <span>{r.periodFrom ? `Tydzień ${compWeekLabel(r)}` : "Bez okresu"}</span>
                                  <span className={net > 0 ? "pos" : net < 0 ? "neg" : ""}>
                                    {net > 0 ? "+" : ""}
                                    {formatPLN(net)}
                                  </span>
                                </div>
                              )}
                              <CompArchiveRow r={r} />
                            </React.Fragment>
                          );
                        })}
                        {archived.length > compArchiveShown && (
                          <button className="tk2-more" onClick={() => setCompArchiveShown((n) => n + 10)}>
                            Pokaż starsze ({archived.length - compArchiveShown})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {compModalOpen && (
            <div className="modal-overlay" onClick={() => !compSaving && setCompModalOpen(false)}>
              <div className="modal-card" onClick={(e) => e.stopPropagation()}>
                <div className="modal-title">
                  {compModalType === "add" ? "Dodaj rekompensatę" : "Odejmij rekompensatę"}
                </div>
                <input
                  className="comp-amount-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="Kwota (zł)"
                  value={compAmount}
                  onChange={(e) => setCompAmount(e.target.value)}
                  autoFocus
                />
                <textarea
                  className="modal-textarea"
                  placeholder={
                    compModalType === "add"
                      ? "Za co ta rekompensata? (np. żarówka, mycie auta...)"
                      : "Za co to odjęcie? (np. mandat, brakująca kwota...)"
                  }
                  value={compNote}
                  onChange={(e) => setCompNote(e.target.value)}
                  style={{ marginTop: 10 }}
                />
                {compErr && <div className="save-err">{compErr}</div>}
                <div className="modal-btns">
                  <button
                    className="btn btn-ghost"
                    disabled={compSaving}
                    onClick={() => {
                      compReqRef.current = null;
                      setCompErr("");
                      setCompModalOpen(false);
                    }}
                  >
                    Anuluj
                  </button>
                  <button className="btn btn-primary" disabled={compSaving} onClick={submitCompensation}>
                    {compSaving ? "Wysyłam…" : "Wyślij"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "czat" && (
        <ChatShell
          kind={activeDmPartner ? "dm" : "boss"}
          reactApi={
            activeDmPartner
              ? {
                  me: driver.name,
                  toggle: (mid, e) => currentDmThread && onToggleDmReaction(currentDmThread.id, mid, e),
                  get: (mid) => (currentDmThread ? (currentDmThread.messages || []).find((m) => m.id === mid) : null),
                }
              : {
                  me: driver.name,
                  toggle: (mid, e) => currentThread && onToggleThreadReaction(currentThread.id, mid, e),
                  get: (mid) => (currentThread ? (currentThread.messages || []).find((m) => m.id === mid) : null),
                }
          }
        >
          {myDmThreads.length > 0 && (
            <div className="dm-switcher-strip">
              <button
                className={`dm-switcher-chip ${!activeDmPartner ? "active" : ""}`}
                onClick={() => setActiveDmPartner(null)}
              >
                <div className="dm-switcher-avatar dm-switcher-avatar-admin">👔</div>
                <span>Szefu</span>
                {myThreads.some((t) => t.unreadForDriver) && <span className="dm-switcher-dot" />}
              </button>
              {myDmThreads.map((t) => (
                <button
                  key={t.id}
                  className={`dm-switcher-chip ${activeDmPartner === t.partnerName ? "active" : ""}`}
                  onClick={() => setActiveDmPartner(t.partnerName)}
                >
                  <div
                    className="dm-switcher-avatar"
                    style={{ background: (avatars && avatars[t.partnerName]) ? "var(--surface-3)" : avatarColorFor(t.partnerName) }}
                  >
                    {(avatars && avatars[t.partnerName]) || initials(t.partnerName)}
                  </div>
                  <span>{t.partnerName.split(" ")[0]}</span>
                  {(t.unreadFor || []).includes(driver.name) && <span className="dm-switcher-dot" />}
                </button>
              ))}
            </div>
          )}
          {activeDmPartner ? (
            <div className="chat-view dm-inline-chat cx-dm">
              <div className="chat-header">
                <button className="cx-back" onClick={() => setTab("rozliczenia")} aria-label="Zamknij czat">
                  <ChevronLeft size={22} />
                </button>
                <div
                  className="dm-chat-header-avatar"
                  style={{ background: (avatars && avatars[activeDmPartner]) ? "var(--surface-3)" : avatarColorFor(activeDmPartner) }}
                >
                  {(avatars && avatars[activeDmPartner]) || initials(activeDmPartner)}
                </div>
                <div className="chat-header-topic cx-who">
                  <span className="cx-who-text">
                    <b>{activeDmPartner}</b>
                    <small>Rozmowa prywatna</small>
                  </span>
                </div>
              </div>
              <ChatScrollFab scrollRef={dmMessagesRef} />
<div className="chat-messages" ref={dmMessagesRef}>
                {!currentDmThread || currentDmThread.messages.length === 0 ? (
                  <div className="empty-state">
                    <MessageCircle size={22} />
                    <span>Napisz pierwszą wiadomość do {activeDmPartner}</span>
                  </div>
                ) : (
                  currentDmThread.messages.map((m, idx, arr) => {
                    const isMineMsg = sameName(m.sender, driver.name);
                    const sameSenderDm = (a, b) => sameName(a.sender, b.sender);
                    const groupedWithPrev = isGroupedWithAdjacent(m, idx > 0 ? arr[idx - 1] : null, sameSenderDm);
                    const groupedWithNext = isGroupedWithAdjacent(m, idx < arr.length - 1 ? arr[idx + 1] : null, sameSenderDm);
                    return (
                      <React.Fragment key={m.id}>
                        {(idx === 0 || new Date(arr[idx - 1].timestamp).toDateString() !== new Date(m.timestamp).toDateString()) && (
                          <ChatDateSeparator timestamp={m.timestamp} />
                        )}
                        <div data-mid={m.id} className={`msg-with-reply ${isMineMsg ? "mine" : "theirs"} ${!groupedWithPrev ? "msg-group-start" : ""}`}>
                          <div
                            className={`chat-bubble ${isMineMsg ? "mine" : "theirs"} ${!groupedWithNext ? "msg-group-last" : ""} ${groupedWithPrev ? "msg-group-notfirst" : ""}`}
                          >
                            {m.replyTo && (
                              <div className="chat-quote">
                                <div className="chat-quote-author">{sameName(m.replyTo.sender, driver.name) ? "Ty" : activeDmPartner}</div>
                                <div className="chat-quote-text">{m.replyTo.text}</div>
                              </div>
                            )}
                            {m.attachment && <AttachmentPreview attachment={m.attachment} />}
                            {linkifyText(m.text)}
                          <span className="cx-stamp">{formatMessageTime(m.timestamp)}</span>
                          </div>
                          <MessageReactions message={m} currentUserName={driver.name} onToggle={(emoji) => onToggleDmReaction(currentDmThread.id, m.id, emoji)} />
                          <button className="msg-reply-btn" onClick={() => setDmReplyTo(m)} aria-label="Odpowiedz">
                            <Reply size={13} />
                          </button>
                        </div>
                        {!groupedWithNext && (
                          <div className={`msg-time ${isMineMsg ? "mine" : "theirs"}`}>{formatMessageTime(m.timestamp)}</div>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
              {dmReplyTo && (
                <div className="reply-preview-banner">
                  <Reply size={13} />
                  <div className="reply-preview-body">
                    <div className="reply-preview-author">{sameName(dmReplyTo.sender, driver.name) ? "Ty" : activeDmPartner}</div>
                    <div className="reply-preview-text">{dmReplyTo.text || (dmReplyTo.attachment ? "📎 Załącznik" : "")}</div>
                  </div>
                  <button className="reply-preview-cancel" onClick={() => setDmReplyTo(null)} aria-label="Anuluj odpowiedź">
                    <X size={15} />
                  </button>
                </div>
              )}
              <UploadProgressBanner progress={dmUploadProgress} />
              {dmSendError && (
                <div className="chat-send-error-banner">
                  <AlertCircle size={14} />
                  <span>{dmSendError}</span>
                  <button className="chat-send-error-close" onClick={() => setDmSendError(null)} aria-label="Zamknij">
                    <X size={13} />
                  </button>
                </div>
              )}
              <div className="chat-input-row">
                <input type="file" ref={dmFileInputRef} style={{ display: "none" }} onChange={handleDmFileSelected} accept="image/*,video/*" multiple />
                <button
                  className="chat-icon-btn"
                  onClick={() => dmFileInputRef.current && dmFileInputRef.current.click()}
                  disabled={uploadingDmAttachment}
                  aria-label="Załącznik"
                >
                  <Paperclip size={17} />
                </button>
                <ComposerField
                  ref={dmField}
                  onEnter={() => sendDmMessageAsDriver()}
                  className="chat-input"
                  placeholder={uploadingDmAttachment ? "Wysyłanie załącznika…" : "Napisz wiadomość…"}
                  onPaste={(e) => handleImagePaste(e, setUploadingDmAttachment, (att) => sendDmMessageAsDriver(att))}
                />
                <button className="chat-send-btn" onClick={() => sendDmMessageAsDriver()} aria-label="Wyślij">
                  <Send size={18} />
                </button>
              </div>
            </div>
          ) : (
          <>
          <div className="chat-view cx-boss">
            <div className="chat-header">
              <button className="cx-back" onClick={() => setTab("rozliczenia")} aria-label="Zamknij czat">
                <ChevronLeft size={22} />
              </button>
              <div className="chat-header-topic cx-who">
                <span className="cx-avatar cx-avatar-boss">👔</span>
                <span className="cx-who-text">
                  <b>Czat z Szefem</b>
                  <small>{isChatBlocked ? "Zablokowany przez administratora" : "Napisz lub zadzwoń"}</small>
                </span>
              </div>
              <CallButton driverName={driver.name} blocked={!!isChatBlocked} />
              {currentThread && currentThread.messages.length > 0 && (
                <button className="chat-search-toggle" onClick={() => setChatSearchOpen((v) => !v)} aria-label="Szukaj">
                  <Search size={16} />
                </button>
              )}
            </div>
            {chatSearchOpen && (
              <div className="chat-search-row">
                <Search size={14} />
                <input
                  className="chat-search-input"
                  placeholder="Szukaj w rozmowie…"
                  value={chatSearchQuery}
                  onChange={(e) => setChatSearchQuery(e.target.value)}
                  autoFocus
                />
                <button
                  className="chat-search-close"
                  onClick={() => {
                    setChatSearchOpen(false);
                    setChatSearchQuery("");
                  }}
                  aria-label="Zamknij szukanie"
                >
                  <X size={15} />
                </button>
              </div>
            )}
            <ChatScrollFab scrollRef={chatMessagesRef} />
<div className="chat-messages" ref={chatMessagesRef}>
              {!currentThread || currentThread.messages.length === 0 ? (
                <div className="empty-state">
                  <MessageCircle size={22} />
                  <span>{chatsLoaded ? "Napisz wiadomość do Szefa poniżej." : chatsError ? "Słaby zasięg — wczytuję rozmowę, próbuję dalej…" : "Ładuję rozmowę…"}</span>
                </div>
              ) : chatSearchQuery.trim() &&
                currentThread.messages.filter((m) => (m.text || "").toLowerCase().includes(chatSearchQuery.trim().toLowerCase())).length ===
                  0 ? (
                <div className="empty-state">
                  <Search size={22} />
                  <span>Brak wiadomości pasujących do "{chatSearchQuery.trim()}".</span>
                </div>
              ) : (
                <>
                {!chatSearchQuery.trim() && chatWin.start > 0 && (
                  <button className="cx-older-btn" onClick={chatWin.more}>
                    ↑ Wcześniejsze wiadomości ({chatWin.start})
                  </button>
                )}
                {(chatSearchQuery.trim()
                  ? currentThread.messages.filter((m) => (m.text || "").toLowerCase().includes(chatSearchQuery.trim().toLowerCase()))
                  : currentThread.messages.slice(chatWin.start)
                ).map((m, idx, arr) => {
                  const isSearching = !!chatSearchQuery.trim();
                  const sameSender1on1 = (a, b) => a.sender === b.sender;
                  const groupedWithPrev = !isSearching && isGroupedWithAdjacent(m, idx > 0 ? arr[idx - 1] : null, sameSender1on1);
                  const groupedWithNext = !isSearching && isGroupedWithAdjacent(m, idx < arr.length - 1 ? arr[idx + 1] : null, sameSender1on1);
                  return (
                    <React.Fragment key={m.id}>
                      {!isSearching &&
                        (idx === 0 || new Date(arr[idx - 1].timestamp).toDateString() !== new Date(m.timestamp).toDateString()) && (
                          <ChatDateSeparator timestamp={m.timestamp} />
                        )}
                      <div data-mid={m.id} className={`msg-with-reply ${m.sender === "driver" ? "mine" : "theirs"} ${!groupedWithPrev ? "msg-group-start" : ""}`}>
                        <div
                          className={`chat-bubble ${m.sender === "driver" ? "mine" : "theirs"} ${m.priority ? "priority-msg" : ""} ${!groupedWithNext ? "msg-group-last" : ""} ${groupedWithPrev ? "msg-group-notfirst" : ""}`}
                        >
                          {m.priority && (
                            <div className="priority-badge">
                              <AlertTriangle size={11} /> WYSOKI PRIORYTET
                            </div>
                          )}
                          {m.replyTo && (
                            <div className="chat-quote">
                              <div className="chat-quote-author">{m.replyTo.sender === "driver" ? "Ty" : "Administrator"}</div>
                              <div className="chat-quote-text">{m.replyTo.text}</div>
                            </div>
                          )}
                          {m.attachment && <AttachmentPreview attachment={m.attachment} />}
                          {chatSearchQuery.trim() ? highlightSearchMatch(m.text, chatSearchQuery.trim()) : linkifyText(m.text)}
                          <span className="cx-stamp">
                            {formatMessageTime(m.timestamp)}
                            {m.sender === "driver" && idx === arr.length - 1 && !isSearching && currentThread && (
                              <MessageTicks read={!currentThread.unreadForAdmin} />
                            )}
                          </span>
                        </div>
                        <MessageReactions message={m} currentUserName={driver.name} onToggle={(emoji) => onToggleThreadReaction(currentThread.id, m.id, emoji)} />
                        <button className="msg-reply-btn" onClick={() => setChatReplyTo(m)} aria-label="Odpowiedz">
                          <Reply size={13} />
                        </button>
                      </div>
                      {!groupedWithNext && (
                        <div className={`msg-time ${m.sender === "driver" ? "mine" : "theirs"}`}>
                          {formatMessageTime(m.timestamp)}
                          {m.sender === "driver" && idx === arr.length - 1 && !isSearching && currentThread && (
                            <MessageTicks read={!currentThread.unreadForAdmin} />
                          )}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
                </>
              )}
              {chatVoiceRec.pending && <PendingVoiceBubble rec={chatVoiceRec} />}
            </div>
            {chatReplyTo && (
              <div className="reply-preview-banner">
                <Reply size={13} />
                <div className="reply-preview-body">
                  <div className="reply-preview-author">{chatReplyTo.sender === "driver" ? "Ty" : "Administrator"}</div>
                  <div className="reply-preview-text">{chatReplyTo.text || (chatReplyTo.attachment ? "📎 Załącznik" : "")}</div>
                </div>
                <button className="reply-preview-cancel" onClick={() => setChatReplyTo(null)} aria-label="Anuluj odpowiedź">
                  <X size={15} />
                </button>
              </div>
            )}
            {priorityMode && (
              <div className="priority-mode-banner">
                <AlertTriangle size={13} /> Ta wiadomość zostanie wysłana jako <b>pilna</b>
                <button className="priority-mode-cancel" onClick={() => setPriorityMode(false)}>
                  Anuluj
                </button>
              </div>
            )}
            <UploadProgressBanner progress={chatUploadProgress} />
            {chatSendError && (
              <div className="chat-send-error-banner">
                <AlertCircle size={14} />
                <span>{chatSendError}</span>
                <button className="chat-send-error-close" onClick={() => setChatSendError(null)} aria-label="Zamknij">
                  <X size={13} />
                </button>
              </div>
            )}
            <VoicePendingBanner rec={chatVoiceRec} />
            {!isChatBlocked && !chatVoiceRec.isRecording && chatEmpty && !chatSearchOpen && (
              <div className="cx-chips">
                {QUICK_REPLIES.map((t) => (
                  <button
                    key={t}
                    className="cx-chip"
                    onClick={() => {
                      fieldSet(chatField, t);
                      if (chatField.current) chatField.current.focus();
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
            <div className={`chat-input-row ${priorityMode ? "priority-active" : ""}`}>
              {isChatBlocked ? (
                <div className="group-muted-notice">
                  🚫 Czat z Szefem i połączenia zostały zablokowane przez administratora
                  {fullDriverRecord.chatBlockedUntil !== "forever" && ` — do ${new Date(fullDriverRecord.chatBlockedUntil).toLocaleString("pl-PL")}`}
                  . Możesz nadal czytać wcześniejsze wiadomości.
                </div>
              ) : chatVoiceRec.isRecording ? (
                <div className="voice-recording-row">
                  <span className="voice-recording-dot" />
                <RecordingWave meter={chatVoiceRec.meterRef} />
                  <span className="voice-recording-time">Nagrywanie… {chatVoiceRec.seconds}s{chatVoiceRec.interrupted ? " ⚠" : ""}</span>
                  <button className="chat-icon-btn" onClick={chatVoiceRec.cancel} aria-label="Anuluj">
                    <XCircle size={17} />
                  </button>
                  <button className="chat-send-btn" onClick={chatVoiceRec.stop} aria-label="Wyślij głosówkę">
                    <Send size={18} />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    ref={chatFileInputRef}
                    style={{ display: "none" }}
                    onChange={handleChatFileSelected}
                    accept="image/*,video/*" multiple
                  />
                  <button
                    className={`chat-icon-btn ${priorityMode ? "priority-icon-active" : ""}`}
                    onClick={() => setPriorityMode((v) => !v)}
                    aria-label="Oznacz jako pilne"
                    title="Oznacz jako pilne"
                  >
                    <AlertTriangle size={17} />
                  </button>
                  <button
                    className="chat-icon-btn"
                    onClick={() => chatFileInputRef.current && chatFileInputRef.current.click()}
                    disabled={uploadingChatAttachment}
                    aria-label="Załącznik"
                  >
                    <Paperclip size={17} />
                  </button>
                  <button className="chat-icon-btn" onClick={chatVoiceRec.start} aria-label="Nagraj głosówkę">
                    <Mic size={17} />
                  </button>
                  <ComposerField
                    ref={chatField}
                    onEmptyChange={setChatEmpty}
                    onEnter={() => sendChatMessageAsDriver()}
                    className="chat-input"
                    placeholder={
                      uploadingChatAttachment
                        ? "Wysyłanie załącznika…"
                        : priorityMode
                          ? "Napisz pilną wiadomość…"
                          : "Napisz wiadomość..."
                    }
                    onPaste={(e) => handleImagePaste(e, setUploadingChatAttachment, (att) => sendChatMessageAsDriver(att))}
                  />
                  <button
                    className={`chat-send-btn ${priorityMode ? "priority-send-btn" : ""}`}
                    onClick={() => sendChatMessageAsDriver()}
                    aria-label="Wyślij"
                  >
                    <Send size={18} />
                  </button>
                </>
              )}
            </div>
          </div>

          {resolvedThreads.length > 0 && (
            <>
              <div
                className="section-label-row"
                style={{ cursor: "pointer", marginTop: 14 }}
                onClick={() => setShowResolvedThreads((v) => !v)}
              >
                <div className="section-label" style={{ margin: 0 }}>
                  Poprzednie rozmowy ({resolvedThreads.length})
                </div>
                <ChevronLeft
                  size={16}
                  className="thread-chevron"
                  style={{ transform: showResolvedThreads ? "rotate(90deg)" : "rotate(-90deg)" }}
                />
              </div>
              {showResolvedThreads && (
                <div className="ticket-list">
                  {resolvedThreads.map((t) => {
                    const last = t.messages[t.messages.length - 1];
                    return (
                      <div className="thread-card" key={t.id} onClick={() => onResolveThread(t.id, false)}>
                        <div className="thread-icon-badge">
                          <MessageCircle size={16} />
                        </div>
                        <div className="thread-info">
                          <div className="thread-topic">{new Date(t.createdAt).toLocaleDateString("pl-PL")}</div>
                          <div className="thread-preview">
                            {last.sender === "driver" ? "Ty: " : "Szefu: "}
                            {last.text}
                          </div>
                        </div>
                        <ChevronLeft size={16} className="thread-chevron" />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          </>
          )}
        </ChatShell>
      )}

      {tab === "grupa" && (
        <ChatShell
          kind="group"
          reactApi={{
            me: driver.name,
            toggle: (mid, e) => onToggleGroupReaction(mid, e),
            get: (mid) => (groupChatMessages || []).find((m) => m.id === mid),
          }}
        >
        <div className="chat-view cx-group">
          <div className="chat-header">
            <button className="cx-back" onClick={() => setTab("rozliczenia")} aria-label="Zamknij czat">
              <ChevronLeft size={22} />
            </button>
            <div className="chat-header-topic cx-who">
              {ui3 ? <Con3 drivers={drivers} presence={presence} avatars={avatars} /> : <span className="cx-avatar cx-avatar-group">👥</span>}
              <span className="cx-who-text">
                <b>Grupa kierowców</b>
                <small>
                  {ui3 && (drivers || []).length > 0
                    ? `${(drivers || []).filter((d) => presence.isOnline(d.name)).length} online · ${(drivers || []).length} kierowców`
                    : (drivers || []).length > 0
                      ? `${(drivers || []).length} kierowców · wspólna rozmowa`
                      : "Wspólna rozmowa"}
                </small>
              </span>
            </div>
          </div>
          {(groupChatMessages || []).find((m) => m.pinned) &&
            (() => {
              const p = groupChatMessages.find((m) => m.pinned);
              return (
                <div className="pinned-banner">
                  <Pin size={13} />
                  <div className="pinned-banner-text">
                    <span className="pinned-banner-author">
                      {p.driverName === "Administrator" ? "Szefu" : groupDisplayName(p.driverName, (drivers || []).map((d) => d.name))}
                    </span>
                    {p.attachment && <AttachmentPreview attachment={p.attachment} />}
                    {p.text ? linkifyText(p.text) : !p.attachment ? "" : null}
                  </div>
                </div>
              );
            })()}
          <ChatScrollFab scrollRef={groupMessagesRef} />
<div className="chat-messages" ref={groupMessagesRef}>
            {(groupChatMessages || []).length === 0 ? (
              <div className="empty-state">
                <Users size={22} />
                <span>{chatsLoaded ? "Brak wiadomości — napisz pierwszą!" : chatsError ? "Słaby zasięg — wczytuję rozmowę, próbuję dalej…" : "Ładuję rozmowę…"}</span>
              </div>
            ) : (
              <>
              {groupWin.start > 0 && (
                <button className="cx-older-btn" onClick={groupWin.more}>
                  ↑ Wcześniejsze wiadomości ({groupWin.start})
                </button>
              )}
              {(groupChatMessages || []).slice(groupWin.start).map((m, idx, arr) => {
                const sameSenderGroup = (a, b) => sameName(a.driverName, b.driverName);
                const groupedWithPrev = isGroupedWithAdjacent(m, idx > 0 ? arr[idx - 1] : null, sameSenderGroup);
                const groupedWithNext = isGroupedWithAdjacent(m, idx < arr.length - 1 ? arr[idx + 1] : null, sameSenderGroup);
                const isMine = sameName(m.driverName, driver.name);
                return (
                <React.Fragment key={m.id}>
                  {(idx === 0 || new Date(arr[idx - 1].timestamp).toDateString() !== new Date(m.timestamp).toDateString()) && (
                    <ChatDateSeparator timestamp={m.timestamp} />
                  )}
                  <div data-mid={m.id} className={`chat-bubble-row ${isMine ? "mine" : "theirs"} ${!groupedWithPrev ? "msg-group-start" : ""}`}>
                  {!isMine &&
                    (!groupedWithNext ? (
                      <div
                        className={`chat-avatar ${m.driverName !== "Administrator" ? "chat-avatar-clickable" : ""}`}
                        onClick={() => m.driverName !== "Administrator" && goToDmWith(m.driverName)}
                        style={
                          m.driverName === "Administrator" || (avatars && avatars[m.driverName])
                            ? { background: "var(--surface-3)" }
                            : { background: avatarColorFor(m.driverName) }
                        }
                      >
                        {m.driverName === "Administrator"
                          ? "👔"
                          : (avatars && avatars[m.driverName]) || m.driverName[0]}
                      </div>
                    ) : (
                      <div className="chat-avatar-spacer" />
                    ))}
                  <div className={`chat-bubble-col ${isMine ? "mine" : "theirs"}`}>
                    <div
                      className={`chat-bubble group ${isMine ? "mine" : "theirs"} ${m.important ? "important" : ""} ${m.driverName === "Administrator" ? "admin-msg" : ""} ${!groupedWithNext ? "msg-group-last" : ""} ${groupedWithPrev ? "msg-group-notfirst" : ""}`}
                    >
                      {!isMine && !groupedWithPrev && (
                        <div
                          className={`chat-bubble-author ${m.driverName === "Administrator" ? "admin-author" : ""}`}
                          style={m.driverName === "Administrator" ? undefined : { color: avatarColorFor(m.driverName) }}
                        >
                          {m.driverName === "Administrator"
                            ? "Szefu"
                            : groupDisplayName(m.driverName, (drivers || []).map((d) => d.name))}
                        </div>
                      )}
                      {m.important && (
                        <div className="important-badge">
                          <AlertTriangle size={11} /> WAŻNE
                        </div>
                      )}
                      {m.replyTo && (
                        <div className="chat-quote">
                          <div className="chat-quote-author">
                            {sameName(m.replyTo.driverName, driver.name)
                              ? "Ty"
                              : m.replyTo.driverName === "Administrator"
                                ? "Szefu"
                                : groupDisplayName(m.replyTo.driverName, (drivers || []).map((d) => d.name))}
                          </div>
                          <div className="chat-quote-text">{m.replyTo.text}</div>
                        </div>
                      )}
                      {m.attachment && <AttachmentPreview attachment={m.attachment} />}
                      {linkifyText(m.text)}
                    <span className="cx-stamp">{formatMessageTime(m.timestamp)}</span>
                    </div>
                    <MessageReactions
                      message={m}
                      currentUserName={driver.name}
                      onToggle={(emoji) => onToggleGroupReaction(m.id, emoji)}
                    />
                    {m.seenBy && m.seenBy.length > 0 && (
                      <div className="chat-seen-by">Zobaczyli: {formatSeenBy(m.seenBy, (drivers || []).map((d) => d.name))}</div>
                    )}
                    {!groupedWithNext && <div className="msg-time-group">{formatMessageTime(m.timestamp)}</div>}
                    <button className="group-reply-link" onClick={() => setGroupReplyTo(m)}>
                      <Reply size={11} /> Odpowiedz
                    </button>
                  </div>
                </div>
                </React.Fragment>
                );
              })}
              </>
            )}
            {groupVoiceRec.pending && <PendingVoiceBubble rec={groupVoiceRec} />}
          </div>
          {groupReplyTo && (
            <div className="reply-preview-banner">
              <Reply size={13} />
              <div className="reply-preview-body">
                <div className="reply-preview-author">
                  {sameName(groupReplyTo.driverName, driver.name)
                    ? "Ty"
                    : groupReplyTo.driverName === "Administrator"
                      ? "Szefu"
                      : groupDisplayName(groupReplyTo.driverName, (drivers || []).map((d) => d.name))}
                </div>
                <div className="reply-preview-text">{groupReplyTo.text || (groupReplyTo.attachment ? "📎 Załącznik" : "")}</div>
              </div>
              <button className="reply-preview-cancel" onClick={() => setGroupReplyTo(null)} aria-label="Anuluj odpowiedź">
                <X size={15} />
              </button>
            </div>
          )}
          {showGroupEmoji && (
            <div className="emoji-picker">
              {EMOJI_LIST.map((e) => (
                <button
                  key={e}
                  className="emoji-picker-btn"
                  onClick={() => {
                    fieldInsert(groupField, e);
                    setShowGroupEmoji(false);
                  }}
                >
                  {e}
                </button>
              ))}
            </div>
          )}
          <UploadProgressBanner progress={groupUploadProgress} />
          <VoicePendingBanner rec={groupVoiceRec} />
          <div className="chat-input-row">
            <input
              type="file"
              ref={groupFileInputRef}
              style={{ display: "none" }}
              onChange={handleGroupFileSelected}
              accept="image/*,video/*" multiple
            />
            {isGroupMuted ? (
              <div className="group-muted-notice">
                🔇 Zostałeś wyciszony na czacie grupowym przez administratora
                {fullDriverRecord.groupMutedUntil !== "forever" &&
                  ` — do ${new Date(fullDriverRecord.groupMutedUntil).toLocaleString("pl-PL")}`}
                .
              </div>
            ) : groupVoiceRec.isRecording ? (
              <div className="voice-recording-row">
                <span className="voice-recording-dot" />
                <RecordingWave meter={groupVoiceRec.meterRef} />
                <span className="voice-recording-time">Nagrywanie… {groupVoiceRec.seconds}s{groupVoiceRec.interrupted ? " ⚠" : ""}</span>
                <button className="chat-icon-btn" onClick={groupVoiceRec.cancel} aria-label="Anuluj">
                  <XCircle size={17} />
                </button>
                <button className="chat-send-btn" onClick={groupVoiceRec.stop} aria-label="Wyślij głosówkę">
                  <Send size={18} />
                </button>
              </div>
            ) : (
              <>
                <button
                  className="chat-icon-btn"
                  onClick={() => groupFileInputRef.current && groupFileInputRef.current.click()}
                  disabled={uploadingGroupAttachment}
                  aria-label="Załącznik"
                >
                  <Paperclip size={17} />
                </button>
                <button
                  className="chat-icon-btn"
                  onClick={() => setShowGroupEmoji((v) => !v)}
                  aria-label="Emotikonki"
                >
                  <Smile size={17} />
                </button>
                <button className="chat-icon-btn" onClick={groupVoiceRec.start} aria-label="Nagraj głosówkę">
                  <Mic size={17} />
                </button>
                <ComposerField
                  ref={groupField}
                  onEnter={submitGroupText}
                  className="chat-input"
                  placeholder={uploadingGroupAttachment ? "Wysyłanie załącznika…" : "Napisz do grupy..."}
                  onPaste={(e) =>
                    handleImagePaste(e, setUploadingGroupAttachment, (att) => {
                      sendGroupMessageAsDriver("", att, groupReplyTo);
                      setGroupReplyTo(null);
                    })
                  }
                />
                <button
                  className="chat-send-btn"
                  onClick={submitGroupText}
                  aria-label="Wyślij"
                >
                  <Send size={18} />
                </button>
              </>
            )}
          </div>
        </div>
        </ChatShell>
      )}

      {tab === "fortuna" && (
        <LazyBoundary name="fortuna">
        <React.Suspense fallback={<SlowFallback name="fortuna" label="koło fortuny" />}>
          <FortuneTab
            driver={driver}
            tasks={tasks}
            previewOnly={trackPresence === false}
            onGoTasks={() => {
              setRankingSubTab("zadania");
              setTab("ranking");
            }}
          />
        </React.Suspense>
        </LazyBoundary>
      )}

      {tab === "ranking" && (
        <div className="ranking-view">
          <div className="ranking-header rk3-hd">
            <div className="ranking-title">
              <Trophy size={18} /> Ranking Kierowców
            </div>
            <div className="ranking-subtitle">
              Liczone rozliczenia od {RANKING_START_DATE.split("-").reverse().join(".")}
            </div>
            <OnlinePill presence={presence} avatars={avatars} />
          </div>

          <div className="ranking-subnav">
            <button
              className={`ranking-subnav-btn ${rankingSubTab === "ranking" ? "active" : ""}`}
              onClick={() => setRankingSubTab("ranking")}
            >
              <Trophy size={13} /> Ranking
            </button>
            <button
              className={`ranking-subnav-btn ${rankingSubTab === "zadania" ? "active" : ""}`}
              onClick={() => setRankingSubTab("zadania")}
            >
              <CheckCircle2 size={13} /> Zadania
              {activeTaskRows.some((row) => !row.myStatus) && <span className="tab-dot" />}
            </button>
            <button
              className={`ranking-subnav-btn ${rankingSubTab === "osiagniecia" ? "active" : ""}`}
              onClick={() => setRankingSubTab("osiagniecia")}
            >
              <Award size={13} /> Osiągnięcia
            </button>
          </div>

          {rankingSubTab === "ranking" && (
            <div className="rk2">
              {ui3 && (
                <Rank3
                  rows={rankingRows}
                  driverName={driver.name}
                  star={rkExtra && rkExtra.star}
                  prev={rkExtra && rkExtra.prev}
                  avatars={avatars}
                  online={(n) => presence.isOnline(n)}
                  onOpen={openDriverProfile}
                />
              )}
              {!ui3 && <RankHero rows={rankingRows} driverName={driver.name} />}
              {!ui3 && rkExtra && rkExtra.star && (
                <LazyBoundary quiet name="weekstar">
                  <React.Suspense fallback={null}>
                    <WeekStarLazy star={rkExtra.star} avatars={avatars} isMe={sameName(rkExtra.star.name, driver.name)} />
                  </React.Suspense>
                </LazyBoundary>
              )}

              {!ui3 && rankingRows.length >= 2 && (
                <div className="rk2-podium">
                  <div className="rk2-spot" aria-hidden="true" />
                  <div className="rk2-sparks" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                  {[1, 0, 2].map((idx) => {
                    const r = rankingRows[idx];
                    if (!r) return <div key={idx} className="rk2-slot rk2-slot-empty" />;
                    return (
                      <PodiumSlot
                        key={r.id}
                        r={r}
                        place={idx + 1}
                        isMe={sameName(r.name, driver.name)}
                        avatars={avatars}
                        bonusRange="100–200 zł"
                        online={presence.isOnline(r.name)}
                        onOpenProfile={openDriverProfile}
                        move={rkExtra && rkExtra.prev && rkExtra.prev[r.name] ? rkExtra.prev[r.name] - (idx + 1) : null}
                        star={!!(rkExtra && rkExtra.star && sameName(rkExtra.star.name, r.name))}
                      />
                    );
                  })}
                </div>
              )}

              <div className={ui3 ? "rk2-list rk3-hide" : "rk2-list"}>
                {rankingRows.slice(rankingRows.length >= 2 ? 3 : 0).map((r, i0) => {
                  const i = i0 + (rankingRows.length >= 2 ? 3 : 0);
                  const place = i + 1;
                  const isMe = sameName(r.name, driver.name);
                  const maxPoints = rankingRows[0] ? rankingRows[0].points : 0;
                  const barPct = maxPoints > 0 ? Math.max(4, Math.round((r.points / maxPoints) * 100)) : 0;
                  // Ile punktów brakuje do wyższego progu bonusowego (tylko przy własnym wierszu).
                  const nextTierTarget = place > 6 ? rankingRows[5] : place <= 6 ? rankingRows[2] : null;
                  const pointsToNextTier = nextTierTarget ? Math.max(0, nextTierTarget.points - r.points + 1) : null;
                  return (
                    <RankingListRow
                      key={r.id}
                      r={r}
                      place={place}
                      isMe={isMe}
                      avatars={avatars}
                      barPct={barPct}
                      animDelay={Math.min(i0, 12) * 55}
                      pointsToNextTier={pointsToNextTier}
                      onOpenProfile={openDriverProfile}
                      online={presence.isOnline(r.name)}
                      move={rkExtra && rkExtra.prev && rkExtra.prev[r.name] ? rkExtra.prev[r.name] - place : null}
                      star={!!(rkExtra && rkExtra.star && sameName(rkExtra.star.name, r.name))}
                    />
                  );
                })}
              </div>

              {companyBestWeek && companyBestWeek.revenue > 0 && (
                <div className="company-best-week-card rk2-record">
                  <div className="company-best-week-glow" />
                  <Trophy size={22} className="company-best-week-icon" />
                  <div className="company-best-week-label">Najlepszy utarg tygodniowy firmy</div>
                  <div className="company-best-week-value">
                    {companyBestWeek.name} <span>{formatPLN(companyBestWeek.revenue)}</span>
                  </div>
                  <div className="company-best-week-period">
                    {companyBestWeek.periodFrom} – {companyBestWeek.periodTo}
                  </div>
                </div>
              )}

              <div className={`rk2-rules ${rankRulesOpen ? "open" : ""}`}>
                <button className="rk2-rules-toggle" onClick={() => setRankRulesOpen((v) => !v)} aria-expanded={rankRulesOpen}>
                  <Sparkles size={16} />
                  <span>Jak zdobywać punkty i bonusy</span>
                  <ChevronDown size={18} className="tk2-chev" />
                </button>
                <div className="rk2-rules-body">
                  <div className="rk2-rules-inner">
                    <div className="rk2-chips">
                      <div className="rk2-chip">
                        <span className="rk2-chip-dot" />
                        500+ zł utargu<b>+40 pkt</b>
                      </div>
                      <div className="rk2-chip">
                        <span className="rk2-chip-dot" />
                        1000+ zł utargu<b>100 pkt</b>
                      </div>
                      <div className="rk2-chip">
                        <span className="rk2-chip-dot amber" />
                        1500+ zł utargu<b>150 pkt</b>
                      </div>
                      <div className="rk2-chip">
                        <span className="rk2-chip-dot amber" />
                        2000+ zł utargu<b>200 pkt</b>
                      </div>
                      <div className="rk2-chip">
                        <span className="rk2-chip-dot amber" />
                        2500+ zł utargu<b>250 pkt</b>
                      </div>
                      <div className="rk2-chip">
                        <span className="rk2-chip-dot red" />
                        3000+ zł utargu<b>400 pkt</b>
                      </div>
                      <div className="rk2-chip rk2-chip-wide">
                        <span className="rk2-chip-dot green" />
                        Zrealizowane zadanie<b>30–100 pkt</b>
                      </div>
                    </div>
                    <div className="rk2-tiers-title">Bonusy za miejsce w rankingu</div>
                    <div className="rk2-tiers">
                      <div className="rk2-tierbox tier-gold">
                        <span>Miejsca 1–3</span>
                        <b>100–200 zł</b>
                      </div>
                      <div className="rk2-tierbox tier-silver">
                        <span>Miejsca 4–6</span>
                        <b>50–100 zł</b>
                      </div>
                      <div className="rk2-tierbox tier-bronze">
                        <span>Miejsca 7+</span>
                        <b>0–50 zł</b>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {rankingSubTab === "zadania" && (
            <div className="tk2">
              {ui3 && (
                <OrbHero pct={taskStats.total > 0 ? taskStats.done / taskStats.total : 0} gid="oh6" c1="#6ee7b7" c2="#60a5fa">
                  <div className="orb3-lbl">ZADANIA</div>
                  <div className="big3">
                    {taskStats.done}
                    <small> / {taskStats.total}</small>
                  </div>
                  <span className="badge badge-green orb3-pill">+{taskStats.points} pkt</span>
                  <span className="orb3-sub">
                    {taskStats.activeCount} {taskStats.activeCount === 1 ? "aktywne" : "aktywnych"}
                  </span>
                </OrbHero>
              )}
              {ui3 && taskRows.length > 0 && (() => {
                const firstOpen = taskRows.findIndex((r) => !r.myStatus);
                return (
                  <div className="goal3">
                    <div className="goal3-h">
                      <b>Wykonaj wszystkie zadania</b>
                      <em>
                        {taskStats.done}/{taskStats.total}
                      </em>
                    </div>
                    <div className="dots3 g">
                      {taskRows.slice(0, 14).map((r, i) => (
                        <i key={i} className={r.myStatus === "completed" ? "on" : i === firstOpen ? "nx" : ""} />
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="tk2-hero">
                <ProgressRing
                  value={taskStats.total > 0 ? Math.round((taskStats.done / taskStats.total) * 100) : 0}
                  size={88}
                  stroke={8}
                  tone="green"
                >
                  <div className="tk2-ring-num">
                    {taskStats.done}
                    <small>/{taskStats.total}</small>
                  </div>
                </ProgressRing>
                <div className="tk2-hero-info">
                  <div className="tk2-hero-label">Twoje zadania</div>
                  <div className="tk2-hero-points">
                    +{taskStats.points}
                    <span> pkt</span>
                  </div>
                  <div className="tk2-hero-sub">
                    {taskStats.activeCount} {taskStats.activeCount === 1 ? "aktywne" : "aktywnych"} · {taskStats.done} zrealizowanych
                  </div>
                </div>
              </div>

              {taskRows.length === 0 ? (
                <EmptyCard emoji="📝" title="Nie ma jeszcze zadań" hint="Gdy szef doda zadanie (także z ticketem), zobaczysz je tutaj." />
              ) : (
                <>
                  <TicketStrip rows={activeTaskRows} onOpen={() => setTab("fortuna")} />
                  <div className="tk2-section-head">
                    <span>
                      <Target size={14} /> Aktywne
                    </span>
                    <span className="tk2-count">{activeTaskRows.length}</span>
                  </div>
                  {activeTaskRows.length === 0 ? (
                    <div className="tk2-allclear">
                      <CheckCircle2 size={26} />
                      <div>
                        <b>Brak aktywnych zadań</b>
                        <span>Nowe zadania pojawią się tutaj, gdy tylko je dodamy.</span>
                      </div>
                    </div>
                  ) : (
                    ui3 ? (
                      <TaskList3 rows={activeTaskRows} todayStr={taskTodayStr} />
                    ) : (
                      activeTaskRows.map((row, i) => <TaskCard key={row.t.id} row={row} index={i} todayStr={taskTodayStr} />)
                    )
                  )}

                  {archiveTaskRows.length > 0 && (
                    <div className={`tk2-archive ${tasksArchiveOpen ? "open" : ""}`}>
                      <button
                        className="tk2-archive-toggle"
                        onClick={() => setTasksArchiveOpen((v) => !v)}
                        aria-expanded={tasksArchiveOpen}
                      >
                        <Archive size={17} />
                        <span className="tk2-archive-title">Archiwum zadań</span>
                        <span className="tk2-count">{archiveTaskRows.length}</span>
                        <span className="tk2-archive-sum">
                          <span className="tk2-mini done">
                            <CheckCircle2 size={11} /> {taskStats.archDone}
                          </span>
                          <span className="tk2-mini missed">
                            <XCircle size={11} /> {taskStats.archMissed}
                          </span>
                        </span>
                        <ChevronDown size={18} className="tk2-chev" />
                      </button>
                      <div className="tk2-archive-body">
                        <div className="tk2-archive-inner">
                          {archiveTaskRows.slice(0, tasksArchiveShown).map((row) => (
                            <TaskArchiveRow key={row.t.id} row={row} />
                          ))}
                          {archiveTaskRows.length > tasksArchiveShown && (
                            <button className="tk2-more" onClick={() => setTasksArchiveShown((n) => n + 10)}>
                              Pokaż starsze ({archiveTaskRows.length - tasksArchiveShown})
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {rankingSubTab === "osiagniecia" && (
            <div className="badges-view">
              <div className="badge-hero-card bh-old">
                <div className="badge-hero-glow" />
                <div className={`badge-hero-icon ${currentStreak >= 4 ? "streak-hot" : ""}`}>🔥</div>
                <div className="badge-hero-value">{currentStreak}</div>
                <div className="badge-hero-label">
                  {currentStreak === 0
                    ? `Zdobądź ${formatPLN(GOOD_WEEK_THRESHOLD)} utargu w tym tygodniu, żeby zacząć nową serię!`
                    : `${currentStreak === 1 ? "tydzień" : currentStreak < 5 ? "tygodnie" : "tygodni"} z rzędu ponad ${formatPLN(GOOD_WEEK_THRESHOLD)}`}
                </div>
              </div>

              {svOn && (
                <LazyBoundary quiet name="levelcard">
                  <React.Suspense fallback={null}>
                    <LevelCardLazy driver={driver} />
                  </React.Suspense>
                </LazyBoundary>
              )}
              {ui3 && (
                <div className="streak-orb">
                  <OrbHero pct={Math.min(1, currentStreak / 8)} gid="oh7" c1="#fb923c" c2="#ffd166">
                    <div className="orb3-lbl">SERIA</div>
                    <div className="big3">
                      🔥 {currentStreak}
                    </div>
                    <span className="orb3-sub">
                      {currentStreak === 0 ? `Zdobądź ${formatPLN(GOOD_WEEK_THRESHOLD).replace(",00", "")} utargu w tygodniu` : `${currentStreak === 1 ? "tydzień" : currentStreak < 5 ? "tygodnie" : "tygodni"} z rzędu ponad ${formatPLN(GOOD_WEEK_THRESHOLD).replace(",00", "")}`}
                    </span>
                  </OrbHero>
                </div>
              )}
              {ui3 && (
                <div className="chips3 streak-chip">
                  <span>🔥 Seria {currentStreak} tyg.</span>
                </div>
              )}

              {ui3 && (
                <>
                  {(() => {
                    const rev = bestWeek ? bestWeek._revenue : 0;
                    const tk = cachedTickets(driver.name) || 0;
                    const goals = [
                      { ic: "🔥", l: "Seria 3 tygodni", cur: currentStreak, t: 3, f: (n) => n + " tyg." },
                      { ic: "🔥", l: "Seria 6 tygodni", cur: currentStreak, t: 6, f: (n) => n + " tyg." },
                      { ic: "✅", l: "10 wykonanych zadań", cur: completedTasksCount, t: 10, f: (n) => String(n) },
                      { ic: "✅", l: "25 wykonanych zadań", cur: completedTasksCount, t: 25, f: (n) => String(n) },
                      { ic: "📅", l: "4 policzone tygodnie", cur: countedMine.length, t: 4, f: (n) => String(n) },
                      { ic: "📅", l: "12 policzonych tygodni", cur: countedMine.length, t: 12, f: (n) => String(n) },
                      { ic: "💰", l: "Utarg 3000 zł w tygodniu", cur: rev, t: 3000, f: (n) => formatPLN(n).replace(",00", "") },
                      { ic: "🎟️", l: "10 ticketów", cur: tk, t: 10, f: (n) => String(n) },
                    ].filter((g) => g.cur < g.t);
                    if (!goals.length) return null;
                    const g = goals.reduce((b, x) => (x.cur / x.t > b.cur / b.t ? x : b), goals[0]);
                    const pc = Math.max(3, Math.round((g.cur / g.t) * 100));
                    return (
                      <div className="nb3">
                        <span className="nb3-ic">{g.ic}</span>
                        <div className="nb3-m">
                          <small>NASTĘPNA ODZNAKA</small>
                          <b>{g.l}</b>
                          <div className="nb3-bar">
                            <i style={{ transform: "scaleX(" + pc / 100 + ")" }} />
                          </div>
                        </div>
                        <span className="nb3-n">
                          {g.f(g.cur)}
                          <small> / {g.f(g.t)}</small>
                        </span>
                      </div>
                    );
                  })()}
                  <div className="section-label">Odznaki</div>
                  <Badges3
                    items={(() => {
                      const rk = rankingRows.findIndex((r) => sameName(r.name, driver.name)) + 1;
                      const tk = cachedTickets(driver.name);
                      const rev = bestWeek ? bestWeek._revenue : 0;
                      const plz = (n) => (n === 1 ? "zadanie" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 > 20) ? "zadania" : "zadań");
                      return [
                        { ic: "🏆", l: bestWeek ? "Rekord " + formatPLN(rev).replace(",00", "") : "Rekord", on: !!bestWeek },
                        { ic: "🔥", l: currentStreak > 0 ? "Seria " + currentStreak : "Seria 3", on: currentStreak > 0 },
                        { ic: "✅", l: completedTasksCount > 0 ? completedTasksCount + " " + plz(completedTasksCount) : "10 zadań", on: completedTasksCount > 0 },
                        { ic: "🎟️", l: tk ? tk + " ticketów" : "10 ticketów", on: !!tk },
                        { ic: "📅", l: countedMine.length > 0 ? countedMine.length + " tygodni" : "4 tygodnie", on: countedMine.length > 0 },
                        { ic: "🔥", l: "Seria 6", on: currentStreak >= 6 },
                        { ic: "💰", l: "Utarg 3000", on: rev >= 3000 },
                        { ic: "👑", l: "Legenda", on: rk === 1 },
                      ];
                    })()}
                  />
                </>
              )}
              <div className="badge-grid bg-old">
                <div className="badge-card">
                  <div className="badge-card-icon">🏆</div>
                  <div className="badge-card-value">{bestWeek ? formatPLN(bestWeek._revenue) : "—"}</div>
                  <div className="badge-card-label">Twój rekord tygodnia</div>
                  {bestWeek && (
                    <div className="badge-card-sub">
                      {bestWeek.periodFrom} – {bestWeek.periodTo}
                    </div>
                  )}
                </div>
                <div className="badge-card">
                  <div className="badge-card-icon">✅</div>
                  <div className="badge-card-value">{completedTasksCount}</div>
                  <div className="badge-card-label">Ukończonych zadań</div>
                </div>
                <div className="badge-card">
                  <div className="badge-card-icon">📅</div>
                  <div className="badge-card-value">{countedMine.length}</div>
                  <div className="badge-card-label">Policzonych tygodni</div>
                </div>
                <div className="badge-card">
                  <div className="badge-card-icon">⭐</div>
                  <div className="badge-card-value">
                    {rankingRows.findIndex((r) => sameName(r.name, driver.name)) + 1 || "—"}
                  </div>
                  <div className="badge-card-label">Miejsce w rankingu</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {canSee("czat") &&
        tab !== "czat" &&
        tab !== "grupa" &&
        createPortal(
          <button
            className="chat-fab"
            onClick={() => {
              setTab("czat");
            }}
            aria-label="Czat"
          >
            <MessageSquare size={32} strokeWidth={2.5} fill="#1a1206" fillOpacity={0.12} />
            {myThreads.some((t) => t.unreadForDriver) && <span className="chat-fab-dot" />}
          </button>,
          document.body
        )}
      {showNew && (
        <LazyBoundary quiet name="whatsnew">
        <React.Suspense fallback={null}>
          <WhatsNewLazy
            pushNeeded={typeof Notification !== "undefined" && "PushManager" in window && Notification.permission !== "granted"}
            onEnablePush={async () => {
              await subscribeAndReportPushStatus(driver.name, true);
              return Notification.permission;
            }}
            onClose={(go) => {
              try {
                localStorage.setItem("patex-whatsnew-v1", "1");
              } catch (e) {
                /* nic */
              }
              setShowNew(false);
              if (go) setTab(go);
            }}
          />
        </React.Suspense>
        </LazyBoundary>
      )}
      {ui3 && tab !== "czat" && tab !== "grupa" && (
        <Dock3
          tab={tab}
          setTab={setTab}
          canSee={canSee}
          moreOpen={moreOpen}
          setMoreOpen={setMoreOpen}
          dots={{
            bonusy: bonusEntries.some((r) => r.active) || scList.some((c) => !c.claimed),
            czat: myThreads.some((t) => t.unreadForDriver) || myDmThreads.some((t) => (t.unreadFor || []).includes(driver.name)),
            more: debtEntries.length > 0 || myCompRequests.some((r) => r.status === "pending") || hasUnreadGroup,
          }}
        />
      )}
      {ui3 && moreOpen && tab !== "czat" && tab !== "grupa" && (
        <MoreSheet3
          setTab={setTab}
          canSee={canSee}
          onClose={() => setMoreOpen(false)}
          dots={{ zadluzenie: debtEntries.length > 0, rekompensaty: myCompRequests.some((r) => r.status === "pending"), grupa: hasUnreadGroup }}
        />
      )}
      {ui3 && profileOpen && (
        <ProfileSheet3
          driver={driver}
          avatar={avatar}
          onSetAvatar={onSetAvatar}
          theme={theme}
          onToggleTheme={onToggleTheme}
          onLogout={onLogout}
          car={car}
          count={mine.length}
          onClose={() => setProfileOpen(false)}
        />
      )}
      {svOpen && (
        <LazyBoundary quiet name="svsheet">
          <React.Suspense fallback={null}>
            <SurveySheetLazy driver={driver} onClose={() => setSvOpen(false)} onSubmitted={(r) => svSave(r)} />
          </React.Suspense>
        </LazyBoundary>
      )}
      {girlPhase === "intro" && (
        <LazyBoundary quiet name="girlintro">
          <React.Suspense fallback={null}>
            <GirlIntroLazy
              onClose={() => {
                girlSet("patex_girl_seen_" + gKey, "1");
                setGirlPhase(null);
              }}
            />
          </React.Suspense>
        </LazyBoundary>
      )}
      {girlPhase === "chip" && (
        <LazyBoundary quiet name="girlchip">
          <React.Suspense fallback={null}>
            <GirlChipLazy onOpen={() => setGirlPhase("intro")} onHide={() => setGirlPhase((c) => (c === "chip" ? null : c))} />
          </React.Suspense>
        </LazyBoundary>
      )}
      {girlPhase === "reveal" && (
        <LazyBoundary quiet name="girlreveal">
          <React.Suspense fallback={null}>
            <GirlRevealLazy
              claim={girlClaim}
              canFortune={canSee("fortuna")}
              onClose={(go) => {
                setGirlPhase(null);
                if (onGirlFreed) onGirlFreed(true); // dopiero teraz tło przechodzi w czerwony księżyc — widać efekt
                if (go) setTab(go);
              }}
            />
          </React.Suspense>
        </LazyBoundary>
      )}
      {tkWow && (
        <LazyBoundary quiet name="ticketearned">
        <React.Suspense fallback={null}>
          <TicketEarnedLazy
            n={tkWow.n}
            title={tkWow.title}
            count={tkWow.count}
            onClose={() => {
              setTkWow(null);
              setCelebration(null);
            }}
            onGo={() => {
              setTkWow(null);
              setCelebration(null);
              setTab("fortuna");
            }}
          />
        </React.Suspense>
        </LazyBoundary>
      )}
      {celebration && !tkWow && <CelebrationOverlay message={celebration} onDone={() => setCelebration(null)} />}
      {celebrationTier && <EarningsCelebration tier={celebrationTier} onClose={() => setCelebrationTier(null)} />}
      {dmProfileDriver &&
        createPortal(
          <div className="modal-overlay" onClick={() => setDmProfileDriver(null)}>
            <div className="modal-card dm-profile-card" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-x" onClick={() => setDmProfileDriver(null)} aria-label="Zamknij">
                <X size={18} />
              </button>
              <div
                className="dm-profile-avatar-lg"
                style={{ background: (avatars && avatars[dmProfileDriver.name]) ? "var(--surface-3)" : avatarColorFor(dmProfileDriver.name) }}
              >
                {(avatars && avatars[dmProfileDriver.name]) || initials(dmProfileDriver.name)}
              </div>
              <div className="dm-profile-name">
                {dmProfileDriver.name}
                {sameName(dmProfileDriver.name, driver.name) && <span className="ranking-me-tag">Ty</span>}
              </div>
              {dmProfileDriver.car ? (
                <div className="dm-profile-car">
                  <div className="dm-profile-car-icon">
                    <Car size={22} />
                  </div>
                  <div className="dm-profile-car-label">{dmProfileDriver.car}</div>
                </div>
              ) : (
                <div className="dm-profile-car dm-profile-car-empty">
                  <div className="dm-profile-car-icon">
                    <Car size={22} />
                  </div>
                  <div className="dm-profile-car-label">Brak przypisanego auta</div>
                </div>
              )}
              {!sameName(dmProfileDriver.name, driver.name) && (
                <button className="btn btn-primary dm-profile-message-btn" onClick={() => goToDmWith(dmProfileDriver.name)}>
                  <MessageCircle size={15} /> Napisz wiadomość
                </button>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}


/* ---------------------------------------------------------------------- */
/* Root                                                                     */
/* ---------------------------------------------------------------------- */
// Siatka bezpieczeństwa — jeśli appka natrafi na błąd JavaScriptu podczas
// renderowania (cokolwiek, gdziekolwiek w drzewie komponentów), React bez
// tego po prostu "wyrzuca" cały interfejs, zostawiając czarny/pusty ekran
// bez żadnej informacji. Ten komponent to łapie i pokazuje przyjazny
// komunikat z przyciskiem odświeżenia zamiast martwego ekranu.
class PateXErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("PateX — błąd renderowania:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            background: "#0a0b0f",
            color: "#f2f3f6",
            fontFamily: "-apple-system, sans-serif",
            padding: 24,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: "linear-gradient(135deg, #ffb84d, #ff9d2e)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              color: "#1a1206",
              fontSize: 20,
            }}
          >
            !
          </div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Coś poszło nie tak</div>
          <div style={{ fontSize: 13, color: "#9ba0ae", maxWidth: 300 }}>
            Appka napotkała błąd. Odśwież stronę — jeśli to się powtarza, daj znać administratorowi.
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "12px 24px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #ffb84d, #ff9d2e)",
              color: "#1a1206",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Odśwież
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function FleetEarningsAppInner() {
  // Usuwa ekran ładowania (z index.html) w momencie, gdy TA appka faktycznie
  // się zamontowała — nie na zgadywanym czasie, tylko na prawdziwym
  // sygnale "React już działa". Jeśli appka w ogóle się nie zamontuje
  // (np. błąd JS), ekran ładowania zostanie widoczny zamiast czarnego tła.
  useEffect(() => {
    const splash = document.getElementById("splash-loader");
    if (splash) splash.remove();
    try {
      if (window.__patexMark) window.__patexMark("mounted");
    } catch (e) {
      /* diagnostyka jest dodatkiem */
    }
  }, []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Komunikat przy wolnym wczytywaniu (zamiast niemego "kręcącego się samochodzika"):
  // po 7 s informacja o słabym zasięgu, po 16 s przycisk "Spróbuj ponownie".
  const [slowLoad, setSlowLoad] = useState(0);
  useEffect(() => {
    if (!loading) return undefined;
    const t1 = setTimeout(() => setSlowLoad(1), 7000);
    const t2 = setTimeout(() => setSlowLoad(2), 16000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line
  }, [loading]);
  const [drivers, setDrivers] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [globalBonuses, setGlobalBonuses] = useState([]);
  const [avatars, setAvatars] = useState({});
  const [payoutDecisions, setPayoutDecisions] = useState({});
  const [compensationRequests, setCompensationRequests] = useState([]);
  const [chatThreads, setChatThreads] = useState([]);
  const [news, setNews] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pointAdjustments, setPointAdjustments] = useState([]);
  const [cars, setCars] = useState([]);
  const [weeklyRevenue, setWeeklyRevenue] = useState([]);
  const [profitLoss, setProfitLoss] = useState([]);
  const [carRepairs, setCarRepairs] = useState([]);
  const [companyExpenses, setCompanyExpenses] = useState([]);
  const [carInfo, setCarInfo] = useState([]);
  const [companyAdjustments, setCompanyAdjustments] = useState([]);
  const [cryptoPortfolio, setCryptoPortfolio] = useState({});
  const [cryptoStrategyParams, setCryptoStrategyParams] = useState({});
  const [cryptoIntradayPortfolio, setCryptoIntradayPortfolio] = useState({});
  const [cryptoIntradayStrategyParams, setCryptoIntradayStrategyParams] = useState({});
  const [debtRepayments, setDebtRepayments] = useState([]);
  const [driverDebts, setDriverDebts] = useState([]);
  const [groupChatMessages, setGroupChatMessages] = useState([]);
  const [dmThreads, setDmThreads] = useState([]);
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem("patex_admin_session") === "true" ? "admin" : "home";
    } catch (e) {
      return "home";
    }
  });
  const sceneRef = useRef(null);
  const [hw] = useState(halloweenOn);
  const [hwReady, setHwReady] = useState(false);
  const [hwPlaying, setHwPlaying] = useState(false);
  const [hwFree, setHwFree] = useState(girlDeviceFreed);
  const [hwMoon, setHwMoon] = useState(girlDeviceFreed); // tło po uwolnieniu dziewczynki: czerwony księżyc (włączane po zamknięciu podziękowania)
  const [hwMoonReady, setHwMoonReady] = useState(false);
  const hwMoonSoon = useRef(girlDeviceFreed() || view === "admin");
  useEffect(() => {
    if (!hw || !(hwFree || hwMoon || view === "admin")) return undefined;
    // nowy obraz dociągamy w tle (przy starcie appki dopiero po chwili); podmiana dopiero, gdy jest gotowy — bez dziury w tle
    const t = setTimeout(() => {
      const im = new Image();
      im.onload = () => setHwMoonReady(true);
      im.src = hwBg2;
    }, hwMoonSoon.current ? 1500 : 0);
    return () => clearTimeout(t);
  }, [hw, hwFree, hwMoon, view === "admin"]);
  useEffect(() => {
    if (!hw || girlDeviceFreed() || view === "admin") return undefined; // po uwolnieniu (i w panelu admina) schody nie są potrzebne
    // obraz dociągamy dopiero po starcie appki (nie konkuruje z ładowaniem pulpitu), potem płynne wejście
    const t = setTimeout(() => {
      const im = new Image();
      im.onload = () => setHwReady(true);
      im.src = hwBg;
    }, 1500);
    return () => clearTimeout(t);
  }, [hw]);
  // Dźwięk przy KAŻDYM uruchomieniu: próba automatycznego startu zaraz po otwarciu appki; jeśli przeglądarka blokuje dźwięk bez dotyku
  // (iPhone, część Androidów), start następuje przy pierwszym dotknięciu. Powrót do appki (gdy dźwięk nie gra) też go wznawia; nagranie jest zapętlone.
  useEffect(() => {
    if (!hw) return undefined;
    const off = () => {
      document.removeEventListener("click", onTap, true);
      document.removeEventListener("touchend", onTap, true);
      document.removeEventListener("keydown", onTap, true);
    };
    const arm = () => {
      document.addEventListener("click", onTap, true);
      document.addEventListener("touchend", onTap, true);
      document.addEventListener("keydown", onTap, true);
    };
    function attempt() {
      if (document.querySelector(".phone-wide")) return; // panel admina: bez dźwięku
      if (girlDeviceFreed()) return; // dziewczynka uwolniona: muzyka już nie gra
      if (HWS.busy || (HWS.a && !HWS.a.paused)) return;
      hwPlay(setHwPlaying).then((ok) => {
        if (ok) off();
      });
    }
    function onTap() {
      attempt();
    }
    arm();
    const auto = setTimeout(attempt, 1800);
    const onVis = () => {
      // każdy powrót do appki: jeśli dźwięk nie gra (system go wstrzymał / appka była w tle) — wznów, a gdy przeglądarka blokuje, to przy dotknięciu
      if (document.hidden || girlDeviceFreed()) return;
      if (HWS.a && !HWS.a.paused) return;
      arm();
      setTimeout(attempt, 300);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearTimeout(auto);
      document.removeEventListener("visibilitychange", onVis);
      off();
    };
  }, [hw]);
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("patex_theme") || "dark";
    } catch (e) {
      return "dark";
    }
  });
  function toggleTheme() {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      try {
        localStorage.setItem("patex_theme", next);
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  }
  useEffect(() => {
    // Delikatna paralaksa — tło nocnej autostrady przesuwa się odrobinę
    // wolniej niż reszta strony, dając subtelne wrażenie głębi.
    function onScroll() {
      if (sceneRef.current) {
        sceneRef.current.style.transform = `translateY(${window.scrollY * 0.06}px)`;
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const [pin, setPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [driver, setDriver] = useState(null);
  const [adminViewingDriverName, setAdminViewingDriverName] = useState(null);
  const [toast, setToast] = useState("");

  // Dane finansowe firmy, krypto, auta itp. są potrzebne TYLKO panelowi admina.
  // Kierowca ich nie używa, więc jego telefon nie pobiera ich przy każdym starcie
  // (mniej zapytań = szybszy start na słabym zasięgu). Panel admina doczytuje je sam
  // przy wejściu (patrz efekt niżej).
  const adminDataLoadedRef = useRef(false);
  useEffect(() => {
    (async () => {
      let adminStart = false;
      try {
        adminStart = localStorage.getItem("patex_admin_session") === "true";
      } catch (e) {
        adminStart = false;
      }
      adminDataLoadedRef.current = adminStart;
      const getIfAdmin = (key) => (adminStart ? window.storage.get(key, true) : Promise.resolve(null));
      let loadedDrivers = [];
      let loadedEarnings = [];
      let loadedBonuses = [];
      let loadedAvatars = {};
      let loadedPayoutDecisions = {};
      let loadedCompensationRequests = [];
      let loadedChatThreads = [];
      let loadedNews = [];
      let loadedTasks = [];
      let loadedPointAdjustments = [];
      let loadedCars = [];
      let loadedWeeklyRevenue = [];
      let loadedProfitLoss = [];
      let loadedCarRepairs = [];
      let loadedCompanyExpenses = [];
      let loadedCarInfo = [];
      let loadedCompanyAdjustments = [];
      let loadedCryptoPortfolio = {};
      let loadedCryptoStrategyParams = {};
      let loadedCryptoIntradayPortfolio = {};
      let loadedCryptoIntradayStrategyParams = {};
      let loadedDebtRepayments = [];
      let loadedDriverDebts = [];
      let loadedGroupChatMessages = [];
      let seeded = false;
      let criticalLoadFailed = false; // lista kierowców albo rozliczenia nie zostały pobrane (brak sieci)
      try {
        const res = await window.storage.get(DRIVERS_KEY, true);
        if (res && res.value) loadedDrivers = JSON.parse(res.value);
        else criticalLoadFailed = true;
      } catch (e) {
        criticalLoadFailed = true;
      }
      try {
        const res = await window.storage.get(EARNINGS_KEY, true);
        if (res && res.value) loadedEarnings = JSON.parse(res.value);
        else criticalLoadFailed = true;
      } catch (e) {
        criticalLoadFailed = true;
      }
      try {
        const res = await window.storage.get(GLOBAL_BONUSES_KEY, true);
        if (res && res.value) loadedBonuses = JSON.parse(res.value);
      } catch (e) {
        /* no global bonuses saved yet */
      }
      try {
        const res = await window.storage.get(AVATARS_KEY, true);
        if (res && res.value) loadedAvatars = JSON.parse(res.value);
      } catch (e) {
        /* no avatars saved yet */
      }
      try {
        const res = await window.storage.get(PAYOUT_DECISIONS_KEY, true);
        if (res && res.value) loadedPayoutDecisions = JSON.parse(res.value);
      } catch (e) {
        /* no payout decisions saved yet */
      }
      try {
        const res = await window.storage.get(COMPENSATION_REQUESTS_KEY, true);
        if (res && res.value) loadedCompensationRequests = JSON.parse(res.value);
      } catch (e) {
        /* no compensation requests saved yet */
      }
      // Czat (wątki) NIE jest pobierany przy starcie — to największa porcja danych; dociąga się zaraz po pokazaniu ekranu.
      try {
        const res = await window.storage.get(NEWS_KEY, true);
        if (res && res.value) loadedNews = JSON.parse(res.value);
      } catch (e) {
        /* no news saved yet */
      }
      try {
        const res = await window.storage.get(TASKS_KEY, true);
        if (res && res.value) loadedTasks = JSON.parse(res.value);
      } catch (e) {
        /* no tasks saved yet */
      }
      try {
        const res = await window.storage.get(POINT_ADJUSTMENTS_KEY, true);
        if (res && res.value) loadedPointAdjustments = JSON.parse(res.value);
      } catch (e) {
        /* no point adjustments saved yet */
      }
      try {
        const res = await getIfAdmin(CARS_KEY);
        if (res && res.value) loadedCars = JSON.parse(res.value);
      } catch (e) {
        /* no cars saved yet */
      }
      try {
        const res = await getIfAdmin(WEEKLY_REVENUE_KEY);
        if (res && res.value) loadedWeeklyRevenue = JSON.parse(res.value);
      } catch (e) {
        /* no weekly revenue saved yet */
      }
      try {
        const res = await getIfAdmin(PROFIT_LOSS_KEY);
        if (res && res.value) loadedProfitLoss = JSON.parse(res.value);
      } catch (e) {
        /* no profit/loss saved yet */
      }
      try {
        const res = await getIfAdmin(CAR_REPAIRS_KEY);
        if (res && res.value) loadedCarRepairs = JSON.parse(res.value);
      } catch (e) {
        /* no car repairs saved yet */
      }
      try {
        const res = await getIfAdmin(COMPANY_EXPENSES_KEY);
        if (res && res.value) loadedCompanyExpenses = JSON.parse(res.value);
      } catch (e) {
        /* no company expenses saved yet */
      }
      try {
        const res = await getIfAdmin(CAR_INFO_KEY);
        if (res && res.value) loadedCarInfo = JSON.parse(res.value);
      } catch (e) {
        /* no car info saved yet */
      }
      try {
        const res = await getIfAdmin(COMPANY_ADJUSTMENTS_KEY);
        if (res && res.value) loadedCompanyAdjustments = JSON.parse(res.value);
      } catch (e) {
        /* no company adjustments saved yet */
      }
      try {
        const res = await getIfAdmin(CRYPTO_PORTFOLIO_KEY);
        if (res && res.value) loadedCryptoPortfolio = JSON.parse(res.value);
      } catch (e) {
        /* no crypto portfolio saved yet */
      }
      try {
        const res = await getIfAdmin(CRYPTO_STRATEGY_PARAMS_KEY);
        if (res && res.value) loadedCryptoStrategyParams = JSON.parse(res.value);
      } catch (e) {
        /* no crypto strategy params saved yet */
      }
      try {
        const res = await getIfAdmin(CRYPTO_INTRADAY_PORTFOLIO_KEY);
        if (res && res.value) loadedCryptoIntradayPortfolio = JSON.parse(res.value);
      } catch (e) {
        /* no crypto intraday portfolio saved yet */
      }
      try {
        const res = await getIfAdmin(CRYPTO_INTRADAY_STRATEGY_PARAMS_KEY);
        if (res && res.value) loadedCryptoIntradayStrategyParams = JSON.parse(res.value);
      } catch (e) {
        /* no crypto intraday strategy params saved yet */
      }
      try {
        const res = await window.storage.get(DEBT_REPAYMENTS_KEY, true);
        if (res && res.value) loadedDebtRepayments = JSON.parse(res.value);
      } catch (e) {
        /* no debt repayments saved yet */
      }
      try {
        const res = await window.storage.get(DRIVER_DEBTS_KEY, true);
        if (res && res.value) loadedDriverDebts = JSON.parse(res.value);
      } catch (e) {
        /* no driver debts saved yet */
      }
      // Czat grupowy też dociąga się po starcie (patrz wyżej).
      const deduped = upsertRecords([], loadedEarnings);
      if (deduped.length !== loadedEarnings.length) {
        loadedEarnings = deduped;
        try {
          await window.storage.set(EARNINGS_KEY, JSON.stringify(loadedEarnings), true);
        } catch (e) {
          /* best effort */
        }
      }
      setDrivers(loadedDrivers);
      setEarnings(loadedEarnings);
      setGlobalBonuses(loadedBonuses);
      setAvatars(loadedAvatars);
      setPayoutDecisions(loadedPayoutDecisions);
      setCompensationRequests(loadedCompensationRequests);
      setChatThreads(loadedChatThreads);
      setNews(loadedNews);
      setTasks(loadedTasks);
      setPointAdjustments(loadedPointAdjustments);
      setCars(loadedCars);
      setWeeklyRevenue(loadedWeeklyRevenue);
      setProfitLoss(loadedProfitLoss);
      setCarRepairs(loadedCarRepairs);
      setCompanyExpenses(loadedCompanyExpenses);
      setCarInfo(loadedCarInfo);
      setCompanyAdjustments(loadedCompanyAdjustments);
      setCryptoPortfolio(loadedCryptoPortfolio);
      setCryptoStrategyParams(loadedCryptoStrategyParams);
      setCryptoIntradayPortfolio(loadedCryptoIntradayPortfolio);
      setCryptoIntradayStrategyParams(loadedCryptoIntradayStrategyParams);
      setDebtRepayments(loadedDebtRepayments);
      setDriverDebts(loadedDriverDebts);
      setGroupChatMessages(loadedGroupChatMessages);

      if (criticalLoadFailed) {
        // Brak sieci / serwer nie odpowiedział: NIE traktuj tego jak "brak kierowców" (to wylogowywało
        // kierowcę i pokazywało pusty ekran logowania) — pokaż komunikat z przyciskiem ponowienia.
        setLoadError(true);
        setLoading(false);
        try {
          if (window.__patexMark) window.__patexMark("loaderr");
        } catch (e) {
          /* diagnostyka */
        }
        return;
      }
      try {
        const saved = JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
        if (saved && saved.pin) {
          const match = loadedDrivers.find((d) => d.pin === saved.pin);
          if (match) {
            setDriver({ pin: match.pin, name: match.name });
            setView("dashboard");
            subscribeAndReportPushStatus(match.name, false);
          } else {
            localStorage.removeItem(SESSION_KEY);
          }
        }
      } catch (e) {
        /* corrupted session data — ignore */
      }

      setLoading(false);
      try {
        window.__patexReady = true;
        if (window.__patexMark) window.__patexMark("ready");
      } catch (e) {
        /* diagnostyka */
      }
      // Po pokazaniu ekranu od razu dociągnij świeże dane i CZAT (przy starcie z pamięci telefonu dane mogą być sprzed
      // kilku godzin; czat w ogóle nie jest pobierany przy starcie).
      setTimeout(
        () => {
          try {
            if (handleReloadRef.current) handleReloadRef.current();
          } catch (e) {
            /* odświeżanie w tle i tak powtórzy się za kilka sekund */
          }
        },
        window.__patexStartup && window.__patexStartup.usedCache ? 0 : 700
      );
    })();
  }, []);

  // Wejście do panelu admina (logowanie albo zapamiętana sesja) — doczytaj dane, których
  // kierowca nie pobiera przy starcie. Przy błędzie sieci ponawia próbę.
  useEffect(() => {
    if (view !== "admin" || adminDataLoadedRef.current) return undefined;
    adminDataLoadedRef.current = true;
    let cancelled = false;
    let finished = false;
    (async () => {
      const jobs = [
        [CARS_KEY, setCars],
        [WEEKLY_REVENUE_KEY, setWeeklyRevenue],
        [PROFIT_LOSS_KEY, setProfitLoss],
        [CAR_REPAIRS_KEY, setCarRepairs],
        [COMPANY_EXPENSES_KEY, setCompanyExpenses],
        [CAR_INFO_KEY, setCarInfo],
        [COMPANY_ADJUSTMENTS_KEY, setCompanyAdjustments],
        [CRYPTO_PORTFOLIO_KEY, setCryptoPortfolio],
        [CRYPTO_STRATEGY_PARAMS_KEY, setCryptoStrategyParams],
        [CRYPTO_INTRADAY_PORTFOLIO_KEY, setCryptoIntradayPortfolio],
        [CRYPTO_INTRADAY_STRATEGY_PARAMS_KEY, setCryptoIntradayStrategyParams],
      ];
      let pending = jobs;
      for (let round = 0; round < 4 && pending.length > 0 && !cancelled; round++) {
        if (round > 0) await new Promise((resolve) => setTimeout(resolve, 2000 * round));
        const failed = [];
        await Promise.all(
          pending.map(async (job) => {
            const [key, setter] = job;
            try {
              const res = await window.storage.get(key, true, { strict: true });
              if (res && res.value && !cancelled) setter(JSON.parse(res.value));
            } catch (e) {
              failed.push(job);
            }
          })
        );
        pending = failed;
      }
      finished = pending.length === 0;
      if (!finished) adminDataLoadedRef.current = false; // niech kolejne wejście do panelu spróbuje jeszcze raz
    })();
    return () => {
      cancelled = true;
      if (!finished) adminDataLoadedRef.current = false; // przerwane w trakcie — następnym razem doczytaj od nowa
    };
  }, [view]);

  // Appka wczytywała wszystkie dane TYLKO RAZ, przy starcie — więc jeśli
  // np. admin dodał komuś dług w jednej sesji, kierowca w swojej (innej)
  // appce nie widział tego, dopóki sam nie zamknął i otworzył jej od nowa.
  // To okresowe, ciche odświeżanie w tle (co 20 sekund) naprawia to dla
  // najważniejszych, najczęściej zmienianych rzeczy — bez przeładowywania
  // całej appki i bez pokazywania żadnego ekranu ładowania.
  useEffect(() => {
    let liveTick = 0;
    async function refreshLiveCollections() {
      if (window.__patexSlowLink && window.__patexSlowLink() && ++liveTick % 3 !== 0) return; // wolne łącze — rzadziej
      const jobs = [
        [EARNINGS_KEY, setEarnings, []],
        [COMPENSATION_REQUESTS_KEY, setCompensationRequests, []],
        [DEBT_REPAYMENTS_KEY, setDebtRepayments, []],
        [DRIVER_DEBTS_KEY, setDriverDebts, []],
        [PAYOUT_DECISIONS_KEY, setPayoutDecisions, {}],
      ];
      for (const [key, setter, fallback] of jobs) {
        try {
          const res = await window.storage.get(key, true);
          setter(res && res.value ? parseCached(key, res.value) : fallback);
        } catch (e) {
          /* pojedynczy nieudany fetch nie powinien zepsuć reszty odświeżania */
        }
      }
    }
    const interval = setInterval(refreshLiveCollections, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  async function persistDrivers(next) {
    setDrivers(next);
    try {
      const res = await window.storage.set(DRIVERS_KEY, JSON.stringify(next), true);
      if (!res) setToast("Nie udało się zapisać danych kierowców.");
    } catch (e) {
      setToast("Nie udało się zapisać danych kierowców.");
    }
  }
  function updateDriverPermissions(driverId, permissions) {
    return mutateArrayCollection(DRIVERS_KEY, setDrivers, (fresh) =>
      fresh.map((d) => (d.id === driverId ? { ...d, permissions } : d))
    );
  }
  function updateDriverRates(driverId, rates) {
    return mutateArrayCollection(DRIVERS_KEY, setDrivers, (fresh) =>
      fresh.map((d) => (d.id === driverId ? { ...d, ...rates } : d))
    );
  }
  function updateDriverCar(driverId, car) {
    return mutateArrayCollection(DRIVERS_KEY, setDrivers, (fresh) => fresh.map((d) => (d.id === driverId ? { ...d, car } : d)));
  }
  // groupMutedUntil: null = nie wyciszony; "forever" = na stałe; liczba =
  // znacznik czasu (ms), do kiedy trwa wyciszenie.
  function updateDriverGroupMute(driverId, groupMutedUntil) {
    return mutateArrayCollection(DRIVERS_KEY, setDrivers, (fresh) =>
      fresh.map((d) => (d.id === driverId ? { ...d, groupMutedUntil } : d))
    );
  }
  // chatBlockedUntil: null = bez blokady; "forever" = na stałe; liczba = znacznik czasu (ms), do kiedy trwa blokada czatu z Szefem i połączeń.
  function updateDriverChatBlock(driverId, chatBlockedUntil) {
    return mutateArrayCollection(DRIVERS_KEY, setDrivers, (fresh) =>
      fresh.map((d) => (d.id === driverId ? { ...d, chatBlockedUntil } : d))
    );
  }
  function addCar(name) {
    return mutateArrayCollection(CARS_KEY, setCars, (fresh) => (fresh.includes(name) ? fresh : [...fresh, name]));
  }
  async function renameCar(oldName, newName) {
    await mutateArrayCollection(CARS_KEY, setCars, (fresh) => fresh.map((c) => (c === oldName ? newName : c)));
    await mutateArrayCollection(DRIVERS_KEY, setDrivers, (fresh) =>
      fresh.map((d) => (d.car === oldName ? { ...d, car: newName } : d))
    );
  }
  function deleteCar(name) {
    return mutateArrayCollection(CARS_KEY, setCars, (fresh) => fresh.filter((c) => c !== name));
  }
  // Generyczny zestaw dodaj/edytuj/usuń — używany przez wszystkie pięć
  // nowych kolekcji finansowych (mają tę samą, prostą strukturę: tablica
  // obiektów z polem `id`).
  function makeCrud(key, setState) {
    return {
      add: (item) =>
        mutateArrayCollection(key, setState, (fresh) => {
          const it = { ...item, id: item.id || `${key}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` };
          return fresh.some((r) => r && r.id === it.id) ? fresh : [...fresh, it];
        }),
      update: (id, patch) =>
        mutateArrayCollection(key, setState, (fresh) => fresh.map((r) => (r.id === id ? { ...r, ...patch } : r))),
      remove: (id) => mutateArrayCollection(key, setState, (fresh) => fresh.filter((r) => r.id !== id)),
    };
  }
  const weeklyRevenueCrud = makeCrud(WEEKLY_REVENUE_KEY, setWeeklyRevenue);
  const profitLossCrud = makeCrud(PROFIT_LOSS_KEY, setProfitLoss);
  const carRepairsCrud = makeCrud(CAR_REPAIRS_KEY, setCarRepairs);
  // Kiedy dodajesz nowy tydzień utargu dla auta, kwota "po podatku 8,5%"
  // automatycznie DOLICZA się do "Zarobki" tego auta w Zyski/Straty (i
  // odpowiednio zmniejsza "Suma do spłacenia"). Kiedy dodajesz naprawę z
  // kosztem, ten koszt automatycznie ODEJMUJE się od "Zarobki" tego samego
  // auta. Dotyczy to tylko DODAWANIA nowych wpisów — edycja/usunięcie
  // starszego wpisu nie przelicza tego na nowo, żeby uniknąć rozjazdów.
  async function adjustProfitLossEarnings(car, delta) {
    if (!car || !delta) return;
    await mutateArrayCollection(PROFIT_LOSS_KEY, setProfitLoss, (fresh) =>
      fresh.map((p) => {
        if (p.car !== car) return p;
        const nextEarnings = Math.round(((p.earnings || 0) + delta) * 100) / 100;
        const nextBalance = Math.round(((p.totalExpenses || 0) - nextEarnings) * 100) / 100;
        return { ...p, earnings: nextEarnings, balance: nextBalance };
      })
    );
  }
  const weeklyRevenueCrudLinked = {
    ...weeklyRevenueCrud,
    add: async (item) => {
      await weeklyRevenueCrud.add(item);
      await adjustProfitLossEarnings(item.car, parseFloat(item.afterTax) || 0);
    },
    update: async (id, patch) => {
      const oldRow = (weeklyRevenue || []).find((r) => r.id === id);
      await weeklyRevenueCrud.update(id, patch);
      if (oldRow) {
        const oldAfterTax = parseFloat(oldRow.afterTax) || 0;
        const newAfterTax = patch.afterTax !== undefined ? parseFloat(patch.afterTax) || 0 : oldAfterTax;
        const delta = newAfterTax - oldAfterTax;
        if (delta) await adjustProfitLossEarnings(oldRow.car, delta);
      }
    },
    remove: async (id) => {
      const oldRow = (weeklyRevenue || []).find((r) => r.id === id);
      await weeklyRevenueCrud.remove(id);
      if (oldRow) await adjustProfitLossEarnings(oldRow.car, -(parseFloat(oldRow.afterTax) || 0));
    },
  };
  const carRepairsCrudLinked = {
    ...carRepairsCrud,
    add: async (item) => {
      await carRepairsCrud.add(item);
      await adjustProfitLossEarnings(item.car, -(parseFloat(item.cost) || 0));
    },
    update: async (id, patch) => {
      const oldRow = (carRepairs || []).find((r) => r.id === id);
      await carRepairsCrud.update(id, patch);
      if (oldRow) {
        const oldCost = parseFloat(oldRow.cost) || 0;
        const newCost = patch.cost !== undefined ? parseFloat(patch.cost) || 0 : oldCost;
        const delta = oldCost - newCost; // koszt wyższy -> odejmij więcej od zarobków (delta ujemna)
        if (delta) await adjustProfitLossEarnings(oldRow.car, delta);
      }
    },
    remove: async (id) => {
      const oldRow = (carRepairs || []).find((r) => r.id === id);
      await carRepairsCrud.remove(id);
      if (oldRow) await adjustProfitLossEarnings(oldRow.car, parseFloat(oldRow.cost) || 0);
    },
  };
  const companyExpensesCrud = makeCrud(COMPANY_EXPENSES_KEY, setCompanyExpenses);
  const carInfoCrud = makeCrud(CAR_INFO_KEY, setCarInfo);
  const companyAdjustmentsCrud = makeCrud(COMPANY_ADJUSTMENTS_KEY, setCompanyAdjustments);
  const debtRepaymentsCrud = makeCrud(DEBT_REPAYMENTS_KEY, setDebtRepayments);
  const driverDebtsCrud = makeCrud(DRIVER_DEBTS_KEY, setDriverDebts);
  async function persistEarnings(next) {
    setEarnings(next);
    try {
      const res = await window.storage.set(EARNINGS_KEY, JSON.stringify(next), true);
      if (!res) setToast("Nie udało się zapisać rozliczeń.");
    } catch (e) {
      setToast("Nie udało się zapisać rozliczeń.");
    }
  }
  function deleteEarningsRecordSafe(key) {
    return mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) => fresh.filter((r) => recordKey(r) !== key));
  }
  function unarchiveEarningsRecord(key) {
    return mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) =>
      fresh.map((r) => (recordKey(r) === key ? { ...r, archived: false } : r))
    );
  }
  async function persistGlobalBonuses(next) {
    setGlobalBonuses(next);
    try {
      const res = await window.storage.set(GLOBAL_BONUSES_KEY, JSON.stringify(next), true);
      if (!res) setToast("Nie udało się zapisać bonusu.");
    } catch (e) {
      setToast("Nie udało się zapisać bonusu.");
    }
  }
  function addBonus(bonus) {
    return mutateArrayCollection(GLOBAL_BONUSES_KEY, setGlobalBonuses, (fresh) => [...fresh, bonus]);
  }
  function deleteBonus(bonusId) {
    return mutateArrayCollection(GLOBAL_BONUSES_KEY, setGlobalBonuses, (fresh) => fresh.filter((b) => b.id !== bonusId));
  }
  // Pełna edycja istniejącego bonusu — tytuł, kwota, przypisani kierowcy,
  // daty, notatka. Osobna funkcja od updateBonusStatus (która zmienia
  // tylko "aktywny"/"zakończony") — ta edytuje WSZYSTKIE pola naraz.
  function updateBonus(bonusId, updates) {
    return mutateArrayCollection(GLOBAL_BONUSES_KEY, setGlobalBonuses, (fresh) =>
      fresh.map((b) => (b.id === bonusId ? { ...b, ...updates } : b))
    );
  }
  async function updateBonusStatus(bonusId, status) {
    // "Aktywny" pozostaje ogólnym statusem CAŁEGO bonusu (czy w ogóle jeszcze
    // obowiązuje) — samo ukończenie/nie zrealizowanie jest teraz per
    // kierowca, patrz setBonusCompletion niżej.
    await mutateArrayCollection(GLOBAL_BONUSES_KEY, setGlobalBonuses, (fresh) =>
      fresh.map((b) => (b.id === bonusId ? { ...b, status } : b))
    );
    return { errors: [] };
  }
  async function setBonusCompletion(bonusId, driverName, driverStatus) {
    const bonus = globalBonuses.find((b) => b.id === bonusId);
    const wasCompleted = bonus && (bonus.completions || {})[driverName] === "completed";
    await mutateArrayCollection(GLOBAL_BONUSES_KEY, setGlobalBonuses, (fresh) =>
      fresh.map((b) => (b.id === bonusId ? { ...b, completions: { ...b.completions, [driverName]: driverStatus } } : b))
    );
    // Kiedy TEN KONKRETNY kierowca pierwszy raz dostaje "zrealizowane",
    // dostaje automatycznie zatwierdzoną rekompensatę na tę kwotę — ale
    // TYLKO do rozliczenia ze statusem "Nie rozpoczęto". Pozostali
    // przypisani kierowcy w ogóle nie są tym ruszani.
    if (driverStatus === "completed" && !wasCompleted && bonus) {
      const targetRecord = earnings.find((r) => sameName(r.name, driverName) && r.status === "notstarted");
      if (!targetRecord) {
        return { errors: [driverName] };
      }
      await mutateArrayCollection(COMPENSATION_REQUESTS_KEY, setCompensationRequests, (fresh) => [
        ...fresh,
        {
          id: `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${driverName.replace(/\s+/g, "")}`,
          driverName,
          periodFrom: targetRecord.periodFrom,
          periodTo: targetRecord.periodTo,
          type: "add",
          amount: bonus.amount,
          note: `Za wykonanie "${bonus.title}"`,
          status: "approved",
          requestedAt: Date.now(),
          decidedAt: Date.now(),
        },
      ]);
      fetch("/api/push-to-driver", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          driverName,
          title: "PateX — bonus ukończony",
          body: `Dostałeś ${formatPLN(bonus.amount)} za wykonanie „${bonus.title}”.`,
        }),
      }).catch(() => {});
    }
    return { errors: [] };
  }
  async function persistAvatars(next) {
    setAvatars(next);
    try {
      await window.storage.set(AVATARS_KEY, JSON.stringify(next), true);
    } catch (e) {
      /* best effort — avatar is purely cosmetic */
    }
  }
  // Zawsze pobiera AKTUALNY stan z serwera przed zapisem — appka nigdy już
  // nie nadpisuje całego pliku tym, co ma akurat w pamięci przeglądarki
  // (to właśnie powodowało znikanie danych, kiedy dwie karty/urządzenia
  // pracowały na starszych kopiach tych samych danych).
  async function fetchFreshCollection(key, fallback) {
    // KRYTYCZNE: jeśli to pobieranie się nie powiedzie (np. serwer akurat się
    // restartuje), NIE WOLNO cicho zwracać pustej listy — bo kod wywołujący
    // dokleiłby do niej nową wiadomość/wpis i zapisał to jako CAŁOŚĆ,
    // kasując wszystko, co było wcześniej. Błąd musi polecieć dalej, żeby
    // cała operacja się przerwała, zamiast nadpisać dane pustką.
    const res = await window.storage.get(key, true, { strict: true });
    return res && res.value ? JSON.parse(res.value) : fallback;
  }
  // To samo, ale dodatkowo zwraca "wersję" kolekcji z serwera — zapis oparty na
  // tym odczycie zostanie odrzucony, jeśli ktoś inny zdążył w międzyczasie
  // zapisać tę samą kolekcję (patrz runMutation niżej).
  async function fetchFreshWithVersion(key, fallback) {
    const res = await window.storage.get(key, true, { strict: true });
    return { data: res && res.value ? JSON.parse(res.value) : fallback, version: (res && res.version) || null };
  }
  // Bez tego, dwa prawie jednoczesne zapisy do TEJ SAMEJ kolekcji (np.
  // wysłanie tekstu, a chwilę potem głosówki, która długo się wysyła)
  // mogły się "prześcignąć" — drugi zapis, oparty na starszej migawce
  // danych, nadpisywał pierwszy, zanim ten zdążył się utrwalić na
  // serwerze. Stąd "znikające" wiadomości, głównie głosówki (bo ich
  // wysyłka trwa najdłużej, więc najłatwiej było w tym czasie wysłać coś
  // jeszcze). Ta kolejka wymusza, żeby zapisy do tego samego klucza zawsze
  // wykonywały się jeden po drugim, nigdy równolegle.
  const mutationQueueRef = useRef({});
  function queueMutation(key, fn) {
    const prev = mutationQueueRef.current[key] || Promise.resolve();
    const next = prev.then(fn, fn); // uruchom fn niezależnie od tego, czy poprzedni zapis się powiódł
    mutationQueueRef.current[key] = next.catch(() => {}); // błąd nie może zablokować kolejki na stałe
    return next;
  }
  // Wspólna logika zmiany kolekcji (tablicy albo obiektu): pobierz świeże dane
  // -> zastosuj zmianę -> zapisz.
  //  * Nieudane pobranie przerywa operację (nigdy nie zaczynamy od pustej listy).
  //  * Odrzucony/nieudany zapis to BŁĄD. Wcześniej window.storage.set zwracał w
  //    takim wypadku po prostu null, a ten kod tego nie sprawdzał — wiadomość
  //    znikała po cichu, bez komunikatu, a ponawianie prób nigdy się nie
  //    uruchamiało (nie było wyjątku, który by je wywołał).
  //  * Jeśli ktoś inny zapisał tę samą kolekcję w międzyczasie (serwer
  //    odpowiada 412), pobieramy ją od nowa i stosujemy zmianę jeszcze raz —
  //    dzięki temu wiadomość jednego nie nadpisuje wiadomości drugiego.
  //    Zmiana (mutatorFn) musi więc być bezpieczna do ponownego wykonania.
  async function runMutation(key, fallback, setStateFn, mutatorFn) {
    return queueMutation(key, async () => {
      let lastFresh = null;
      for (let round = 0; round < 6; round++) {
        let fresh;
        let version;
        try {
          ({ data: fresh, version } = await fetchFreshWithVersion(key, fallback));
          lastFresh = fresh;
        } catch (e) {
          if (lastFresh) {
            try {
              setStateFn(lastFresh); // wcześniej pokazana zmiana nie zapisała się na serwerze — nie udawaj
            } catch (x) {
              /* ignore */
            }
          }
          throw new Error(`[krok: pobieranie] ${e && e.message ? e.message : e}`);
        }
        let next;
        try {
          next = mutatorFn(fresh);
        } catch (e) {
          throw new Error(`[krok: łączenie danych] ${e && e.message ? e.message : e}`);
        }
        try {
          setStateFn(next);
        } catch (e) {
          throw new Error(`[krok: zapis lokalny] ${e && e.message ? e.message : e}`);
        }
        const body = JSON.stringify(next);
        let conflict = false;
        let lastError = null;
        for (let attempt = 0; attempt <= 3; attempt++) {
          if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
          try {
            const saved = await window.storage.set(key, body, true, version ? { ifVersion: version } : undefined);
            if (saved) return next;
            lastError = new Error("serwer odrzucił zapis albo brak połączenia");
          } catch (e) {
            if (e && e.conflict) {
              conflict = true;
              break;
            }
            lastError = e;
          }
        }
        if (!conflict) {
          // Nie udało się zapisać — cofnij zmianę na ekranie, żeby appka nie
          // udawała, że wiadomość poszła, skoro serwer jej nie ma.
          try {
            setStateFn(fresh);
          } catch (e) {
            /* ignore */
          }
          throw new Error(`[krok: zapis na serwer, po 3 próbach] ${lastError && lastError.message ? lastError.message : lastError}`);
        }
        // Konflikt — krótka, losowa pauza, żeby dwie strony nie kolidowały w kółko.
        await new Promise((resolve) => setTimeout(resolve, 150 + Math.random() * 350));
      }
      if (lastFresh) {
        try {
          setStateFn(lastFresh);
        } catch (x) {
          /* ignore */
        }
      }
      throw new Error("[krok: zapis na serwer] ta kolekcja zmienia się zbyt często — spróbuj ponownie za chwilę");
    });
  }
  async function mutateArrayCollection(key, setStateFn, mutatorFn) {
    return runMutation(key, [], setStateFn, mutatorFn);
  }
  async function mutateObjectCollection(key, setStateFn, mutatorFn) {
    return runMutation(key, {}, setStateFn, mutatorFn);
  }

  function setPayoutDecisionSafe(recordId, decision) {
    return mutateObjectCollection(PAYOUT_DECISIONS_KEY, setPayoutDecisions, (fresh) => ({
      ...fresh,
      [recordId]: decision,
    }));
  }
  function updateCryptoPortfolio(patch) {
    return mutateObjectCollection(CRYPTO_PORTFOLIO_KEY, setCryptoPortfolio, (fresh) => ({ ...fresh, ...patch }));
  }
  function updateCryptoStrategyParams(patch) {
    return mutateObjectCollection(CRYPTO_STRATEGY_PARAMS_KEY, setCryptoStrategyParams, (fresh) => ({ ...fresh, ...patch }));
  }
  function updateCryptoIntradayPortfolio(patch) {
    return mutateObjectCollection(CRYPTO_INTRADAY_PORTFOLIO_KEY, setCryptoIntradayPortfolio, (fresh) => ({ ...fresh, ...patch }));
  }
  function updateCryptoIntradayStrategyParams(patch) {
    return mutateObjectCollection(CRYPTO_INTRADAY_STRATEGY_PARAMS_KEY, setCryptoIntradayStrategyParams, (fresh) => ({
      ...fresh,
      ...patch,
    }));
  }
  function deletePayoutDecision(recordId) {
    return mutateObjectCollection(PAYOUT_DECISIONS_KEY, setPayoutDecisions, (fresh) => {
      const next = { ...fresh };
      delete next[recordId];
      return next;
    });
  }
  function getNextMondaySundayPeriod(periodTo) {
    const endDate = new Date(periodTo);
    let start = new Date(endDate);
    start.setDate(start.getDate() + 1);
    // Zabezpieczenie: jeśli z jakiegoś powodu to nie wypada w poniedziałek,
    // appka dociąga do najbliższego kolejnego poniedziałku.
    const dow = start.getDay(); // 0=niedziela, 1=poniedziałek, ...
    if (dow !== 1) {
      const daysToAdd = dow === 0 ? 1 : 8 - dow;
      start.setDate(start.getDate() + daysToAdd);
    }
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { periodFrom: fmt(start), periodTo: fmt(end) };
  }
  // Zakłada kierowcy rozliczenie na kolejny tydzień (poniedziałek — niedziela)
  // po podanym periodTo, z góry wypełnione stałym ZUS-em i odliczeniem za
  // auto (jeśli je ustawiłeś w Stawkach), z "Do wypłaty" już przeliczonym
  // na żywo (nie sztywnym zerem) — i jeśli jest jakaś kwota do przeniesienia
  // (dodatnia albo ujemna, z rekompensatami wliczonymi), dolicza ją od razu
  // jako rekompensatę z opisową notatką. Używane po: oznaczeniu wypłaty jako
  // "Wypłacone", wyborze przez kierowcę "Przenieś na kolejny tydzień", oraz
  // bezpośredniej zmianie statusu na "Przeniesiono"/"Zaległość" w panelu
  // admina — we wszystkich trzech przypadkach potrzebny jest kolejny
  // tydzień, do którego można coś doliczyć.
  //
  // explicitCarry (opcjonalnie): { amount, note } — kiedy WIADOMO z góry
  // dokładnie ile i za co przenieść (np. bo właśnie zapisujemy to
  // rozliczenie w formularzu) — omija wtedy szukanie w payoutDecisions i
  // dolicza dokładnie tę podaną kwotę, dodatnią lub ujemną.
  async function ensureNextWeekRecordExists(driverName, periodTo, explicitCarry) {
    const nextPeriod = getNextMondaySundayPeriod(periodTo);
    const driverRec = (drivers || []).find((d) => sameName(d.name, driverName));
    const zusForNext = driverRec && driverRec.fixedZus !== null && driverRec.fixedZus !== undefined ? driverRec.fixedZus : 0;
    const carDeductionForNext =
      driverRec && driverRec.fixedCarDeduction !== null && driverRec.fixedCarDeduction !== undefined
        ? driverRec.fixedCarDeduction
        : 0;
    const alreadyExists = earnings.some(
      (r) => sameName(r.name, driverName) && r.periodFrom === nextPeriod.periodFrom && r.periodTo === nextPeriod.periodTo
    );
    if (!alreadyExists) {
      const newRecord = {
        name: driverName,
        periodFrom: nextPeriod.periodFrom,
        periodTo: nextPeriod.periodTo,
        status: "notstarted",
        appEarnings: 0,
        cashEarnings: 0,
        appSettlement: 0,
        zus: zusForNext,
        carDeduction: carDeductionForNext,
        compensations: 0,
        // "Do wypłaty" liczone od razu na żywo (utarg=0, ale ZUS i
        // Odliczenie za auto już wliczone) — więc od razu widać np. "-20 zł"
        // zamiast sztywnego zera, nawet zanim ktokolwiek zacznie cokolwiek
        // wpisywać w ten tydzień.
        payout: computeRecordPayout({ appEarnings: 0, cashEarnings: 0, zus: zusForNext, carDeduction: carDeductionForNext }),
        notatka: "",
        debtNote: "",
      };
      await mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) => upsertRecords(fresh, [newRecord]));
    }

    if (explicitCarry && explicitCarry.amount) {
      // Kwota (dodatnia lub ujemna) i notatka podane wprost przez wywołującego —
      // normalizujemy znak na typ "dodaj"/"odejmij" + zawsze dodatnią liczbę,
      // żeby wyświetlanie (+/-) było poprawne wszędzie w appce.
      const type = explicitCarry.amount >= 0 ? "add" : "subtract";
      await addAdminCompensation({
        id: `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        driverName,
        periodFrom: nextPeriod.periodFrom,
        periodTo: nextPeriod.periodTo,
        type,
        amount: Math.round(Math.abs(explicitCarry.amount) * 100) / 100,
        note: explicitCarry.note || "Kasa za poprzedni tydzień",
        status: "approved",
        requestedAt: Date.now(),
        decidedAt: Date.now(),
      });
      return;
    }

    // UWAGA: usunięty stąd stary, "zombie" mechanizm, który przy KAŻDYM
    // wywołaniu bez jawnie podanej kwoty sam szukał zawieszonych decyzji
    // "Przenieś na kolejny tydzień" u tego kierowcy — niezależnie, którego
    // okresu dotyczyły — i doliczał je tutaj. To on powodował duchy:
    // kwota, która już dawno została poprawnie przeniesiona gdzie indziej,
    // potrafiła "odpalić się" jeszcze raz przy zupełnie innym,
    // niepowiązanym tygodniu (np. przy zwykłym oznaczeniu innego tygodnia
    // jako "Wypłacone"). Każde miejsce w appce, które faktycznie przenosi
    // kwotę, robi to teraz jawnie, przez explicitCarry powyżej — więc jeśli
    // go tu nie ma, to znaczy, że naprawdę nie ma nic do przeniesienia.
  }
  async function markPayoutAsPaid(recordId, paid) {
    const decision = payoutDecisions[recordId];
    if (decision && decision.driverName) {
      await mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) =>
        fresh.map((r) =>
          sameName(r.name, decision.driverName) && r.periodFrom === decision.periodFrom && r.periodTo === decision.periodTo
            ? { ...r, status: paid ? "paid" : "inprogress" }
            : r
        )
      );
      if (paid) {
        fetch("/api/push-to-driver", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            driverName: decision.driverName,
            title: "PateX — Wypłacono",
            body: `Twoje wynagrodzenie za ${decision.periodFrom} – ${decision.periodTo} zostało wysłane.`,
          }),
        }).catch(() => {});
        await ensureNextWeekRecordExists(decision.driverName, decision.periodTo);
      }
    }
    return mutateObjectCollection(PAYOUT_DECISIONS_KEY, setPayoutDecisions, (fresh) => ({
      ...fresh,
      [recordId]: { ...fresh[recordId], paid },
    }));
  }
  function addCompensationRequestSafe(reqObj) {
    return mutateArrayCollection(COMPENSATION_REQUESTS_KEY, setCompensationRequests, (fresh) => (fresh.some((x) => x && x.id === reqObj.id) ? fresh : [...fresh, reqObj]));
  }
  function updateCompensationRequestSafe(id, updates) {
    return mutateArrayCollection(COMPENSATION_REQUESTS_KEY, setCompensationRequests, (fresh) =>
      fresh.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
  }
  function addAdminCompensation(reqObj) {
    return mutateArrayCollection(COMPENSATION_REQUESTS_KEY, setCompensationRequests, (fresh) => [...fresh, reqObj]);
  }
  function deleteCompensationRequest(id) {
    return mutateArrayCollection(COMPENSATION_REQUESTS_KEY, setCompensationRequests, (fresh) =>
      fresh.filter((r) => r.id !== id)
    );
  }
  // Zatwierdza wniosek kierowcy o spłatę zadłużenia. Jeśli wybrał "Odejmij
  // od najbliższego rozliczenia" — dolicza to jako zwykłą rekompensatę typu
  // "odejmij" do jego NAJBLIŻSZEGO tygodnia ze statusem "Nie rozpoczęto"
  // (dokładnie ta sama zasada co przy zwykłym dodawaniu rekompensat — nie
  // da się tego zrobić, jeśli żaden taki tydzień już nie istnieje). Jeśli
  // wybrał BLIK — po prostu oznacza wniosek jako zatwierdzony (Ty sam
  // sprawdzasz, czy przelew faktycznie doszedł, zanim klikniesz).
  async function approveDebtRepayment(id) {
    const req = debtRepayments.find((r) => r.id === id);
    if (!req) return;
    if (req.method === "deduct_next_week") {
      const target = (earnings || [])
        .filter((r) => sameName(r.name, req.driverName) && r.status === "notstarted")
        .sort(byPeriodDesc)
        .pop(); // najstarszy z "Nie rozpoczęto" — najbliższy nadchodzący tydzień
      if (!target) {
        alert(`Brak tygodnia ze statusem "Nie rozpoczęto" dla ${req.driverName} — nie można dodać potrącenia. Poczekaj na nowy tydzień albo wybierz spłatę przez BLIK.`);
        return;
      }
      await addAdminCompensation({
        id: `comp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        driverName: req.driverName,
        periodFrom: target.periodFrom,
        periodTo: target.periodTo,
        type: "subtract",
        amount: req.amount,
        note: `Spłata zadłużenia: ${req.note}`,
        status: "approved",
        requestedAt: Date.now(),
        decidedAt: Date.now(),
      });
    }
    // WAŻNE: samo zatwierdzenie ZGŁOSZENIA spłaty (poniżej) nigdy nie
    // zmniejszało realnie zadłużenia widocznego na liście "driverDebts" —
    // to dwie osobne kolekcje danych, i nic wcześniej ich ze sobą nie
    // łączyło. Stąd błąd: admin zatwierdza spłatę, a kierowcy dalej
    // wisi pełna, niezmieniona kwota długu, dopóki admin nie poprawi jej
    // ręcznie. Zgłoszenie spłaty nie wskazuje też, KTÓREGO konkretnie
    // długu dotyczy (kierowca może mieć kilka naraz) — więc zamiast
    // zgadywać, który zmniejszyć, dokładamy osobny wpis z UJEMNĄ kwotą,
    // który działa jak "kredyt" obniżający SUMĘ wszystkich długów razem.
    // Bezpieczne też przy spłacie CZĘŚCIOWEJ (np. 200 zł z 2200 zł) — nie
    // trzeba wybierać, na poczet którego konkretnego długu ta wpłata idzie.
    await driverDebtsCrud.add({
      driverName: req.driverName,
      amount: -req.amount,
      note: `Spłata (${req.method === "blik" ? "BLIK" : "potrącenie z wypłaty"})${req.note && req.note !== "Spłata zadłużenia" ? `: ${req.note}` : ""}`,
      createdAt: Date.now(),
      status: "active",
    });
    await debtRepaymentsCrud.update(id, { status: "approved", decidedAt: Date.now() });
  }
  function rejectDebtRepayment(id) {
    return debtRepaymentsCrud.update(id, { status: "rejected", decidedAt: Date.now() });
  }
  function deleteDebtRepayment(id) {
    return debtRepaymentsCrud.remove(id);
  }
  function addNews(text) {
    const item = { id: `news_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, text, createdAt: Date.now() };
    return mutateArrayCollection(NEWS_KEY, setNews, (fresh) => [item, ...fresh]);
  }
  function deleteNews(id) {
    return mutateArrayCollection(NEWS_KEY, setNews, (fresh) => fresh.filter((n) => n.id !== id));
  }
  function addTask(title, dateFrom, dateTo, priority, points, milestone) {
    const item = {
      id: `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title,
      dateFrom: dateFrom || "",
      dateTo: dateTo || "",
      completions: {},
      status: "active", // "active" (W trakcie) albo "completed" (Zakończone) — status CAŁEGO zadania, osobno od tego, czy poszczególni kierowcy je ukończyli
      priority: !!priority,
      points: points != null ? points : null, // null = domyślna liczba punktów (TASK_COMPLETE_POINTS)
      milestone: milestone ? true : null, // zadanie z ticketem do Koła fortuny (liczba ticketów w polu "tickets")
      tickets: milestone ? Math.max(1, Math.min(20, parseInt(milestone, 10) || 1)) : null,
      createdAt: Date.now(),
    };
    fetch("/api/push-broadcast", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: priority ? "🚨 PateX — PILNE zadanie" : "PateX — nowe zadanie 🎯",
        body: title,
      }),
    }).catch(() => {});
    return mutateArrayCollection(TASKS_KEY, setTasks, (fresh) => [item, ...fresh]);
  }
  function setTaskStatus(taskId, status) {
    return mutateArrayCollection(TASKS_KEY, setTasks, (fresh) => fresh.map((t) => (t.id === taskId ? { ...t, status } : t)));
  }
  function setTaskCompletion(taskId, driverName, status) {
    if (status === "completed") {
      const task = tasks.find((t) => t.id === taskId);
      const taskPoints = task && task.points != null ? task.points : TASK_COMPLETE_POINTS;
      // Ta sama logika ponawiania co przy powiadomieniach czatu — chwilowe
      // problemy z siecią nie powinny cicho "gubić" gratulacji dla
      // kierowcy, który akurat wykonał zadanie.
      const sendCompletionPush = async () => {
        const body = JSON.stringify({
          driverName,
          title: ticketsOfTask(task) ? "🎟️ Ticket zdobyty!" : "Gratulacje! 🎉",
          body:
            ticketsOfTask(task)
              ? `Zadanie „${task.title}" wykonane — dostajesz +${ticketsOfTask(task)} 🎟 do Koła fortuny! (+${taskPoints} pkt do rankingu)`
              : task
                ? `Wykonałeś zadanie: „${task.title}" (+${taskPoints} pkt do rankingu)`
                : `Wykonałeś zadanie! (+${taskPoints} pkt do rankingu)`,
        });
        for (let attempt = 0; attempt < 3; attempt++) {
          if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 700 * attempt));
          try {
            const res = await fetch("/api/push-to-driver", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body,
            });
            if (res.ok) return;
          } catch (e) {
            /* spróbuj ponownie */
          }
        }
      };
      sendCompletionPush();
    }
    return mutateArrayCollection(TASKS_KEY, setTasks, (fresh) =>
      fresh.map((t) => (t.id === taskId ? { ...t, completions: { ...t.completions, [driverName]: status } } : t))
    );
  }
  function deleteTask(id) {
    return mutateArrayCollection(TASKS_KEY, setTasks, (fresh) => fresh.filter((t) => t.id !== id));
  }
  // Pełna edycja istniejącego zadania — tytuł, daty, priorytet, punkty.
  function updateTask(taskId, updates) {
    return mutateArrayCollection(TASKS_KEY, setTasks, (fresh) => fresh.map((t) => (t.id === taskId ? { ...t, ...updates } : t)));
  }
  function addPointAdjustment(driverName, points, note) {
    const item = {
      id: `padj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      driverName,
      points,
      note,
      createdAt: Date.now(),
    };
    return mutateArrayCollection(POINT_ADJUSTMENTS_KEY, setPointAdjustments, (fresh) => [...fresh, item]);
  }
  // ---- Dopisywanie wiadomości "po jednej" ----
  // Zamiast pobierać całą rozmowę, dopisywać do niej wiadomość i odsyłać CAŁOŚĆ
  // (setki kilobajtów z telefonu, i wiadomości dwóch osób potrafiły się
  // nadpisać), appka wysyła do serwera samą nową wiadomość (/api/chat-op), a
  // serwer dokłada ją do pliku. Ta operacja może tylko DODAWAĆ. Wiadomość ma
  // unikalne id, więc powtórzenie (np. po zerwaniu połączenia) niczego nie
  // dubluje. Gdyby serwer nie znał tej operacji (stara wersja), appka wraca do
  // dotychczasowego sposobu (zapis całej kolekcji).
  async function postChatOp(payload) {
    let lastError = null;
    for (let attempt = 0; attempt <= 3; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const res = await fetch("/api/chat-op", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        clearTimeout(timer);
        if (res.ok) return "ok";
        if (res.status === 404 || res.status === 405) return "unsupported";
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j && j.error) detail = j.error;
        } catch (e) {
          /* brak treści błędu */
        }
        if (res.status < 500 && res.status !== 408 && res.status !== 429) {
          const fatal = new Error(detail);
          fatal.fatal = true;
          throw fatal;
        }
        lastError = new Error(detail);
      } catch (e) {
        clearTimeout(timer);
        if (e && e.fatal) throw e;
        lastError = e;
      }
    }
    throw new Error(`[krok: zapis na serwer, po 3 próbach] ${lastError && lastError.message ? lastError.message : lastError}`);
  }
  // Pokaż zmianę na ekranie od razu, wyślij ją na serwer jako operację, a przy
  // błędzie cofnij (quiet=true: bez cofania i bez błędu — dla "przeczytane").
  async function runChatOp(key, setStateFn, payload, applyLocal, undoLocal, fallbackMutator, quiet) {
    const outcome = await queueMutation(key, async () => {
      setStateFn(applyLocal);
      try {
        return await postChatOp(payload);
      } catch (e) {
        if (quiet) return "failed";
        if (undoLocal) setStateFn(undoLocal);
        throw e;
      }
    });
    if (outcome === "unsupported") return runMutation(key, [], setStateFn, fallbackMutator);
    return true;
  }
  function appendGroupMessage(msg) {
    const addLocal = (list) => (list.some((m) => m.id === msg.id) ? list : [...list, msg]);
    return runChatOp(
      GROUP_CHAT_KEY,
      setGroupChatMessages,
      { collection: "group-chat-messages", op: "append", message: msg },
      addLocal,
      (list) => list.filter((m) => m.id !== msg.id),
      addLocal
    );
  }
  function appendChatMessage(threadId, msg, sender, priority) {
    const patch = {
      unreadForDriver: sender === "admin",
      unreadForAdmin: sender === "driver",
      ...(sender === "driver" && priority ? { hasPriorityUnread: true } : {}),
    };
    const addLocal = (threads) =>
      threads.map((t) =>
        t.id === threadId
          ? { ...t, messages: t.messages.some((m) => m.id === msg.id) ? t.messages : [...t.messages, msg], ...patch }
          : t
      );
    const undoLocal = (threads) =>
      threads.map((t) => (t.id === threadId ? { ...t, messages: t.messages.filter((m) => m.id !== msg.id) } : t));
    return runChatOp(
      CHAT_THREADS_KEY,
      setChatThreads,
      { collection: "chat-threads", op: "append", threadId, message: msg, patch },
      addLocal,
      undoLocal,
      addLocal
    );
  }
  function sendGroupMessage(driverName, text, attachment, replyTo) {
    if (driverName !== "Administrator") {
      const driverRec = (drivers || []).find((d) => sameName(d.name, driverName));
      const muted =
        driverRec &&
        driverRec.groupMutedUntil &&
        (driverRec.groupMutedUntil === "forever" || driverRec.groupMutedUntil > Date.now());
      if (muted) return Promise.resolve();
    }
    const msg = {
      id: `gmsg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      driverName,
      text,
      timestamp: Date.now(),
      seenBy: [],
      ...(attachment ? { attachment } : {}),
      ...(replyTo ? { replyTo: { driverName: replyTo.driverName, text: replyTo.text || "" } } : {}),
    };
    return appendGroupMessage(msg).then((result) => {
      const notifyText = text || (attachment ? `📎 ${attachment.filename}` : "");
      if (driverName !== "Administrator") {
        sendNtfyNotification("PateX — Grupa", `${driverName}: ${notifyText}`);
      }
      fetch("/api/push-broadcast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: "PateX — Grupa", body: `${driverName}: ${notifyText}`, excludeDriverName: driverName }),
      }).catch(() => {
        /* best effort — najważniejsze, że wiadomość już jest zapisana w appce */
      });
      return result;
    });
  }
  // "Zawołaj wszystkich": wiadomość na grupie + WAŻNE powiadomienie push do wszystkich kierowców.
  // Najpierw usługa z wysokim priorytetem (/call-api/alert); jeśli nie działa — zwykłe powiadomienie jak przy "Ważnej wiadomości".
  async function callAllGroup(text) {
    const clean = String(text || "").trim() || "Wejdźcie na czat grupowy!";
    appendGroupMessage({
      id: `gmsg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      driverName: "Administrator",
      text: `📣 SZEF WOŁA WSZYSTKICH: ${clean}`,
      timestamp: Date.now(),
      seenBy: [],
      important: true,
      callAll: true,
    });
    try {
      const res = await fetch("/call-api/alert", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: "📣 Szef woła wszystkich!", body: clean, tab: "grupa" }),
      });
      const j = await res.json().catch(() => null);
      if (res.ok && j && j.ok) return { sent: j.sent, total: j.total, drivers: j.drivers, via: "alert" };
      if (res.status === 429) return { error: "Poczekaj chwilę — zawołanie było wysłane przed momentem." };
    } catch (e) {
      /* spróbujemy zwykłego powiadomienia */
    }
    try {
      await fetch("/api/push-broadcast", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: "📣 Szef woła wszystkich!", body: clean, excludeDriverName: "Administrator" }),
      });
      return { via: "broadcast" };
    } catch (e) {
      return { via: "none" };
    }
  }
  function sendImportantGroupMessage(text) {
    const msg = {
      id: `gmsg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      driverName: "Administrator",
      text: `⚠️ WAŻNA WIADOMOŚĆ: ${text}`,
      timestamp: Date.now(),
      seenBy: [],
      important: true,
    };
    appendGroupMessage(msg);
    fetch("/api/push-broadcast", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: "⚠️ Ważna wiadomość od Szefa",
        body: text,
        excludeDriverName: "Administrator",
      }),
    }).catch(() => {
      /* best effort — wiadomość i tak jest już zapisana w appce */
    });
  }
  function deleteGroupMessage(id) {
    return mutateArrayCollection(GROUP_CHAT_KEY, setGroupChatMessages, (fresh) => fresh.filter((m) => m.id !== id));
  }
  function editGroupMessage(id, newText) {
    return mutateArrayCollection(GROUP_CHAT_KEY, setGroupChatMessages, (fresh) =>
      fresh.map((m) => (m.id === id ? { ...m, text: newText, editedAt: Date.now() } : m))
    );
  }
  function togglePinGroupMessage(id) {
    return mutateArrayCollection(GROUP_CHAT_KEY, setGroupChatMessages, (fresh) =>
      fresh.map((m) => (m.id === id ? { ...m, pinned: !m.pinned } : { ...m, pinned: false }))
    );
  }
  function toggleGroupMessageReaction(messageId, emoji, userName) {
    return mutateArrayCollection(GROUP_CHAT_KEY, setGroupChatMessages, (fresh) =>
      fresh.map((m) => {
        if (m.id !== messageId) return m;
        return withReaction(m, emoji, userName);
      })
    );
  }
  // Reakcje w czacie z Szefem i w rozmowach prywatnych: natychmiast na ekranie (optymistycznie), potem zapis na serwerze.
  function toggleThreadMessageReaction(threadId, messageId, emoji, userName) {
    const apply = (list) =>
      (list || []).map((t) =>
        t.id !== threadId ? t : { ...t, messages: (t.messages || []).map((m) => (m.id === messageId ? withReaction(m, emoji, userName) : m)) }
      );
    setChatThreads((prev) => apply(prev));
    return mutateArrayCollection(CHAT_THREADS_KEY, setChatThreads, apply).catch(() => {});
  }
  function toggleDmMessageReaction(threadId, messageId, emoji, userName) {
    const apply = (list) =>
      (list || []).map((t) =>
        t.id !== threadId ? t : { ...t, messages: (t.messages || []).map((m) => (m.id === messageId ? withReaction(m, emoji, userName) : m)) }
      );
    setDmThreads((prev) => apply(prev));
    return mutateArrayCollection(DM_THREADS_KEY, setDmThreads, apply).catch(() => {});
  }
  function markGroupMessagesSeen(viewerName) {
    // Nic do oznaczenia = żadnego zapisu (wcześniej każde otwarcie zakładki
    // odsyłało na serwer całą rozmowę grupową).
    const needsMarking = (groupChatMessages || []).some(
      (m) => m.driverName !== viewerName && !(m.seenBy || []).some((s) => s.name === viewerName)
    );
    if (!needsMarking) return Promise.resolve(true);
    const seenAt = Date.now();
    const markLocal = (list) =>
      list.map((m) => {
        if (m.driverName === viewerName) return m;
        if ((m.seenBy || []).some((s) => s.name === viewerName)) return m;
        return { ...m, seenBy: [...(m.seenBy || []), { name: viewerName, seenAt }] };
      });
    return runChatOp(
      GROUP_CHAT_KEY,
      setGroupChatMessages,
      { collection: "group-chat-messages", op: "seen", viewer: viewerName, seenAt },
      markLocal,
      null,
      markLocal,
      true
    );
  }
  function createChatThread(threadObj) {
    const addLocal = (threads) => (threads.some((t) => t.id === threadObj.id) ? threads : [...threads, threadObj]);
    const initialMessages = Array.isArray(threadObj.messages) ? threadObj.messages : [];
    if (initialMessages.length !== 1) return mutateArrayCollection(CHAT_THREADS_KEY, setChatThreads, addLocal);
    const { messages: _initialMessages, ...threadInit } = threadObj;
    return runChatOp(
      CHAT_THREADS_KEY,
      setChatThreads,
      { collection: "chat-threads", op: "append", threadId: threadObj.id, message: initialMessages[0], threadInit },
      addLocal,
      (threads) => threads.filter((t) => t.id !== threadObj.id),
      addLocal
    );
  }
  function sendChatMessage(threadId, sender, text, attachment, priority, replyTo) {
    const msg = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      sender,
      text,
      timestamp: Date.now(),
      ...(attachment ? { attachment } : {}),
      ...(priority ? { priority: true } : {}),
      ...(replyTo ? { replyTo: { sender: replyTo.sender, text: replyTo.text || "" } } : {}),
    };
    return appendChatMessage(threadId, msg, sender, priority).then((result) => {
      // Powiadomienie push leci DOPIERO PO potwierdzonym zapisie wiadomości —
      // inaczej mogłoby dotrzeć nawet wtedy, gdy sama wiadomość nigdy
      // naprawdę nie trafiła na serwer (dokładnie ten mylący objaw: "push
      // przyszedł, a wiadomości nie ma").
      if (sender === "admin") {
        const thread = chatThreads.find((t) => t.id === threadId);
        if (thread) {
          fetch("/api/push-to-driver", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              driverName: thread.driverName,
              title: "PateX — nowa wiadomość",
              body: text || (attachment ? `📎 ${attachment.filename}` : ""),
            }),
          }).catch(() => {
            /* wiadomość i tak zostanie zapisana w appce poniżej */
          });
        }
      } else if (priority) {
        // Wiadomość o wysokim priorytecie od kierowcy — powiadomienie do
        // admina z wyraźnie pilnym tytułem, żeby się wyróżniało na tle innych.
        sendNtfyNotification("🚨 PILNA wiadomość", text || (attachment ? `📎 ${attachment.filename}` : ""));
      }
      return result;
    });
  }
  function markThreadRead(threadId, side) {
    const markLocal = (threads) =>
      threads.map((t) =>
        t.id === threadId
          ? {
              ...t,
              [side === "driver" ? "unreadForDriver" : "unreadForAdmin"]: false,
              ...(side === "admin" ? { hasPriorityUnread: false } : {}),
            }
          : t
      );
    return runChatOp(
      CHAT_THREADS_KEY,
      setChatThreads,
      { collection: "chat-threads", op: "markRead", threadId, side },
      markLocal,
      null,
      markLocal,
      true
    );
  }
  // Prywatne wiadomości MIĘDZY dwoma kierowcami (nie z adminem). ID wątku
  // jest DETERMINISTYCZNE — te same dwie osoby zawsze trafiają do tego
  // samego wątku, niezależnie od tego, kto pierwszy napisał.
  function dmThreadIdFor(nameA, nameB) {
    return `dm_${[nameA, nameB].sort().join("__")}`;
  }
  async function sendDmMessage(fromName, toName, text, attachment, replyTo) {
    const threadId = dmThreadIdFor(fromName, toName);
    const msg = {
      id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      sender: fromName,
      text,
      timestamp: Date.now(),
      ...(attachment ? { attachment } : {}),
      ...(replyTo ? { replyTo: { sender: replyTo.sender, text: replyTo.text || "" } } : {}),
    };
    const dmMutator = (fresh) => {
      const existing = fresh.find((t) => t.id === threadId);
      if (existing) {
        return fresh.map((t) =>
          t.id === threadId
            ? { ...t, messages: t.messages.some((m) => m.id === msg.id) ? t.messages : [...t.messages, msg], unreadFor: [...new Set([...(t.unreadFor || []), toName])] }
            : t
        );
      }
      return [
        ...fresh,
        { id: threadId, participants: [fromName, toName], messages: [msg], unreadFor: [toName], createdAt: Date.now() },
      ];
    };
    const result = await runChatOp(
      DM_THREADS_KEY,
      setDmThreads,
      {
        collection: "dm-threads",
        op: "append",
        threadId,
        message: msg,
        unreadForAdd: [toName],
        threadInit: { id: threadId, participants: [fromName, toName], createdAt: Date.now() },
      },
      dmMutator,
      (threads) =>
        threads
          .map((t) => (t.id === threadId ? { ...t, messages: (t.messages || []).filter((m) => m.id !== msg.id) } : t))
          .filter((t) => t.id !== threadId || (t.messages || []).length > 0),
      dmMutator
    );
    // Powiadomienie push DOPIERO PO potwierdzonym zapisie — ta sama zasada
    // co w czacie z adminem, żeby push nigdy nie "wyprzedził" zapisu.
    fetch("/api/push-to-driver", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        driverName: toName,
        title: `Wiadomość od ${fromName}`,
        body: text || (attachment ? `📎 ${attachment.filename}` : ""),
      }),
    }).catch(() => {
      /* wiadomość i tak jest już zapisana w appce */
    });
    return result;
  }
  function markDmThreadRead(threadId, readerName) {
    const markLocal = (threads) =>
      threads.map((t) => (t.id === threadId ? { ...t, unreadFor: (t.unreadFor || []).filter((n) => n !== readerName) } : t));
    return runChatOp(
      DM_THREADS_KEY,
      setDmThreads,
      { collection: "dm-threads", op: "read", threadId, reader: readerName },
      markLocal,
      null,
      markLocal,
      true
    );
  }
  function deleteThreadMessage(threadId, messageId) {
    return mutateArrayCollection(CHAT_THREADS_KEY, setChatThreads, (fresh) =>
      fresh.map((t) => (t.id === threadId ? { ...t, messages: t.messages.filter((m) => m.id !== messageId) } : t))
    );
  }
  function resolveThread(threadId, resolved) {
    return mutateArrayCollection(CHAT_THREADS_KEY, setChatThreads, (fresh) =>
      fresh.map((t) => (t.id === threadId ? { ...t, resolved } : t))
    );
  }
  function completePinLogin(match) {
    setDriver({ pin: match.pin, name: match.name });
    setLoginError("");
    setView("dashboard");
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({ pin: match.pin }));
    } catch (e) {
      /* localStorage unavailable — session just won't survive a refresh */
    }
    subscribeAndReportPushStatus(match.name);
  }
  function handlePinSubmit() {
    if (!pin) return;
    const match = drivers.find((d) => d.pin === pin);
    if (match) {
      completePinLogin(match);
    } else if (window.__patexStartup && window.__patexStartup.usedCache) {
      // Lista kierowców mogła pochodzić z pamięci telefonu (sprzed dodania tego kierowcy) —
      // zanim pokażemy błąd, sprawdź świeżą listę z serwera.
      const typedPin = pin;
      setLoginError("Sprawdzam…");
      (async () => {
        let fresh = null;
        try {
          const res = await window.storage.get(DRIVERS_KEY, true, { strict: true });
          fresh = res && res.value ? JSON.parse(res.value) : null;
        } catch (e) {
          fresh = null;
        }
        const found = fresh && fresh.find((d) => d.pin === typedPin);
        if (found) {
          setDrivers(fresh);
          completePinLogin(found);
        } else {
          setLoginError(
            fresh ? "Nie znaleziono kierowcy o tym kodzie PIN." : "Nie udało się sprawdzić kodu PIN — sprawdź zasięg i spróbuj ponownie."
          );
          setPin("");
        }
      })();
    } else {
      setLoginError("Nie znaleziono kierowcy o tym kodzie PIN.");
      setPin("");
    }
  }

  function goHome() {
    setView("home");
    setPin("");
    setLoginError("");
    setDriver(null);
    try {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem("patex_admin_session");
    } catch (e) {
      /* ignore */
    }
  }

  const polledRef = useRef({});
  const [chatsLoaded, setChatsLoaded] = useState(false); // czat dociąga się PO starcie (patrz wyżej)
  async function handleReload() {
    try {
      const before = earnings.length + drivers.length + globalBonuses.length;
      const {
        drivers: freshDrivers,
        earnings: freshEarnings,
        globalBonuses: freshBonuses,
        avatars: freshAvatars,
        payoutDecisions: freshPayoutDecisions,
        compensationRequests: freshCompensationRequests,
        chatThreads: freshChatThreads,
        groupChatMessages: freshGroupChatMessages,
        dmThreads: freshDmThreads,
        news: freshNews,
        tasks: freshTasks,
        pointAdjustments: freshPointAdjustments,
      } = await reloadFromStorage(true); // czat ładuje się osobno (loadChats) — jego problemy nie blokują reszty
      setDrivers(freshDrivers);
      setEarnings(freshEarnings);
      setGlobalBonuses(freshBonuses);
      setAvatars(freshAvatars);
      setPayoutDecisions(freshPayoutDecisions);
      setCompensationRequests(freshCompensationRequests);
      // WAŻNE: NIE zastępujemy stanu czatu bezwarunkowo tym, co przyszło z
      // serwera. To odpytywanie leci co 4 sekundy — jeśli ktoś wyśle
      // wiadomość dokładnie w momencie, gdy poprzedni odczyt z serwera
      // jeszcze "nie widział" tej wiadomości (bo zdążył ją odczytać tuż
      // PRZED zapisem), taka nadpisująca odpowiedź kasowałaby świeżo
      // wysłaną wiadomość z ekranu — mimo że naprawdę trafiła na serwer
      // (stąd błąd: powiadomienie push przychodzi, ale wiadomość "znika").
      // Scalanie zamiast zastępowania: bierzemy wszystko ze świeżych
      // danych, i DOKŁADAMY (nie kasujemy) to, co jest tylko lokalnie —
      // appka nigdy nie "zapomina" wiadomości, którą już pokazała.
      // Jeśli serwer zwrócił dokładnie to samo co poprzednio — nie ruszaj stanu
      // (unikamy zbędnego przerysowania całej rozmowy co 4 sekundy).
      if (loadChatsRef.current) loadChatsRef.current(); // czat osobno, bez czekania
      setNews(freshNews);
      setTasks(freshTasks);
      setPointAdjustments(freshPointAdjustments);
      const after = freshEarnings.length + freshDrivers.length + freshBonuses.length;
      return { ok: true, count: Math.max(0, after - before) };
    } catch (e) {
      return { ok: false, count: 0 };
    }
  }

  // Czat ładuje się OSOBNO od reszty danych. Kierowca pobiera tylko swoje rozmowy (lekki odczyt); pełny odczyt (admin albo
  // gdy lekki nie działa) idzie po kolei, z cierpliwymi limitami czasu, żeby nie dławić słabego łącza.
  const chatBusyRef = useRef(false);
  const chatSliceTextRef = useRef(null);
  const chatNetOkRef = useRef(false);
  const chatSliceDeadUntilRef = useRef(0);
  const [chatsError, setChatsError] = useState(false);
  async function loadChats() {
    if (chatBusyRef.current) return;
    chatBusyRef.current = true;
    try {
      const drv = driverRef.current;
      if (!drv && viewRef.current !== "admin") return; // ekran logowania — czat nikomu jeszcze nie jest potrzebny (nie dławimy łącza)
      const asDriver = !!drv && viewRef.current === "dashboard";
      if (asDriver && Date.now() > chatSliceDeadUntilRef.current) {
        // Pierwszy odczyt: ostatnie 100 wiadomości; kolejne (co kilka sekund) tylko ostatnie 40 (30 na wolnym łączu) — nowe wiadomości
        // i tak się w nich mieszczą, a transfer jest ~2,5x mniejszy.
        const slowNow = window.__patexSlowLink && window.__patexSlowLink();
        // Ostatnio pobrana rozmowa leży w pamięci telefonu: przy pierwszym wejściu pokazujemy ją OD RAZU (zanim cokolwiek dojdzie z sieci),
        // a sieć tylko ją dopełnia. Dzięki temu czat nie jest pusty/„Ładuję…" nawet gdy łącze komórkowe się zacina.
        if (chatSliceTextRef.current === null) {
          try {
            const cached = localStorage.getItem("patex-chat-" + drv.name);
            const cd = cached ? JSON.parse(cached) : null;
            if (cd && cd.ok && Array.isArray(cd.chat) && Array.isArray(cd.group) && Array.isArray(cd.dm)) {
              chatSliceTextRef.current = cached;
              setChatThreads((prev) => mergeChatThreads(prev, cd.chat));
              setGroupChatMessages((prev) => mergeMessagesById(prev, cd.group));
              setDmThreads((prev) => mergeChatThreads(prev, cd.dm));
              setChatsLoaded(true);
            }
          } catch (e) {
            /* pamięć podręczna jest tylko dodatkiem */
          }
        }
        const conn = typeof navigator !== "undefined" ? navigator.connection : null;
        const weakNow = !!conn && ((conn.downlink > 0 && conn.downlink < 3) || /^(slow-2g|2g|3g)$/.test(conn.effectiveType || ""));
        // pierwszy odczyt z sieci: 100 wiadomości (40 przy słabym łączu, 30 w trybie wolnego łącza); kolejne: 40
        const sliceN = slowNow ? 30 : weakNow ? 40 : !chatNetOkRef.current ? 100 : 40;
        const slice = await fetchChatSlice(drv.name, sliceN);
        if (slice === "net") {
          if (chatSliceTextRef.current === null) setChatsError(true);
          return; // spróbuje ponownie przy następnym tyknięciu — bez ciężkiego odczytu awaryjnego
        }
        if (slice) {
          chatNetOkRef.current = true;
          if (slice.text !== chatSliceTextRef.current) {
            chatSliceTextRef.current = slice.text;
            try {
              localStorage.setItem("patex-chat-" + drv.name, slice.text);
            } catch (e) {
              /* brak miejsca — trudno */
            }
            setChatThreads((prev) => mergeChatThreads(prev, slice.data.chat));
            setGroupChatMessages((prev) => mergeMessagesById(prev, slice.data.group));
            setDmThreads((prev) => mergeChatThreads(prev, slice.data.dm));
          }
          setChatsLoaded(true);
          setChatsError(false);
          return;
        }
        chatSliceDeadUntilRef.current = Date.now() + 60000; // lekki odczyt nie działa — przez minutę pełny
      }
      const get = (key) => window.storage.get(key, true, { strict: true, patient: true });
      const chatRes = await get(CHAT_THREADS_KEY);
      const groupRes = await get(GROUP_CHAT_KEY);
      const dmRes = await get(DM_THREADS_KEY);
      const freshChat = chatRes && chatRes.value ? parseCached(CHAT_THREADS_KEY, chatRes.value) : [];
      const freshGroup = groupRes && groupRes.value ? parseCached(GROUP_CHAT_KEY, groupRes.value) : [];
      const freshDm = dmRes && dmRes.value ? parseCached(DM_THREADS_KEY, dmRes.value) : [];
      const polled = polledRef.current;
      if (polled.chat !== freshChat) setChatThreads((prev) => mergeChatThreads(prev, freshChat));
      if (polled.group !== freshGroup) setGroupChatMessages((prev) => mergeMessagesById(prev, freshGroup));
      if (polled.dm !== freshDm) setDmThreads((prev) => mergeChatThreads(prev, freshDm));
      polledRef.current = { chat: freshChat, group: freshGroup, dm: freshDm };
      setChatsLoaded(true);
      setChatsError(false);
    } catch (e) {
      setChatsError(true);
    } finally {
      chatBusyRef.current = false;
    }
  }
  const loadChatsRef = useRef(loadChats);
  loadChatsRef.current = loadChats;

  const handleReloadRef = useRef(handleReload);
  handleReloadRef.current = handleReload;

  const serverVersionRef = useRef(null);

  const driverRef = useRef(driver);
  driverRef.current = driver;

  useEffect(() => {
    async function checkVersionAndRefreshData() {
      try {
        const res = await fetch("/api/version");
        const data = await res.json();
        if (serverVersionRef.current === null) {
          serverVersionRef.current = data.startedAt;
        } else if (data.startedAt !== serverVersionRef.current) {
          // Serwer został zrestartowany od czasu wczytania strony (nowa wersja
          // appki) — przeładuj całą stronę, żeby pobrać świeży kod.
          window.location.reload();
          return;
        }
      } catch (e) {
        /* jeśli nie uda się sprawdzić wersji, po prostu pomiń ten krok tym razem */
      }
      handleReloadRef.current();
      // Ponów próbę powiadomień push — przyda się kierowcy, który wcześniej
      // odmówił/nie zadziałało, a w międzyczasie coś poprawił w ustawieniach
      // telefonu. Bezpieczne do wywoływania wielokrotnie (nie duplikuje).
      if (driverRef.current) {
        subscribeAndReportPushStatus(driverRef.current.name, false);
      }
    }

    const interval = setInterval(checkVersionAndRefreshData, 5 * 60 * 1000); // co 5 minut
    return () => clearInterval(interval);
  }, []);

  const viewRef = useRef(view);
  viewRef.current = view;
  const adminFormOpenRef = useRef(false);
  const reloadBusyRef = useRef(false);

  useEffect(() => {
    let tickN = 0;
    async function refreshEverythingFast() {
      // Lekkie, częste odpytywanie WSZYSTKICH danych — dzięki temu zmiany
      // wprowadzone w panelu admina (zadania, uprawnienia, newsy, status
      // wypłat itd.) pojawiają się u kierowcy w kilka sekund, bez potrzeby
      // zamykania i otwierania appki na nowo.
      if (viewRef.current !== "dashboard" && viewRef.current !== "admin") return;
      if (viewRef.current === "admin" && adminFormOpenRef.current) return;
      if (isAnyRecordingActive) return;
      // Nie zaczynaj kolejnego odświeżania, dopóki poprzednie trwa — przy słabym
      // zasięgu odświeżania się nawarstwiały i starsze odpowiedzi wyprzedzały
      // nowsze (oraz zapychały limit połączeń przeglądarki).
      if (typeof document !== "undefined" && document.hidden) return; // appka w tle — nie pobieramy nic
      if (reloadBusyRef.current) return;
      if (window.__patexSlowLink && window.__patexSlowLink() && ++slowTick % 3 !== 0) return; // wolne łącze — co ~12 s zamiast co 4 s
      reloadBusyRef.current = true;
      try {
        // Pełne dane co ~12 s (co 3. tyknięcie), sam czat co ~4 s — mniej pracy procesora i transferu na słabym telefonie.
        tickN++;
        if (tickN % 3 === 1 || !loadChatsRef.current) await handleReloadRef.current();
      } finally {
        reloadBusyRef.current = false;
      }
    }

    let slowTick = 0;
    const chatInterval = setInterval(refreshEverythingFast, 4000); // co 4 sekundy
    // Czat ma OSOBNY zegar: wcześniej startował dopiero po skończeniu pobierania wszystkich pozostałych danych (a na zacinającym się łączu
    // komórkowym to potrafiło trwać bardzo długo albo w ogóle się nie kończyć). Teraz idzie równolegle; loadChats ma własną blokadę przed dublowaniem.
    let chatSlowTick = 0;
    const chatOnly = setInterval(() => {
      if (viewRef.current !== "dashboard" && viewRef.current !== "admin") return;
      if (viewRef.current === "admin" && adminFormOpenRef.current) return;
      if (isAnyRecordingActive || (typeof document !== "undefined" && document.hidden)) return;
      if (window.__patexSlowLink && window.__patexSlowLink() && ++chatSlowTick % 2 !== 0) return;
      if (loadChatsRef.current) loadChatsRef.current();
    }, 4000);
    return () => {
      clearInterval(chatInterval);
      clearInterval(chatOnly);
    };
  }, []);
  // Wejście do panelu kierowcy: czat zaczyna się ładować natychmiast (nie czeka na pierwsze tyknięcie zegara ani na resztę danych).
  useEffect(() => {
    if (view !== "dashboard" || !driver) return undefined;
    const t = setTimeout(() => {
      if (loadChatsRef.current) loadChatsRef.current();
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [view, driver && driver.name]);

  return (
    <div className={`app-root ${theme === "light" ? "light-theme" : ""} ${hw && theme !== "light" ? "hw" : ""} ${hwReady ? "hw-ready" : ""} ${hwPlaying ? "hw-playing" : ""} ${hwFree ? "hw-freed" : ""} ${(hwMoon || view === "admin") && hwMoonReady ? "hw-moon" : ""}`} style={hw ? { "--hw-img": `url(${hwBg})`, ...(hwFree || hwMoon || view === "admin" ? { "--hw-img2": `url(${hwBg2})` } : null) } : undefined}>
      <style>{CSS}</style>
      <div className="road-scene-bg" ref={sceneRef} aria-hidden="true">
        <div className="road-scene-stars" />
        <div className="road-scene-streak s1" />
        <div className="road-scene-streak s2" />
        <div className="road-scene-streak s3" />
        <div className="road-scene-streak s4" />
        <div className="road-scene-glow" />
        <div className="road-scene-road" />
      </div>
      {loading ? (
        <div className="phone">
          <div className="loading">
            <div className="loading-road-scene">
              <div className="loading-car">🚗</div>
              <div className="loading-road" />
            </div>
            <span>Wczytywanie…</span>
            {slowLoad >= 1 && (
              <span style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center", maxWidth: 260, lineHeight: 1.5 }}>
                Słaby zasięg — wczytuję dane. To może potrwać chwilę…
              </span>
            )}
            {slowLoad >= 2 && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  marginTop: 6,
                  padding: "10px 22px",
                  borderRadius: 12,
                  border: "none",
                  background: "linear-gradient(135deg, #ffc76f, #ff9a2e)",
                  color: "#1a1206",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Spróbuj ponownie
              </button>
            )}
          </div>
        </div>
      ) : loadError ? (
        <div className="phone">
          <div className="loading" style={{ textAlign: "center", padding: 24, gap: 14 }}>
            <div style={{ fontSize: 34 }}>📡</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>Nie udało się wczytać danych</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", maxWidth: 280, lineHeight: 1.5 }}>
              Sprawdź zasięg i połączenie z internetem, po czym spróbuj ponownie. Twoje logowanie zostało zachowane.
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "12px 26px",
                borderRadius: 12,
                border: "none",
                background: "linear-gradient(135deg, #ffc76f, #ff9a2e)",
                color: "#1a1206",
                fontWeight: 700,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      ) : (
        <div className={`phone ${view === "admin" ? "phone-wide" : ""}`}>
          {toast && <div className="toast">{toast}</div>}
          {view === "home" && (
            <HomeScreen
              onDriver={() => setView("pin")}
              onAdmin={() => setView("adminlogin")}
              earnings={earnings}
              drivers={drivers}
              news={news}
            />
          )}
          {view === "pin" && (
            <PinScreen pin={pin} setPin={setPin} error={loginError} onSubmit={handlePinSubmit} onBack={goHome} />
          )}
          {view === "adminlogin" && (
            <AdminLoginScreen
              onSuccess={() => {
                try {
                  localStorage.setItem("patex_admin_session", "true");
                } catch (e) {
                  /* localStorage niedostępny — sesja po prostu nie przetrwa odświeżenia */
                }
                setView("admin");
              }}
              onBack={goHome}
            />
          )}
          {view === "dashboard" && driver && (
            <DashboardScreen
              driver={driver}
              drivers={drivers}
              earnings={earnings}
              onSaveEarnings={(rows) => mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) => upsertRecords(fresh, rows))}
              onEnsureNextWeekRecord={ensureNextWeekRecordExists}
              globalBonuses={globalBonuses}
              avatar={avatars[driver.name]}
              avatars={avatars}
              onSetAvatar={(emoji) => persistAvatars({ ...avatars, [driver.name]: emoji })}
              payoutDecisions={payoutDecisions}
              onSetPayoutDecision={setPayoutDecisionSafe}
              compensationRequests={compensationRequests}
              onAddCompensationRequest={addCompensationRequestSafe}
              debtRepayments={debtRepayments}
              onAddDebtRepayment={debtRepaymentsCrud.add}
              driverDebts={driverDebts}
              chatThreads={chatThreads}
              onCreateChatThread={createChatThread}
              onSendChatMessage={sendChatMessage}
              onMarkThreadRead={markThreadRead}
              dmThreads={dmThreads}
              onSendDm={sendDmMessage}
              onMarkDmThreadRead={markDmThreadRead}
              onResolveThread={resolveThread}
              groupChatMessages={groupChatMessages}
              onSendGroupMessage={(text, attachment, replyTo) => sendGroupMessage(driver.name, text, attachment, replyTo)}
              onMarkGroupMessagesSeen={() => markGroupMessagesSeen(driver.name)}
              onToggleGroupReaction={(messageId, emoji) => toggleGroupMessageReaction(messageId, emoji, driver.name)}
              onToggleThreadReaction={(threadId, messageId, emoji) => toggleThreadMessageReaction(threadId, messageId, emoji, driver.name)}
              chatsLoaded={chatsLoaded}
              chatsError={chatsError}
              onToggleDmReaction={(threadId, messageId, emoji) => toggleDmMessageReaction(threadId, messageId, emoji, driver.name)}
              tasks={tasks}
              pointAdjustments={pointAdjustments}
              onRefresh={handleReload}
              onLogout={goHome}
              theme={theme}
              onToggleTheme={toggleTheme}
              onGirlFreed={(bg) => {
                setHwFree(true);
                if (bg) setHwMoon(true);
              }}
            />
          )}
          {view === "admin" && adminViewingDriverName && (() => {
            const viewedDriver = drivers.find((d) => sameName(d.name, adminViewingDriverName));
            if (!viewedDriver) return null;
            return (
              <div className="admin-view-as-wrap">
                <div className="admin-view-as-banner">
                  <Eye size={15} />
                  <span>
                    Oglądasz panel jako <b>{viewedDriver.name}</b> — zmiany, które tu zrobisz, naprawdę się zapiszą.
                  </span>
                  <button className="admin-view-as-exit" onClick={() => setAdminViewingDriverName(null)}>
                    <X size={14} /> Wróć do panelu admina
                  </button>
                </div>
                <DashboardScreen
                  trackPresence={false}
                  driver={viewedDriver}
                  drivers={drivers}
                  earnings={earnings}
                  onSaveEarnings={(rows) => mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) => upsertRecords(fresh, rows))}
                  onEnsureNextWeekRecord={ensureNextWeekRecordExists}
                  globalBonuses={globalBonuses}
                  avatar={avatars[viewedDriver.name]}
                  avatars={avatars}
                  onSetAvatar={(emoji) => persistAvatars({ ...avatars, [viewedDriver.name]: emoji })}
                  payoutDecisions={payoutDecisions}
                  onSetPayoutDecision={setPayoutDecisionSafe}
                  compensationRequests={compensationRequests}
                  onAddCompensationRequest={addCompensationRequestSafe}
                  debtRepayments={debtRepayments}
                  onAddDebtRepayment={debtRepaymentsCrud.add}
                  driverDebts={driverDebts}
                  chatThreads={chatThreads}
                  onCreateChatThread={createChatThread}
                  onSendChatMessage={sendChatMessage}
                  onMarkThreadRead={markThreadRead}
                  dmThreads={dmThreads}
                  onSendDm={sendDmMessage}
                  onMarkDmThreadRead={markDmThreadRead}
                  onResolveThread={resolveThread}
                  groupChatMessages={groupChatMessages}
                  onSendGroupMessage={(text, attachment, replyTo) => sendGroupMessage(viewedDriver.name, text, attachment, replyTo)}
                  onMarkGroupMessagesSeen={() => markGroupMessagesSeen(viewedDriver.name)}
                  onToggleGroupReaction={(messageId, emoji) => toggleGroupMessageReaction(messageId, emoji, viewedDriver.name)}
                  onToggleThreadReaction={(threadId, messageId, emoji) => toggleThreadMessageReaction(threadId, messageId, emoji, viewedDriver.name)}
                  chatsLoaded={chatsLoaded}
              chatsError={chatsError}
                  onToggleDmReaction={(threadId, messageId, emoji) => toggleDmMessageReaction(threadId, messageId, emoji, viewedDriver.name)}
                  tasks={tasks}
                  pointAdjustments={pointAdjustments}
                  onRefresh={handleReload}
                  onLogout={() => setAdminViewingDriverName(null)}
                  theme={theme}
                  onToggleTheme={toggleTheme}
                />
              </div>
            );
          })()}
          {view === "admin" && (
            <React.Suspense fallback={null}>
              <AdminCallListenerLazy />
            </React.Suspense>
          )}
          {view === "admin" && !adminViewingDriverName && (
            <React.Suspense
              fallback={
                <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0a0b0f" }}>
                  <div className="spin-icon" style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #ffb84d, #ff9d2e)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontFamily: "sans-serif", fontWeight: 800, color: "#1a1206", fontSize: 15 }}>P</span>
                  </div>
                </div>
              }
            >
            <AdminScreen
              adminKey={ADMIN_PASSWORD}
              onViewDriverPanel={(name) => setAdminViewingDriverName(name)}
              drivers={drivers}
              earnings={earnings}
              onEnsureNextWeekRecord={ensureNextWeekRecordExists}
              debtRepayments={debtRepayments}
              onApproveDebtRepayment={approveDebtRepayment}
              onRejectDebtRepayment={rejectDebtRepayment}
              onDeleteDebtRepayment={deleteDebtRepayment}
              onEditDebtRepayment={debtRepaymentsCrud.update}
              driverDebts={driverDebts}
              driverDebtsCrud={driverDebtsCrud}
              cryptoPortfolio={cryptoPortfolio}
              onUpdateCryptoPortfolio={updateCryptoPortfolio}
              cryptoStrategyParams={cryptoStrategyParams}
              onUpdateCryptoStrategyParams={updateCryptoStrategyParams}
              cryptoIntradayPortfolio={cryptoIntradayPortfolio}
              onUpdateCryptoIntradayPortfolio={updateCryptoIntradayPortfolio}
              cryptoIntradayStrategyParams={cryptoIntradayStrategyParams}
              onUpdateCryptoIntradayStrategyParams={updateCryptoIntradayStrategyParams}
              globalBonuses={globalBonuses}
              onAddBonus={addBonus}
              onUpdateBonus={updateBonus}
              onDeleteBonus={deleteBonus}
              onUpdateBonusStatus={updateBonusStatus}
              onSetBonusCompletion={setBonusCompletion}
              avatars={avatars}
              payoutDecisions={payoutDecisions}
              onMarkPayoutAsPaid={markPayoutAsPaid}
              tasks={tasks}
              onAddTask={addTask}
              onSetTaskCompletion={setTaskCompletion}
              onSetTaskStatus={setTaskStatus}
              onDeleteTask={deleteTask}
              onUpdateTask={updateTask}
              pointAdjustments={pointAdjustments}
              onAddPointAdjustment={(driverName, points, note) => addPointAdjustment(driverName, points, note)}
              onSaveEarnings={(rows) => mutateArrayCollection(EARNINGS_KEY, setEarnings, (fresh) => upsertRecords(fresh, rows))}
              onDeleteEarningsRecord={deleteEarningsRecordSafe}
              onUnarchiveEarningsRecord={unarchiveEarningsRecord}
              onFormOpenChange={(open) => {
                adminFormOpenRef.current = open;
              }}
              onRegisterDriver={(d) => persistDrivers([...drivers, d])}
              onRemoveDriver={(id) => persistDrivers(drivers.filter((d) => d.id !== id))}
              onUpdateDriverPermissions={updateDriverPermissions}
              onUpdateDriverRates={updateDriverRates}
              cars={cars}
              onUpdateDriverCar={updateDriverCar}
              onUpdateDriverGroupMute={updateDriverGroupMute}
              onUpdateDriverChatBlock={updateDriverChatBlock}
              onAddCar={addCar}
              onRenameCar={renameCar}
              onDeleteCar={deleteCar}
              weeklyRevenue={weeklyRevenue}
              weeklyRevenueCrud={weeklyRevenueCrudLinked}
              profitLoss={profitLoss}
              profitLossCrud={profitLossCrud}
              carRepairs={carRepairs}
              carRepairsCrud={carRepairsCrudLinked}
              companyExpenses={companyExpenses}
              companyExpensesCrud={companyExpensesCrud}
              carInfo={carInfo}
              carInfoCrud={carInfoCrud}
              companyAdjustments={companyAdjustments}
              companyAdjustmentsCrud={companyAdjustmentsCrud}
              compensationRequests={compensationRequests}
              onUpdateCompensationRequest={updateCompensationRequestSafe}
              onAddAdminCompensation={addAdminCompensation}
              onDeleteCompensationRequest={deleteCompensationRequest}
              onSetPayoutDecision={setPayoutDecisionSafe}
              onDeletePayoutDecision={deletePayoutDecision}
              chatThreads={chatThreads}
              onCreateChatThread={createChatThread}
              onSendChatMessage={sendChatMessage}
              onMarkThreadRead={markThreadRead}
              dmThreads={dmThreads}
              onSendDm={sendDmMessage}
              onMarkDmThreadRead={markDmThreadRead}
              onResolveThread={resolveThread}
              onDeleteThreadMessage={deleteThreadMessage}
              groupChatMessages={groupChatMessages}
              onSendGroupMessage={(text, attachment, replyTo) => sendGroupMessage("Administrator", text, attachment, replyTo)}
              onSendImportantMessage={sendImportantGroupMessage}
              onCallAll={callAllGroup}
              onMarkGroupMessagesSeen={() => markGroupMessagesSeen("Administrator")}
              onToggleGroupReaction={(messageId, emoji) => toggleGroupMessageReaction(messageId, emoji, "Administrator")}
              onToggleThreadReaction={(threadId, messageId, emoji) => toggleThreadMessageReaction(threadId, messageId, emoji, "Administrator")}
              onDeleteGroupMessage={deleteGroupMessage}
              onEditGroupMessage={editGroupMessage}
              onTogglePinGroupMessage={togglePinGroupMessage}
              news={news}
              onAddNews={addNews}
              onDeleteNews={deleteNews}
              onClearAll={() => {
                persistDrivers([]);
                persistEarnings([]);
              }}
              onReload={handleReload}
              onBack={goHome}
            />
            </React.Suspense>
          )}
          <div className="app-footer">PateX Car Rental</div>
        </div>
      )}
    </div>
  );
}

// To jedyny eksport domyślny tego pliku — opakowuje właściwą appkę w
// siatkę bezpieczeństwa na błędy (PateXErrorBoundary), zdefiniowaną wyżej.
// Dźwięki halloweenowe: pierwsze dotknięcie ekranu (raz dziennie) uruchamia nagranie; przycisk w rogu zatrzymuje/odtwarza.
// HW_ENV = głośność nagrania co 0,1 s (0–9) — dynia błyska w rytm dźwięków (bez analizatora audio, więc zero ryzyka ciszy na iPhonie).
const HW_ENV = "0000034403444200000000000000000000144420000000000000000000000000454134245411000000000000000000245355530000000000000000000001543444200002000000003244254454000023100000000554234431000000000000000000002433344021000000000000000213101038002000000001010000000000033350044541000000004240340744343430444100000000000000000255530000002444666666541000006650455410000000003666346511000000001100013055664645542454120000004555515555443000000477522004000000000646654110100110000000000006666555566640100000366656565455455420100200010000577777666577756554047678774620100000000000001555423105665345205556555544200565056665655100000005777553014776663115766651006663111000000772577765413787662211677767676221000000110101211067632552577654214553115405666421145421441566514404566552115666665652222111101676664256322561055554100476652100000000000000000764355665544335445453342037766665478554652522444244433253134444431244421011011455544545554331011259743322554555332000001345103324134430010334333144345555531000336514666232433434434443431455332665541249846676462555536665556215655663255553112212266775666666554221256974342556677654212111356523444626653111354555446636767764220356862678744365555665665535366652577674453221000111001343453310000002100000002223656432212110100001105655420100011100110001110584657442100101000010121100056742315644400000000002225233000000000045532430443214303555530044342100034513534555234324443420055431000000001666641147655210554542256451464254554214555530035655444332000505645445653000000000000000000";
const HWS = { a: null, raf: 0, fade: 0, cur: 0, el: null, busy: false };
function hwAudio() {
  if (!HWS.a) {
    const a = new Audio();
    a.preload = "auto";
    a.loop = true; // nagranie leci w kółko
    a.src = hwSnd;
    HWS.a = a;
  }
  return HWS.a;
}
function hwStopLoop() {
  clearTimeout(HWS.raf);
  HWS.raf = 0;
  if (HWS.el) HWS.el.style.removeProperty("--hw-lvl");
}
function hwLoop() {
  const a = HWS.a;
  if (!a || a.paused) return hwStopLoop();
  // 10 odświeżeń na sekundę (wygładzenie robi CSS transition) — mało pracy dla głównego wątku
  const i = Math.min(HW_ENV.length - 1, Math.max(0, Math.floor(a.currentTime * 10)));
  const target = (HW_ENV.charCodeAt(i) - 48) / 9;
  if (target !== HWS.cur && HWS.el) {
    HWS.cur = target;
    HWS.el.style.setProperty("--hw-lvl", target.toFixed(2));
  }
  HWS.raf = setTimeout(hwLoop, 100);
}
function hwSay(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch (e) {}
}
function hwPlay(setPlaying) {
  if (HWS.busy) return Promise.resolve(false);
  HWS.busy = true;
  const a = hwAudio();
  a.onpause = a.onended = () => {
    hwStopLoop();
    setPlaying(false);
  };
  if (a.ended || a.currentTime > 0) a.currentTime = 0;
  a.volume = 0;
  let p;
  try {
    p = a.play();
  } catch (e) {
    HWS.busy = false;
    return Promise.resolve(false);
  }
  return Promise.resolve(p)
    .then(() => {
      HWS.busy = false;
      setPlaying(true);
      clearInterval(HWS.fade);
      let v = 0;
      HWS.fade = setInterval(() => {
        v = Math.min(0.9, v + 0.1);
        a.volume = v;
        if (v >= 0.9) clearInterval(HWS.fade);
      }, 120);
      const calm = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      HWS.el = document.querySelector(".road-scene-bg");
      HWS.cur = 0;
      if (!calm) HWS.raf = setTimeout(hwLoop, 0);
      return true;
    })
    .catch(() => {
      HWS.busy = false;
      return false;
    });
}
function hwStop(ms) {
  const a = HWS.a;
  if (!a) return;
  clearInterval(HWS.fade);
  let v = a.volume;
  const step = Math.max(0.01, v / ((ms || 240) / 60));
  HWS.fade = setInterval(() => {
    v = Math.max(0, v - step);
    a.volume = v;
    if (v <= 0) {
      clearInterval(HWS.fade);
      a.pause();
    }
  }, 60);
}
// Zabawa "uwolnij dziewczynkę": stan zapisany na telefonie (klucz kierowcy: "pending" = uwolniona, ticket jeszcze niepotwierdzony przez serwer, "1" = potwierdzony;
// klucz urządzenia: dźwięk już nigdy nie gra). Ticket przyznaje serwer (jednorazowo na kierowcę): POST /call-api/fortune/girl.
const GIRL_SHOWN = { v: false };
function girlKey(name) {
  return "patex_girl_" + String(name || "").trim().toLowerCase();
}
function girlGet(k) {
  try {
    return localStorage.getItem(k);
  } catch (e) {
    return null;
  }
}
function girlSet(k, v) {
  try {
    localStorage.setItem(k, v);
  } catch (e) {
    /* nic */
  }
}
function girlDeviceFreed() {
  return girlGet("patex_girl_device") === "1";
}
async function girlCall(driver, action) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), 9000) : null;
  try {
    const res = await fetch("/call-api/fortune/girl", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: driver.name, pin: driver.pin, action }),
      cache: "no-store",
      signal: ctl ? ctl.signal : undefined,
    });
    return await res.json();
  } catch (e) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
// Ankieta zadowolenia: stan (czy kierowca już odpowiedział + wynik) z serwera; 404 (serwer jeszcze bez ankiety) = karta się nie pokazuje.
async function svPost(driver, path) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), 9000) : null;
  try {
    const res = await fetch("/call-api/fortune/survey/" + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: driver.name, pin: driver.pin }),
      cache: "no-store",
      signal: ctl ? ctl.signal : undefined,
    });
    return await res.json();
  } catch (e) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
// Zdrapki: lista kart kierowcy z serwera (404 = serwer jeszcze bez zdrapek, nic się nie pokazuje)
async function fzPostJson(path, driver) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), 9000) : null;
  try {
    const res = await fetch("/call-api/fortune" + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: driver.name, pin: driver.pin }),
      cache: "no-store",
      signal: ctl ? ctl.signal : undefined,
    });
    return await res.json();
  } catch (e) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
// Tło halloweenowe: włączone automatycznie 20.10–2.11 (czas warszawski); ?halloween=1 wymusza, ?halloween=0 wyłącza.
function halloweenOn() {
  try {
    if (/[?&]halloween=0/.test(location.search)) return false;
    if (/[?&]halloween=1/.test(location.search)) return true;
    const [, m, d] = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Warsaw" }).split("-").map(Number);
    return (m === 10 && d >= 20) || (m === 11 && d <= 2);
  } catch (e) {
    return false;
  }
}
export default function FleetEarningsApp() {
  return (
    <PateXErrorBoundary>
      <FleetEarningsAppInner />
    </PateXErrorBoundary>
  );
}

/* ---------------------------------------------------------------------- */
/* Styles                                                                   */
/* ---------------------------------------------------------------------- */
const CSS = `
/* Czcionki hostowane lokalnie (wcześniej @import z fonts.googleapis.com blokował start na słabym internecie) */
@font-face { font-family: 'IBM Plex Sans'; font-style: normal; font-weight: 400 700; font-display: swap; src: url(/webfonts/ibm-plex-sans-latin-400-700.sub.woff2) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+2013-2014, U+2018-201E, U+2022, U+2026, U+20AC, U+2122, U+2191, U+2193, U+2212; }
@font-face { font-family: 'IBM Plex Sans'; font-style: normal; font-weight: 400 700; font-display: swap; src: url(/webfonts/ibm-plex-sans-latin-ext-400-700.sub.woff2) format('woff2'); unicode-range: U+0100-017F; }
@font-face { font-family: 'IBM Plex Sans'; font-style: normal; font-weight: 400 700; font-display: swap; src: url(/webfonts/ibm-plex-sans-cyrillic-400-700.woff2) format('woff2'); unicode-range: U+0301, U+0400-045F, U+0490-0491, U+04B0-04B1, U+2116; }
@font-face { font-family: 'IBM Plex Sans'; font-style: normal; font-weight: 400 700; font-display: swap; src: url(/webfonts/ibm-plex-sans-cyrillic-ext-400-700.woff2) format('woff2'); unicode-range: U+0460-052F, U+1C80-1C8A, U+20B4, U+2DE0-2DFF, U+A640-A69F, U+FE2E-FE2F; }
@font-face { font-family: 'Space Mono'; font-style: normal; font-weight: 400; font-display: swap; src: url(/webfonts/space-mono-latin-400.sub.woff2) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+2013-2014, U+2018-201E, U+2022, U+2026, U+20AC, U+2122, U+2191, U+2193, U+2212; }
@font-face { font-family: 'Space Mono'; font-style: normal; font-weight: 400; font-display: swap; src: url(/webfonts/space-mono-latin-ext-400.sub.woff2) format('woff2'); unicode-range: U+0100-017F; }
@font-face { font-family: 'Space Mono'; font-style: normal; font-weight: 700; font-display: swap; src: url(/webfonts/space-mono-latin-700.sub.woff2) format('woff2'); unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+2013-2014, U+2018-201E, U+2022, U+2026, U+20AC, U+2122, U+2191, U+2193, U+2212; }
@font-face { font-family: 'Space Mono'; font-style: normal; font-weight: 700; font-display: swap; src: url(/webfonts/space-mono-latin-ext-700.sub.woff2) format('woff2'); unicode-range: U+0100-017F; }

:root {
  --bg: #12151a;
  --surface: #1a1f26;
  --surface-2: #232a33;
  --surface-3: #2c343f;
  --border: #313a46;
  --text: #edeff3;
  --text-muted: #8b96a5;
  --text-dim: #7a8697;
  --amber: #ffa630;
  --amber-dim: #c97f1e;
  --green: #6ee7b7;
  --red: #ff6b6b;
  --radius: 14px;
}
.app-root.light-theme {
  --bg: #f5f3ee;
  --surface: #ffffff;
  --surface-2: #f1efe9;
  --surface-3: #e6e3da;
  --border: #ddd8cc;
  --text: #23262b;
  --text-muted: #6b6f76;
  --text-dim: #7d818a;
  --amber: #d9820f;
  --amber-dim: #b56d0c;
  --green: #1f9d5c;
  --red: #d9453f;
}
.app-root.light-theme .road-scene-bg { opacity: 0.4; }
.app-root {
  font-family: 'IBM Plex Sans', sans-serif;
  display: flex;
  justify-content: center;
  background:
    radial-gradient(ellipse 600px 300px at 50% -8%, rgba(255,166,48,0.12), transparent 60%),
    radial-gradient(ellipse 500px 400px at 100% 100%, rgba(110,231,183,0.05), transparent 55%),
    var(--bg);
  min-height: 100vh;
  min-height: 100dvh;
  padding: 0;
  color: var(--text);
  position: relative;
  isolation: isolate;
}
.app-root * { box-sizing: border-box; }

/* ---- Halloween: ciemna klatka schodowa ze zjawą za całą appką (tylko ciemny motyw, 20.10–2.11) ---- */
.app-root.hw .road-scene-bg { background: #060806; }
.app-root.hw .road-scene-bg > * { display: none; }
.app-root.hw .road-scene-bg::before { content: ""; position: absolute; z-index: 0; left: 0; right: 0; bottom: 0; height: 64%; background: var(--hw-img) 54% 100% / auto 100% no-repeat; -webkit-mask-image: linear-gradient(180deg, transparent 0%, #000 30%); mask-image: linear-gradient(180deg, transparent 0%, #000 30%); opacity: 0; transition: opacity 1.8s ease; transform-origin: 54% 45%; will-change: transform; animation: hwCreep 70s ease-in-out infinite alternate; }
.app-root.hw-ready .road-scene-bg::before { opacity: 1; }
.app-root.hw .road-scene-bg::after { content: ""; position: absolute; z-index: 1; inset: 0; background: radial-gradient(ellipse 85% 55% at 52% 66%, rgba(4,6,4,0), rgba(3,4,3,0.4) 100%), linear-gradient(180deg, rgba(3,4,3,0.55) 0%, rgba(3,4,3,0.15) 45%, rgba(3,4,3,0.1) 100%); }
.app-root.hw .road-scene-bg > .road-scene-stars { display: block; position: absolute; z-index: 2; inset: 0; background-image: radial-gradient(ellipse 24% 14% at 52% 58%, rgba(190,235,205,0.3), transparent 72%); opacity: 0.4; animation: hwAura 6s ease-in-out infinite alternate; }
.app-root.hw .phone { background: radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1.5px) 0 0/18px 18px; border-left-color: rgba(170,220,190,0.12); border-right-color: rgba(170,220,190,0.12); box-shadow: none; }
@keyframes hwCreep { from { transform: scale(1); } to { transform: scale(1.12); } }
@keyframes hwAura { from { opacity: 0.15; } to { opacity: 0.7; } }
.app-root.hw-playing .road-scene-bg::after { opacity: calc(1 - var(--hw-lvl, 0) * 0.65); transition: opacity 0.14s linear; }
.app-root.hw-playing .road-scene-bg > .road-scene-stars { animation: none; opacity: calc(0.2 + var(--hw-lvl, 0) * 0.8); transition: opacity 0.14s linear; }
.app-root.hw.hw-freed .road-scene-bg::after { background: radial-gradient(ellipse 85% 55% at 52% 60%, rgba(255,205,120,0.2), rgba(3,4,3,0.22) 100%), linear-gradient(180deg, rgba(3,4,3,0.3) 0%, rgba(3,4,3,0.05) 45%, rgba(3,4,3,0) 100%); }
.app-root.hw.hw-freed .road-scene-bg > .road-scene-stars { background-image: radial-gradient(ellipse 26% 16% at 52% 56%, rgba(255,214,130,0.5), transparent 72%); opacity: 0.7; }
/* ---- Po uwolnieniu dziewczynki: czerwony księżyc, mgła, iskry i migoczące dynie ---- */
.app-root.hw-moon .road-scene-bg { background: #3a0810; }
.app-root.hw-moon .road-scene-bg::before { opacity: 0; animation: none; }
.app-root.hw-moon .road-scene-bg > .road-scene-glow { display: block; position: absolute; z-index: 0; left: 0; top: 0; bottom: auto; width: 100%; height: 100%; transform: none; transform-origin: 52% 43%; background: var(--hw-img2) 50% 100% / cover no-repeat; opacity: 1; will-change: transform, opacity; animation: hwMoonIn 3.6s cubic-bezier(0.2, 0.7, 0.2, 1) both, hwMoonDrift 60s ease-in-out 3.6s infinite alternate; }
@keyframes hwMoonIn { from { opacity: 0; transform: scale(1.18); } to { opacity: 1; transform: scale(1); } }
@keyframes hwMoonDrift { from { transform: scale(1); } to { transform: scale(1.07); } }
.app-root.hw-moon .road-scene-bg::after { z-index: 4; background: radial-gradient(ellipse 60% 26% at 52% 43%, rgba(50,4,14,0.32), transparent 78%), linear-gradient(180deg, rgba(34,2,10,0.55) 0%, rgba(34,2,10,0.2) 22%, rgba(34,2,10,0.24) 62%, rgba(34,2,10,0.6) 100%); }
.app-root.hw-moon .road-scene-bg > .road-scene-stars { z-index: 3; background-image: radial-gradient(ellipse 52% 25% at 52% 43%, rgba(255,170,150,0.55), transparent 72%); opacity: 0.5; animation: hwAura 5s ease-in-out infinite alternate; }
.app-root.hw-moon .road-scene-bg > .road-scene-road { display: block; position: absolute; z-index: 1; left: -25%; right: auto; width: 150%; top: auto; bottom: 12%; height: 38%; overflow: visible; background: radial-gradient(ellipse 45% 50% at 25% 60%, rgba(255,120,110,0.3), transparent 70%), radial-gradient(ellipse 40% 45% at 72% 45%, rgba(255,170,140,0.24), transparent 70%); will-change: transform; animation: hwFog 22s ease-in-out infinite alternate; }
@keyframes hwFog { from { transform: translateX(-6%); } to { transform: translateX(6%); } }
.app-root.hw-moon .road-scene-bg > .road-scene-streak.s3, .app-root.hw-moon .road-scene-bg > .road-scene-streak.s4 { display: block; position: absolute; z-index: 2; left: 50%; top: auto; bottom: 0; width: min(100vw, 640px); margin-left: calc(min(100vw, 640px) / -2); height: calc(min(100vw, 640px) * 0.245); border-radius: 0; filter: none; -webkit-mask-image: linear-gradient(180deg, transparent, #000 42%), linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent); -webkit-mask-composite: source-in; mask-image: linear-gradient(180deg, transparent, #000 42%), linear-gradient(90deg, transparent, #000 7%, #000 93%, transparent); mask-composite: intersect; }
.app-root.hw-moon .road-scene-bg > .road-scene-streak.s3 { background: var(--hw-img2) 50% 100% / 100% auto no-repeat; animation: hwBandIn 2.6s ease-out 1.2s both; }
.app-root.hw-moon .road-scene-bg > .road-scene-streak.s4 { background: radial-gradient(ellipse 9% 42% at 11% 66%, rgba(255,190,60,0.6), transparent 72%), radial-gradient(ellipse 8% 36% at 23.5% 70%, rgba(255,190,60,0.5), transparent 72%), radial-gradient(ellipse 7% 34% at 73% 66%, rgba(255,190,60,0.5), transparent 72%), radial-gradient(ellipse 9% 42% at 88.6% 64%, rgba(255,190,60,0.6), transparent 72%); animation: hwFlicker 3.1s steps(1) infinite; }
@keyframes hwBandIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes hwFlicker { 0% { opacity: 0.55; } 12% { opacity: 1; } 24% { opacity: 0.7; } 41% { opacity: 0.95; } 55% { opacity: 0.5; } 68% { opacity: 1; } 82% { opacity: 0.75; } 100% { opacity: 0.55; } }
.app-root.hw-moon .road-scene-bg > .road-scene-streak.s1, .app-root.hw-moon .road-scene-bg > .road-scene-streak.s2 { display: block; position: absolute; z-index: 2; left: 0; top: auto; bottom: 0; width: 100%; height: 72%; border-radius: 0; filter: none; will-change: transform, opacity; }
.app-root.hw-moon .road-scene-bg > .road-scene-streak.s1 { background: radial-gradient(3px 3px at 10% 88%, #ffb347, transparent), radial-gradient(2px 2px at 24% 66%, #ffd27a, transparent), radial-gradient(3px 3px at 41% 82%, #ff9a3c, transparent), radial-gradient(2px 2px at 63% 58%, #ffd27a, transparent), radial-gradient(3px 3px at 78% 90%, #ffb347, transparent), radial-gradient(2px 2px at 90% 70%, #ff9a3c, transparent); animation: hwEmbers 17s linear infinite; }
.app-root.hw-moon .road-scene-bg > .road-scene-streak.s2 { background: radial-gradient(2px 2px at 5% 72%, #ffd27a, transparent), radial-gradient(3px 3px at 18% 92%, #ff9a3c, transparent), radial-gradient(2px 2px at 35% 60%, #ffb347, transparent), radial-gradient(3px 3px at 55% 86%, #ffd27a, transparent), radial-gradient(2px 2px at 70% 64%, #ff9a3c, transparent), radial-gradient(3px 3px at 93% 80%, #ffb347, transparent); animation: hwEmbers 24s linear -9s infinite; }
@keyframes hwEmbers { 0% { transform: translateY(10%); opacity: 0; } 15% { opacity: 0.95; } 85% { opacity: 0.8; } 100% { transform: translateY(-26%); opacity: 0; } }
/* Szeroki ekran (komputer, panel admina): obraz mieści się w całości, więc dynie z rogów są częścią obrazu — bez dolnego paska; poświata księżyca liczona z geometrii obrazu */
@media (min-aspect-ratio: 5/4) {
  .app-root.hw-moon .road-scene-bg > .road-scene-glow { transform-origin: 51% 36%; }
  .app-root.hw-moon .road-scene-bg > .road-scene-streak.s3, .app-root.hw-moon .road-scene-bg > .road-scene-streak.s4 { display: none; }
  .app-root.hw-moon .road-scene-bg > .road-scene-stars { background-image: radial-gradient(ellipse 19vw 17vw at 51% min(39%, calc(100% - 39.4vw)), rgba(255,170,150,0.55), transparent 72%); }
  .app-root.hw-moon .road-scene-bg::after { background: radial-gradient(ellipse 18vw 15vw at 51% min(39%, calc(100% - 39.4vw)), rgba(50,4,14,0.3), transparent 80%), radial-gradient(ellipse 85% 85% at 50% 52%, rgba(34,2,10,0) 50%, rgba(34,2,10,0.62) 100%), linear-gradient(180deg, rgba(34,2,10,0.42) 0%, rgba(34,2,10,0.12) 24%, rgba(34,2,10,0.18) 70%, rgba(34,2,10,0.42) 100%); }
  .app-root.hw-moon .road-scene-bg > .road-scene-road { left: -10%; width: 120%; bottom: 4%; height: 34%; }
  .app-root.hw-moon .road-scene-bg > .road-scene-streak.s1, .app-root.hw-moon .road-scene-bg > .road-scene-streak.s2 { height: 86%; }
}
.app-root.hw-moon .phone-wide .section-label, .app-root.hw-moon .phone-wide .section-label-row { text-shadow: 0 1px 8px rgba(20,0,6,0.95), 0 0 2px rgba(20,0,6,0.8); }
@media (prefers-reduced-motion: reduce) { .app-root.hw-moon .road-scene-bg > * { animation: none !important; } }
.hw-snd { position: fixed; left: 14px; bottom: calc(14px + env(safe-area-inset-bottom, 0px)); z-index: 60; width: 42px; height: 42px; padding: 0; border-radius: 50%; display: grid; place-items: center; color: #ffb02e; opacity: 0.8; background: rgba(10,12,10,0.86); border: 1px solid rgba(255,176,46,0.55); box-shadow: 0 0 18px -2px rgba(255,176,46,0.45); cursor: pointer; }
.hw-snd.on { opacity: 1; color: #fff3c4; border-color: #ffb02e; }
.hw-snd.on::after { content: ""; position: absolute; top: -1px; right: -1px; bottom: -1px; left: -1px; border-radius: 50%; border: 2px solid #ffb02e; pointer-events: none; will-change: transform, opacity; animation: hwRing 1.5s ease-out infinite; }
@keyframes hwRing { from { transform: scale(1); opacity: 0.7; } to { transform: scale(1.7); opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .app-root.hw .road-scene-bg::before, .app-root.hw .road-scene-bg > .road-scene-stars, .hw-snd.on::after { animation: none !important; } }

/* ---- Animowana scena tła: nocna autostrada ---- */
.road-scene-bg {
  position: fixed; inset: 0; z-index: -1; overflow: hidden; pointer-events: none;
  background: linear-gradient(180deg, #0a0c10 0%, #12151a 55%, #171a20 100%);
}
.road-scene-stars {
  position: absolute; inset: 0;
  background-image:
    radial-gradient(1.5px 1.5px at 12% 18%, rgba(255,255,255,0.35), transparent),
    radial-gradient(1px 1px at 28% 32%, rgba(255,255,255,0.22), transparent),
    radial-gradient(1.5px 1.5px at 45% 12%, rgba(255,255,255,0.28), transparent),
    radial-gradient(1px 1px at 62% 25%, rgba(255,255,255,0.2), transparent),
    radial-gradient(1.5px 1.5px at 78% 15%, rgba(255,255,255,0.3), transparent),
    radial-gradient(1px 1px at 88% 30%, rgba(255,255,255,0.18), transparent),
    radial-gradient(1.5px 1.5px at 20% 45%, rgba(255,255,255,0.16), transparent),
    radial-gradient(1px 1px at 70% 42%, rgba(255,255,255,0.14), transparent);
  animation: starsTwinkle 5s ease-in-out infinite;
}
@keyframes starsTwinkle { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }
.road-scene-glow {
  position: absolute; left: 50%; bottom: 8%; width: 140%; height: 260px; transform: translateX(-50%);
  background: radial-gradient(ellipse 50% 100% at 50% 100%, rgba(255,166,48,0.14), transparent 70%);
  animation: bgBreathe 7s ease-in-out infinite;
}
@keyframes bgBreathe { 0%, 100% { opacity: 0.75; } 50% { opacity: 1; } }
.road-scene-streak {
  position: absolute; height: 2px; width: 140px; border-radius: 2px; left: -160px;
  background: linear-gradient(90deg, transparent, rgba(255,200,120,0.85), transparent);
  filter: blur(0.4px);
  animation-name: streakMove; animation-timing-function: linear; animation-iteration-count: infinite;
}
@keyframes streakMove { from { transform: translateX(0); opacity: 0; } 8% { opacity: 1; } 92% { opacity: 1; } to { transform: translateX(calc(100vw + 300px)); opacity: 0; } }
.road-scene-streak.s1 { top: 28%; width: 110px; animation-duration: 3.2s; animation-delay: 0.2s; }
.road-scene-streak.s2 { top: 41%; width: 160px; animation-duration: 4.4s; animation-delay: 1.6s; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent); }
.road-scene-streak.s3 { top: 34%; width: 90px; animation-duration: 2.6s; animation-delay: 3s; }
.road-scene-streak.s4 { top: 50%; width: 130px; animation-duration: 5s; animation-delay: 0.9s; background: linear-gradient(90deg, transparent, rgba(255,120,120,0.5), transparent); }
.road-scene-road {
  position: absolute; left: 0; right: 0; bottom: 0; height: 34%;
  background: linear-gradient(180deg, transparent, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.75));
  overflow: hidden;
}
@media (min-width: 480px) {
  .road-scene-glow { bottom: 4%; }
}

/* ---- Ikona ładowania: jadący samochodzik ---- */
.loading-road-scene { position: relative; width: 160px; height: 46px; margin-bottom: 4px; overflow: hidden; }
.loading-car {
  position: absolute; bottom: 12px; font-size: 26px; line-height: 1;
  animation: loadingCarDrive 1.6s cubic-bezier(.45,0,.55,1) infinite;
  filter: drop-shadow(0 3px 6px rgba(255,166,48,0.35));
}
@keyframes loadingCarDrive {
  0% { left: -30px; transform: scaleX(-1); }
  48% { left: calc(100% - 4px); transform: scaleX(-1); }
  50% { left: calc(100% - 4px); transform: scaleX(1); }
  98% { left: -30px; transform: scaleX(1); }
  100% { left: -30px; transform: scaleX(-1); }
}
.loading-road {
  position: absolute; bottom: 8px; left: 0; right: 0; height: 2px;
  background-image: repeating-linear-gradient(90deg, var(--amber-dim) 0 8px, transparent 8px 16px);
  animation: loadingRoadMove 0.5s linear infinite;
}
@keyframes loadingRoadMove { from { background-position-x: 0; } to { background-position-x: -16px; } }

.screen { animation: screenIn 0.32s ease both; }
@keyframes screenIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.phone {
  width: 100%;
  max-width: 430px;
  min-height: 640px;
  background:
    radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1.5px) 0 0/18px 18px,
    var(--bg);
  position: relative;
  padding: 18px 18px 100px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255,255,255,0.05);
  border-right: 1px solid rgba(255,255,255,0.05);
  box-shadow: 0 30px 80px -20px rgba(0,0,0,0.6);
}
.phone::before {
  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px; z-index: 5;
  background: linear-gradient(90deg, transparent, var(--amber), transparent);
  opacity: 0.55;
}
.phone-wide { max-width: 1800px; }
@media (min-width: 960px) {
  .phone-wide .tab-bar { gap: 12px; }
  .phone-wide .tab-btn {
    font-size: 15px; padding: 16px 10px; border-radius: 14px; gap: 9px;
  }
  .phone-wide .tab-btn svg { width: 18px; height: 18px; }
  .phone-wide .section-label { font-size: 15px; }
  .phone-wide .register-card,
  .phone-wide .sync-card { max-width: 900px; margin-left: auto; margin-right: auto; }
  .phone-wide .ticket-list { max-width: 1100px; margin-left: auto; margin-right: auto; }
  .phone-wide .section-label,
  .phone-wide .section-label-row,
  .phone-wide .empty-state { max-width: 1100px; margin-left: auto; margin-right: auto; }
  .phone-wide .earnings-form-grid { grid-template-columns: 1fr 1fr 1fr; }
}
.app-footer {
  text-align: center; font-size: 9.5px; letter-spacing: 1.5px; color: var(--text-dim);
  text-transform: uppercase; padding-top: 14px; opacity: 0.55;
}
@media (min-width: 480px) {
  .phone {
    margin: 24px 0;
    border-radius: 32px;
    box-shadow: 0 0 0 8px #05070a, 0 20px 60px rgba(0,0,0,0.5), 0 0 90px -30px rgba(255,166,48,0.25);
    min-height: 720px;
  }
}
.loading { margin: auto; color: var(--text-muted); font-size: 13px; display: flex; flex-direction: column; align-items: center; gap: 10px; }

.toast {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--surface-3);
  border: 1px solid var(--border);
  color: var(--text);
  font-size: 12.5px;
  padding: 8px 14px;
  border-radius: 999px;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  animation: toastIn 0.3s cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
}
@keyframes toastIn { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }

/* Home */
.home { justify-content: center; padding-top: 28px; gap: 20px; position: relative; overflow: visible; }
.home-anim { opacity: 0; animation: homeFadeUp 0.55s cubic-bezier(.22,1,.36,1) both; }
@keyframes homeFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
.home-blob {
  position: absolute; border-radius: 50%; pointer-events: none; filter: blur(30px); z-index: 0;
  animation: blobFloat 9s ease-in-out infinite;
}
.home-blob-1 {
  width: 200px; height: 200px; top: -40px; right: -60px;
  background: radial-gradient(circle, rgba(255,166,48,0.16), transparent 70%);
}
.home-blob-2 {
  width: 220px; height: 220px; bottom: 40px; left: -80px;
  background: radial-gradient(circle, rgba(110,231,183,0.10), transparent 70%);
  animation-delay: -4s;
}
@keyframes blobFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(10px, -16px) scale(1.08); }
}
.home-bg-glow {
  position: absolute; top: -60px; left: 50%; transform: translateX(-50%);
  width: 320px; height: 320px; border-radius: 50%; pointer-events: none;
  background: radial-gradient(circle, rgba(255,166,48,0.10), transparent 70%);
  filter: blur(4px);
}
.home-hero { text-align: center; margin-bottom: 8px; display: flex; flex-direction: column; align-items: center; gap: 7px; cursor: default; position: relative; z-index: 1; }
.hero-icon-ring {
  width: 58px; height: 58px; border-radius: 50%; margin-bottom: 4px;
  display: flex; align-items: center; justify-content: center;
  background: radial-gradient(circle, rgba(255,166,48,0.18), rgba(255,166,48,0.02));
  border: 1px solid rgba(255,166,48,0.28);
  box-shadow: 0 0 30px rgba(255,166,48,0.12);
  animation: heroRingPulse 3.2s ease-in-out infinite;
}
@keyframes heroRingPulse {
  0%, 100% { box-shadow: 0 0 30px rgba(255,166,48,0.12), 0 0 0 0 rgba(255,166,48,0.14); }
  50% { box-shadow: 0 0 34px rgba(255,166,48,0.22), 0 0 0 8px rgba(255,166,48,0.03); }
}
.hero-icon { color: var(--amber); }
.hero-icon-img { width: 30px; height: auto; object-fit: contain; }
.hero-eyebrow {
  font-size: 10.5px; letter-spacing: 3px; text-transform: uppercase; color: var(--text-dim);
}
.brand-name {
  font-family: 'Space Mono', monospace; font-size: 32px; font-weight: 700; line-height: 1.1;
  color: var(--amber); letter-spacing: 0.5px;
}
.brand-accent { color: var(--text); }
.brand-name-light {
  font-family: 'Space Mono', monospace; font-weight: 400; color: var(--text-muted);
  font-size: 14px; letter-spacing: 2px; text-transform: uppercase; margin-top: -2px;
}
.road-strip {
  width: 90px; height: 3px; margin: 2px 0; border-radius: 2px; opacity: 0.55;
  background: repeating-linear-gradient(90deg, var(--amber-dim) 0 10px, transparent 10px 20px);
  background-size: 40px 3px;
  animation: roadMove 1.4s linear infinite;
}
@keyframes roadMove { from { background-position-x: 0; } to { background-position-x: -40px; } }
.brand-tagline { font-size: 12px; color: var(--text-muted); letter-spacing: 0.2px; }
.partners-strip { display: flex; gap: 14px; align-items: center; justify-content: center; position: relative; z-index: 1; margin-bottom: 26px; }
.partner-logo-box {
  height: 34px; width: 68px; display: flex; align-items: center; justify-content: center;
  background: var(--surface); border: 1px solid var(--border); border-radius: 10px;
}
.partner-logo {
  max-height: 16px; max-width: 46px; width: auto; height: auto; object-fit: contain; opacity: 0.9;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
  transition: opacity 0.15s ease;
}
.partner-logo-box:hover .partner-logo { opacity: 1; }
.big-card {
  position: relative; overflow: hidden; z-index: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 24px 18px; color: var(--amber);
  cursor: pointer; text-align: center; width: 100%;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.2s ease;
}
.big-card:hover { border-color: var(--amber-dim); transform: translateY(-2px); box-shadow: 0 10px 26px rgba(255,166,48,0.14); }
.big-card:active { transform: scale(0.98); }
.big-card-shine {
  position: absolute; top: 0; left: -60%; width: 40%; height: 100%;
  background: linear-gradient(120deg, transparent, rgba(255,255,255,0.06), transparent);
  transform: skewX(-18deg); animation: bigCardShine 3.5s ease-in-out infinite;
}
@keyframes bigCardShine { 0% { left: -60%; } 45%, 100% { left: 130%; } }
.big-card-icon {
  width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,166,48,0.12); color: var(--amber);
}
.big-card-text { text-align: center; }
.big-card-title { color: var(--text); font-size: 15px; font-weight: 600; }
.big-card-sub { color: var(--text-muted); font-size: 12.5px; margin-top: 2px; }
.link-btn {
  margin-top: auto; background: none; border: none; color: var(--text-dim);
  font-size: 13px; text-decoration: underline; cursor: pointer; padding: 10px;
}
.link-btn:hover { color: var(--text-muted); }


.news-widget {
  background: linear-gradient(160deg, rgba(255,166,48,0.1), var(--surface));
  border: 1px solid rgba(255,166,48,0.25); border-radius: var(--radius);
  padding: 14px 16px; margin-bottom: 14px;
}
.news-widget-header {
  display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 700;
  color: var(--amber); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 10px;
}
.news-widget-list { display: flex; flex-direction: column; gap: 9px; }
.news-item { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.news-item-text { font-size: 13px; color: var(--text); line-height: 1.4; }
.news-item-date { font-size: 10.5px; color: var(--text-dim); flex-shrink: 0; margin-top: 1px; }
.news-admin-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 13px 14px; margin-bottom: 10px;
}
.news-admin-text { font-size: 13.5px; color: var(--text); line-height: 1.5; }
.news-admin-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 8px; }
.news-admin-date { font-size: 10.5px; color: var(--text-dim); font-family: 'Space Mono', monospace; }
.push-status-card {
  display: flex; align-items: center; gap: 10px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 11px 14px; margin-bottom: 8px;
}
.push-status-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.push-status-dot.green { background: var(--green); }
.push-status-dot.red { background: var(--red); }
.push-status-dot.amber { background: var(--amber); }
.push-status-info { flex: 1; min-width: 0; }
.push-status-name { font-size: 13px; font-weight: 600; color: var(--text); }
.push-status-device { font-size: 11px; color: var(--text-dim); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.push-status-label { font-size: 11px; font-weight: 700; flex-shrink: 0; }
.push-status-label.green { color: var(--green); }
.push-status-label.red { color: var(--red); }
.push-status-label.amber { color: var(--amber); }

.leaderboard {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 16px; animation: screenIn 0.4s ease both; animation-delay: 0.1s;
}
.leaderboard-header {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; letter-spacing: 0.4px; color: var(--amber);
}
.leaderboard-period { font-size: 11px; color: var(--text-dim); margin: 3px 0 12px; }
.leaderboard-list { display: flex; flex-direction: column; gap: 10px; }
.leaderboard-row { display: flex; align-items: center; gap: 10px; }
.rank-badge {
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Space Mono', monospace; font-size: 11px; font-weight: 700;
}
.rank-gold { background: rgba(255,199,84,0.18); color: #ffc754; }
.rank-silver { background: rgba(200,208,218,0.18); color: #c8d0da; }
.rank-bronze { background: rgba(205,141,90,0.18); color: #cd8d5a; }
.leaderboard-name { flex: 1; font-size: 13px; color: var(--text); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.leaderboard-car {
  display: flex; align-items: center; gap: 4px; flex-shrink: 0; max-width: 96px;
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-dim);
  font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 999px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.leaderboard-amount { font-family: 'Space Mono', monospace; font-size: 13px; color: var(--amber); flex-shrink: 0; }

/* Top bar */
.topbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.topbar-title { font-size: 14.5px; font-weight: 600; color: var(--text); }
.icon-btn {
  background: var(--surface); border: 1px solid var(--border); color: var(--text);
  width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: border-color 0.15s ease, transform 0.1s ease;
}
.icon-btn:hover { border-color: var(--amber-dim); }
.icon-btn:active { transform: scale(0.93); }
.icon-btn-spacer { width: 34px; height: 34px; }
.ghost-icon { border-color: transparent; background: transparent; color: var(--text-dim); width: 28px; height: 28px; }

/* PIN screen */
.pin-wrap {
  position: relative;
  margin: 68px 0 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 18px;
  text-align: center;
  font-family: 'Space Mono', monospace;
  font-size: 24px;
  letter-spacing: 6px;
  color: var(--amber);
  min-height: 32px;
}
.pin-wrap.shake { animation: shake 0.4s ease; border-color: var(--red); }
.admin-lock-icon {
  width: 52px; height: 52px; border-radius: 50%; margin: 24px auto 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,166,48,0.12); border: 1px solid rgba(255,166,48,0.3); color: var(--amber);
}
.admin-pw-wrap { text-align: left; font-size: 15px; letter-spacing: normal; }
.admin-pw-input {
  width: 100%; background: none; border: none; color: var(--text);
  font-family: 'IBM Plex Sans', sans-serif; font-size: 16px; padding-right: 30px;
}
.admin-pw-input:focus { outline: none; }
.admin-pw-input::placeholder { color: var(--text-dim); }
@keyframes shake {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-5px); }
  40%, 60% { transform: translateX(5px); }
}
.pin-placeholder { font-family: 'IBM Plex Sans', sans-serif; font-size: 13px; letter-spacing: normal; color: var(--text-dim); }
.eye-btn {
  position: absolute; right: 10px; top: 10px; background: none; border: none;
  color: var(--text-dim); cursor: pointer; padding: 6px;
}
.error-line {
  display: flex; align-items: center; gap: 6px; color: var(--red);
  font-size: 12.5px; margin: 6px 2px 4px;
}
.keypad {
  margin-top: auto;
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
  padding-top: 20px;
}
.key {
  background: var(--surface); border: 1px solid var(--border); color: var(--text);
  font-family: 'Space Mono', monospace; font-size: 19px;
  border-radius: 12px; padding: 16px 0; cursor: pointer;
  transition: transform 0.1s ease, background 0.1s ease;
}
.key:active { background: var(--surface-2); transform: scale(0.93); }
.key-action { display: flex; align-items: center; justify-content: center; color: var(--text-muted); }
.key-confirm { color: var(--bg); background: var(--amber); border-color: var(--amber); }

/* Dashboard */
.dash-header-row { display: flex; align-items: center; gap: 12px; margin-top: 10px; }
.avatar {
  width: 42px; height: 42px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, rgba(255,166,48,0.22), rgba(255,166,48,0.06));
  border: 1px solid rgba(255,166,48,0.3);
  color: var(--amber); font-family: 'Space Mono', monospace; font-size: 14px; font-weight: 700;
}
.avatar-wrap { position: relative; flex-shrink: 0; }
.avatar-btn { cursor: pointer; font-size: 18px; transition: transform 0.15s ease; padding: 0; }
.avatar-btn:hover { transform: scale(1.05); }
.avatar-btn:active { transform: scale(0.94); }
.avatar-edit-badge {
  position: absolute; bottom: -2px; right: -2px; width: 16px; height: 16px; border-radius: 50%;
  background: var(--amber); color: var(--bg); display: flex; align-items: center; justify-content: center;
  border: 2px solid var(--bg); pointer-events: none;
}
.avatar-picker {
  position: absolute; top: 50px; left: 0; z-index: 50; width: 220px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 14px;
  padding: 12px; box-shadow: 0 16px 40px rgba(0,0,0,0.45);
  animation: bonusCardIn 0.25s cubic-bezier(.34,1.56,.64,1) both;
}
.avatar-picker-title { font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; }
.avatar-picker-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.avatar-emoji-btn {
  width: 100%; aspect-ratio: 1; border-radius: 10px; font-size: 19px; cursor: pointer;
  background: var(--surface-3); border: 1px solid transparent; display: flex; align-items: center; justify-content: center;
  transition: transform 0.12s ease, border-color 0.12s ease;
}
.avatar-emoji-btn:hover { transform: scale(1.08); }
.avatar-emoji-btn.selected { border-color: var(--amber); background: rgba(255,166,48,0.14); }
.avatar-reset-btn {
  width: 100%; margin-top: 10px; background: none; border: none; color: var(--text-dim);
  font-size: 11px; text-decoration: underline; cursor: pointer; padding: 4px 0;
}
.dash-greeting { font-size: 15px; color: var(--text); font-weight: 600; }
.dash-subtitle { font-size: 11.5px; color: var(--text-dim); margin-top: 2px; }
.car-badge {
  margin-left: auto; display: flex; align-items: center; gap: 5px;
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-muted);
  font-size: 11px; padding: 5px 10px; border-radius: 999px; flex-shrink: 0;
}
.car-badge svg { color: var(--amber); }
.dash-hero {
  position: relative;
  overflow: hidden;
  background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
  padding: 24px 18px; text-align: center; margin: 16px 0 20px;
  box-shadow: 0 12px 32px -16px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03);
  animation: heroRise 0.45s cubic-bezier(0.16,1,0.3,1) both;
}
@keyframes heroRise { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
.dash-hero-glow {
  position: absolute; top: -70%; left: 50%; transform: translateX(-50%);
  width: 260px; height: 260px; border-radius: 50%;
  pointer-events: none; filter: blur(14px); animation: cryptoGlowPulse 4s ease-in-out infinite;
}
.dash-hero-amber .dash-hero-glow { background: radial-gradient(circle, rgba(255,166,48,0.22), transparent 70%); }
.dash-hero-green .dash-hero-glow { background: radial-gradient(circle, rgba(110,231,183,0.22), transparent 70%); }
.dash-hero-red .dash-hero-glow { background: radial-gradient(circle, rgba(255,107,107,0.22), transparent 70%); }
.hero-sparkle { position: absolute; top: 14px; right: 16px; color: var(--amber-dim); opacity: 0.6; z-index: 1; }
.dash-hero-green { border-color: rgba(110,231,183,0.35); background: linear-gradient(160deg, rgba(110,231,183,0.08), var(--surface)); }
.dash-hero-red { border-color: rgba(255,107,107,0.35); background: linear-gradient(160deg, rgba(255,107,107,0.08), var(--surface)); }
.dash-hero-amber { border-color: rgba(255,166,48,0.3); }
.dash-hero-label { position: relative; z-index: 1; font-size: 12px; letter-spacing: 2px; color: var(--text-dim); text-transform: uppercase; margin-bottom: 8px; }
.dash-hero-sub { position: relative; z-index: 1; font-size: 12.5px; color: var(--text-muted); margin-top: 6px; }
.digit { font-family: 'Space Mono', monospace; font-variant-numeric: tabular-nums; }
.digit-lg { font-size: 32px; }

.tab-bar { display: flex; flex-wrap: wrap; gap: 8px; margin: 16px 0 4px; }
.tab-btn {
  position: relative;
  flex: 1 1 calc(33.333% - 6px); min-width: 100px; display: flex; align-items: center; justify-content: center; gap: 6px;
  background: var(--surface); border: 1px solid var(--border); color: var(--text-dim);
  font-size: 12.5px; font-weight: 600; padding: 10px 6px; border-radius: 10px; cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, transform 0.15s ease, box-shadow 0.2s ease;
}
.tab-btn:hover { color: var(--text); transform: translateY(-2px); }
.tab-btn:active { transform: translateY(0) scale(0.97); }
.tab-btn.active {
  color: var(--amber); border-color: var(--amber-dim); background: rgba(255,166,48,0.1);
  box-shadow: 0 6px 16px rgba(255,166,48,0.16);
}
.tab-btn.active svg { animation: tabIconPop 0.35s cubic-bezier(.34,1.56,.64,1); }
@keyframes tabIconPop { 0% { transform: scale(0.7); } 60% { transform: scale(1.15); } 100% { transform: scale(1); } }
.tab-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--red); margin-left: 2px; }
.chat-fab {
  position: fixed; bottom: 26px; right: 22px; z-index: 999;
  width: 68px; height: 68px; border-radius: 50%;
  background: linear-gradient(150deg, #ffb84d, var(--amber));
  border: 3px solid #1a1206; color: #1a1206; display: flex; align-items: center; justify-content: center;
  cursor: pointer; box-shadow: 0 10px 30px rgba(255,166,48,0.65), 0 4px 14px rgba(0,0,0,0.5), 0 0 0 5px rgba(255,166,48,0.18);
  transition: transform 0.15s ease;
}
.chat-fab:hover { transform: scale(1.06); }
.chat-fab:active { transform: scale(0.96); }

/* ---------- Podgląd panelu kierowcy z poziomu admina ---------- */
.admin-view-as-wrap { min-height: 100vh; }
.admin-view-as-banner {
  display: flex; align-items: center; gap: 10px; padding: 12px 16px; margin-bottom: 14px;
  background: linear-gradient(135deg, rgba(167,139,250,0.16), rgba(167,139,250,0.05));
  border: 1px solid rgba(167,139,250,0.35); border-radius: 14px; color: var(--purple, #a78bfa);
  font-size: 12.5px; font-weight: 600; flex-wrap: wrap; position: sticky; top: 8px; z-index: 30;
  backdrop-filter: blur(12px);
}
.admin-view-as-banner b { color: var(--text); }
.admin-view-as-banner span { flex: 1; min-width: 200px; }
.admin-view-as-exit {
  display: flex; align-items: center; gap: 6px; padding: 7px 13px; border-radius: 10px;
  border: 1px solid rgba(167,139,250,0.4); background: var(--surface); color: var(--purple, #a78bfa);
  font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.15s ease; flex-shrink: 0;
}
.admin-view-as-exit:hover { background: rgba(167,139,250,0.12); }
.icon-btn-highlight { color: var(--amber); border-color: var(--amber-dim); }
.icon-btn-highlight:hover { background: rgba(255,166,48,0.14); }

/* ---------- Zakładka Dashboard ---------- */
.dash-hero-welcome {
  position: relative; overflow: hidden; border-radius: 22px; padding: 26px 24px; margin-bottom: 20px;
  background: linear-gradient(155deg, rgba(255,166,48,0.1), var(--surface));
  border: 1px solid var(--border-light);
}
.dash-hero-welcome-glow {
  position: absolute; top: -60%; right: -10%; width: 300px; height: 300px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,166,48,0.22), transparent 70%); filter: blur(20px); pointer-events: none;
}
.dash-hero-welcome-label { position: relative; z-index: 1; font-size: 12px; font-weight: 700; color: var(--amber); text-transform: uppercase; letter-spacing: 0.6px; }
.dash-hero-welcome-title { position: relative; z-index: 1; font-size: 21px; font-weight: 800; margin-top: 6px; letter-spacing: -0.3px; }

.admin-glance-row { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
.admin-glance-card {
  flex: 1; min-width: 140px; position: relative; overflow: hidden;
  background: linear-gradient(160deg, var(--surface-2), var(--surface));
  border: 1px solid var(--border); border-radius: 16px; padding: 16px 16px 13px;
  animation: tileIn 0.4s cubic-bezier(.22,1,.36,1) both;
}
.admin-glance-card-wide { flex: 2; min-width: 220px; }
.admin-glance-icon {
  width: 32px; height: 32px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  margin-bottom: 9px;
}
.glance-amber { background: rgba(255,166,48,0.14); color: var(--amber); }
.glance-blue { background: rgba(77,141,255,0.14); color: #7aabff; }
.glance-gold { background: rgba(255,196,77,0.18); color: #ffc44d; }
.admin-glance-value { font-size: 26px; font-weight: 800; font-family: 'Space Mono', monospace; color: var(--text); line-height: 1; }
.admin-glance-value-sm { font-size: 16px; font-family: inherit; font-weight: 700; }
.admin-glance-value-sm span { color: var(--amber); margin-left: 5px; }
.admin-glance-label { font-size: 11px; color: var(--text-dim); margin-top: 5px; text-transform: uppercase; letter-spacing: 0.3px; }

.dash-two-col { display: flex; gap: 20px; flex-wrap: wrap; }
.dash-col { flex: 1; min-width: 300px; }
.glass-empty { background: var(--surface-2); border-radius: 16px; border: 1px solid var(--border); padding: 30px 16px; }

.attention-list { display: flex; flex-direction: column; gap: 8px; }
.attention-item {
  display: flex; align-items: center; gap: 12px; width: 100%; padding: 13px 14px; border-radius: 14px;
  background: var(--surface-2); border: 1px solid var(--border); cursor: pointer; text-align: left;
  font-family: inherit; transition: all 0.15s ease; animation: fadeUp 0.35s ease both;
}
.attention-item:hover { border-color: var(--amber-dim); transform: translateX(2px); }
.attention-item-icon {
  width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
}
.attention-tone-amber { background: rgba(255,166,48,0.14); color: var(--amber); }
.attention-tone-blue { background: rgba(77,141,255,0.14); color: #7aabff; }
.attention-tone-red { background: rgba(255,107,107,0.14); color: var(--red); }
.attention-item-text { flex: 1; font-size: 13px; color: var(--text); font-weight: 600; }
.attention-item-arrow { color: var(--text-dim); transform: rotate(180deg); flex-shrink: 0; }

.recent-msg-list { display: flex; flex-direction: column; gap: 6px; }
.recent-msg-item {
  display: flex; align-items: center; gap: 11px; width: 100%; padding: 10px 12px; border-radius: 13px;
  background: var(--surface-2); border: 1px solid var(--border); cursor: pointer; text-align: left;
  font-family: inherit; transition: all 0.15s ease; animation: fadeUp 0.35s ease both; position: relative;
}
.recent-msg-item:hover { border-color: var(--amber-dim); }
.recent-msg-item.unread { border-color: rgba(77,141,255,0.35); background: linear-gradient(160deg, rgba(77,141,255,0.08), var(--surface-2)); }
.recent-msg-body { flex: 1; min-width: 0; }
.recent-msg-name { font-size: 13px; font-weight: 700; color: var(--text); }
.recent-msg-preview { font-size: 11.5px; color: var(--text-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-top: 1px; }
.recent-msg-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--blue, #4d8dff); flex-shrink: 0; }

/* ---------- Wykres zarobków na Dashboardzie ---------- */
.dash-chart-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: 20px;
  padding: 20px 20px 8px; margin-bottom: 20px;
}
.dash-chart-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
.dash-chart-toggle { display: flex; gap: 4px; background: var(--surface-2); border-radius: 10px; padding: 3px; }
.dash-chart-toggle-btn {
  padding: 6px 13px; border-radius: 8px; border: none; background: none; color: var(--text-dim);
  font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; transition: all 0.15s ease;
}
.dash-chart-toggle-btn.active { background: var(--amber); color: #1a1206; }
.dash-chart-empty { padding: 30px 10px; text-align: center; color: var(--text-dim); font-size: 12.5px; }

.dash-insight-top { display: flex; align-items: baseline; gap: 10px; margin-bottom: 8px; }
.dash-insight-value { font-size: 32px; font-weight: 800; font-family: 'Space Mono', monospace; color: var(--text); letter-spacing: -0.5px; }
.dash-insight-badge {
  display: inline-flex; align-items: center; gap: 3px; padding: 4px 10px; border-radius: 999px;
  font-size: 12.5px; font-weight: 800; font-family: 'Space Mono', monospace;
}
.dash-insight-badge.up { background: rgba(74,222,144,0.14); color: var(--green, #4ade90); }
.dash-insight-badge.down { background: rgba(255,107,107,0.14); color: var(--red); }
.dash-insight-text { font-size: 13px; color: var(--text-muted); line-height: 1.5; margin-bottom: 16px; }
.dash-insight-text b { color: var(--text); }
.dash-sparkline { width: 100%; height: 90px; display: block; overflow: visible; }
.dash-sparkline-labels { display: flex; justify-content: space-between; margin-top: 6px; padding: 0 1px; }
.dash-sparkline-labels span { font-size: 10px; color: var(--text-dim); font-weight: 600; }

/* ---------- Zadania per kierowca na Dashboardzie ---------- */
.dash-tasks-card { background: var(--surface); border: 1px solid var(--border); border-radius: 20px; padding: 18px 20px; }
.dash-tasks-header {
  display: flex; align-items: center; justify-content: space-between; width: 100%;
  background: none; border: none; cursor: pointer; padding: 0;
}
.dash-tasks-list { display: flex; flex-direction: column; gap: 10px; margin-top: 16px; }
.dash-tasks-row { display: flex; align-items: center; gap: 12px; animation: fadeUp 0.3s ease both; }
.dash-tasks-info { flex: 1; min-width: 0; }
.dash-tasks-name { font-size: 12.5px; font-weight: 700; color: var(--text); margin-bottom: 4px; }
.dash-tasks-bar-track { height: 6px; border-radius: 4px; background: var(--surface-3); overflow: hidden; }
.dash-tasks-bar-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, var(--amber), var(--amber-light, #ffb84d)); transition: width 0.4s ease; }
.dash-tasks-bar-fill-gift { background: linear-gradient(90deg, var(--purple, #a78bfa), #c4b5fd); }
.dash-tasks-count { flex-shrink: 0; font-family: 'Space Mono', monospace; font-size: 12.5px; text-align: right; }
.dash-tasks-count-done { color: var(--green, #4ade90); font-weight: 800; }
.dash-tasks-count-missed { color: var(--text-dim); font-size: 10.5px; margin-left: 3px; }

/* ---------- Zyskowność aut na Dashboardzie ---------- */
.dash-car-spotlight { display: flex; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.dash-car-spot {
  flex: 1; min-width: 190px; display: flex; align-items: center; gap: 11px; padding: 13px 14px;
  border-radius: 15px; position: relative; overflow: hidden; animation: fadeUp 0.4s ease both;
  transition: transform 0.2s cubic-bezier(.22,1,.36,1), box-shadow 0.2s ease;
}
.dash-car-spot:hover { transform: translateY(-2px); }
.dash-car-spot-best:hover { box-shadow: 0 10px 26px -8px rgba(74,222,144,0.35); }
.dash-car-spot-worst:hover { box-shadow: 0 10px 26px -8px rgba(255,107,107,0.35); }
.dash-car-spot-best {
  background: linear-gradient(135deg, rgba(74,222,144,0.12), rgba(74,222,144,0.02));
  border: 1px solid rgba(74,222,144,0.3);
}
.dash-car-spot-worst {
  background: linear-gradient(135deg, rgba(255,107,107,0.12), rgba(255,107,107,0.02));
  border: 1px solid rgba(255,107,107,0.3); animation-delay: 0.08s;
}
.dash-car-spot-icon {
  width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
}
.dash-car-spot-best .dash-car-spot-icon { background: rgba(74,222,144,0.18); color: var(--green, #4ade90); animation: dashSpotPulse 2.4s ease-in-out infinite; }
.dash-car-spot-worst .dash-car-spot-icon { background: rgba(255,107,107,0.18); color: var(--red); animation: dashSpotPulse 2.4s ease-in-out infinite; animation-delay: 0.3s; }
@keyframes dashSpotPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.1); } }
.dash-car-spot-label { font-size: 10px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.4px; font-weight: 700; }
.dash-car-spot-name { font-size: 13.5px; font-weight: 800; color: var(--text); margin-top: 2px; }
.dash-car-spot-value { margin-left: auto; font-family: 'Space Mono', monospace; font-size: 14px; font-weight: 800; flex-shrink: 0; }
.dash-car-spot-value.up { color: var(--green, #4ade90); }
.dash-car-spot-value.down { color: var(--red); }
.dash-car-icon-mini {
  width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
}
.dash-car-icon-mini.up { background: rgba(74,222,144,0.14); color: var(--green, #4ade90); }
.dash-car-icon-mini.down { background: rgba(255,107,107,0.14); color: var(--red); }
.dash-tasks-bar-fill-neg { background: linear-gradient(90deg, #ff9b9b, var(--red)); }
.dash-tasks-count-neg { color: var(--red); font-weight: 800; }
@keyframes tileIn { from { opacity: 0; transform: translateY(8px) scale(0.96); } to { opacity: 1; transform: none; } }

/* ---------- Panel admina: sidebar (elegancki, z logo i wylogowaniem) ---------- */
.admin-layout { display: flex; flex-direction: column; gap: 0; align-items: stretch; }
.admin-main-content { flex: 1; min-width: 0; }

/* Mobile (domyślnie): logo + dzwonek w jednym rzędzie na górze, a pod nimi
   pozioma, przewijalna belka zakładek — pełny, pionowy sidebar nie miałby
   sensu na wąskim ekranie telefonu. */
.admin-sidebar {
  display: flex; flex-direction: column; gap: 8px;
  padding: 8px 4px 12px; margin-bottom: 4px;
  border-bottom: 1px solid var(--border); width: 100%;
}
.admin-sidebar-top-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 0 4px 10px; }
.admin-sidebar-top-title { font-size: 13px; font-weight: 700; color: var(--text-muted); }
/* Duże logo na dole sidebara, pod "Wyloguj" — pełna szerokość, bez tła i
   obramowania, żeby zlewało się z tłem paska, zamiast wyglądać jak
   niedopasowany "kwadracik". Widoczne tylko na desktopie (patrz media
   query niżej) — na telefonie sidebar jest poziomym paskiem bez miejsca
   na coś tak dużego. */
.admin-sidebar-logo-footer { display: none; }
.admin-sidebar-nav { display: flex; flex-direction: row; gap: 6px; overflow-x: auto; overflow-y: hidden; padding-bottom: 2px; }
.admin-sidebar-btn {
  position: relative; display: flex; flex-direction: column; align-items: center; gap: 4px;
  flex-shrink: 0; padding: 9px 12px; border-radius: 13px; border: 1px solid var(--border);
  background: var(--surface-2); color: var(--text-muted); cursor: pointer; font-family: inherit;
  transition: all 0.18s ease; animation: tileIn 0.35s cubic-bezier(.22,1,.36,1) both;
}
.admin-sidebar-btn-label { font-size: 9.5px; font-weight: 700; white-space: nowrap; }
.admin-sidebar-btn.active {
  background: linear-gradient(160deg, rgba(255,166,48,0.18), rgba(255,166,48,0.05));
  border-color: var(--amber); color: var(--text);
  box-shadow: 0 4px 14px rgba(255,166,48,0.2);
}
.admin-sidebar-dot {
  position: absolute; top: 5px; right: 8px; width: 8px; height: 8px; border-radius: 50%;
  background: var(--red); border: 2px solid var(--surface-2); animation: bonusPulse 1.8s ease-in-out infinite;
}
.admin-sidebar-logout { display: none; }

/* Desktop (phone-wide): prawdziwy, pionowy sidebar z boku, z logo u góry i
   wylogowaniem na dole — dokładnie jak w profesjonalnych panelach admina. */
/* Prawdziwe zapytanie o szerokość ekranu — NIE klasa phone-wide, bo ta
   włącza się zawsze w widoku admina, niezależnie od tego, czy oglądasz to
   na telefonie czy komputerze. Sidebar z boku ma sens tylko na
   rzeczywiście szerokim ekranie. */
@media (min-width: 900px) {
  .admin-layout { flex-direction: row; gap: 0; align-items: stretch; min-height: 100vh; }
  .admin-sidebar {
    flex-direction: column; align-items: stretch; width: 268px; flex-shrink: 0;
    overflow: visible; padding: 4px 16px 16px; margin: 0 28px 0 0; border-bottom: none;
    border-right: 1px solid var(--border); position: sticky; top: 0; height: 100vh; align-self: flex-start;
  }
  .admin-sidebar-top-row { padding: 6px 4px 20px; }
  .admin-sidebar-top-title { font-size: 15px; font-weight: 800; color: var(--text); letter-spacing: -0.2px; }
  .admin-sidebar-nav { flex-direction: column; gap: 3px; overflow-y: auto; }
  .admin-sidebar-btn {
    flex-direction: row; justify-content: flex-start; gap: 12px; width: 100%;
    padding: 12px 14px; border-radius: 12px; border: 1px solid transparent; background: none;
  }
  .admin-sidebar-btn:hover { background: var(--surface-2); color: var(--text); }
  .admin-sidebar-btn.active {
    background: linear-gradient(135deg, rgba(255,166,48,0.16), rgba(255,166,48,0.04));
    border-color: var(--amber-dim); box-shadow: none;
  }
  .admin-sidebar-btn-label { font-size: 14px; }
  .admin-sidebar-dot { top: 50%; right: 12px; transform: translateY(-50%); }
  .admin-sidebar-logout {
    display: flex; align-items: center; gap: 10px; margin-top: 18px; padding: 12px 14px;
    border-radius: 12px; border: 1px solid var(--border); background: none; color: var(--text-dim);
    cursor: pointer; font-size: 13px; font-weight: 600; font-family: inherit; transition: all 0.15s ease;
  }
  .admin-sidebar-logout:hover { border-color: var(--red); color: var(--red); }

  /* Duże logo firmy, pełna szerokość sidebara, pod przyciskiem "Wyloguj" —
     bez tła/obramowania/cienia, żeby obrazek po prostu "leżał" na pasku,
     zamiast wyglądać jak osobny, niedopasowany element. */
  .admin-sidebar-logo-footer {
    display: block; margin-top: 20px; padding-top: 18px; border-top: 1px solid var(--border);
    cursor: pointer; border-radius: 10px; transition: opacity 0.15s ease;
  }
  .admin-sidebar-logo-footer:hover { opacity: 0.82; }
  .admin-sidebar-logo-footer-img { display: block; width: 100%; height: auto; max-height: 120px; object-fit: contain; }
  .admin-sidebar-logo-footer-empty {
    display: flex; align-items: center; justify-content: center; gap: 7px; width: 100%; padding: 16px 8px;
    border: 1.5px dashed var(--border-light); border-radius: 12px; color: var(--text-dim); font-size: 12px; font-weight: 600;
  }
  .admin-sidebar-logo-footer:hover .admin-sidebar-logo-footer-empty { border-color: var(--amber-dim); color: var(--amber); }
}

/* ---------- Dzwonek powiadomień ---------- */
.admin-notif-wrap { position: relative; margin-left: auto; }
.admin-notif-bell {
  position: relative; width: 36px; height: 36px; border-radius: 11px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-muted);
  cursor: pointer; transition: all 0.15s ease;
}
.admin-notif-bell:hover { color: var(--amber); border-color: var(--amber-dim); }
.admin-notif-dot {
  position: absolute; top: 6px; right: 7px; width: 9px; height: 9px; border-radius: 50%;
  background: var(--red); border: 2px solid var(--surface-2); animation: bonusPulse 1.8s ease-in-out infinite;
}
.admin-notif-dropdown {
  position: absolute; top: calc(100% + 8px); right: 0; z-index: 40; width: 300px; max-width: 80vw;
  background: var(--surface); border: 1px solid var(--border-light); border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.4); padding: 8px; animation: popIn 0.18s ease both;
}
.admin-notif-empty { padding: 20px 12px; text-align: center; color: var(--text-dim); font-size: 12.5px; }
.admin-notif-item {
  display: flex; align-items: center; gap: 11px; width: 100%; padding: 10px; border-radius: 11px;
  border: none; background: none; text-align: left; cursor: pointer; font-family: inherit; transition: background 0.15s ease;
}
.admin-notif-item:hover { background: var(--surface-2); }
.admin-notif-item-icon {
  width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.notif-icon-msg { background: rgba(77,141,255,0.14); color: #7aabff; }
.notif-icon-comp { background: rgba(255,166,48,0.14); color: var(--amber); }
.notif-icon-car { background: rgba(255,107,107,0.14); color: var(--red); }
.admin-notif-item-text { font-size: 12.5px; color: var(--text-muted); line-height: 1.35; }
.admin-notif-item-text b { color: var(--text); }

/* Osobna, mniejsza podnawigacja wewnątrz zakładki Finanse — inny cel niż
   główny sidebar, więc świadomie osobne, prostsze klasy. */
.finance-subtile-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)); gap: 8px; margin-bottom: 18px; }
.finance-subtile {
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 13px; padding: 12px 6px 10px;
  cursor: pointer; color: var(--text-muted); font-family: inherit; transition: all 0.15s ease;
}
.finance-subtile:hover { border-color: var(--amber-dim); }
.finance-subtile.active {
  background: linear-gradient(160deg, rgba(255,166,48,0.16), rgba(255,166,48,0.03));
  border-color: var(--amber); color: var(--text);
}
.finance-subtile-icon {
  width: 32px; height: 32px; border-radius: 9px; display: flex; align-items: center; justify-content: center;
  background: var(--surface-3); color: var(--text-dim);
}
.finance-subtile.active .finance-subtile-icon { background: var(--amber); color: #1a1206; }
.finance-subtile-label { font-size: 10px; font-weight: 700; text-align: center; line-height: 1.2; }
.chat-fab-dot {
  position: absolute; top: 6px; right: 6px; width: 13px; height: 13px; border-radius: 50%;
  background: var(--red); border: 2px solid var(--bg);
}
@media (min-width: 480px) {
  .chat-fab { bottom: 40px; right: calc(50% - 195px); }
}
.phone-wide .chat-fab { right: 40px; }
.tab-dot.bonus { background: var(--green); box-shadow: 0 0 6px var(--green); animation: bonusPulse 1.8s ease-in-out infinite; }
@keyframes bonusPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.35); opacity: 0.7; } }

.debt-label { color: var(--red) !important; }
.debt-card {
  background: rgba(255,107,107,0.06); border: 1px solid rgba(255,107,107,0.3);
  border-radius: 10px; padding: 12px 14px; margin-bottom: 10px;
  animation: ticketIn 0.4s ease both;
}
.debt-period { font-size: 13px; font-weight: 600; color: var(--text); }
.debt-note-text {
  display: flex; align-items: flex-start; gap: 6px;
  font-size: 12px; color: var(--text-muted); margin-top: 8px; line-height: 1.5;
}
.debt-note-text svg { flex-shrink: 0; margin-top: 2px; color: var(--red); opacity: 0.8; }

.bonus-label { color: var(--green) !important; }
.bonus-hero {
  position: relative; overflow: hidden;
  background: linear-gradient(155deg, rgba(110,231,183,0.10), var(--surface) 60%);
  border: 1px solid rgba(110,231,183,0.3); border-radius: var(--radius);
  padding: 22px 18px; text-align: center; margin: 12px 0 4px;
  animation: bonusCardIn 0.5s cubic-bezier(.34,1.56,.64,1) both;
}
.bonus-shine {
  position: absolute; top: -50%; left: -20%; width: 60%; height: 200%;
  background: linear-gradient(115deg, transparent, rgba(110,231,183,0.16), transparent);
  transform: skewX(-18deg); animation: bonusSweep 3.2s ease-in-out infinite;
}
@keyframes bonusSweep { 0% { left: -30%; } 45%, 100% { left: 130%; } }
.bonus-hero-icon { color: var(--green); margin: 0 auto 8px; filter: drop-shadow(0 0 10px rgba(110,231,183,0.5)); }
@keyframes bonusCardIn { from { opacity: 0; transform: scale(0.92) translateY(10px); } to { opacity: 1; transform: none; } }

.ranking-view { padding-bottom: 8px; }
.ranking-header { margin-bottom: 14px; }
.ranking-title {
  display: flex; align-items: center; gap: 8px; font-size: 18px; font-weight: 800; color: var(--text);
}
.ranking-title svg { color: var(--amber); animation: rankingTrophyGlow 2.6s ease-in-out infinite; }
@keyframes rankingTrophyGlow {
  0%, 100% { filter: drop-shadow(0 0 0px rgba(255,166,48,0)); }
  50% { filter: drop-shadow(0 0 7px rgba(255,166,48,0.65)); }
}
.ranking-subtitle { font-size: 11.5px; color: var(--text-dim); margin-top: 4px; }

.ranking-rules {
  display: flex; flex-direction: column; gap: 6px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 12px 14px; margin-bottom: 16px;
}
.ranking-rule { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text-muted); }
.ranking-rule b { color: var(--text); }
.ranking-rule-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--green); flex-shrink: 0; }
.ranking-rule-dot.amber { background: var(--amber); }
.ranking-rule-dot.red { background: var(--red); }
.ranking-rule-dot.green { background: var(--green); }

/* --- Najlepszy utarg tygodniowy firmy --- */
.company-best-week-card {
  position: relative; overflow: hidden; text-align: center; border-radius: 20px; padding: 22px 18px;
  margin-bottom: 18px; background: linear-gradient(165deg, #241a0c 0%, #16120c 60%, #120f0d 100%);
  border: 1px solid rgba(255,196,77,0.28);
  box-shadow: 0 12px 32px -14px rgba(255,166,48,0.3), inset 0 1px 0 rgba(255,255,255,0.03);
  animation: cryptoPositionRise 0.4s cubic-bezier(0.16,1,0.3,1) both;
}
.company-best-week-glow {
  position: absolute; top: -70%; left: 50%; transform: translateX(-50%);
  width: 260px; height: 260px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,196,77,0.3), transparent 70%);
  filter: blur(14px); pointer-events: none; animation: cryptoGlowPulse 3.5s ease-in-out infinite;
}
.company-best-week-icon { position: relative; z-index: 1; color: var(--amber); filter: drop-shadow(0 0 10px rgba(255,166,48,0.6)); }
.company-best-week-label {
  position: relative; z-index: 1; font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  color: var(--text-dim); font-weight: 700; margin: 8px 0 6px;
}
.company-best-week-value { position: relative; z-index: 1; font-size: 22px; font-weight: 800; color: var(--text); }
.company-best-week-value span { color: var(--amber); margin-left: 6px; }
.company-best-week-period { position: relative; z-index: 1; font-size: 11px; color: var(--text-dim); margin-top: 6px; font-family: 'Space Mono', monospace; }

/* --- Podium top 3 --- */
.podium { display: flex; align-items: flex-end; justify-content: center; gap: 10px; margin-bottom: 20px; padding: 0 4px; }
.podium-slot {
  flex: 1; max-width: 130px; display: flex; flex-direction: column; align-items: center;
  animation: popIn 0.5s cubic-bezier(.34,1.56,.64,1) both; position: relative;
}
.podium-crown-glow {
  position: absolute; top: -18px; left: 50%; transform: translateX(-50%); z-index: 0;
  width: 100px; height: 100px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,214,106,0.5), transparent 70%);
  filter: blur(10px); pointer-events: none;
  animation: crownGlowPulse 2.2s ease-in-out infinite;
}
@keyframes crownGlowPulse {
  0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(1); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.18); }
}
.podium-slot > *:not(.podium-crown-glow) { position: relative; z-index: 1; }
.ranking-next-tier-hint {
  display: flex; align-items: center; gap: 4px; font-size: 10.5px; color: var(--amber); font-weight: 700;
  margin-top: 5px; background: rgba(255,166,48,0.1); padding: 3px 8px; border-radius: 999px; width: fit-content;
  animation: homeFadeUp 0.3s ease both;
}
.podium-empty { visibility: hidden; }
.podium-place-1 { animation-delay: 0.15s; }
.podium-place-2 { animation-delay: 0.05s; }
.podium-place-3 { animation-delay: 0.25s; }
.podium-medal { font-size: 26px; margin-bottom: 4px; filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4)); }
.podium-place-1 .podium-medal { font-size: 34px; }
.podium-avatar {
  width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 800; color: #1a1206; margin-bottom: 8px; flex-shrink: 0;
}
.podium-place-1 .podium-avatar { width: 60px; height: 60px; font-size: 18px; background: linear-gradient(160deg, #ffe08a, #ffb84d); box-shadow: 0 0 0 3px rgba(255,196,77,0.3), 0 6px 18px -4px rgba(255,166,48,0.5); }
.podium-place-2 .podium-avatar { background: linear-gradient(160deg, #e2e6ec, #b8bfc9); box-shadow: 0 0 0 3px rgba(184,191,201,0.25); }
.podium-place-3 .podium-avatar { background: linear-gradient(160deg, #e3ac7a, #c17f45); box-shadow: 0 0 0 3px rgba(193,127,69,0.25); }
.podium-name { font-size: 12px; font-weight: 700; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.podium-points { font-size: 11px; color: var(--text-dim); font-family: 'Space Mono', monospace; margin-top: 2px; margin-bottom: 8px; }
.podium-bar { width: 100%; border-radius: 8px 8px 0 0; }
.podium-bar-1 { height: 58px; background: linear-gradient(180deg, #ffe08a, #e5942e); box-shadow: 0 0 20px -4px rgba(255,166,48,0.5); }
.podium-bar-2 { height: 40px; background: linear-gradient(180deg, #e2e6ec, #9aa1ac); }
.podium-bar-3 { height: 28px; background: linear-gradient(180deg, #e3ac7a, #a8703c); }
.podium-me .podium-name { color: var(--amber); }

.ranking-subnav { display: flex; gap: 8px; margin-bottom: 14px; }
.ranking-subnav-btn {
  position: relative;
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  background: var(--surface); border: 1px solid var(--border); color: var(--text-dim);
  font-size: 12.5px; font-weight: 600; padding: 9px 0; border-radius: 10px; cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.ranking-subnav-btn.active { color: var(--amber); border-color: var(--amber-dim); background: rgba(255,166,48,0.08); }

.ranking-bonus-callout {
  display: flex; align-items: flex-start; gap: 12px;
  background: linear-gradient(135deg, rgba(255,166,48,0.14), rgba(255,166,48,0.03));
  border: 1px solid rgba(255,166,48,0.3); border-radius: var(--radius);
  padding: 14px 16px; margin-bottom: 16px;
}
.ranking-bonus-callout svg { color: var(--amber); flex-shrink: 0; margin-top: 1px; }
.ranking-bonus-callout-title { font-size: 13px; font-weight: 700; color: var(--amber); margin-bottom: 3px; }
.ranking-bonus-callout-text { font-size: 12px; color: var(--text-muted); line-height: 1.5; }

.task-card {
  display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 13px 14px; margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15); transition: box-shadow 0.2s ease, border-color 0.15s ease;
}
.task-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.22); }
.task-icon-badge {
  width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.task-icon-badge.completed { background: rgba(110,231,183,0.14); color: var(--green); }
.task-icon-badge.pending { background: rgba(255,166,48,0.14); color: var(--amber); }
.task-info { flex: 1; min-width: 0; }
.task-title { font-size: 13.5px; color: var(--text); }
.task-status-tag {
  display: inline-block; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px;
  border-radius: 999px; padding: 2px 8px; margin-left: 8px; vertical-align: middle; flex-shrink: 0;
}
.task-status-tag.active { background: rgba(255,166,48,0.14); color: var(--amber); border: 1px solid var(--amber-dim); }
.task-status-tag.completed { background: var(--surface-2); color: var(--text-dim); border: 1px solid var(--border); }
.task-date { font-size: 10.5px; color: var(--text-dim); margin-top: 3px; font-family: 'Space Mono', monospace; }

/* --- Zadania: karta kierowcy (v2) --- */
.task-card-v2 {
  background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 14px;
  margin-bottom: 10px; animation: ticketIn 0.35s ease both; transition: border-color 0.15s ease, transform 0.15s ease;
}
.task-card-v2:hover { transform: translateY(-1px); }
.task-card-v2-top { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.task-icon-badge-v2 {
  width: 38px; height: 38px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.task-icon-badge-v2.waiting { background: var(--surface-2); color: var(--text-dim); }
.task-icon-badge-v2.done { background: rgba(110,231,183,0.14); color: var(--green); }
.task-icon-badge-v2.missed { background: rgba(255,107,107,0.14); color: var(--red); }
.task-result-tag {
  display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 700;
  border-radius: 10px; padding: 8px 12px;
}
.task-result-tag.waiting { background: var(--surface-2); color: var(--text-muted); }
.task-result-tag.done { background: rgba(110,231,183,0.1); color: var(--green); border: 1px solid rgba(110,231,183,0.3); }
.task-result-tag.missed { background: rgba(255,107,107,0.1); color: var(--red); border: 1px solid rgba(255,107,107,0.3); }

/* --- Zadania: lista kierowców w panelu admina (v2) --- */
.task-driver-row-v2 {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 10px 14px; border-bottom: 1px solid var(--border);
}
.task-driver-row-v2:last-child { border-bottom: none; }
.task-driver-name { font-size: 12.5px; color: var(--text-muted); flex-shrink: 0; }
.task-driver-actions { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.task-mini-btn {
  display: flex; align-items: center; gap: 4px; font-size: 10.5px; font-weight: 700;
  border-radius: 8px; padding: 5px 9px; cursor: pointer; border: 1px solid var(--border);
  background: var(--surface-2); color: var(--text-dim); transition: all 0.15s ease;
}
.task-mini-btn:hover { border-color: var(--amber-dim); }
.task-mini-btn.done.active { background: rgba(110,231,183,0.14); color: var(--green); border-color: rgba(110,231,183,0.4); }
.task-mini-btn.missed.active { background: rgba(255,107,107,0.14); color: var(--red); border-color: rgba(255,107,107,0.4); }
.task-mini-btn.reset { color: var(--text-dim); }
.task-priority-toggle {
  display: flex; align-items: center; gap: 6px; white-space: nowrap;
  padding: 0 14px; height: 42px; border-radius: 10px; font-size: 12.5px; font-weight: 700;
  background: var(--surface-2); border: 1.5px solid var(--border); color: var(--text-muted); cursor: pointer;
  transition: all 0.15s ease;
}
.task-priority-toggle.active { background: rgba(255,107,107,0.14); border-color: var(--red); color: var(--red); }
.task-priority-badge {
  display: inline-flex; align-items: center; gap: 4px; font-size: 9px; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.5px; background: rgba(255,107,107,0.14); color: var(--red); border: 1px solid rgba(255,107,107,0.4);
  border-radius: 999px; padding: 2px 8px; margin-left: 6px; vertical-align: middle;
}
.task-priority-card { border-color: rgba(255,107,107,0.35); box-shadow: 0 0 0 1px rgba(255,107,107,0.08); }
.task-driver-checklist {
  background: var(--surface-2); border: 1px solid var(--border); border-top: none;
  border-radius: 0 0 var(--radius) var(--radius); padding: 6px 14px; margin: -6px 0 10px;
}
.task-driver-row {
  display: flex; align-items: center; justify-content: space-between; padding: 9px 0;
  font-size: 12.5px; color: var(--text); border-bottom: 1px solid var(--border);
}
.task-driver-row:last-child { border-bottom: none; }

.ranking-table { display: flex; flex-direction: column; gap: 8px; }
.ranking-row {
  display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 12px 14px;
  animation: rankingRowIn 0.45s cubic-bezier(.22,1,.36,1) both;
  transition: transform 0.15s ease, border-color 0.15s ease;
}
.ranking-row:hover { transform: translateX(2px); border-color: var(--amber-dim); }
.ranking-row-me { border-color: var(--amber); background: rgba(255,166,48,0.06); }
/* Trzy progi bonusowe — złoty (1-3, to podium, osobna sekcja), srebrny
   (4-6) i brązowy (7+) — każdy z osobnym akcentem koloru na lewej
   krawędzi wiersza, żeby od razu było widać, w jakim progu jest dany
   kierowca, bez czytania samej liczby punktów. */
.ranking-row-tier-silver { border-left: 3px solid #c9ced6; background: rgba(201,206,214,0.05); }
.ranking-row-tier-bronze { border-left: 3px solid #b8794a; background: rgba(184,121,74,0.04); }
.ranking-row-tier-silver.ranking-row-me { background: rgba(255,166,48,0.06); border-left-color: #c9ced6; }
.ranking-row-tier-bronze.ranking-row-me { background: rgba(255,166,48,0.06); border-left-color: #b8794a; }
.ranking-tier-badge {
  font-size: 9.5px; font-weight: 700; padding: 2px 7px; border-radius: 999px; margin-top: 3px; white-space: nowrap;
}
.ranking-tier-badge.tier-silver { background: rgba(201,206,214,0.16); color: #c9ced6; }
.ranking-tier-badge.tier-bronze { background: rgba(184,121,74,0.16); color: #d99a63; }
.podium-bonus-badge {
  font-size: 10px; font-weight: 700; color: #1a1206; background: linear-gradient(135deg, #ffd76a, #e8a33d);
  padding: 2px 9px; border-radius: 999px; margin-top: 4px;
}
.ranking-bonus-callout-tiers { align-items: flex-start; }
.ranking-tier-legend { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
.ranking-tier-legend-row { font-size: 12px; color: var(--text-muted); display: flex; align-items: center; gap: 6px; }
.ranking-tier-legend-row b { color: var(--text); margin-left: auto; }
.ranking-tier-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.ranking-tier-dot.tier-gold { background: linear-gradient(135deg, #ffd76a, #e8a33d); }
.ranking-tier-dot.tier-silver { background: #c9ced6; }
.ranking-tier-dot.tier-bronze { background: #b8794a; }

@keyframes rankingRowIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: none; }
}

.ranking-rank {
  width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; color: var(--text-dim); background: var(--surface-2); flex-shrink: 0;
  position: relative;
}
.ranking-rank.gold { background: linear-gradient(135deg, #ffd76a, #e8a33d); color: #1a1206; }
.ranking-rank.silver { background: linear-gradient(135deg, #e6e9ee, #b8bec8); color: #1a1206; }
.ranking-rank.bronze { background: linear-gradient(135deg, #d99a63, #a8632f); color: #1a1206; }

.ranking-avatar {
  width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-dim);
  font-size: 12px; font-weight: 700; flex-shrink: 0;
}

.ranking-info { flex: 1; min-width: 0; }
.ranking-name {
  display: flex; align-items: center; gap: 7px; font-size: 13.5px; font-weight: 600; color: var(--text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.ranking-me-tag {
  font-size: 9.5px; font-weight: 700; color: #1a1206; background: var(--amber); padding: 1px 6px; border-radius: 999px;
  flex-shrink: 0;
}
.ranking-bar-track { height: 4px; border-radius: 999px; background: var(--surface-2); margin-top: 6px; overflow: hidden; }
.ranking-bar-fill {
  height: 100%; border-radius: 999px; background: var(--green);
  transition: width 0.6s cubic-bezier(.22,1,.36,1);
}
.ranking-bar-fill.gold { background: linear-gradient(90deg, #e8a33d, #ffd76a); }
.ranking-bar-fill.silver { background: linear-gradient(90deg, #b8bec8, #e6e9ee); }
.ranking-bar-fill.bronze { background: linear-gradient(90deg, #a8632f, #d99a63); }
.ranking-car {
  display: flex; align-items: center; gap: 4px; font-size: 10px; color: var(--text-dim); margin-top: 5px;
}

.ranking-points { text-align: center; flex-shrink: 0; min-width: 44px; }
.ranking-points-value { font-family: 'Space Mono', monospace; font-size: 18px; font-weight: 800; color: var(--amber); line-height: 1; }
.ranking-points-label { font-size: 9px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2px; }

.bonus-card {
  display: flex; align-items: center; gap: 12px;
  background: var(--surface); border: 1px solid rgba(110,231,183,0.22);
  border-radius: 10px; padding: 12px 14px; margin-bottom: 10px;
  animation: bonusCardIn 0.45s cubic-bezier(.34,1.56,.64,1) both;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.2s ease;
}
.bonus-card:hover { border-color: rgba(110,231,183,0.5); transform: translateY(-2px); }
.bonus-card-expired {
  border-color: rgba(255,107,107,0.28); background: rgba(255,107,107,0.045); opacity: 0.85;
}
.bonus-card-expired:hover { border-color: rgba(255,107,107,0.5); }
.bonus-card-expired .bonus-icon-badge { background: rgba(255,107,107,0.14); color: var(--red); }
.bonus-card-expired .bonus-amount { color: var(--red); text-decoration: line-through; text-decoration-color: rgba(255,107,107,0.6); }
.bonus-tag-expired { background: rgba(255,107,107,0.16); color: var(--red); }
.bonus-tag-upcoming { background: rgba(255,166,48,0.14); color: var(--amber); }
.bonus-tag-upcoming-strong {
  display: inline-flex; align-items: center; gap: 3px;
  background: rgba(255,166,48,0.22); color: var(--amber); border: 1px solid var(--amber);
  font-weight: 800; box-shadow: 0 0 8px rgba(255,166,48,0.25);
}
.bonus-card-upcoming { border-color: rgba(255,166,48,0.28); opacity: 0.9; }
.bonus-card-upcoming .bonus-icon-badge { background: rgba(255,166,48,0.14); color: var(--amber); }
.bonus-card-upcoming .bonus-amount { color: var(--amber); }
.bonus-tag-completed { background: rgba(110,231,183,0.16); color: var(--green); }
.bonus-card-completed { border-color: rgba(110,231,183,0.35); }

.bonus-card-admin { flex-wrap: wrap; }
.bonus-admin-actions {
  width: 100%; display: flex; justify-content: flex-end; align-items: center; gap: 8px;
  margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--border);
}
.status-select {
  flex: 1; min-width: 0; background: var(--surface-2); border: 1px solid var(--border); color: var(--text);
  border-radius: 8px; padding: 7px 8px; font-size: 12px; font-family: 'IBM Plex Sans', sans-serif; cursor: pointer;
}
.status-select:focus { outline: none; border-color: var(--amber-dim); }
.bonus-hint { font-size: 11.5px; color: var(--text-dim); line-height: 1.5; margin: -6px 0 12px; }
.bonus-tag-seeded { background: rgba(255,166,48,0.14); color: var(--amber); }
.bonus-locked-note {
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; color: var(--text-dim);
}
.bonus-icon-badge {
  width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(110,231,183,0.14); color: var(--green);
}
.bonus-info { flex: 1; min-width: 0; }
.bonus-period { font-size: 13px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }
.bonus-tag {
  font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;
  background: rgba(110,231,183,0.16); color: var(--green); padding: 2px 7px; border-radius: 999px;
}
.bonus-dates { font-size: 10.5px; color: var(--text-dim); margin-top: 3px; font-family: 'Space Mono', monospace; }
.bonus-note { font-size: 11px; color: var(--text-dim); margin-top: 3px; font-style: italic; }
.bonus-recipients { font-size: 10.5px; color: var(--text-dim); margin-top: 3px; }
.bonus-amount { font-family: 'Space Mono', monospace; font-size: 14px; font-weight: 700; color: var(--green); flex-shrink: 0; }

.comp-action-row { display: flex; gap: 10px; margin: 4px 0 16px; }
.comp-blocked-note {
  display: flex; align-items: flex-start; gap: 12px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 14px 16px; margin: 4px 0 16px;
}
.comp-blocked-note svg { color: var(--text-dim); flex-shrink: 0; margin-top: 1px; }
.comp-blocked-title { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 3px; }
.comp-blocked-text { font-size: 12px; color: var(--text-muted); line-height: 1.5; }
.comp-btn {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 6px;
  padding: 16px 10px; border-radius: var(--radius); font-size: 13px; font-weight: 700; cursor: pointer;
  border: 1px solid var(--border); background: var(--surface); color: var(--text);
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.comp-btn:hover { transform: translateY(-2px); }
.comp-btn-add { color: var(--green); }
.comp-btn-add:hover { border-color: rgba(110,231,183,0.5); background: rgba(110,231,183,0.1); }
.comp-btn-subtract { color: var(--red); }
.comp-btn-subtract:hover { border-color: rgba(255,107,107,0.5); background: rgba(255,107,107,0.1); }

.comp-adjustment-note {
  display: flex; align-items: center; gap: 8px; background: rgba(110,231,183,0.1); color: var(--green);
  border-radius: var(--radius); padding: 12px 14px; margin-bottom: 16px; font-size: 12.5px; line-height: 1.5;
}
.comp-adjustment-note svg { flex-shrink: 0; }

.comp-card {
  display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 13px 14px; margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15); transition: box-shadow 0.2s ease, border-color 0.15s ease;
}
.comp-card:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.22); }
.comp-icon-badge {
  width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.comp-icon-badge.add { background: rgba(110,231,183,0.14); color: var(--green); }
.comp-icon-badge.subtract { background: rgba(255,107,107,0.14); color: var(--red); }
.comp-info { flex: 1; min-width: 0; }
.comp-period { font-size: 12.5px; color: var(--text-muted); display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.comp-note { font-size: 13px; color: var(--text); margin-top: 3px; }
.comp-decided-date { font-size: 10.5px; color: var(--text-dim); margin-top: 3px; font-family: 'Space Mono', monospace; }
.comp-tag { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
.comp-tag-pending { background: rgba(255,166,48,0.14); color: var(--amber); }
.comp-tag-approved { background: rgba(110,231,183,0.14); color: var(--green); }
.comp-tag-rejected { background: rgba(255,107,107,0.14); color: var(--red); }
.comp-amount { font-family: 'Space Mono', monospace; font-size: 14px; font-weight: 700; flex-shrink: 0; }
.comp-amount.add { color: var(--green); }
.comp-amount.subtract { color: var(--red); }
.comp-amount-input {
  width: 100%; background: var(--surface); border: 1px solid var(--border); color: var(--text);
  border-radius: 10px; padding: 12px; font-size: 15px; font-family: 'Space Mono', monospace;
}
.comp-amount-input:focus { outline: none; border-color: var(--amber-dim); }
.section-count {
  display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px;
  padding: 0 6px; margin-left: 8px; border-radius: 999px; background: var(--amber); color: #1a1206;
  font-size: 11px; font-weight: 700;
}
.comp-approve-btns { display: flex; gap: 6px; flex-shrink: 0; }
.comp-approve-btn {
  width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--border); background: var(--surface-2); cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.comp-approve-btn.approve { color: var(--green); }
.comp-approve-btn.approve:hover { background: rgba(110,231,183,0.14); border-color: rgba(110,231,183,0.5); }
.comp-approve-btn.reject { color: var(--red); }
.comp-approve-btn.reject:hover { background: rgba(255,107,107,0.14); border-color: rgba(255,107,107,0.5); }

.new-thread-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%;
  padding: 13px; border-radius: var(--radius); font-size: 14px; font-weight: 700; cursor: pointer;
  border: 1px solid var(--border); background: var(--surface); color: var(--amber);
  transition: border-color 0.15s ease, background 0.15s ease;
}
.new-thread-btn:hover { border-color: var(--amber-dim); background: rgba(255,166,48,0.08); }

.thread-card {
  display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 13px 14px; margin-bottom: 10px; cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15); transition: border-color 0.15s ease, box-shadow 0.2s ease;
}
.thread-card:hover { border-color: var(--amber-dim); box-shadow: 0 4px 14px rgba(0,0,0,0.22); }
.thread-icon-badge {
  width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; background: rgba(255,166,48,0.14); color: var(--amber);
}
.thread-info { flex: 1; min-width: 0; }
.thread-topic { font-size: 13.5px; font-weight: 600; color: var(--text); display: flex; align-items: center; gap: 7px; }
.thread-preview {
  font-size: 12.5px; color: var(--text-muted); margin-top: 3px; overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap;
}
.thread-chevron { flex-shrink: 0; color: var(--text-dim); transform: rotate(180deg); }

.chat-view { display: flex; flex-direction: column; height: 60vh; min-height: 420px; }
.chat-header {
  display: flex; align-items: center; gap: 10px; padding-bottom: 14px; border-bottom: 1px solid var(--border);
  margin-bottom: 14px; position: relative;
}
.chat-header::after {
  content: ""; position: absolute; bottom: -1px; left: 0; width: 56px; height: 2px; border-radius: 2px;
  background: linear-gradient(90deg, var(--amber), transparent);
}
.chat-date-separator { display: flex; justify-content: center; margin: 14px 0; align-self: center; width: 100%; }
.chat-date-separator span {
  font-size: 10.5px; font-weight: 700; color: var(--text-dim); letter-spacing: 0.4px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px; padding: 4px 12px;
}
.upload-progress-banner {
  display: flex; align-items: center; gap: 10px; padding: 10px 14px; margin: 0 12px 8px;
  background: linear-gradient(155deg, rgba(255,166,48,0.1), var(--surface-2)); border: 1px solid var(--amber-dim);
  border-radius: 10px; animation: homeFadeUp 0.2s ease both;
}
.upload-progress-spinner { color: var(--amber); animation: spin 1s linear infinite; flex-shrink: 0; }
.upload-progress-body { flex: 1; min-width: 0; }
.upload-progress-label { font-size: 11.5px; font-weight: 700; color: var(--amber); margin-bottom: 5px; }
.upload-progress-track { height: 5px; border-radius: 3px; background: var(--surface-3); overflow: hidden; }
.upload-progress-fill { height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--amber), #ffb84d); transition: width 0.2s ease; }
.chat-search-toggle {
  width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--border); background: var(--surface-2); color: var(--text-muted); cursor: pointer;
  transition: all 0.15s ease; flex-shrink: 0;
}
.chat-search-toggle:hover { color: var(--amber); border-color: var(--amber-dim); }
.chat-search-row {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; margin-bottom: 10px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 10px; color: var(--text-dim);
  animation: homeFadeUp 0.2s ease both;
}
.chat-search-input { flex: 1; background: none; border: none; outline: none; color: var(--text); font-size: 13px; }
.chat-search-input::placeholder { color: var(--text-dim); }
.chat-search-close { background: none; border: none; color: var(--text-dim); cursor: pointer; display: flex; flex-shrink: 0; }
.chat-search-close:hover { color: var(--red); }
.chat-search-highlight { background: var(--amber); color: #1a1206; border-radius: 3px; padding: 0 2px; font-weight: 700; }
.chat-back-btn {
  width: 36px; height: 36px; border-radius: 11px; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--border); background: var(--surface-2); color: var(--text); cursor: pointer;
  transition: border-color 0.15s ease, transform 0.15s ease;
}
.chat-back-btn:hover { border-color: var(--amber-dim); transform: translateX(-2px); }
.chat-header-topic { font-size: 15.5px; font-weight: 700; color: var(--text); flex: 1; }
.chat-resolve-btn {
  font-size: 11.5px; font-weight: 600; padding: 7px 12px; border-radius: 8px;
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-dim); cursor: pointer;
  flex-shrink: 0; transition: color 0.15s ease, border-color 0.15s ease;
}
.chat-resolve-btn:hover { color: var(--amber); border-color: var(--amber-dim); }
.chat-messages {
  flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 3px; padding: 6px 4px;
  background:
    radial-gradient(ellipse 220px 140px at 15% 8%, rgba(255,166,48,0.05), transparent 70%),
    radial-gradient(ellipse 200px 160px at 85% 92%, rgba(110,231,183,0.04), transparent 70%);
  border-radius: 14px;
}
.chat-bubble {
  max-width: 78%; padding: 11px 15px; border-radius: 18px; font-size: 13.5px; line-height: 1.5; word-break: break-word;
  animation: bubbleIn 0.25s cubic-bezier(.22,1,.36,1) both; box-shadow: 0 3px 10px rgba(0,0,0,0.16);
}
@keyframes bubbleIn { from { opacity: 0; transform: translateY(8px) scale(0.96); } to { opacity: 1; transform: none; } }
.chat-bubble.mine {
  align-self: flex-end; background: linear-gradient(155deg, #ffb84d, var(--amber)); color: #1a1206;
  border-bottom-right-radius: 18px;
}
.chat-bubble.theirs {
  align-self: flex-start; background: linear-gradient(155deg, var(--surface-3), var(--surface-2));
  border: 1px solid rgba(255,255,255,0.05); color: var(--text); border-bottom-left-radius: 18px;
}
/* Grupowanie w stylu Messengera: kolejne wiadomości od TEJ SAMEJ osoby, blisko
   w czasie, "sklejają się" ciasno (patrz .msg-group-start niżej) — "ogonek"
   (podcięty róg) pokazuje się TYLKO na ostatniej wiadomości serii, a
   pierwszy wewnętrzny róg kolejnych wiadomości jest mniej zaokrąglony,
   żeby wizualnie się ze sobą łączyły. */
.chat-bubble.mine.msg-group-last { border-bottom-right-radius: 5px; }
.chat-bubble.theirs.msg-group-last { border-bottom-left-radius: 5px; }
.chat-bubble.mine.msg-group-notfirst { border-top-right-radius: 6px; }
.chat-bubble.theirs.msg-group-notfirst { border-top-left-radius: 6px; }
.msg-group-start { margin-top: 12px; }
.msg-time {
  font-size: 10px; color: var(--text-dim); margin: 3px 6px 0; opacity: 0.75; flex-shrink: 0;
}
.msg-time.mine { align-self: flex-end; }
.msg-time.theirs { align-self: flex-start; }
.msg-time-group { font-size: 10px; color: var(--text-dim); margin-top: 2px; padding: 0 4px; opacity: 0.75; }
.chat-avatar-spacer { width: 28px; flex-shrink: 0; }
.chat-bubble.important {
  background: linear-gradient(155deg, rgba(255,184,77,0.22), rgba(255,166,48,0.1)) !important;
  border: 1px solid var(--amber-dim) !important; color: var(--text) !important; font-weight: 600;
  box-shadow: 0 3px 14px rgba(255,166,48,0.18) !important;
}
.important-badge {
  display: flex; align-items: center; gap: 4px; font-size: 9.5px; font-weight: 800; letter-spacing: 0.5px;
  color: var(--amber); margin-bottom: 5px;
}
.chat-bubble.admin-msg {
  background: linear-gradient(155deg, #8fe6b8, #34c77b) !important; color: #0d2a19 !important;
  border: none !important; box-shadow: 0 3px 12px rgba(52,199,123,0.32);
}
.chat-bubble-author.admin-author { color: #0d2a19; }
.chat-bubble.admin-msg.important {
  background: linear-gradient(155deg, rgba(255,184,77,0.9), rgba(255,166,48,0.85)) !important;
  color: #1a1206 !important; border: 1px solid var(--amber-dim) !important;
  box-shadow: 0 3px 14px rgba(255,166,48,0.3) !important;
}
.chat-bubble.admin-msg.important .chat-bubble-author.admin-author { color: #1a1206; }
.msg-with-delete {
  display: flex; align-items: flex-end; gap: 6px; max-width: 78%;
}
.msg-with-delete.mine { align-self: flex-end; flex-direction: row-reverse; }
.msg-with-delete.theirs { align-self: flex-start; }
.msg-with-delete .chat-bubble { max-width: 100%; align-self: auto; }
.msg-delete-btn {
  flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: none; border: none; color: var(--text-dim); cursor: pointer; opacity: 0; transition: opacity 0.15s ease, color 0.15s ease;
}
.msg-with-delete:hover .msg-delete-btn { opacity: 1; }
.msg-delete-btn:hover { color: var(--red); }
.msg-with-reply {
  display: flex; align-items: flex-end; gap: 6px; max-width: 78%;
}
.msg-with-reply.mine { align-self: flex-end; flex-direction: row-reverse; }
.msg-with-reply.theirs { align-self: flex-start; }
.msg-with-reply .chat-bubble { max-width: 100%; align-self: auto; }
.msg-reply-btn {
  flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: none; border: none; color: var(--text-dim); cursor: pointer; opacity: 0; transition: opacity 0.15s ease, color 0.15s ease;
}
.msg-with-reply:hover .msg-reply-btn, .msg-with-delete:hover .msg-reply-btn { opacity: 1; }
.msg-reply-btn:hover { color: var(--amber); }
.chat-quote {
  border-left: 2.5px solid var(--amber-dim); background: rgba(255,255,255,0.05);
  border-radius: 6px; padding: 5px 9px; margin-bottom: 6px; opacity: 0.85;
}
.chat-quote-author { font-size: 10px; font-weight: 700; color: var(--amber); margin-bottom: 1px; }
.chat-quote-text { font-size: 11.5px; color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.reply-preview-banner {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px; margin: 0 12px 8px;
  background: var(--surface-2); border-left: 3px solid var(--amber); border-radius: 8px;
  color: var(--amber); animation: homeFadeUp 0.2s ease both;
}
.reply-preview-body { flex: 1; min-width: 0; }
.reply-preview-author { font-size: 10.5px; font-weight: 700; color: var(--amber); }
.reply-preview-text { font-size: 12px; color: var(--text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.reply-preview-cancel {
  flex-shrink: 0; background: none; border: none; color: var(--text-dim); cursor: pointer; display: flex;
}
.reply-preview-cancel:hover { color: var(--red); }
.chat-avatar {
  width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0; margin-bottom: 2px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #1a1206; text-transform: uppercase;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
}
.chat-avatar-clickable { cursor: pointer; transition: transform 0.12s ease; }
.chat-avatar-clickable:active { transform: scale(0.88); }
.chat-bubble-author { font-size: 10.5px; font-weight: 700; color: var(--amber); margin-bottom: 3px; }
.chat-bubble-row { display: flex; align-items: flex-end; gap: 6px; max-width: 100%; }
.chat-bubble-row.theirs { align-self: flex-start; }
.chat-bubble-row.mine { align-self: flex-end; flex-direction: row-reverse; }
.chat-bubble-col { display: flex; flex-direction: column; max-width: 78%; animation: bubbleIn 0.25s cubic-bezier(.22,1,.36,1) both; }
.chat-bubble-col.mine { align-self: flex-end; align-items: flex-end; }
.chat-bubble-col.theirs { align-self: flex-start; align-items: flex-start; }
.chat-bubble-col .chat-bubble { max-width: 100%; animation: none; }
.chat-seen-by { font-size: 9.5px; color: var(--text-dim); margin-top: 3px; padding: 0 4px; }
.chat-msg-time { font-size: 10px; color: var(--text-dim); margin-top: 3px; padding: 0 4px; }
.chat-msg-time.mine { text-align: right; }
.chat-msg-time.theirs { text-align: left; }
.msg-reactions { display: flex; align-items: center; gap: 4px; margin-top: 4px; padding: 0 4px; position: relative; flex-wrap: wrap; }
.msg-reactions.has-reactions {
  margin-top: -11px; z-index: 2; padding: 0 8px;
}
.msg-reactions.has-reactions .msg-reaction-pill {
  box-shadow: 0 2px 6px rgba(0,0,0,0.35); background: var(--surface);
}
.msg-reaction-pill {
  display: flex; align-items: center; gap: 3px; background: var(--surface-2); border: 1px solid var(--border);
  border-radius: 999px; padding: 2px 7px; font-size: 11px; color: var(--text-muted); cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.msg-reaction-pill.mine { border-color: var(--amber-dim); background: rgba(255,166,48,0.12); color: var(--amber); }
.msg-reaction-add {
  width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: none; border: 1px solid var(--border); color: var(--text-dim); cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}
.msg-reaction-add:hover { color: var(--amber); border-color: var(--amber-dim); }
.msg-reaction-picker {
  position: absolute; bottom: 26px; left: 4px; z-index: 20; display: flex; gap: 3px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px; padding: 5px 7px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.35); animation: tileIn 0.15s ease both;
}
.msg-reaction-picker button { background: none; border: none; font-size: 17px; cursor: pointer; padding: 2px; }
.msg-reaction-picker button:hover { transform: scale(1.2); }
.car-tab-row { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 12px; }
.car-tab-pill {
  font-size: 12px; font-weight: 700; padding: 8px 14px; border-radius: 999px;
  background: var(--surface); border: 1px solid var(--border); color: var(--text-muted); cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease, transform 0.15s ease, box-shadow 0.2s ease;
}
.car-tab-pill:hover { color: var(--text); transform: translateY(-2px); }
.car-tab-pill:active { transform: translateY(0) scale(0.96); }
.car-tab-pill.active {
  color: var(--amber); border-color: var(--amber-dim); background: rgba(255,166,48,0.1);
  box-shadow: 0 4px 14px rgba(255,166,48,0.18);
}
.deadlines-panel { margin-bottom: 16px; }
.notif-bar { display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px; }
.notif-chip {
  display: flex; align-items: center; gap: 7px; width: 100%; text-align: left;
  font-size: 12px; font-weight: 700; padding: 10px 14px; border-radius: 12px; cursor: pointer;
  animation: homeFadeUp 0.3s ease both; transition: transform 0.15s ease;
}
.notif-chip:hover { transform: translateX(2px); }
.notif-chip-msg {
  background: linear-gradient(155deg, rgba(255,184,77,0.16), rgba(255,166,48,0.05));
  border: 1px solid var(--amber-dim); color: var(--amber);
}
.notif-chip-car {
  background: linear-gradient(155deg, rgba(255,107,107,0.14), rgba(255,107,107,0.04));
  border: 1px solid rgba(255,107,107,0.4); color: #ff6b6b;
}
.notif-chip-car-wrap { display: flex; flex-direction: column; }
.notif-deadline-list {
  background: var(--surface); border: 1px solid rgba(255,107,107,0.3); border-top: none;
  border-radius: 0 0 12px 12px; padding: 4px 12px 10px; margin-top: -7px;
  animation: homeFadeUp 0.2s ease both;
}
.notif-deadline-item { padding: 8px 0; border-bottom: 1px solid var(--border); }
.notif-deadline-item:last-of-type { border-bottom: none; }
.notif-deadline-goto {
  width: 100%; margin-top: 8px; background: none; border: none; color: var(--amber);
  font-size: 11.5px; font-weight: 700; cursor: pointer; text-align: left; padding: 4px 0;
}
.deadline-row {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 11px 14px;
  margin-bottom: 8px; animation: homeFadeUp 0.3s ease both;
}
.deadline-row.deadline-urgent { border-color: rgba(255,166,48,0.45); background: rgba(255,166,48,0.06); }
.deadline-row.deadline-overdue {
  border-color: rgba(255,107,107,0.5); background: rgba(255,107,107,0.08);
  animation: homeFadeUp 0.3s ease both, priorityPulse 2.6s ease-in-out infinite;
}
.deadline-info { min-width: 0; }
.deadline-car { font-size: 13px; font-weight: 700; color: var(--text); }
.deadline-label { font-size: 11.5px; color: var(--text-muted); margin-top: 2px; }
.deadline-renew-btn {
  flex-shrink: 0; font-size: 11.5px; font-weight: 700; padding: 7px 13px; border-radius: 8px;
  background: var(--surface-2); border: 1px solid var(--amber-dim); color: var(--amber); cursor: pointer;
  transition: background 0.15s ease;
}
.deadline-renew-btn:hover { background: rgba(255,166,48,0.12); }
.deadline-renew-row { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
.company-month-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: 14px; margin-bottom: 10px;
  overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.company-month-header {
  width: 100%; display: flex; align-items: center; gap: 12px; padding: 14px 16px; background: none; border: none;
  cursor: pointer; text-align: left;
}
.company-month-name { font-size: 14px; font-weight: 700; color: var(--text); text-transform: capitalize; }
.company-month-sub { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
.company-month-amount {
  margin-left: auto; font-size: 18px; font-weight: 700; color: var(--amber); font-family: 'Space Mono', monospace;
}
.company-month-body { padding: 0 16px 16px; border-top: 1px solid var(--border); }
.company-week-row {
  display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 0;
  border-bottom: 1px solid var(--border);
}
.company-week-row:last-of-type { border-bottom: none; }
.company-week-row.company-week-expense { color: var(--red); }
.company-week-amount { font-size: 13.5px; font-weight: 700; color: var(--text); font-family: 'Space Mono', monospace; flex-shrink: 0; }
.driver-breakdown-toggle {
  display: block; width: 100%; background: rgba(255,166,48,0.06); border: 1px dashed var(--amber-dim);
  border-radius: 8px; color: var(--amber); font-size: 11px; font-weight: 700; cursor: pointer;
  padding: 8px 10px; text-align: left; margin-bottom: 8px; transition: background 0.15s ease;
}
.driver-breakdown-toggle:hover { background: rgba(255,166,48,0.12); }
.crypto-slider {
  -webkit-appearance: none; appearance: none; height: 4px; border-radius: 2px; background: var(--border); outline: none;
}
.crypto-slider::-webkit-slider-thumb {
  -webkit-appearance: none; appearance: none; width: 16px; height: 16px; border-radius: 50%;
  background: var(--amber); cursor: pointer; box-shadow: 0 0 0 3px rgba(255,166,48,0.2);
}
.crypto-slider::-moz-range-thumb {
  width: 16px; height: 16px; border-radius: 50%; background: var(--amber); cursor: pointer; border: none;
  box-shadow: 0 0 0 3px rgba(255,166,48,0.2);
}
.crypto-recommendation {
  display: flex; align-items: center; gap: 14px; padding: 16px 18px; border-radius: 14px;
  animation: homeFadeUp 0.3s ease both;
}
.crypto-rec-buy {
  background: linear-gradient(155deg, rgba(110,231,183,0.18), rgba(110,231,183,0.04));
  border: 1.5px solid var(--green); color: var(--green);
}
.crypto-rec-sell {
  background: linear-gradient(155deg, rgba(255,107,107,0.18), rgba(255,107,107,0.04));
  border: 1.5px solid var(--red); color: var(--red);
}
.crypto-rec-hold {
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-muted);
}
.crypto-rec-headline { font-size: 16px; font-weight: 700; color: var(--text); }
.crypto-rec-sub { font-size: 11.5px; margin-top: 2px; opacity: 0.85; }
.crypto-autopilot-toggle {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px;
  border-radius: 14px; background: var(--surface); border: 1px solid var(--border); margin-bottom: 16px;
}
.crypto-autopilot-on {
  background: linear-gradient(155deg, rgba(255,166,48,0.14), rgba(255,166,48,0.03));
  border-color: var(--amber-dim);
}

/* --- Krypto Lab: karta otwartej pozycji, premium --- */
.crypto-position-card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 20px 20px 16px;
  margin-bottom: 18px;
  background: linear-gradient(165deg, #171a24 0%, #12141c 60%, #0f1117 100%);
  border: 1px solid rgba(255,255,255,0.09);
  box-shadow: 0 12px 32px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04);
  animation: cryptoPositionRise 0.4s cubic-bezier(0.16,1,0.3,1) both;
}
@keyframes cryptoPositionRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.crypto-position-glow {
  position: absolute; top: -60%; left: 50%; transform: translateX(-50%);
  width: 220px; height: 220px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,166,48,0.22), transparent 70%);
  pointer-events: none; filter: blur(10px);
  animation: cryptoGlowPulse 4s ease-in-out infinite;
}
@keyframes cryptoGlowPulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
.crypto-position-top { display: flex; justify-content: space-between; align-items: flex-start; position: relative; z-index: 1; margin-bottom: 16px; }
.crypto-position-coin { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; color: var(--text-muted); letter-spacing: 0.3px; }
.crypto-position-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--amber); box-shadow: 0 0 8px var(--amber); animation: cryptoDotBlink 1.8s ease-in-out infinite; }
@keyframes cryptoDotBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
.crypto-position-auto-tag {
  font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px;
  background: rgba(255,166,48,0.16); color: var(--amber); border: 1px solid rgba(255,166,48,0.4);
  border-radius: 999px; padding: 2px 8px;
}
.crypto-position-pnl { font-size: 26px; font-weight: 800; letter-spacing: -0.5px; line-height: 1; text-align: right; }
.crypto-position-pnl.pos { color: var(--green); }
.crypto-position-pnl.neg { color: var(--red); }
.crypto-position-pnl-pct { display: block; font-size: 12px; font-weight: 600; opacity: 0.75; margin-top: 3px; }
.crypto-position-mid { display: flex; gap: 10px; position: relative; z-index: 1; margin-bottom: 14px; }
.crypto-position-stat { flex: 1; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; padding: 9px 10px; }
.crypto-position-stat-label { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-dim); margin-bottom: 3px; }
.crypto-position-stat-value { font-size: 13.5px; font-weight: 700; color: var(--text); }
.crypto-position-track { position: relative; height: 6px; border-radius: 3px; background: linear-gradient(90deg, var(--red), var(--border) 45%, var(--border) 55%, var(--green)); margin-bottom: 10px; }
.crypto-position-track-fill { display: none; }
.crypto-position-track-marker {
  position: absolute; top: 50%; width: 12px; height: 12px; border-radius: 50%;
  background: #fff; border: 2px solid var(--bg); transform: translate(-50%, -50%);
  box-shadow: 0 0 0 3px rgba(255,255,255,0.15), 0 2px 6px rgba(0,0,0,0.4);
  transition: left 0.4s ease;
}
.crypto-position-bottom { display: flex; justify-content: space-between; align-items: center; position: relative; z-index: 1; }
.crypto-position-stoplabel { font-size: 11px; color: var(--text-dim); font-weight: 600; }
.crypto-sell-btn {
  background: linear-gradient(155deg, #ff7a7a, #e14b4b);
  color: #fff; font-weight: 700; font-size: 13px; border: none; border-radius: 12px;
  padding: 10px 22px; cursor: pointer; box-shadow: 0 6px 16px -4px rgba(225,75,75,0.5);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.crypto-sell-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 20px -4px rgba(225,75,75,0.6); }
.crypto-sell-btn:active { transform: translateY(0); }
.crypto-direction-toggle { display: flex; gap: 8px; }
.crypto-direction-btn {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 12px; border-radius: 10px; font-size: 12.5px; font-weight: 600;
  background: var(--surface-2); border: 1.5px solid var(--border); color: var(--text-muted);
  cursor: pointer; transition: all 0.15s ease;
}
.crypto-direction-btn.long.active { background: rgba(110,231,183,0.12); border-color: var(--green); color: var(--green); }
.crypto-direction-btn.short.active { background: rgba(255,107,107,0.12); border-color: var(--red); color: var(--red); }
.crypto-direction-badge {
  font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.6px;
  border-radius: 999px; padding: 2px 8px; margin-left: 6px;
}
.crypto-direction-badge.long { background: rgba(110,231,183,0.14); color: var(--green); border: 1px solid rgba(110,231,183,0.4); }
.crypto-direction-badge.short { background: rgba(255,107,107,0.14); color: var(--red); border: 1px solid rgba(255,107,107,0.4); }

/* --- Zadłużenie: przebudowany, premium wygląd --- */
.debt-hero-card {
  position: relative; overflow: hidden; border-radius: 20px; padding: 22px 20px;
  margin-bottom: 16px; text-align: center;
  background: linear-gradient(165deg, #211316 0%, #16100f 60%, #120e0d 100%);
  border: 1px solid rgba(255,107,107,0.18);
  box-shadow: 0 12px 32px -14px rgba(193,42,42,0.35), inset 0 1px 0 rgba(255,255,255,0.03);
  animation: cryptoPositionRise 0.4s cubic-bezier(0.16,1,0.3,1) both;
}
.debt-hero-glow {
  position: absolute; top: -70%; left: 50%; transform: translateX(-50%);
  width: 240px; height: 240px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,107,107,0.25), transparent 70%);
  filter: blur(12px); pointer-events: none; animation: cryptoGlowPulse 4s ease-in-out infinite;
}
.debt-hero-label { position: relative; z-index: 1; font-size: 11.5px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-dim); margin-bottom: 8px; font-weight: 700; }
.debt-hero-value { position: relative; z-index: 1; font-size: 34px; font-weight: 800; letter-spacing: -0.8px; color: var(--red); }
.debt-hero-repaid { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; gap: 5px; font-size: 12px; color: var(--green); margin-top: 8px; font-weight: 600; }
.debt-repay-cta {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 14px; border-radius: 14px; margin-bottom: 16px; border: none; cursor: pointer;
  font-size: 14px; font-weight: 700; color: #fff;
  background: linear-gradient(155deg, #ff7a7a, #c13030);
  box-shadow: 0 8px 20px -6px rgba(193,48,48,0.5);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.debt-repay-cta:hover { transform: translateY(-1px); box-shadow: 0 10px 24px -6px rgba(193,48,48,0.6); }
.debt-repay-form {
  border-radius: 18px; padding: 18px; margin-bottom: 16px;
  background: var(--surface); border: 1px solid var(--border);
  animation: cryptoPositionRise 0.3s ease both;
}
.debt-full-btn {
  white-space: nowrap; padding: 0 14px; border-radius: 10px; font-size: 12px; font-weight: 700;
  background: rgba(255,107,107,0.12); border: 1.5px solid var(--red); color: var(--red); cursor: pointer;
  transition: background 0.15s ease;
}
.debt-full-btn:hover { background: rgba(255,107,107,0.2); }
.debt-info-note {
  display: flex; gap: 10px; align-items: flex-start; padding: 12px 14px; border-radius: 12px; margin-bottom: 14px;
  background: rgba(110,231,183,0.07); border: 1px solid rgba(110,231,183,0.3); color: var(--green); font-size: 12px; line-height: 1.5;
}
.debt-info-note.warn { background: rgba(255,166,48,0.08); border-color: rgba(255,166,48,0.35); color: var(--amber); }
.debt-info-note b { display: block; margin-bottom: 2px; font-size: 12.5px; }
.debt-info-note div div { color: var(--text-muted); font-weight: 400; }
.debt-history-list { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.debt-history-row {
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 12px;
  background: var(--surface); border: 1px solid var(--border);
}
.debt-history-icon {
  width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: var(--surface-2); color: var(--text-muted);
}
.debt-history-icon.approved { background: rgba(110,231,183,0.12); color: var(--green); }
.debt-history-icon.rejected { background: rgba(255,107,107,0.12); color: var(--red); }
.debt-history-mid { flex: 1; }
.debt-history-amount { font-size: 14px; font-weight: 700; color: var(--text); }
.debt-history-method { font-size: 11.5px; color: var(--text-muted); margin-top: 1px; }
.debt-card-v2 { display: flex; align-items: center; gap: 12px; }
.debt-card-icon {
  width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,107,107,0.1); color: var(--red); border: 1px solid rgba(255,107,107,0.25);
}
.debt-card-body { flex: 1; min-width: 0; }
.debt-card-note { font-size: 13.5px; font-weight: 600; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.debt-card-date { font-size: 11px; color: var(--text-dim); margin-top: 2px; }
.debt-card-amount { font-size: 15px; font-weight: 800; color: var(--red); flex-shrink: 0; }

/* --- Krypto Lab: nagłówek / branding --- */
.crypto-lab-brand {
  display: flex; align-items: center; gap: 10px; margin-bottom: 14px;
}
.crypto-lab-brand-mark {
  width: 30px; height: 30px; border-radius: 9px;
  background: linear-gradient(155deg, #ffc97a, #e5942e);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(229,148,46,0.35);
  font-size: 15px; flex-shrink: 0;
}
.crypto-lab-brand-text { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-dim); }
.crypto-lab-brand-text b { color: var(--amber); }

.company-record-badge {
  display: inline-block; font-size: 9.5px; font-weight: 800; color: var(--amber); margin-left: 8px;
  background: rgba(255,166,48,0.12); border: 1px solid var(--amber-dim); border-radius: 999px; padding: 2px 8px;
  vertical-align: middle;
}
.company-trend {
  display: inline-flex; align-items: center; gap: 2px; font-size: 10px; font-weight: 700; margin-left: 6px;
  padding: 1px 6px; border-radius: 999px;
}
.company-trend-up { color: var(--green); background: rgba(110,231,183,0.12); }
.company-trend-down { color: var(--red); background: rgba(255,107,107,0.12); }
.driver-breakdown-table {
  background: var(--surface-2); border-radius: 10px; padding: 8px 10px; margin-bottom: 10px; overflow-x: auto;
}
.driver-breakdown-row {
  display: grid; grid-template-columns: 1.4fr 0.9fr 0.9fr 0.9fr 0.9fr 0.9fr; gap: 6px; padding: 6px 4px; font-size: 11px;
  border-bottom: 1px solid var(--border); white-space: nowrap;
}
.driver-breakdown-net { font-weight: 700; }
.driver-breakdown-row:last-child { border-bottom: none; }
.driver-breakdown-head { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-dim); font-weight: 700; }
.reconcile-table { margin-top: 4px; }
.reconcile-row {
  display: grid; grid-template-columns: 1.6fr 1fr 1fr 1.1fr; gap: 6px; padding: 7px 4px; font-size: 11.5px;
  border-bottom: 1px solid var(--border);
}
.reconcile-row:last-child { border-bottom: none; }
.reconcile-head { font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-dim); font-weight: 700; }
.driver-breakdown-car { color: var(--text-dim); font-size: 10px; }
.driver-breakdown-shared-tag { color: var(--text-dim); font-size: 9px; font-style: italic; }
.car-ranking-panel {
  background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 16px;
  margin-bottom: 16px; box-shadow: 0 3px 12px rgba(0,0,0,0.18);
}
.car-ranking-highlights { display: flex; gap: 10px; margin: 10px 0 14px; flex-wrap: wrap; }
.car-highlight-card {
  flex: 1; min-width: 140px; border-radius: 14px; padding: 14px 16px; text-align: center;
  animation: homeFadeUp 0.35s ease both;
}
.car-highlight-best {
  background: linear-gradient(160deg, rgba(110,231,183,0.18), rgba(110,231,183,0.04));
  border: 1px solid rgba(110,231,183,0.4); box-shadow: 0 3px 14px rgba(110,231,183,0.15);
}
.car-highlight-worst {
  background: linear-gradient(160deg, rgba(255,107,107,0.2), rgba(255,107,107,0.05));
  border: 1.5px solid rgba(255,107,107,0.55); box-shadow: 0 3px 16px rgba(255,107,107,0.22);
  animation: homeFadeUp 0.35s ease both, priorityPulse 3s ease-in-out infinite;
}
.car-highlight-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-muted); margin-bottom: 4px; }
.car-highlight-car { font-size: 14px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
.car-highlight-amount { font-size: 20px; font-weight: 700; font-family: 'Space Mono', monospace; }
.car-highlight-best .car-highlight-amount { color: var(--green); }
.car-highlight-worst .car-highlight-amount { color: var(--red); }
.car-ranking-row-loss { background: rgba(255,107,107,0.06); border-radius: 6px; }
.finance-total-card {
  background: linear-gradient(160deg, rgba(255,166,48,0.14), rgba(255,166,48,0.03));
  border: 1px solid var(--amber-dim); border-radius: 14px; padding: 18px; text-align: center;
  animation: financeTotalIn 0.4s cubic-bezier(.22,1,.36,1) both;
  box-shadow: 0 0 0 0 rgba(255,166,48,0.25);
}
@keyframes financeTotalIn {
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to { opacity: 1; transform: none; }
}
.finance-total-label { font-size: 11.5px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.finance-total-amount {
  font-size: 30px; font-weight: 700; color: var(--amber); font-family: 'Space Mono', monospace;
  animation: financeAmountPop 0.45s cubic-bezier(.34,1.56,.64,1) both; animation-delay: 0.08s;
}
@keyframes financeAmountPop { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
.finance-row-card-v2 {
  display: flex; align-items: center; gap: 10px; background: var(--surface); border: 1px solid var(--border);
  border-radius: 12px; padding: 12px 14px; margin-bottom: 8px;
  animation: homeFadeUp 0.35s cubic-bezier(.22,1,.36,1) both;
  transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.2s ease;
}
.finance-row-card-v2:hover { transform: translateX(2px); border-color: var(--amber-dim); box-shadow: 0 4px 14px rgba(0,0,0,0.2); }
.finance-row-main { flex: 1; min-width: 0; }
.finance-row-title { font-size: 13.5px; font-weight: 700; color: var(--text); }
.finance-row-secondary { font-size: 11.5px; color: var(--text-muted); margin-top: 3px; overflow-wrap: break-word; }
.finance-row-amount { font-size: 15px; font-weight: 700; color: var(--amber); flex-shrink: 0; white-space: nowrap; }
.finance-row-actions { display: flex; gap: 4px; flex-shrink: 0; }
.chat-delete-btn {
  width: 26px; height: 26px; border-radius: 8px; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--border); background: var(--surface-2); color: var(--text-dim); cursor: pointer;
  flex-shrink: 0; transition: color 0.15s ease, border-color 0.15s ease;
}
.chat-delete-btn:hover { color: var(--red); border-color: rgba(255,107,107,0.5); }
.notify-setup {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 12px 14px; margin-bottom: 12px;
}
.notify-setup summary { font-size: 13px; font-weight: 600; color: var(--amber); cursor: pointer; }
.notify-setup-body { margin-top: 10px; font-size: 12.5px; color: var(--text-muted); line-height: 1.6; }
.notify-setup-body p { margin: 0 0 8px; }
.notify-setup-hint { font-size: 11.5px; color: var(--text-dim); }
.notify-setup-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; flex-wrap: wrap; }
.notify-setup-label { color: var(--text-dim); flex-shrink: 0; }
.notify-setup-row code {
  background: var(--surface-2); padding: 3px 8px; border-radius: 6px; font-family: 'Space Mono', monospace;
  font-size: 12px; color: var(--text); word-break: break-all;
}
.chat-input-row {
  display: flex; gap: 4px; margin-top: 14px; align-items: center;
  background: var(--surface); border: 1px solid var(--border); border-radius: 20px; padding: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.18); transition: border-color 0.2s ease;
}
.chat-input-row:focus-within { border-color: var(--amber-dim); }
.chat-icon-btn {
  width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; color: var(--text-dim); cursor: pointer;
  flex-shrink: 0; transition: color 0.15s ease, background 0.15s ease;
}
.chat-icon-btn:hover:not(:disabled) { color: var(--amber); background: rgba(255,166,48,0.1); }
.chat-icon-btn:disabled { opacity: 0.5; cursor: default; }
.emoji-picker {
  display: grid; grid-template-columns: repeat(8, 1fr); gap: 4px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 8px; margin-top: 8px;
}
.emoji-picker-btn {
  background: none; border: none; font-size: 19px; padding: 5px; border-radius: 8px; cursor: pointer;
  transition: background 0.12s ease;
}
.emoji-picker-btn:hover { background: var(--surface-3); }
.chat-attachment-img {
  max-width: 220px; max-height: 220px; border-radius: 10px; display: block; margin-bottom: 6px; cursor: zoom-in;
  box-shadow: 0 3px 10px rgba(0,0,0,0.25);
}
.chat-attachment-video {
  max-width: 260px; max-height: 260px; border-radius: 10px; display: block; margin-bottom: 6px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.25); background: #000;
}
.image-lightbox-overlay {
  position: fixed; inset: 0; z-index: 998; background: rgba(5,6,8,0.94); backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center; padding: 30px;
  animation: fadeIn 0.2s ease;
}
.image-lightbox-img {
  max-width: 100%; max-height: 100%; border-radius: 10px; box-shadow: 0 20px 60px rgba(0,0,0,0.6);
  animation: financeTotalIn 0.25s cubic-bezier(.22,1,.36,1) both; cursor: default;
}
.image-lightbox-close {
  position: absolute; top: 20px; right: 20px; width: 40px; height: 40px; border-radius: 50%;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.chat-attachment-file {
  display: flex; align-items: center; gap: 6px; background: var(--surface-2); border: 1px solid var(--border);
  border-radius: 8px; padding: 7px 10px; margin-bottom: 6px; font-size: 12px; color: var(--amber); text-decoration: none;
  width: fit-content;
}
.chat-attachment-audio { max-width: 220px; height: 36px; margin-bottom: 6px; display: block; }
.audio-boost-row { display: flex; align-items: center; gap: 4px; margin: -2px 0 6px; color: var(--text-dim); }
.chat-attachment-audio-duration { font-size: 11px; color: var(--text-dim); font-family: 'Space Mono', monospace; flex-shrink: 0; }
.audio-boost-btn {
  font-size: 10px; font-weight: 700; padding: 3px 7px; border-radius: 999px;
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text-muted); cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.audio-boost-btn:hover { color: var(--text); }
.audio-boost-btn.active { color: var(--amber); border-color: var(--amber-dim); background: rgba(255,166,48,0.12); }
.voice-recording-row {
  display: flex; align-items: center; gap: 10px; flex: 1;
  background: rgba(255,107,107,0.08); border: 1px solid rgba(255,107,107,0.3); border-radius: 14px; padding: 0 12px;
  height: 42px;
}
.voice-recording-dot {
  width: 10px; height: 10px; border-radius: 50%; background: var(--red); flex-shrink: 0;
  animation: bonusPulse 1s ease-in-out infinite;
}
.voice-recording-time { font-size: 12.5px; font-weight: 600; color: var(--red); flex: 1; font-family: 'Space Mono', monospace; }
.group-muted-notice {
  flex: 1; display: flex; align-items: center; justify-content: center; text-align: center;
  background: rgba(255,107,107,0.08); border: 1px solid rgba(255,107,107,0.25); border-radius: 14px;
  padding: 10px 14px; font-size: 11.5px; color: var(--red); font-weight: 600;
}
.chat-input {
  flex: 1; background: transparent; border: none; color: var(--text);
  padding: 9px 8px; font-size: 14px;
}
.chat-input:focus { outline: none; }
.chat-input::placeholder { color: var(--text-dim); }
.chat-send-btn {
  width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  border: none; background: linear-gradient(150deg, #ffb84d, var(--amber)); color: #1a1206; cursor: pointer; flex-shrink: 0;
  box-shadow: 0 3px 10px rgba(255,166,48,0.32); transition: transform 0.12s ease, box-shadow 0.2s ease;
}
.chat-send-btn:hover { transform: scale(1.06); box-shadow: 0 4px 14px rgba(255,166,48,0.42); }
.chat-send-btn:active { transform: scale(0.94); }
.section-label-row { display: flex; align-items: center; justify-content: space-between; margin: 20px 0 10px; }
.comp-export-btn {
  display: flex; align-items: center; gap: 6px; background: var(--surface-2); border: 1px solid var(--border);
  color: var(--text-muted); font-size: 12px; font-weight: 600; padding: 8px 12px; border-radius: 9px; cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}
.comp-export-btn:hover { border-color: var(--amber-dim); color: var(--amber); }

.recipient-header {
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 6px;
  font-size: 11.5px; color: var(--text-muted); margin-top: 2px;
}
.recipient-header-actions { display: flex; gap: 12px; }
.link-btn-sm {
  background: none; border: none; color: var(--amber); font-size: 11px; cursor: pointer; padding: 0;
  text-decoration: underline;
}
.recipient-list {
  max-height: 190px; overflow-y: auto; display: flex; flex-direction: column; gap: 2px;
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 10px; padding: 6px;
}
.recipient-row {
  display: flex; align-items: center; gap: 9px; padding: 7px 8px; border-radius: 7px;
  font-size: 12.5px; color: var(--text); cursor: pointer; transition: background 0.12s ease;
}
.recipient-row:hover { background: var(--surface-3); }
.recipient-row input { accent-color: var(--amber); width: 15px; height: 15px; flex-shrink: 0; cursor: pointer; }
.permissions-list { display: flex; flex-direction: column; gap: 4px; margin: 6px 0 14px; }
.permission-row {
  display: flex; align-items: center; justify-content: space-between; padding: 10px 4px;
  font-size: 13px; color: var(--text); border-bottom: 1px solid var(--border);
}
.permission-row:last-child { border-bottom: none; }
.permission-row input { accent-color: var(--amber); width: 17px; height: 17px; cursor: pointer; }
.digit-amber { color: var(--amber); text-shadow: 0 0 18px rgba(255,166,48,0.35); }
.digit-green { color: var(--green); text-shadow: 0 0 18px rgba(110,231,183,0.3); }
.digit-red { color: var(--red); text-shadow: 0 0 18px rgba(255,107,107,0.3); }

.refresh-row { display: flex; align-items: center; gap: 10px; margin: -8px 0 4px; }
.pull-refresh-indicator {
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  color: var(--amber); transition: height 0.15s ease;
}
.earnings-chart {
  background: var(--surface); border: 1px solid var(--border); border-radius: 14px;
  padding: 16px 12px 10px; margin-bottom: 14px;
}
.earnings-chart-bars { display: flex; align-items: flex-end; height: 90px; gap: 4px; }
.earnings-chart-bar-col {
  display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%;
  position: relative; cursor: pointer;
}
.earnings-chart-bar {
  width: 68%; border-radius: 5px 5px 2px 2px; background: linear-gradient(180deg, var(--surface-3), var(--surface-2));
  animation: chartBarIn 0.5s cubic-bezier(.22,1,.36,1) both;
}
.earnings-chart-bar.current { background: linear-gradient(180deg, #ffb84d, var(--amber)); }
@keyframes chartBarIn { from { height: 0 !important; opacity: 0; } to { opacity: 1; } }
.earnings-chart-tooltip {
  position: absolute; bottom: 100%; margin-bottom: 6px; left: 50%; transform: translateX(-50%);
  background: var(--surface-3); border: 1px solid var(--border); color: var(--text); font-size: 10.5px;
  font-weight: 700; padding: 3px 7px; border-radius: 6px; white-space: nowrap; z-index: 2;
  animation: financeAmountPop 0.15s ease both;
}
.earnings-chart-labels {
  display: flex; justify-content: space-between; margin-top: 8px; font-size: 9.5px; color: var(--text-dim);
}
.badges-view { display: flex; flex-direction: column; gap: 14px; }
.badge-hero-card {
  position: relative; overflow: hidden;
  text-align: center; background: linear-gradient(160deg, rgba(255,166,48,0.16), rgba(255,166,48,0.02));
  border: 1px solid var(--amber-dim); border-radius: 18px; padding: 24px 16px;
  animation: financeTotalIn 0.4s cubic-bezier(.22,1,.36,1) both;
}
.badge-hero-glow {
  position: absolute; top: -60%; left: 50%; transform: translateX(-50%);
  width: 220px; height: 220px; border-radius: 50%;
  background: radial-gradient(circle, rgba(255,166,48,0.28), transparent 70%);
  filter: blur(12px); pointer-events: none; animation: cryptoGlowPulse 3s ease-in-out infinite;
}
.badge-hero-icon { position: relative; z-index: 1; font-size: 36px; display: inline-block; animation: heroRingPulse 2.4s ease-in-out infinite; filter: drop-shadow(0 0 10px rgba(255,166,48,0.5)); }
.badge-hero-icon.streak-hot { font-size: 44px; filter: drop-shadow(0 0 18px rgba(255,166,48,0.75)); animation: heroRingPulse 1.6s ease-in-out infinite; }
.badge-hero-value {
  position: relative; z-index: 1;
  font-size: 42px; font-weight: 700; color: var(--amber); font-family: 'Space Mono', monospace; line-height: 1.1;
  margin: 6px 0 4px;
}
.badge-hero-label { position: relative; z-index: 1; font-size: 12.5px; color: var(--text-muted); }
.badge-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.badge-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 16px 12px;
  text-align: center; animation: homeFadeUp 0.35s cubic-bezier(.22,1,.36,1) both;
  transition: transform 0.15s ease, border-color 0.15s ease;
}
.badge-card:hover { transform: translateY(-2px); border-color: var(--amber-dim); }
.badge-card-icon { font-size: 24px; margin-bottom: 6px; }
.badge-card-value { font-size: 19px; font-weight: 700; color: var(--text); font-family: 'Space Mono', monospace; }
.badge-card-label { font-size: 10.5px; color: var(--text-muted); margin-top: 2px; }
.streak-mini-chip {
  display: flex; width: fit-content; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 999px;
  background: linear-gradient(160deg, rgba(255,166,48,0.16), rgba(255,166,48,0.04));
  border: 1px solid var(--amber-dim); font-size: 12px; font-weight: 700; color: var(--amber);
  margin: 14px auto 16px; animation: homeFadeUp 0.3s ease both;
}
.streak-mini-chip span.flame { font-size: 14px; filter: drop-shadow(0 0 6px rgba(255,166,48,0.6)); }
.badge-card-sub { font-size: 9px; color: var(--text-dim); margin-top: 3px; }
@keyframes wrappedCardIn { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: none; } }
.celebration-overlay {
  position: fixed; inset: 0; z-index: 999; display: flex; align-items: center; justify-content: center;
  pointer-events: auto; overflow: hidden;
}
.confetti-piece {
  position: absolute; top: -20px; width: 9px; height: 14px; border-radius: 2px;
  animation-name: confettiFall; animation-timing-function: ease-in; animation-iteration-count: 1; animation-fill-mode: forwards;
}
@keyframes confettiFall {
  from { transform: translateY(-20px) rotate(0deg); opacity: 1; }
  to { transform: translateY(115vh) rotate(360deg); opacity: 0.9; }
}
.celebration-card {
  position: relative; background: linear-gradient(160deg, #1a1f26, #12151a); border: 1px solid var(--amber-dim);
  border-radius: 20px; padding: 26px 32px; text-align: center; box-shadow: 0 20px 60px rgba(0,0,0,0.6);
  animation: wrappedCardIn 0.4s cubic-bezier(.22,1,.36,1) both;
}
.celebration-emoji { font-size: 40px; animation: financeAmountPop 0.5s cubic-bezier(.34,1.56,.64,1) both; animation-delay: 0.1s; }
.celebration-msg { font-size: 14px; font-weight: 700; color: var(--text); margin-top: 8px; max-width: 240px; }
.skeleton-row {
  background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 14px;
  margin-bottom: 8px; animation: homeFadeUp 0.3s ease both;
}
.skeleton-line {
  height: 11px; border-radius: 6px; margin-bottom: 8px;
  background: linear-gradient(90deg, var(--surface-2) 25%, var(--surface-3) 50%, var(--surface-2) 75%);
  background-size: 200% 100%; animation: skeletonShimmer 1.4s ease-in-out infinite;
}
.skeleton-line:last-child { margin-bottom: 0; }
.skeleton-line-title { width: 60%; }
.skeleton-line-sub { width: 38%; height: 9px; }
.pinned-banner {
  display: flex; align-items: flex-start; gap: 8px; background: rgba(255,166,48,0.1);
  border: 1px solid var(--amber-dim); border-radius: 12px; padding: 9px 12px; margin-bottom: 10px;
  color: var(--amber); animation: homeFadeUp 0.3s ease both;
}
.pinned-banner-text { flex: 1; font-size: 12px; color: var(--text); line-height: 1.4; min-width: 0; overflow-wrap: break-word; }
.pinned-banner-author { font-weight: 700; color: var(--amber); margin-right: 5px; }

/* ---------- "Ludzik" z gratulacjami za dobry utarg (progi 1000/1500/2000/3000+) ---------- */
.earn-celeb-overlay {
  position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center;
  background: rgba(5,6,9,0.7); backdrop-filter: blur(3px); padding: 24px; animation: financeAmountPop 0.25s ease both;
}
.earn-celeb-card {
  position: relative; background: linear-gradient(160deg, #1a1f26, #12151a);
  border: 1.5px solid var(--celeb-accent, var(--amber)); border-radius: 24px; padding: 34px 30px 26px;
  text-align: center; max-width: 320px; overflow: hidden;
  box-shadow: 0 24px 70px rgba(0,0,0,0.65), 0 0 50px -12px var(--celeb-accent, var(--amber));
  animation: earnCelebCardIn 0.5s cubic-bezier(.22,1,.36,1) both;
}
@keyframes earnCelebCardIn {
  0% { opacity: 0; transform: scale(0.7) translateY(30px); }
  60% { transform: scale(1.04) translateY(-4px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}
.earn-celeb-shake { animation: earnCelebCardIn 0.5s cubic-bezier(.22,1,.36,1) both, earnCelebShake 0.5s ease 0.5s 2; }
@keyframes earnCelebShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px) rotate(-1deg); }
  40% { transform: translateX(5px) rotate(1deg); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(3px); }
}
.earn-celeb-mascot {
  display: flex; justify-content: center; margin-bottom: 10px;
  animation: earnCelebMascotBounce 1.1s cubic-bezier(.34,1.56,.64,1) infinite;
  filter: drop-shadow(0 8px 20px var(--celeb-accent, var(--amber)));
}
.earn-celeb-face-svg { display: block; }
@keyframes earnCelebMascotBounce {
  0%, 100% { transform: translateY(0) scale(1) rotate(0deg); }
  30% { transform: translateY(-14px) scale(1.08) rotate(-4deg); }
  50% { transform: translateY(0) scale(0.98) rotate(3deg); }
  70% { transform: translateY(-6px) scale(1.03) rotate(-2deg); }
}
.earn-celeb-title {
  font-size: 21px; font-weight: 900; color: var(--celeb-accent, var(--amber)); letter-spacing: -0.3px;
  margin-bottom: 8px; text-shadow: 0 0 24px var(--celeb-accent, var(--amber));
}
.earn-celeb-subtitle { font-size: 13.5px; color: var(--text-muted); line-height: 1.5; margin-bottom: 22px; }
.earn-celeb-close {
  padding: 11px 26px; border-radius: 12px; border: none; font-weight: 800; font-size: 13.5px; cursor: pointer;
  background: linear-gradient(135deg, var(--celeb-accent, var(--amber)), var(--amber)); color: #1a1206;
  box-shadow: 0 8px 24px -6px var(--celeb-accent, var(--amber)); transition: transform 0.15s ease;
}
.earn-celeb-close:active { transform: scale(0.95); }
.earn-celeb-confetti { position: absolute; inset: 0; overflow: hidden; pointer-events: none; border-radius: 24px; }
.earn-celeb-confetti-piece {
  position: absolute; top: -14px; width: 8px; height: 13px; opacity: 0;
  animation: earnCelebConfettiFall 2.2s ease-in both;
}
.earn-celeb-confetti-piece.confetti-0 { background: var(--amber); }
.earn-celeb-confetti-piece.confetti-1 { background: #6ee7b7; }
.earn-celeb-confetti-piece.confetti-2 { background: #60a5fa; }
.earn-celeb-confetti-piece.confetti-3 { background: #f472b6; }
.earn-celeb-confetti-piece.confetti-4 { background: #ffffff; }
@keyframes earnCelebConfettiFall {
  0% { opacity: 1; transform: translateY(-14px) rotate(0deg); }
  100% { opacity: 0; transform: translateY(340px) rotate(600deg); }
}
.pinned-banner-close {
  flex-shrink: 0; background: none; border: none; color: var(--text-dim); cursor: pointer; padding: 2px;
}
.pinned-banner-close:hover { color: var(--red); }
.chat-edit-row {
  background: var(--surface-2); border: 1px solid var(--amber-dim); border-radius: 14px; padding: 10px;
  min-width: 200px;
}
.chat-edit-textarea {
  width: 100%; background: var(--surface); border: 1px solid var(--border); border-radius: 8px;
  color: var(--text); font-size: 13px; font-family: inherit; padding: 8px; resize: vertical; min-height: 50px;
}
.chat-edit-textarea:focus { outline: none; border-color: var(--amber-dim); }
.chat-edit-actions { display: flex; justify-content: flex-end; gap: 6px; margin-top: 8px; }
.chat-edited-tag { font-size: 10px; opacity: 0.7; font-style: italic; }

/* --- Priorytetowe wiadomości w Czacie z Szefem --- */
.priority-mode-banner {
  display: flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: #ff6b6b;
  background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); border-radius: 12px;
  padding: 8px 12px; margin-top: 12px; animation: homeFadeUp 0.25s ease both;
}
.chat-send-error-banner {
  display: flex; align-items: center; gap: 7px; font-size: 12px; font-weight: 600; color: #ff6b6b;
  background: rgba(255,107,107,0.12); border: 1px solid rgba(255,107,107,0.35); border-radius: 12px;
  padding: 9px 12px; margin-top: 10px; animation: homeFadeUp 0.25s ease both;
}
.chat-send-error-banner span { flex: 1; line-height: 1.4; }
.chat-send-error-close {
  flex-shrink: 0; background: none; border: none; color: rgba(255,107,107,0.75); cursor: pointer;
  display: flex; align-items: center; justify-content: center; padding: 2px;
}
.priority-mode-cancel {
  margin-left: auto; background: none; border: none; color: rgba(255,107,107,0.75); font-size: 11px;
  font-weight: 600; cursor: pointer; text-decoration: underline;
}
.chat-input-row.priority-active {
  border-color: rgba(255,107,107,0.55); box-shadow: 0 4px 16px rgba(255,107,107,0.15);
}
.chat-icon-btn.priority-icon-active { color: #ff6b6b; background: rgba(255,107,107,0.14); }
.chat-send-btn.priority-send-btn {
  background: linear-gradient(150deg, #ff8a80, #e5484d); box-shadow: 0 3px 10px rgba(229,72,77,0.4);
}
.chat-bubble.priority-msg {
  border: 1px solid rgba(255,107,107,0.55) !important;
  box-shadow: 0 0 0 1px rgba(255,107,107,0.15), 0 3px 14px rgba(255,107,107,0.22) !important;
  animation: bubbleIn 0.25s cubic-bezier(.22,1,.36,1) both, priorityPulse 2.6s ease-in-out infinite 0.3s;
}
@keyframes priorityPulse {
  0%, 100% { box-shadow: 0 0 0 1px rgba(255,107,107,0.15), 0 3px 14px rgba(255,107,107,0.22); }
  50% { box-shadow: 0 0 0 1px rgba(255,107,107,0.3), 0 3px 20px rgba(255,107,107,0.38); }
}
.priority-badge {
  display: flex; align-items: center; gap: 4px; font-size: 9.5px; font-weight: 800; letter-spacing: 0.5px;
  color: #ff6b6b; margin-bottom: 5px;
}
.thread-card-priority {
  border-color: rgba(255,107,107,0.5) !important;
  box-shadow: 0 0 0 1px rgba(255,107,107,0.15), 0 3px 14px rgba(255,107,107,0.2) !important;
  animation: priorityPulse 2.6s ease-in-out infinite;
}
.thread-priority-badge {
  display: inline-flex; align-items: center; gap: 3px; font-size: 9px; font-weight: 800; letter-spacing: 0.4px;
  color: #ff6b6b; background: rgba(255,107,107,0.12); border: 1px solid rgba(255,107,107,0.35);
  border-radius: 999px; padding: 2px 7px; margin-left: 6px;
}
.priority-toast {
  position: fixed; top: 16px; left: 50%; transform: translateX(-50%); z-index: 997;
  display: flex; align-items: center; gap: 10px; max-width: 340px;
  background: linear-gradient(155deg, #2a1010, #1a1f26); border: 1px solid rgba(255,107,107,0.5);
  color: #ff8a80; border-radius: 14px; padding: 12px 16px; cursor: pointer;
  box-shadow: 0 12px 30px rgba(0,0,0,0.5); animation: priorityToastIn 0.35s cubic-bezier(.22,1,.36,1) both;
}
@keyframes priorityToastIn { from { opacity: 0; transform: translate(-50%, -16px); } to { opacity: 1; transform: translate(-50%, 0); } }
.priority-toast-title { font-size: 12.5px; font-weight: 800; color: #ff8a80; }
.priority-toast-sub { font-size: 11px; color: #e8b8b5; margin-top: 1px; }
@keyframes skeletonShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.payout-action-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 16px; margin: 4px 0 14px;
}
.payout-waiting-card {
  display: flex; align-items: center; gap: 8px; background: var(--surface); border: 1px solid var(--border);
  border-radius: var(--radius); padding: 13px 16px; margin: 4px 0 14px; font-size: 12px; color: var(--text-dim);
}
.payout-status-card {
  display: flex; align-items: center; gap: 8px; border-radius: var(--radius);
  padding: 13px 16px; margin: 4px 0 14px; font-size: 13px; font-weight: 600;
}
.payout-status-card.approved { background: rgba(110,231,183,0.12); color: var(--green); }
.payout-status-card.rejected { background: rgba(255,107,107,0.12); color: var(--red); }
.payout-status-card.next_week { background: rgba(110,231,183,0.12); color: var(--green); }
.payout-status-card.checking { background: rgba(255,166,48,0.12); color: var(--amber); }
.payout-status-card.paid { background: rgba(110,231,183,0.2); color: var(--green); font-weight: 700; }
.payout-waiting-card svg { flex-shrink: 0; color: var(--amber); }
.payout-action-label { font-size: 12px; color: var(--text-dim); margin-bottom: 10px; text-align: center; }
.payout-action-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.payout-btn {
  flex: 1; min-width: 90px; display: flex; flex-direction: column; align-items: center; gap: 5px;
  padding: 12px 8px; border-radius: 10px; font-size: 11.5px; font-weight: 600; cursor: pointer;
  border: 1px solid var(--border); background: var(--surface-2); color: var(--text);
  transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
}
.payout-btn:hover { transform: translateY(-2px); }
.payout-btn.approve:hover { border-color: rgba(110,231,183,0.5); background: rgba(110,231,183,0.1); color: var(--green); }
.payout-btn.reject:hover { border-color: rgba(255,107,107,0.5); background: rgba(255,107,107,0.1); color: var(--red); }
.payout-btn.review:hover { border-color: rgba(255,166,48,0.5); background: rgba(255,166,48,0.1); color: var(--amber); }

.modal-overlay {
  position: fixed; inset: 0; z-index: 200; background: rgba(0,0,0,0.6); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.modal-card {
  background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 20px; width: 100%; max-width: 340px;
  animation: bonusCardIn 0.3s cubic-bezier(.34,1.56,.64,1) both;
}
.modal-card-wide { max-width: 640px; }

/* --- Profil kierowcy + prywatny czat kierowca-kierowca --- */
.podium-slot, .ranking-row { cursor: pointer; }
.dm-profile-card {
  position: relative; text-align: center; padding-top: 30px;
  display: flex; flex-direction: column; align-items: center;
}
.modal-close-x {
  position: absolute; top: 12px; right: 12px; background: none; border: none; color: var(--text-dim);
  cursor: pointer; padding: 4px; display: flex; align-items: center; justify-content: center;
}
.modal-close-x:hover { color: var(--text); }
.dm-profile-avatar-lg {
  width: 76px; height: 76px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 26px; font-weight: 800; color: #1a1206; margin-bottom: 12px;
  box-shadow: 0 6px 20px -4px rgba(255,166,48,0.4);
}
.dm-profile-name { font-size: 17px; font-weight: 800; color: var(--text); display: flex; align-items: center; gap: 8px; justify-content: center; }
.dm-profile-car {
  display: flex; align-items: center; gap: 10px; margin-top: 16px; background: var(--surface);
  border: 1px solid var(--border); border-radius: 14px; padding: 10px 16px; width: 100%;
}
.dm-profile-car-empty { opacity: 0.6; }
.dm-profile-car-icon {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(150deg, #ffb84d, var(--amber)); color: #1a1206;
  box-shadow: 0 4px 12px -2px rgba(255,166,48,0.4);
}
.dm-profile-car-label { font-size: 13.5px; font-weight: 600; color: var(--text); text-align: left; }
.dm-profile-message-btn { width: 100%; margin-top: 18px; display: flex; align-items: center; justify-content: center; gap: 7px; }

.dm-chat-header-avatar {
  width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; color: #1a1206; flex-shrink: 0;
}
/* --- Pasek przełączania rozmów na górze zakładki Czat: Szefu + każda
   prywatna rozmowa z innym kierowcą, jako pozioma, przewijalna lista
   "chipów" z awatarem, imieniem i kropką nieprzeczytanych. --- */
.dm-switcher-strip {
  display: flex; gap: 8px; overflow-x: auto; padding: 2px 2px 12px; margin-bottom: 4px;
  scrollbar-width: none;
}
.dm-switcher-strip::-webkit-scrollbar { display: none; }
.dm-switcher-chip {
  position: relative; display: flex; flex-direction: column; align-items: center; gap: 5px;
  background: none; border: none; cursor: pointer; flex-shrink: 0; padding: 4px 2px; width: 58px;
  opacity: 0.68; transition: opacity 0.15s ease, transform 0.15s ease;
}
.dm-switcher-chip:active { transform: scale(0.95); }
.dm-switcher-chip.active { opacity: 1; }
.dm-switcher-chip span {
  font-size: 10.5px; font-weight: 600; color: var(--text-muted); white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; max-width: 56px;
}
.dm-switcher-chip.active span { color: var(--amber); font-weight: 800; }
.dm-switcher-avatar {
  width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 800; color: #1a1206; box-shadow: 0 3px 10px rgba(0,0,0,0.25);
  border: 2px solid transparent; transition: border-color 0.15s ease;
}
.dm-switcher-chip.active .dm-switcher-avatar { border-color: var(--amber); }
.dm-switcher-avatar-admin { background: var(--surface-3); font-size: 19px; }
.dm-switcher-dot {
  position: absolute; top: 2px; right: 10px; width: 10px; height: 10px; border-radius: 50%;
  background: var(--red); border: 2px solid var(--bg);
}
.dm-inline-chat { animation: homeFadeUp 0.25s ease both; }
@media (min-width: 700px) {
  .modal-card-wide .earnings-form-grid { grid-template-columns: 1fr 1fr; gap: 0 14px; }
}
.modal-title { font-size: 14.5px; font-weight: 700; color: var(--text); margin-bottom: 12px; }
.modal-textarea {
  width: 100%; min-height: 90px; background: var(--surface); border: 1px solid var(--border); color: var(--text);
  border-radius: 10px; padding: 12px; font-size: 13px; font-family: 'IBM Plex Sans', sans-serif; resize: vertical;
}
.modal-textarea:focus { outline: none; border-color: var(--amber-dim); }
.modal-btns { display: flex; gap: 10px; margin-top: 14px; }
.modal-btns .btn { flex: 1; justify-content: center; }


.refresh-btn {
  display: flex; align-items: center; gap: 6px;
  background: none; border: none; color: var(--text-dim);
  font-size: 11.5px; cursor: pointer; padding: 4px 0;
}
.refresh-btn:hover { color: var(--text-muted); }
.refresh-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.refresh-msg { font-size: 11px; color: var(--text-dim); }
.spin { animation: spin 0.9s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.section-label {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; letter-spacing: 1px; text-transform: uppercase; color: var(--text-dim);
  margin: 16px 2px 10px;
  position: relative; padding-left: 10px;
}
.section-label::before {
  content: ""; position: absolute; left: 0; top: 50%; transform: translateY(-50%);
  width: 3px; height: 14px; border-radius: 2px;
  background: linear-gradient(180deg, var(--amber), var(--amber-dim));
}
.empty-state {
  color: var(--text-dim); font-size: 13px; padding: 28px 4px; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
}
.empty-state svg { opacity: 0.4; }

.ticket-list { display: flex; flex-direction: column; gap: 10px; }
@keyframes ticketIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.stat-label { color: var(--text-dim); font-family: 'IBM Plex Sans', sans-serif; }
.badge {
  display: inline-flex; align-items: center; gap: 4px; margin-top: 6px; font-size: 10.5px; padding: 3px 9px;
  border-radius: 999px; letter-spacing: 0.3px;
}
.badge-amber { background: rgba(255,166,48,0.14); color: var(--amber); }
.badge-green { background: rgba(110,231,183,0.14); color: var(--green); }
.badge-red { background: rgba(255,107,107,0.14); color: var(--red); }

/* Admin */
.zadania-two-col { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; }
.zadania-col { flex: 1; min-width: 300px; }
.rekompensaty-two-col { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; }
.rekompensaty-col { flex: 1; min-width: 320px; }
.kierowcy-header-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
.register-card {
  display: flex; flex-direction: column; gap: 8px;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 14px;
}
.text-input {
  background: var(--surface-2); border: 1px solid var(--border); color: var(--text);
  border-radius: 10px; padding: 10px 12px; font-size: 13px; font-family: 'IBM Plex Sans', sans-serif;
}
.text-input:focus { outline: none; border-color: var(--amber-dim); }
.text-input[type="date"] { color-scheme: dark; }
.form-label { display: block; font-size: 11px; color: var(--text-dim); margin: 10px 0 5px; font-weight: 600; }
.earnings-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 10px; }
.bonus-date-field { display: flex; flex-direction: column; gap: 6px; }
.bonus-date-field label { font-size: 11px; color: var(--text-dim); text-transform: uppercase; letter-spacing: 0.5px; }

.sync-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 14px; display: flex; flex-direction: column; gap: 10px;
}
.sync-text { font-size: 12px; color: var(--text-muted); line-height: 1.5; }
.sync-msg { font-size: 12px; color: var(--amber); }
.sync-msg-error { color: var(--red); }

.dropzone {
  border: 1.5px dashed var(--border); border-radius: var(--radius);
  padding: 26px 16px; text-align: center; color: var(--text-muted);
  cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease;
}
.dropzone:hover { border-color: var(--amber-dim); }
.dropzone.dragging { border-color: var(--amber); color: var(--amber); background: rgba(255,166,48,0.05); }
.dropzone-title { font-size: 13.5px; color: var(--text); margin-top: 10px; }
.dropzone-sub { font-size: 12px; color: var(--text-dim); margin-top: 3px; }

.help-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: 10px;
  padding: 12px 14px; margin-top: 12px;
}
.help-title { font-size: 11.5px; letter-spacing: 1px; text-transform: uppercase; color: var(--text-dim); margin-bottom: 5px; }
.help-text { font-size: 12px; color: var(--text-muted); line-height: 1.5; }

.preview-card {
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 14px; margin-top: 14px;
}
.preview-header { display: flex; justify-content: space-between; font-size: 12.5px; color: var(--text); margin-bottom: 8px; }
.preview-skipped { color: var(--red); }
.preview-list { display: flex; flex-direction: column; gap: 6px; }
.preview-row { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; color: var(--text-muted); }
.preview-name { flex: 1; color: var(--text); }
.preview-amount { font-family: 'Space Mono', monospace; color: var(--amber); }
.preview-more { font-size: 11.5px; color: var(--text-dim); margin-top: 4px; }
.preview-actions { display: flex; gap: 10px; margin-top: 14px; }

.btn {
  flex: 1; border-radius: 10px; padding: 11px 0; font-size: 13px; font-weight: 600;
  cursor: pointer; border: 1px solid transparent; transition: transform 0.15s ease, opacity 0.15s ease, box-shadow 0.2s ease;
}
.btn:hover:not(:disabled) { transform: translateY(-1px); }
.btn:active:not(:disabled) { transform: scale(0.97); }
.btn-sm { flex: none; padding: 7px 12px; font-size: 11.5px; border-radius: 8px; }
.btn-primary {
  background: linear-gradient(150deg, #ffb84d, var(--amber)); color: #1a1206;
  box-shadow: 0 4px 14px rgba(255,166,48,0.28);
}
.btn-primary:hover:not(:disabled) { box-shadow: 0 6px 18px rgba(255,166,48,0.38); }
.btn-ghost { background: transparent; border-color: var(--border); color: var(--text-muted); }
.btn-danger { background: var(--red); color: #2a0d0d; }
.btn-danger-ghost {
  background: transparent; border: 1px solid var(--border); color: var(--red);
  display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%;
}
.btn:disabled { opacity: 0.4; cursor: not-allowed; }

.driver-list { display: flex; flex-direction: column; gap: 8px; }
.driver-row {
  display: flex; justify-content: space-between; align-items: center; gap: 10px;
  background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 10px 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15); transition: border-color 0.15s ease, box-shadow 0.2s ease;
}
.driver-row:hover { border-color: var(--amber-dim); box-shadow: 0 4px 14px rgba(0,0,0,0.22); }
.avatar-sm { width: 30px; height: 30px; font-size: 11px; flex-shrink: 0; }
.driver-info { flex: 1; min-width: 0; }
.driver-name { font-size: 13px; color: var(--text); }
.driver-pin { font-size: 11px; color: var(--text-dim); font-family: 'Space Mono', monospace; margin-top: 2px; }
.driver-car {
  display: flex; align-items: center; gap: 4px;
  font-size: 10.5px; color: var(--text-muted); margin-top: 3px;
}
.driver-car svg { color: var(--amber); }
.driver-right { text-align: right; flex: 1; }
.driver-pending { font-family: 'Space Mono', monospace; font-size: 13px; color: var(--amber); }
.driver-count { font-size: 10.5px; color: var(--text-dim); margin-top: 2px; }
.driver-debt { font-size: 10.5px; color: var(--red); margin-top: 2px; font-family: 'Space Mono', monospace; }

.danger-zone { margin-top: 22px; }
.confirm-box {
  background: rgba(255,107,107,0.08); border: 1px solid var(--red); border-radius: 10px;
  padding: 12px 14px; font-size: 12.5px; color: var(--text);
}
.confirm-actions { display: flex; gap: 10px; margin-top: 10px; }

/* =====================================================================
   Czat 2.0 — nowy wygląd rozmów (czat z Szefem, czat grupowy, prywatne).
   To osobna warstwa NA KOŃCU arkusza: nadpisuje wcześniejsze reguły, więc
   żeby wrócić do starego wyglądu wystarczy usunąć ten blok.
   ===================================================================== */
.chat-view { position: relative; }

/* --- Nagłówek rozmowy --- */
.chat-header { padding: 2px 2px 14px; gap: 12px; }
.chat-header-topic { display: flex; align-items: center; gap: 11px; letter-spacing: -0.2px; font-size: 16px; }
.chat-header-icon {
  width: 36px; height: 36px; border-radius: 13px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; font-size: 18px;
  background: linear-gradient(145deg, rgba(255,166,48,0.26), rgba(255,166,48,0.06));
  border: 1px solid rgba(255,166,48,0.32);
  box-shadow: 0 8px 18px -8px rgba(255,140,30,0.7), inset 0 1px 0 rgba(255,255,255,0.12);
}

/* --- Obszar wiadomości --- */
.chat-messages {
  padding: 12px 8px 16px; gap: 3px; border-radius: 22px;
  background:
    radial-gradient(ellipse 280px 190px at 10% 4%, rgba(255,166,48,0.085), transparent 70%),
    radial-gradient(ellipse 260px 210px at 94% 96%, rgba(96,165,250,0.07), transparent 70%),
    linear-gradient(180deg, rgba(255,255,255,0.028), rgba(255,255,255,0));
  border: 1px solid rgba(255,255,255,0.05);
  scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.2) transparent;
}
.chat-messages::-webkit-scrollbar { width: 6px; }
.chat-messages::-webkit-scrollbar-track { background: transparent; }
.chat-messages::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.16); border-radius: 6px; }
.chat-messages::-webkit-scrollbar-thumb:hover { background: rgba(255,166,48,0.5); }

/* Daty jako szklane "pastylki" */
.chat-date-separator { margin: 16px 0 10px; }
.chat-date-separator span {
  font-size: 10px; font-weight: 700; letter-spacing: 0.9px; text-transform: uppercase;
  color: var(--text-muted); padding: 5px 14px;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1);
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 8px 16px -10px rgba(0,0,0,0.7);
}

/* --- Dymki --- */
@keyframes bubbleIn {
  0% { opacity: 0; transform: translateY(14px) scale(0.88); }
  60% { opacity: 1; transform: translateY(-2px) scale(1.012); }
  100% { opacity: 1; transform: none; }
}
.chat-bubble:not(.priority-msg) { animation-duration: 0.42s; }
.chat-bubble-col { animation-duration: 0.42s; }
.chat-bubble.mine, .chat-bubble-col.mine, .msg-with-reply.mine { transform-origin: 100% 100%; }
.chat-bubble.theirs, .chat-bubble-col.theirs, .msg-with-reply.theirs { transform-origin: 0 100%; }
.chat-bubble {
  padding: 10px 14px; border-radius: 20px; font-size: 14px; line-height: 1.5; letter-spacing: 0.005em;
}
.chat-bubble.mine {
  background: linear-gradient(150deg, #ffc76f 0%, #ffab3d 46%, #ff8f24 100%);
  color: #1d1305;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 10px 22px -10px rgba(255,140,30,0.75);
}
.chat-bubble.theirs {
  background: linear-gradient(160deg, rgba(255,255,255,0.1), rgba(255,255,255,0.038));
  border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 10px 24px -14px rgba(0,0,0,0.8);
}
.chat-bubble.admin-msg {
  background: linear-gradient(150deg, #a3f2c8 0%, #4fd591 52%, #2fb673 100%) !important;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.45), 0 10px 22px -10px rgba(47,182,115,0.75) !important;
}
.chat-bubble.admin-msg.important {
  background: linear-gradient(155deg, #ffc76f, #ff9a2e) !important;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.45), 0 10px 26px -10px rgba(255,140,30,0.8) !important;
}
.chat-bubble-author { font-size: 11px; letter-spacing: 0.1px; margin-bottom: 4px; }
.chat-bubble.admin-msg.important .important-badge { color: #7a3a00; }
.chat-bubble.mine .important-badge { color: #7a3a00; }
/* Rozmowa trochę wyższa — więcej wiadomości na ekranie telefonu */
.chat-view { height: max(340px, calc(100dvh - 290px)); }
.msg-time, .msg-time-group, .chat-msg-time { font-size: 10px; letter-spacing: 0.3px; }

/* Cytat (odpowiedź na wiadomość) */
.chat-quote {
  border-left: 3px solid var(--amber); background: rgba(0,0,0,0.16);
  border-radius: 10px; padding: 6px 10px; margin-bottom: 7px; opacity: 1;
}
.chat-bubble.mine .chat-quote { background: rgba(29,19,5,0.14); border-left-color: #1d1305; }
.chat-bubble.mine .chat-quote-author { color: #1d1305; }
.chat-bubble.mine .chat-quote-text { color: rgba(29,19,5,0.72); }
.chat-bubble.admin-msg .chat-quote-author { color: #0d2a19; }
.chat-bubble.admin-msg .chat-quote-text { color: rgba(13,42,25,0.75); }

/* Same emotikony — duże, bez dymka */
.emoji-jumbo { display: block; white-space: nowrap; font-size: 40px; line-height: 1.15; letter-spacing: 5px; filter: drop-shadow(0 6px 10px rgba(0,0,0,0.3)); }
.chat-bubble:has(> .emoji-jumbo) {
  background: transparent !important; border-color: transparent !important; box-shadow: none !important;
  backdrop-filter: none; -webkit-backdrop-filter: none; padding: 2px 4px;
}
.pinned-banner .emoji-jumbo { font-size: inherit; letter-spacing: 0; display: inline; filter: none; }

/* Załączniki */
.chat-attachment-img { border-radius: 14px; max-width: min(240px, 100%); border: 1px solid rgba(255,255,255,0.1); box-shadow: 0 10px 24px -12px rgba(0,0,0,0.7); }
.chat-attachment-video { border-radius: 14px; }

/* Reakcje — "wyskakują" po dodaniu */
@keyframes reactPop { 0% { transform: scale(0.4); } 60% { transform: scale(1.18); } 100% { transform: scale(1); } }
.msg-reaction-pill { animation: reactPop 0.32s cubic-bezier(.34,1.56,.64,1) both; }

/* --- Odtwarzacz głosówek --- */
.voice-player-wrap { margin-bottom: 4px; }
.voice-player { display: flex; align-items: center; gap: 11px; width: 236px; min-width: 0; max-width: 100%; padding: 1px 2px 2px 0; }
.voice-audio-hidden { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.voice-play-btn {
  width: 42px; height: 42px; border-radius: 50%; border: none; flex-shrink: 0; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #1a1206; background: linear-gradient(145deg, #ffc76f, #ff9a2e);
  box-shadow: 0 8px 16px -6px rgba(255,140,30,0.85), inset 0 1px 0 rgba(255,255,255,0.55);
  transition: transform 0.18s cubic-bezier(.34,1.56,.64,1), box-shadow 0.2s ease;
}
.voice-play-btn:hover { transform: scale(1.06); }
.voice-play-btn:active { transform: scale(0.9); }
.voice-player.playing .voice-play-btn { box-shadow: 0 0 0 5px rgba(255,166,48,0.18), 0 8px 16px -6px rgba(255,140,30,0.85), inset 0 1px 0 rgba(255,255,255,0.55); }
.chat-bubble.mine .voice-play-btn { background: #1d1305; color: #ffc76f; box-shadow: 0 8px 16px -6px rgba(0,0,0,0.55); }
.chat-bubble.mine .voice-player.playing .voice-play-btn { box-shadow: 0 0 0 5px rgba(29,19,5,0.16), 0 8px 16px -6px rgba(0,0,0,0.55); }
.chat-bubble.admin-msg .voice-play-btn { background: #0d2a19; color: #a3f2c8; box-shadow: 0 8px 16px -6px rgba(0,0,0,0.5); }
.voice-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.voice-wave { height: 34px; display: flex; align-items: center; gap: 2px; cursor: pointer; touch-action: pan-y; user-select: none; -webkit-user-select: none; }
.voice-bar {
  flex: 1; min-width: 2px; border-radius: 3px; background: currentColor; opacity: 0.3;
  transform-origin: 50% 50%; transition: opacity 0.14s ease, background 0.14s ease;
}
.voice-bar.on { opacity: 1; }
.chat-bubble.theirs .voice-bar.on { background: var(--amber); }
.voice-player.playing .voice-bar.on:nth-child(odd) { animation: voicePulse 0.9s ease-in-out infinite; animation-delay: calc(var(--i) * -55ms); }
@keyframes voicePulse { 50% { transform: scaleY(0.7); } }
.voice-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-family: 'Space Mono', monospace; font-size: 10.5px; }
.voice-time { opacity: 0.85; }
.voice-speed-btn {
  background: transparent; color: inherit; opacity: 0.8; cursor: pointer;
  border: 1px solid currentColor; border-radius: 999px; padding: 1px 8px;
  font: 700 10px 'Space Mono', monospace; transition: opacity 0.15s ease, transform 0.15s ease;
}
.voice-speed-btn:hover { opacity: 1; }
.voice-speed-btn:active { transform: scale(0.92); }
.audio-boost-row { margin: 2px 0 6px 52px; }

/* --- Pole pisania (pływająca szklana kapsuła) --- */
.chat-input-row {
  margin-top: 12px; padding: 6px 6px 6px 8px; border-radius: 30px; gap: 3px;
  background: linear-gradient(160deg, rgba(255,255,255,0.1), rgba(255,255,255,0.045));
  border: 1px solid rgba(255,255,255,0.11);
  backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 16px 34px -16px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.07);
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
}
.chat-input-row:focus-within {
  border-color: rgba(255,166,48,0.6);
  box-shadow: 0 0 0 3px rgba(255,166,48,0.16), 0 16px 34px -14px rgba(255,140,30,0.45), inset 0 1px 0 rgba(255,255,255,0.07);
}
.chat-input { font-size: 15px; }
.chat-icon-btn { transition: color 0.15s ease, background 0.15s ease, transform 0.15s ease; }
.chat-icon-btn:active:not(:disabled) { transform: scale(0.88); }
@keyframes sendPop { 0% { transform: scale(0.7); } 60% { transform: scale(1.14); } 100% { transform: scale(1); } }
.chat-send-btn {
  width: 44px; height: 44px;
  background: linear-gradient(145deg, #ffc76f, #ff9a2e);
  box-shadow: 0 10px 20px -6px rgba(255,140,30,0.75), inset 0 1px 0 rgba(255,255,255,0.55);
  transition: transform 0.22s cubic-bezier(.34,1.56,.64,1), opacity 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease;
}
.chat-send-btn:hover { transform: scale(1.08) rotate(-8deg); }
.chat-input-row:has(.chat-input:placeholder-shown) .chat-send-btn { opacity: 0.5; filter: saturate(0.55); transform: scale(0.9); box-shadow: none; }
.chat-input-row:has(.chat-input:not(:placeholder-shown)) .chat-send-btn { animation: sendPop 0.36s cubic-bezier(.34,1.56,.64,1); }

/* Nagrywanie głosówki */
.voice-recording-row {
  height: 46px; border-radius: 26px; gap: 10px;
  background: linear-gradient(90deg, rgba(255,107,107,0.2), rgba(255,107,107,0.06));
  border: 1px solid rgba(255,107,107,0.45); animation: recGlow 1.8s ease-in-out infinite;
}
@keyframes recGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,107,107,0.28); } 50% { box-shadow: 0 0 0 6px rgba(255,107,107,0); } }
.voice-recording-dot { width: 11px; height: 11px; animation: recDot 1.2s ease-out infinite; }
@keyframes recDot { 0% { box-shadow: 0 0 0 0 rgba(255,107,107,0.7); } 100% { box-shadow: 0 0 0 9px rgba(255,107,107,0); } }
.voice-recording-time { flex: 0 0 auto; font-size: 12px; }
.rec-wave { flex: 1; min-width: 0; height: 26px; display: inline-flex; align-items: center; justify-content: center; gap: 3px; overflow: hidden; }
.rec-wave i {
  display: block; width: 3px; height: 100%; border-radius: 3px; flex-shrink: 0;
  background: linear-gradient(180deg, #ffa39b, #ff5e5e); transform-origin: 50% 50%;
  animation: recBar 0.9s ease-in-out infinite;
}
@keyframes recBar { 0%, 100% { transform: scaleY(0.2); } 50% { transform: scaleY(1); } }

/* --- Przycisk "przewiń do najnowszych" --- */
.chat-scroll-fab {
  position: absolute; right: 16px; z-index: 6; width: 42px; height: 42px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text);
  background: rgba(32,38,48,0.86); border: 1px solid rgba(255,255,255,0.16);
  backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 12px 26px -10px rgba(0,0,0,0.8);
  opacity: 0; transform: translateY(12px) scale(0.8); pointer-events: none;
  transition: opacity 0.2s ease, transform 0.28s cubic-bezier(.34,1.56,.64,1);
}
.chat-scroll-fab.visible { opacity: 1; transform: none; pointer-events: auto; }
.chat-scroll-fab:hover { color: var(--amber); border-color: var(--amber-dim); }
.chat-scroll-fab-badge {
  position: absolute; top: -7px; right: -5px; min-width: 20px; height: 20px; padding: 0 5px; border-radius: 999px;
  display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800;
  color: #1a1206; background: linear-gradient(145deg, #ffc76f, #ff8f24);
  box-shadow: 0 4px 10px -2px rgba(255,140,30,0.7); animation: sendPop 0.3s cubic-bezier(.34,1.56,.64,1);
}

/* --- Ptaszki (wysłano / przeczytano) --- */
.msg-ticks { display: inline-flex; align-items: center; gap: 3px; margin-left: 7px; color: var(--text-dim); vertical-align: middle; transition: color 0.3s ease; }
.msg-ticks.read { color: #5eead4; }
.msg-ticks-label { font-size: 9.5px; font-weight: 600; letter-spacing: 0.2px; }

/* --- Czat grupowy: dodatki --- */
.group-reply-link {
  align-self: flex-start; display: inline-flex; align-items: center; gap: 4px; margin: 2px 0 0; padding: 3px 9px;
  background: none; border: none; border-radius: 999px; cursor: pointer; opacity: 0.7;
  color: var(--text-dim); font: 600 10.5px 'IBM Plex Sans', sans-serif;
  transition: color 0.15s ease, background 0.15s ease, opacity 0.15s ease;
}
.group-reply-link:hover { color: var(--amber); background: rgba(255,166,48,0.1); opacity: 1; }
.chat-bubble-col.mine .group-reply-link { align-self: flex-end; }
.chat-seen-by { font-size: 10px; letter-spacing: 0.1px; }
.chat-avatar { width: 30px; height: 30px; box-shadow: 0 6px 12px -4px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.3); }
.pinned-banner { border-radius: 16px; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }

/* --- Wariant jasny --- */
.app-root.light-theme .chat-messages {
  background:
    radial-gradient(ellipse 280px 190px at 10% 4%, rgba(217,130,15,0.1), transparent 70%),
    radial-gradient(ellipse 260px 210px at 94% 96%, rgba(96,165,250,0.1), transparent 70%),
    linear-gradient(180deg, rgba(255,255,255,0.6), rgba(255,255,255,0.2));
  border-color: rgba(0,0,0,0.06);
  scrollbar-color: rgba(0,0,0,0.25) transparent;
}
.app-root.light-theme .chat-messages::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.2); }
.app-root.light-theme .chat-bubble.theirs {
  background: #ffffff; border: 1px solid rgba(0,0,0,0.07); box-shadow: 0 10px 24px -14px rgba(60,40,10,0.45);
}
.app-root.light-theme .chat-date-separator span { background: rgba(255,255,255,0.85); border-color: rgba(0,0,0,0.08); box-shadow: 0 8px 16px -10px rgba(60,40,10,0.35); }
.app-root.light-theme .chat-input-row {
  background: rgba(255,255,255,0.9); border-color: rgba(0,0,0,0.1);
  box-shadow: 0 16px 34px -16px rgba(60,40,10,0.4), inset 0 1px 0 rgba(255,255,255,0.9);
}
.app-root.light-theme .chat-scroll-fab { background: rgba(255,255,255,0.92); border-color: rgba(0,0,0,0.1); box-shadow: 0 12px 26px -10px rgba(60,40,10,0.45); }
.app-root.light-theme .chat-quote { background: rgba(0,0,0,0.05); }
.app-root.light-theme .chat-bubble-author[style] { filter: brightness(0.62) saturate(1.35); }
.app-root.light-theme .chat-attachment-img { border-color: rgba(0,0,0,0.08); }
.app-root.light-theme .msg-ticks.read { color: #0d9488; }

/* --- Płynność przewijania rozmowy --- */
/* Przewijanie listy nie "przechodzi" na całą stronę (koniec sprężynowania/skoków strony na telefonie). */
.chat-messages { overscroll-behavior-y: contain; -webkit-overflow-scrolling: touch; overflow-anchor: auto; animation: chatOpen 0.22s ease both; }
@keyframes chatOpen { from { opacity: 0; } to { opacity: 1; } }
/* Wiadomości obecne przy otwarciu rozmowy pojawiają się od razu (bez 400 animacji naraz);
   animują się dopiero te, które przyjdą później (patrz useSmartAutoScroll: data-ready). */
.chat-messages:not([data-ready]) .chat-bubble, .chat-messages:not([data-ready]) .chat-bubble-col { animation-duration: 0s; }
.chat-messages:not([data-ready]) .msg-reaction-pill { animation-duration: 0s; }

/* =====================================================================
   Ranking i zadania 2.0 — nowy wygląd (zakładka Ranking: Ranking / Zadania).
   Osobna warstwa NA KOŃCU arkusza; klasy rk2-* i tk2-* — usunięcie bloku
   przywraca stary wygląd nagłówka (stare klasy zostają w arkuszu wyżej).
   ===================================================================== */
.ranking-title { font-size: 21px; letter-spacing: -0.4px; }
.ranking-subnav {
  gap: 4px; padding: 4px; border-radius: 16px; background: var(--surface); border: 1px solid var(--border);
}
.ranking-subnav-btn {
  border: none; background: transparent; border-radius: 12px; padding: 10px 0;
  transition: color .2s ease, background .25s ease, box-shadow .25s ease, transform .15s ease;
}
.ranking-subnav-btn:active { transform: scale(0.96); }
.ranking-subnav-btn.active {
  color: var(--amber); border: none;
  background: linear-gradient(150deg, rgba(255,184,77,0.24), rgba(255,166,48,0.07));
  box-shadow: 0 8px 18px -10px rgba(255,140,30,0.8), inset 0 0 0 1px rgba(255,166,48,0.45);
}

/* ---------- pierścień postępu ---------- */
.rk2-ring { position: relative; flex-shrink: 0; border-radius: 50%; box-shadow: 0 0 26px -8px rgba(255,166,48,0.5); }
.rk2-ring-green { box-shadow: 0 0 26px -8px rgba(110,231,183,0.5); }
.rk2-ring svg { display: block; }
.rk2-ring-track { stroke: rgba(255,255,255,0.09); }
.rk2-ring-fill { stroke: var(--amber); transition: stroke-dashoffset 1.4s cubic-bezier(.22,1,.36,1); }
.rk2-ring-green .rk2-ring-fill { stroke: var(--green); }
.rk2-ring-inner { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }

/* ---------- karta "Twoja pozycja" ---------- */
.rk2-hero {
  position: relative; overflow: hidden; display: flex; align-items: center; gap: 16px;
  padding: 18px 16px; border-radius: 22px; margin-bottom: 16px;
  background: linear-gradient(150deg, rgba(255,184,77,0.17), rgba(255,166,48,0.03) 62%), var(--surface);
  border: 1px solid rgba(255,166,48,0.5);
  box-shadow: 0 18px 38px -22px rgba(255,140,30,0.75), inset 0 1px 0 rgba(255,255,255,0.06);
  animation: rk2Pop .7s cubic-bezier(.22,1,.36,1) both;
}
.rk2-hero::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: -60%; width: 40%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.11), transparent);
  transform: skewX(-18deg); animation: rk2Sweep 5.5s ease-in-out infinite 1.2s;
}
.rk2-hero-gold { border-color: rgba(255,200,90,0.65); }
.rk2-hero-place { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 30px; line-height: 1; color: var(--text); }
.rk2-hero-place small { font-size: 15px; color: var(--amber); margin-right: 1px; }
.rk2-hero-info { min-width: 0; position: relative; }
.rk2-hero-label { font-size: 10.5px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-dim); font-weight: 700; }
.rk2-hero-points { font-family: 'Space Mono', monospace; font-size: 30px; font-weight: 700; color: var(--amber); margin: 3px 0 2px; line-height: 1.1; }
.rk2-hero-points span { font-size: 14px; color: var(--text-muted); }
.rk2-hero-hint { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--text-muted); font-weight: 600; margin-bottom: 8px; }
.rk2-hero-hint svg { color: var(--amber); flex-shrink: 0; }

.rk2-tier { display: inline-block; font-size: 10.5px; font-weight: 700; padding: 3px 9px; border-radius: 999px; }
.rk2-tier.tier-gold { background: rgba(255,200,90,0.16); color: #ffc94d; }
.rk2-tier.tier-silver { background: rgba(201,206,214,0.16); color: #c9ced6; }
.rk2-tier.tier-bronze { background: rgba(184,121,74,0.18); color: #d99a63; }

/* ---------- podium ---------- */
.rk2-podium {
  position: relative; display: flex; align-items: flex-end; justify-content: center; gap: 8px;
  padding: 38px 2px 0; margin-bottom: 18px;
}
.rk2-spot {
  position: absolute; left: 50%; top: -30px; width: 320px; height: 320px; margin-left: -160px; border-radius: 50%; pointer-events: none;
  background: conic-gradient(from 0deg, rgba(255,205,100,0) 0deg, rgba(255,205,100,0.26) 35deg, rgba(255,205,100,0) 75deg, rgba(255,205,100,0) 180deg, rgba(255,205,100,0.18) 215deg, rgba(255,205,100,0) 255deg);
  -webkit-mask-image: radial-gradient(circle, #000 18%, transparent 66%); mask-image: radial-gradient(circle, #000 18%, transparent 66%);
  animation: rk2Spin 16s linear infinite;
}
.rk2-sparks { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.rk2-sparks i {
  position: absolute; width: 4px; height: 4px; border-radius: 50%; background: #ffd978; opacity: 0;
  box-shadow: 0 0 8px 2px rgba(255,205,100,0.7); animation: rk2Float 4.2s ease-in-out infinite;
}
.rk2-sparks i:nth-child(1) { left: 12%; top: 62%; animation-delay: 0s; }
.rk2-sparks i:nth-child(2) { left: 24%; top: 30%; animation-delay: .7s; width: 3px; height: 3px; }
.rk2-sparks i:nth-child(3) { left: 38%; top: 12%; animation-delay: 1.5s; }
.rk2-sparks i:nth-child(4) { left: 62%; top: 14%; animation-delay: 2.1s; width: 3px; height: 3px; }
.rk2-sparks i:nth-child(5) { left: 76%; top: 34%; animation-delay: .3s; }
.rk2-sparks i:nth-child(6) { left: 88%; top: 64%; animation-delay: 1.1s; width: 3px; height: 3px; }
.rk2-sparks i:nth-child(7) { left: 50%; top: 8%; animation-delay: 2.8s; width: 3px; height: 3px; }
.rk2-sparks i:nth-child(8) { left: 6%; top: 40%; animation-delay: 3.3s; }

.rk2-slot { position: relative; z-index: 1; flex: 1; min-width: 0; max-width: 130px; display: flex; flex-direction: column; align-items: center; cursor: pointer; }
.rk2-slot-empty { visibility: hidden; }
.rk2-place-1 { --g1: #ffe08a; --g2: #ff9a2e; --glow: rgba(255,170,50,0.85); --h: 96px; --d: .55s; }
.rk2-place-2 { --g1: #f1f4f7; --g2: #aab3be; --glow: rgba(200,210,222,0.55); --h: 68px; --d: .35s; }
.rk2-place-3 { --g1: #eab27c; --g2: #b3703a; --glow: rgba(204,128,66,0.6); --h: 50px; --d: .45s; }
.rk2-avatar-ring {
  position: relative; width: 60px; height: 60px; border-radius: 50%; padding: 3px;
  background: conic-gradient(from 180deg, var(--g1), var(--g2), var(--g1));
  box-shadow: 0 10px 24px -8px var(--glow);
  animation: rk2Drop .85s cubic-bezier(.34,1.56,.64,1) both; animation-delay: var(--d);
}
.rk2-place-1 .rk2-avatar-ring { width: 76px; height: 76px; padding: 4px; }
.rk2-avatar {
  width: 100%; height: 100%; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: var(--surface-2); color: var(--text); font-weight: 800; font-size: 17px; text-transform: uppercase;
}
.rk2-place-1 .rk2-avatar { font-size: 22px; }
.rk2-crown {
  position: absolute; top: -27px; left: 50%; margin-left: -15px; color: #ffc94d; fill: rgba(255,201,77,0.28);
  filter: drop-shadow(0 0 8px rgba(255,190,60,0.85)); animation: rk2Bob 2.6s ease-in-out infinite;
}
.rk2-place-chip {
  position: absolute; right: -5px; bottom: -3px; width: 24px; height: 24px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 12px; color: #241a04;
  background: linear-gradient(150deg, var(--g1), var(--g2)); border: 2px solid var(--bg);
}
.rk2-slot-name { margin-top: 11px; max-width: 100%; font-size: 12px; font-weight: 700; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rk2-me .rk2-slot-name { color: var(--amber); }
.rk2-slot-points { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 17px; color: var(--g1); margin-top: 2px; }
.rk2-slot-points small { font-size: 10px; color: var(--text-dim); }
.rk2-slot-bonus { margin-top: 4px; font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 999px; background: rgba(255,200,90,0.14); color: #ffc94d; }
.rk2-place-2 .rk2-slot-bonus, .rk2-place-3 .rk2-slot-bonus { background: rgba(255,200,90,0.14); }
.rk2-pedestal {
  position: relative; overflow: hidden; width: 100%; height: var(--h); margin-top: 9px; border-radius: 14px 14px 0 0;
  background: linear-gradient(180deg, var(--g1), var(--g2)); transform-origin: bottom;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 -10px 26px -12px var(--glow);
  animation: rk2Rise .95s cubic-bezier(.22,1,.36,1) both; animation-delay: calc(var(--d) - .3s);
}
.rk2-pedestal::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: -70%; width: 45%;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.45), transparent); transform: skewX(-20deg);
  animation: rk2Sweep 4.5s ease-in-out infinite 1.5s;
}
.rk2-pedestal-num { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 40px; font-weight: 800; color: rgba(36,26,4,0.3); }
.rk2-me-tag {
  display: inline-block; margin-left: 5px; font-size: 9px; font-weight: 800; letter-spacing: .4px; padding: 2px 6px; border-radius: 999px;
  background: var(--amber); color: #1a1206; vertical-align: middle;
}
.rk2-slot .rk2-me-tag { margin: 3px 0 0; }

/* ---------- lista miejsc 4+ ---------- */
.rk2-list { display: flex; flex-direction: column; gap: 9px; margin-bottom: 16px; }
.rk2-row {
  position: relative; display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 18px; cursor: pointer;
  background: var(--surface); border: 1px solid var(--border); animation: rk2RowIn .6s cubic-bezier(.22,1,.36,1) both;
  transition: transform .15s ease, border-color .2s ease;
}
.rk2-row:active { transform: scale(0.985); }
.rk2-row-rank { width: 26px; text-align: center; font-family: 'Space Mono', monospace; font-weight: 700; font-size: 15px; color: var(--text-dim); flex-shrink: 0; }
.rk2-row-avatar {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 13px; text-transform: uppercase; background: var(--surface-3); color: var(--text-muted);
  box-shadow: inset 0 0 0 2px var(--border);
}
.rk2-row-silver .rk2-row-avatar { box-shadow: inset 0 0 0 2px rgba(201,206,214,0.55); }
.rk2-row-bronze .rk2-row-avatar { box-shadow: inset 0 0 0 2px rgba(184,121,74,0.5); }
.rk2-row-main { flex: 1; min-width: 0; }
.rk2-row-name { font-size: 14px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rk2-row-bar { height: 6px; border-radius: 4px; background: var(--surface-3); overflow: hidden; margin: 7px 0 5px; }
.rk2-row-bar-fill {
  height: 100%; border-radius: 4px; transform-origin: left; background: linear-gradient(90deg, var(--amber-dim), var(--amber));
  animation: rk2Grow 1.1s cubic-bezier(.22,1,.36,1) both;
}
.rk2-row-silver .rk2-row-bar-fill { background: linear-gradient(90deg, #8f98a4, #d4d9e0); }
.rk2-row-bronze .rk2-row-bar-fill { background: linear-gradient(90deg, #9a6233, #d99a63); }
.rk2-row-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; font-size: 10.5px; color: var(--text-dim); }
.rk2-row-meta span { display: inline-flex; align-items: center; gap: 4px; }
.rk2-row-hint { color: var(--amber); font-weight: 700; }
.rk2-row-pts { text-align: right; flex-shrink: 0; }
.rk2-row-pts-val { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 19px; color: var(--amber); line-height: 1; }
.rk2-row-pts-lbl { font-size: 9.5px; color: var(--text-dim); margin: 2px 0 4px; }
.rk2-row-me {
  border-color: rgba(255,166,48,0.6);
  background: linear-gradient(150deg, rgba(255,184,77,0.13), var(--surface) 60%);
  animation: rk2RowIn .6s cubic-bezier(.22,1,.36,1) both, rk2MeGlow 2.8s ease-in-out infinite 1s;
}

/* rekord firmy i zasady */
.rk2-record { margin-bottom: 14px; }
.rk2-rules { border: 1px solid var(--border); border-radius: 18px; background: var(--surface); overflow: hidden; }
.rk2-rules-toggle, .tk2-archive-toggle {
  width: 100%; display: flex; align-items: center; gap: 10px; padding: 14px 16px; background: none; border: none;
  color: var(--text); font: 700 13.5px 'IBM Plex Sans', sans-serif; cursor: pointer; text-align: left;
}
.rk2-rules-toggle > svg:first-child { color: var(--amber); }
.tk2-chev { margin-left: auto; flex-shrink: 0; color: var(--text-dim); transition: transform .35s cubic-bezier(.22,1,.36,1); }
.rk2-rules.open .tk2-chev, .tk2-archive.open .tk2-chev { transform: rotate(180deg); }
.rk2-rules-body, .tk2-archive-body { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .42s cubic-bezier(.22,1,.36,1); }
.rk2-rules.open .rk2-rules-body, .tk2-archive.open .tk2-archive-body { grid-template-rows: 1fr; }
.rk2-rules-inner, .tk2-archive-inner { overflow: hidden; min-height: 0; padding: 0 16px; }
.rk2-rules.open .rk2-rules-inner, .tk2-archive.open .tk2-archive-inner { padding-bottom: 16px; }
.rk2-chips { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.rk2-chip {
  position: relative; display: flex; flex-direction: column; gap: 2px; padding: 10px 12px 10px 24px; border-radius: 12px;
  background: var(--surface-2); border: 1px solid var(--border); font-size: 11.5px; color: var(--text-muted);
}
.rk2-chip b { color: var(--text); font-size: 14px; font-family: 'Space Mono', monospace; }
.rk2-chip-wide { grid-column: 1 / -1; }
.rk2-chip-dot { position: absolute; left: 11px; top: 14px; width: 8px; height: 8px; border-radius: 50%; background: var(--green); }
.rk2-chip-dot.amber { background: var(--amber); } .rk2-chip-dot.red { background: var(--red); } .rk2-chip-dot.green { background: var(--green); }
.rk2-tiers-title { margin: 16px 0 8px; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; font-weight: 700; color: var(--text-dim); }
.rk2-tiers { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.rk2-tierbox { display: flex; flex-direction: column; gap: 3px; padding: 10px 8px; border-radius: 12px; text-align: center; font-size: 10.5px; border: 1px solid transparent; }
.rk2-tierbox b { font-size: 13px; font-family: 'Space Mono', monospace; }
.rk2-tierbox.tier-gold { background: rgba(255,200,90,0.12); color: #ffc94d; border-color: rgba(255,200,90,0.3); }
.rk2-tierbox.tier-silver { background: rgba(201,206,214,0.12); color: #c9ced6; border-color: rgba(201,206,214,0.28); }
.rk2-tierbox.tier-bronze { background: rgba(184,121,74,0.14); color: #d99a63; border-color: rgba(184,121,74,0.3); }

/* =================== ZADANIA =================== */
.tk2-hero {
  position: relative; overflow: hidden; display: flex; align-items: center; gap: 16px; padding: 16px; border-radius: 22px; margin-bottom: 18px;
  background: linear-gradient(150deg, rgba(110,231,183,0.14), rgba(110,231,183,0.02) 62%), var(--surface);
  border: 1px solid rgba(110,231,183,0.4); box-shadow: 0 18px 38px -24px rgba(110,231,183,0.7), inset 0 1px 0 rgba(255,255,255,0.05);
  animation: rk2Pop .7s cubic-bezier(.22,1,.36,1) both;
}
.tk2-hero::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: -60%; width: 40%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.09), transparent); transform: skewX(-18deg); animation: rk2Sweep 6s ease-in-out infinite 1.5s;
}
.tk2-ring-num { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 22px; color: var(--text); }
.tk2-ring-num small { font-size: 12px; color: var(--text-dim); }
.tk2-hero-info { position: relative; min-width: 0; }
.tk2-hero-label { font-size: 10.5px; letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-dim); font-weight: 700; }
.tk2-hero-points { font-family: 'Space Mono', monospace; font-size: 28px; font-weight: 700; color: var(--green); margin: 3px 0 2px; }
.tk2-hero-points span { font-size: 14px; color: var(--text-muted); }
.tk2-hero-sub { font-size: 12.5px; color: var(--text-muted); font-weight: 600; }

.tk2-section-head { display: flex; align-items: center; justify-content: space-between; margin: 4px 2px 10px; font-size: 12px; letter-spacing: 1px; text-transform: uppercase; font-weight: 700; color: var(--text-muted); }
.tk2-section-head > span:first-child { display: inline-flex; align-items: center; gap: 7px; }
.tk2-section-head svg { color: var(--amber); }
.tk2-count { display: inline-flex; align-items: center; justify-content: center; min-width: 24px; height: 22px; padding: 0 8px; border-radius: 999px; font-size: 11.5px; font-weight: 800; background: rgba(255,166,48,0.16); color: var(--amber); letter-spacing: 0; }

.tk2-card {
  position: relative; overflow: hidden; border-radius: 18px; padding: 14px 14px 12px 17px; margin-bottom: 10px;
  background: var(--surface); border: 1px solid var(--border); animation: tk2In .6s cubic-bezier(.22,1,.36,1) both;
}
.tk2-card::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--amber); }
.tk2-done::before { background: var(--green); } .tk2-missed::before { background: var(--red); }
.tk2-done { background: linear-gradient(150deg, rgba(110,231,183,0.08), var(--surface) 60%); }
.tk2-priority {
  border-color: rgba(255,107,107,0.5); background: linear-gradient(150deg, rgba(255,107,107,0.1), var(--surface) 58%);
  animation: tk2In .6s cubic-bezier(.22,1,.36,1) both, tk2PrioGlow 2.8s ease-in-out infinite .8s;
}
.tk2-priority::before { background: linear-gradient(180deg, #ff8a80, #e5484d); }
.tk2-card-top { display: flex; align-items: center; gap: 12px; }
.tk2-badge { width: 40px; height: 40px; border-radius: 13px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.tk2-badge-waiting { background: rgba(255,166,48,0.14); color: var(--amber); }
.tk2-badge-done { background: rgba(110,231,183,0.16); color: var(--green); }
.tk2-badge-missed { background: rgba(255,107,107,0.14); color: var(--red); }
.tk2-main { flex: 1; min-width: 0; }
.tk2-title { font-size: 14.5px; font-weight: 700; line-height: 1.3; }
.tk2-sub { font-size: 11px; color: var(--text-dim); margin-top: 3px; font-family: 'Space Mono', monospace; }
.tk2-points {
  position: relative; overflow: hidden; flex-shrink: 0; display: inline-flex; align-items: center; gap: 2px; padding: 6px 11px; border-radius: 999px;
  background: linear-gradient(150deg, #ffc76f, #ff9a2e); color: #1a1206; font: 800 13px 'Space Mono', monospace;
  box-shadow: 0 8px 16px -8px rgba(255,140,30,0.85);
}
.tk2-points small { font-size: 9.5px; font-weight: 700; }
.tk2-points::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: -70%; width: 50%;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.65), transparent); transform: skewX(-20deg); animation: rk2Sweep 3.8s ease-in-out infinite 1s;
}
.tk2-done .tk2-points { background: linear-gradient(150deg, #8fe9c5, #38c98a); box-shadow: 0 8px 16px -8px rgba(56,201,138,0.8); }
.tk2-missed .tk2-points { background: var(--surface-3); color: var(--text-dim); box-shadow: none; }
.tk2-missed .tk2-points::after { display: none; }
.tk2-time { height: 5px; border-radius: 3px; background: var(--surface-3); overflow: hidden; margin: 12px 0 2px; }
.tk2-time i { display: block; height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--amber-dim), var(--amber)); transform-origin: left; animation: rk2Grow 1.2s cubic-bezier(.22,1,.36,1) both .2s; }
.tk2-time.late i { background: linear-gradient(90deg, #ff9a6b, #ff5e5e); }
.tk2-footer { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 8px; margin-top: 11px; }
.tk2-result { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; padding: 6px 11px; border-radius: 10px; }
.tk2-result-waiting { background: var(--surface-2); color: var(--text-muted); }
.tk2-result-done { background: rgba(110,231,183,0.12); color: var(--green); }
.tk2-result-missed { background: rgba(255,107,107,0.12); color: var(--red); }
.tk2-chip { font-size: 11px; font-weight: 700; padding: 5px 10px; border-radius: 999px; background: rgba(255,166,48,0.12); color: var(--amber); }
.tk2-chip.urgent { background: rgba(255,107,107,0.18); color: #ff8a80; animation: tk2Urgent 1.5s ease-in-out infinite; }
.tk2-prio { display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; font-weight: 800; padding: 5px 9px; border-radius: 999px; background: rgba(255,107,107,0.16); color: #ff8a80; margin-left: auto; }
.tk2-prio svg { animation: rk2Bob 1.6s ease-in-out infinite; }

.tk2-allclear { display: flex; align-items: center; gap: 14px; padding: 18px; border-radius: 18px; margin-bottom: 12px; background: rgba(110,231,183,0.08); border: 1px dashed rgba(110,231,183,0.4); color: var(--green); }
.tk2-allclear div { display: flex; flex-direction: column; gap: 3px; font-size: 12px; color: var(--text-muted); }
.tk2-allclear b { color: var(--text); font-size: 14px; }

/* archiwum zadań */
.tk2-archive { margin-top: 14px; border-radius: 18px; border: 1px solid var(--border); background: var(--surface); overflow: hidden; }
.tk2-archive-toggle > svg:first-child { color: var(--text-muted); }
.tk2-archive-title { flex: 0 1 auto; }
.tk2-archive-sum { display: inline-flex; gap: 6px; margin-left: 4px; }
.tk2-archive-toggle .tk2-count { background: var(--surface-3); color: var(--text-muted); }
.tk2-mini { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; font-weight: 800; }
.tk2-mini.done { color: var(--green); } .tk2-mini.missed { color: var(--red); }
.tk2-arow { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border); }
.tk2-arow:last-of-type { border-bottom: none; }
.tk2-arow-ic { width: 26px; height: 26px; border-radius: 9px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: var(--surface-3); color: var(--text-dim); }
.tk2-arow-ic-done { background: rgba(110,231,183,0.14); color: var(--green); }
.tk2-arow-ic-missed { background: rgba(255,107,107,0.14); color: var(--red); }
.tk2-arow-main { flex: 1; min-width: 0; }
.tk2-arow-title { font-size: 12.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tk2-arow-sub { font-size: 10px; color: var(--text-dim); font-family: 'Space Mono', monospace; margin-top: 2px; }
.tk2-arow-res { flex-shrink: 0; font-size: 11px; font-weight: 700; color: var(--text-dim); }
.tk2-arow-res-done { color: var(--green); } .tk2-arow-res-missed { color: var(--red); }
.tk2-more {
  width: 100%; margin-top: 10px; padding: 11px; border-radius: 12px; cursor: pointer; background: var(--surface-2); border: 1px solid var(--border);
  color: var(--amber); font: 700 12.5px 'IBM Plex Sans', sans-serif; transition: transform .15s ease, border-color .2s ease;
}
.tk2-more:active { transform: scale(0.97); }

/* ---------- animacje (tylko transform/opacity — płynnie także na słabszych telefonach) ---------- */
@keyframes rk2Pop { 0% { opacity: 0; transform: translateY(18px) scale(0.94); } 100% { opacity: 1; transform: none; } }
@keyframes rk2Sweep { 0% { left: -70%; } 45%, 100% { left: 140%; } }
@keyframes rk2Spin { to { transform: rotate(360deg); } }
@keyframes rk2Float { 0% { opacity: 0; transform: translateY(14px) scale(0.5); } 25% { opacity: 1; } 100% { opacity: 0; transform: translateY(-34px) scale(1); } }
@keyframes rk2Drop { 0% { opacity: 0; transform: translateY(-34px) scale(0.6); } 100% { opacity: 1; transform: none; } }
@keyframes rk2Rise { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }
@keyframes rk2Bob { 0%, 100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-4px) rotate(3deg); } }
@keyframes rk2RowIn { 0% { opacity: 0; transform: translateX(-22px); } 100% { opacity: 1; transform: none; } }
@keyframes rk2Grow { 0% { transform: scaleX(0); } 100% { transform: scaleX(1); } }
@keyframes rk2MeGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,166,48,0); } 50% { box-shadow: 0 0 0 4px rgba(255,166,48,0.16); } }
@keyframes tk2In { 0% { opacity: 0; transform: translateY(22px) scale(0.97); } 100% { opacity: 1; transform: none; } }
@keyframes tk2PrioGlow { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,107,107,0); } 50% { box-shadow: 0 0 0 4px rgba(255,107,107,0.14), 0 12px 28px -14px rgba(255,107,107,0.55); } }
@keyframes tk2Urgent { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
@media (prefers-reduced-motion: reduce) {
  .rk2 *, .tk2 *, .rk2-hero::before, .tk2-hero::before { animation: none !important; transition: none !important; }
}

/* ---------- wariant jasny ---------- */
.app-root.light-theme .rk2-hero { background: linear-gradient(150deg, rgba(217,130,15,0.14), rgba(217,130,15,0.02) 62%), #fff; }
.app-root.light-theme .tk2-hero { background: linear-gradient(150deg, rgba(31,157,92,0.14), rgba(31,157,92,0.02) 62%), #fff; }
.app-root.light-theme .rk2-ring-track { stroke: rgba(0,0,0,0.09); }
.app-root.light-theme .rk2-avatar { background: #fff; }
.app-root.light-theme .rk2-slot-bonus { color: #a86800; background: rgba(217,130,15,0.14); }
.app-root.light-theme .rk2-tier.tier-gold, .app-root.light-theme .rk2-tierbox.tier-gold { color: #a86800; }
.app-root.light-theme .rk2-spot { opacity: 0.55; }
.app-root.light-theme .rk2-slot-points { color: #b86e00; }
.app-root.light-theme .rk2-place-2 .rk2-slot-points { color: #5f6873; }
.app-root.light-theme .rk2-place-3 .rk2-slot-points { color: #a05a24; }
.app-root.light-theme .tk2-prio, .app-root.light-theme .tk2-chip.urgent { color: #c0392b; }

/* =====================================================================
   Panel 2.0 — nowy wygląd zakładek kierowcy (Rozliczenia, Zadłużenie, Bonusy,
   Rekompensaty, Osiągnięcia, nagłówek i pasek zakładek).
   Sama warstwa CSS na KOŃCU arkusza, bez zmian w logice — usunięcie bloku
   przywraca poprzedni wygląd. Animacje tylko transform/opacity (płynnie na
   słabych telefonach), nieskończone tylko na pojedynczych elementach "hero".
   ===================================================================== */
@keyframes pn2In { 0% { opacity: 0; transform: translateY(14px) scale(0.975); } 100% { opacity: 1; transform: none; } }
@keyframes pn2Sweep { 0% { transform: translateX(-160%) skewX(-18deg); } 42%, 100% { transform: translateX(430%) skewX(-18deg); } }
@keyframes pn2Pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.5); opacity: 0.55; } }
@keyframes pn2Bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
@keyframes pn2Spin { to { transform: rotate(360deg); } }
@keyframes pn2Twinkle { 0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.55; } 50% { transform: scale(1.35) rotate(25deg); opacity: 1; } }
@keyframes pn2Flame { 0%, 100% { transform: scale(1) rotate(-2deg); } 50% { transform: scale(1.1) rotate(2deg); } }
@keyframes pn2Breath { 0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(1); } 50% { opacity: 1; transform: translateX(-50%) scale(1.12); } }

/* ---------- nagłówek: awatar z wirującym pierścieniem ---------- */
.dash-header-row .avatar {
  position: relative; isolation: isolate; width: 44px; height: 44px; border: none;
  box-shadow: 0 0 0 2px var(--bg), 0 10px 22px -8px rgba(255,150,30,0.7);
}
.dash-header-row .avatar::before {
  content: ""; position: absolute; inset: -4px; border-radius: 50%; z-index: -1; pointer-events: none;
  background: conic-gradient(from 0deg, #ffd27a, #ff9a1f, rgba(255,166,48,0.08) 55%, #ffd27a);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  animation: pn2Spin 6s linear infinite;
}
.dash-greeting { font-size: 15px; font-weight: 700; letter-spacing: -0.2px; }
.car-badge {
  padding: 5px 10px; font-weight: 700;
  background: linear-gradient(150deg, rgba(255,184,77,0.16), rgba(255,166,48,0.04)), var(--surface);
  border-color: rgba(255,166,48,0.4); color: var(--text);
  box-shadow: 0 8px 18px -12px rgba(255,140,30,0.8), inset 0 1px 0 rgba(255,255,255,0.05);
}

/* ---------- pasek zakładek ---------- */
.tab-bar { gap: 7px; margin: 16px 0 6px; }
.tab-btn {
  overflow: hidden; border-radius: 14px; padding: 9px 6px; font-weight: 700;
  background: linear-gradient(160deg, var(--surface-2), var(--surface));
  box-shadow: 0 8px 16px -12px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04);
}
.tab-btn:hover { transform: none; }
.tab-btn:active { transform: scale(0.95); }
.tab-btn.active {
  color: #1d1406; border-color: transparent;
  background: linear-gradient(150deg, #ffcb6b, #ff9a1f);
  box-shadow: 0 14px 24px -12px rgba(255,140,30,0.95), inset 0 1px 0 rgba(255,255,255,0.55);
}
.tab-btn.active::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 38%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.5), transparent);
  animation: pn2Sweep 1.1s ease-out 0.1s 1 both;
}
.tab-dot { width: 7px; height: 7px; box-shadow: 0 0 8px var(--red); animation: pn2Pulse 1.8s ease-in-out infinite; }
.tab-btn.active .tab-dot { box-shadow: 0 0 0 2px rgba(255,255,255,0.7); }
.app-root.light-theme .tab-btn { background: linear-gradient(160deg, #fff, var(--surface-2)); box-shadow: 0 8px 16px -12px rgba(0,0,0,0.25), inset 0 1px 0 #fff; }
.app-root.light-theme .tab-btn.active { color: #fff; background: linear-gradient(150deg, #f0a23a, #d9820f); }

/* ---------- Rozliczenia: karta "hero" ---------- */
.dash-hero {
  border-radius: 26px; padding: 28px 18px 24px; margin: 16px 0 18px;
  background: radial-gradient(120% 90% at 50% 0%, rgba(255,166,48,0.22), transparent 62%), linear-gradient(180deg, var(--surface-2), var(--surface));
  border-color: rgba(255,166,48,0.5);
  box-shadow: 0 28px 46px -28px rgba(255,140,30,0.85), inset 0 1px 0 rgba(255,255,255,0.08);
  animation: pn2In 0.5s cubic-bezier(0.22,1,0.36,1) both;
}
.dash-hero::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 34%; pointer-events: none; z-index: 0;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.1), transparent);
  animation: pn2Sweep 6s ease-in-out infinite 1.4s;
}
.dash-hero-green {
  background: radial-gradient(120% 90% at 50% 0%, rgba(110,231,183,0.24), transparent 62%), linear-gradient(180deg, var(--surface-2), var(--surface));
  border-color: rgba(110,231,183,0.55); box-shadow: 0 28px 46px -28px rgba(40,200,140,0.8), inset 0 1px 0 rgba(255,255,255,0.08);
}
.dash-hero-red {
  background: radial-gradient(120% 90% at 50% 0%, rgba(255,107,107,0.24), transparent 62%), linear-gradient(180deg, var(--surface-2), var(--surface));
  border-color: rgba(255,107,107,0.55); box-shadow: 0 28px 46px -28px rgba(255,80,80,0.75), inset 0 1px 0 rgba(255,255,255,0.08);
}
.dash-hero-label { font-size: 11.5px; font-weight: 700; letter-spacing: 2.4px; margin-bottom: 10px; }
.dash-hero .digit-lg { font-size: 42px; font-weight: 700; letter-spacing: -1.2px; line-height: 1.1; }
.dash-hero-sub { font-weight: 600; margin-top: 8px; }
.hero-sparkle { animation: pn2Twinkle 2.8s ease-in-out infinite; }
.streak-mini-chip { position: relative; overflow: hidden; font-weight: 800; box-shadow: 0 10px 20px -12px rgba(255,140,30,0.9); }
.streak-mini-chip::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 40%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.22), transparent);
  animation: pn2Sweep 4.5s ease-in-out infinite 1s;
}

/* ---------- Rozliczenia: wypłata ---------- */
.payout-action-card {
  border-radius: 22px; padding: 18px 16px; border-color: rgba(255,166,48,0.42);
  background: linear-gradient(160deg, rgba(255,184,77,0.14), var(--surface) 58%);
  box-shadow: 0 18px 32px -22px rgba(255,140,30,0.8); animation: pn2In 0.5s cubic-bezier(0.22,1,0.36,1) both;
}
.payout-action-label { font-weight: 700; color: var(--text-muted); margin-bottom: 12px; }
.payout-btn { border-radius: 16px; padding: 14px 8px; font-weight: 700; box-shadow: inset 0 1px 0 rgba(255,255,255,0.04); }
.payout-btn:hover { transform: none; }
.payout-btn:active { transform: scale(0.94); }
.payout-btn.approve { color: var(--green); border-color: rgba(110,231,183,0.4); background: linear-gradient(160deg, rgba(110,231,183,0.16), rgba(110,231,183,0.04)); }
.payout-btn.reject { color: var(--red); border-color: rgba(255,107,107,0.4); background: linear-gradient(160deg, rgba(255,107,107,0.16), rgba(255,107,107,0.04)); }
.payout-btn.review { color: var(--amber); border-color: rgba(255,166,48,0.42); background: linear-gradient(160deg, rgba(255,166,48,0.18), rgba(255,166,48,0.04)); }
.payout-waiting-card { border-radius: 18px; border-style: dashed; }
.payout-status-card { border-radius: 18px; padding: 14px 16px; border: 1px solid transparent; animation: pn2In 0.45s cubic-bezier(0.22,1,0.36,1) both; }
.payout-status-card.approved, .payout-status-card.next_week, .payout-status-card.paid { border-color: rgba(110,231,183,0.4); box-shadow: 0 14px 26px -20px rgba(40,200,140,0.9); }
.payout-status-card.rejected { border-color: rgba(255,107,107,0.4); box-shadow: 0 14px 26px -20px rgba(255,80,80,0.9); }
.payout-status-card.checking { border-color: rgba(255,166,48,0.4); box-shadow: 0 14px 26px -20px rgba(255,140,30,0.9); }

/* ---------- wspólne: etykiety sekcji, odświeżanie, puste stany ---------- */
.section-label { font-weight: 700; letter-spacing: 1.4px; }
.section-label::after { content: ""; flex: 1; height: 1px; margin-left: 6px; background: linear-gradient(90deg, var(--border), transparent); }
.refresh-btn { padding: 6px 12px; border-radius: 999px; background: var(--surface); border: 1px solid var(--border); font-weight: 600; transition: transform 0.15s ease, color 0.15s ease; }
.refresh-btn:active { transform: scale(0.95); }
.empty-state { padding: 34px 12px; border: 1px dashed var(--border); border-radius: 22px; background: linear-gradient(160deg, var(--surface), transparent); }
.empty-state svg { animation: pn2Bob 3.4s ease-in-out infinite; }

/* ---------- wykres zarobków ---------- */
.earnings-chart {
  border-radius: 22px; padding: 18px 14px 12px;
  background: linear-gradient(180deg, var(--surface-2), var(--surface));
  box-shadow: 0 16px 30px -24px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05);
}
.earnings-chart-bars { background: repeating-linear-gradient(0deg, transparent 0 21px, rgba(255,255,255,0.05) 21px 22px); }
.earnings-chart-bar { width: 74%; border-radius: 8px 8px 3px 3px; background: linear-gradient(180deg, var(--surface-3), var(--surface-2)); }
.earnings-chart-bar.current { background: linear-gradient(180deg, #ffd27a, #ff9a1f); box-shadow: 0 0 20px -2px rgba(255,166,48,0.8), inset 0 1px 0 rgba(255,255,255,0.45); }
.earnings-chart-tooltip { border-radius: 9px; padding: 4px 9px; box-shadow: 0 8px 18px -8px rgba(0,0,0,0.7); }
.app-root.light-theme .earnings-chart-bars { background: repeating-linear-gradient(0deg, transparent 0 21px, rgba(0,0,0,0.05) 21px 22px); }

/* ---------- bilety rozliczeń ---------- */
.ticket-list { gap: 12px; }
.badge { padding: 4px 10px; font-weight: 700; }
.badge::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.skeleton-row { border-radius: 22px; }

/* ---------- Zadłużenie ---------- */
.debt-label { letter-spacing: 1.6px; }
.debt-hero-card {
  border-radius: 26px; padding: 30px 20px 26px;
  background: radial-gradient(120% 100% at 50% 0%, rgba(255,107,107,0.26), transparent 62%), linear-gradient(165deg, #2b1519, #170f10);
  border-color: rgba(255,107,107,0.45);
  box-shadow: 0 28px 48px -26px rgba(255,70,70,0.7), inset 0 1px 0 rgba(255,255,255,0.07);
  animation: pn2In 0.5s cubic-bezier(0.22,1,0.36,1) both;
}
.debt-hero-card::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 34%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.09), transparent);
  animation: pn2Sweep 6.5s ease-in-out infinite 1.6s;
}
.debt-hero-label { font-size: 11px; letter-spacing: 2.4px; }
.debt-hero-value { font-size: 44px; letter-spacing: -1.4px; text-shadow: 0 0 32px rgba(255,107,107,0.55); line-height: 1.1; }
.debt-hero-repaid { width: fit-content; margin: 14px auto 0; padding: 5px 12px; border-radius: 999px; background: rgba(110,231,183,0.12); border: 1px solid rgba(110,231,183,0.3); }
.debt-repay-cta {
  position: relative; overflow: hidden; border-radius: 18px; padding: 16px; font-size: 14.5px; letter-spacing: 0.2px;
  background: linear-gradient(150deg, #ff8a8a, #d12f2f); box-shadow: 0 16px 28px -12px rgba(220,50,50,0.7), inset 0 1px 0 rgba(255,255,255,0.35);
}
.debt-repay-cta::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 30%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.4), transparent);
  animation: pn2Sweep 3.8s ease-in-out infinite 1s;
}
.debt-repay-cta:hover { transform: none; }
.debt-repay-cta:active { transform: scale(0.97); }
.debt-repay-form { border-radius: 24px; border-color: rgba(255,107,107,0.3); background: linear-gradient(160deg, rgba(255,107,107,0.08), var(--surface) 50%); }
.debt-full-btn { border-radius: 12px; }
.debt-info-note { border-radius: 16px; }
.debt-history-row { border-radius: 18px; background: linear-gradient(160deg, var(--surface-2) -50%, var(--surface) 60%); animation: pn2In 0.4s cubic-bezier(0.22,1,0.36,1) both; }
.debt-history-icon { width: 38px; height: 38px; border-radius: 13px; }
.debt-history-amount { font-family: 'Space Mono', monospace; font-weight: 700; }
.debt-card { position: relative; border-radius: 20px; padding: 14px 16px 14px 20px; background: linear-gradient(160deg, rgba(255,107,107,0.12), var(--surface) 62%); border-color: rgba(255,107,107,0.35); animation: pn2In 0.45s cubic-bezier(0.22,1,0.36,1) both; }
.debt-card::before { content: ""; position: absolute; left: 0; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0; background: linear-gradient(180deg, var(--red), transparent 130%); box-shadow: 0 0 12px var(--red); }
.debt-card-icon { width: 42px; height: 42px; border-radius: 14px; box-shadow: 0 8px 18px -10px rgba(255,80,80,0.9); }
.debt-card-amount { font-family: 'Space Mono', monospace; font-size: 16px; text-shadow: 0 0 16px rgba(255,107,107,0.35); }
.app-root.light-theme .debt-hero-card { background: radial-gradient(120% 100% at 50% 0%, rgba(217,69,63,0.2), transparent 62%), linear-gradient(165deg, #fff1f0, #ffffff); }

/* ---------- Bonusy ---------- */
.bonus-hero {
  border-radius: 26px; padding: 30px 18px 26px; margin: 12px 0 6px;
  background: radial-gradient(120% 100% at 50% 0%, rgba(110,231,183,0.26), transparent 62%), linear-gradient(180deg, var(--surface-2), var(--surface));
  border-color: rgba(110,231,183,0.55); box-shadow: 0 28px 46px -28px rgba(40,200,140,0.8), inset 0 1px 0 rgba(255,255,255,0.08);
}
.bonus-hero-icon { animation: pn2Bob 3s ease-in-out infinite; filter: drop-shadow(0 0 16px rgba(110,231,183,0.75)); }
.bonus-card {
  position: relative; border-radius: 22px; padding: 14px 16px 14px 20px; margin-bottom: 12px; border-color: rgba(110,231,183,0.3);
  background: linear-gradient(160deg, rgba(110,231,183,0.1), var(--surface) 60%);
  box-shadow: 0 14px 26px -22px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04);
  animation: pn2In 0.45s cubic-bezier(0.22,1,0.36,1) both;
}
.bonus-card::before { content: ""; position: absolute; left: 0; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0; background: linear-gradient(180deg, var(--green), transparent 130%); box-shadow: 0 0 12px var(--green); }
.bonus-card:hover { transform: none; }
.bonus-card:active { transform: scale(0.985); }
.bonus-card-expired::before { background: linear-gradient(180deg, var(--red), transparent 130%); box-shadow: 0 0 12px var(--red); }
.bonus-card-upcoming::before { background: linear-gradient(180deg, var(--amber), transparent 130%); box-shadow: 0 0 12px var(--amber); }
.bonus-icon-badge { width: 40px; height: 40px; border-radius: 14px; box-shadow: 0 8px 18px -10px rgba(40,200,140,0.9); }
.bonus-period { font-weight: 700; }
.bonus-tag { font-weight: 800; padding: 3px 8px; }
.bonus-amount { font-size: 17px; text-shadow: 0 0 16px rgba(110,231,183,0.35); }
.bonus-card-expired .bonus-amount, .bonus-card-upcoming .bonus-amount { text-shadow: none; }
.ranking-subnav { border-radius: 16px; }

/* ---------- Rekompensaty ---------- */
.comp-action-row { gap: 12px; margin: 6px 0 18px; }
.comp-btn {
  position: relative; overflow: hidden; border-radius: 20px; padding: 18px 10px; font-weight: 800;
  box-shadow: 0 14px 26px -20px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05);
}
.comp-btn:hover { transform: none; }
.comp-btn:active { transform: scale(0.95); }
.comp-btn-add { border-color: rgba(110,231,183,0.45); background: linear-gradient(160deg, rgba(110,231,183,0.2), rgba(110,231,183,0.04)); box-shadow: 0 14px 26px -20px rgba(40,200,140,0.9), inset 0 1px 0 rgba(255,255,255,0.06); }
.comp-btn-subtract { border-color: rgba(255,107,107,0.45); background: linear-gradient(160deg, rgba(255,107,107,0.2), rgba(255,107,107,0.04)); box-shadow: 0 14px 26px -20px rgba(255,80,80,0.9), inset 0 1px 0 rgba(255,255,255,0.06); }
.comp-btn svg { filter: drop-shadow(0 0 8px currentColor); }
.comp-blocked-note { border-radius: 20px; border-style: dashed; }
.comp-adjustment-note { border-radius: 18px; border: 1px solid rgba(110,231,183,0.3); font-weight: 600; }
.comp-card {
  --acc: var(--amber); position: relative; border-radius: 22px; padding: 14px 16px 14px 20px; margin-bottom: 12px;
  background: linear-gradient(160deg, var(--surface-2) -50%, var(--surface) 55%);
  box-shadow: 0 14px 26px -22px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04);
  animation: pn2In 0.45s cubic-bezier(0.22,1,0.36,1) both;
}
.comp-card:has(.comp-icon-badge.add) { --acc: var(--green); }
.comp-card:has(.comp-icon-badge.subtract) { --acc: var(--red); }
.comp-card::before { content: ""; position: absolute; left: 0; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0; background: linear-gradient(180deg, var(--acc), transparent 130%); box-shadow: 0 0 12px var(--acc); }
.comp-card:hover { border-color: var(--acc); box-shadow: 0 16px 30px -18px var(--acc); }
.comp-icon-badge { width: 40px; height: 40px; border-radius: 14px; }
.comp-tag { font-weight: 800; padding: 3px 9px; }
.comp-amount { font-size: 17px; text-shadow: 0 0 16px rgba(255,166,48,0.25); }
.comp-amount.add { text-shadow: 0 0 16px rgba(110,231,183,0.3); }
.comp-amount.subtract { text-shadow: 0 0 16px rgba(255,107,107,0.3); }

/* ---------- Osiągnięcia ---------- */
.badges-view { gap: 16px; }
.badge-hero-card {
  border-radius: 26px; padding: 30px 16px 26px; border-color: rgba(255,166,48,0.5);
  background: radial-gradient(120% 100% at 50% 0%, rgba(255,166,48,0.26), transparent 62%), linear-gradient(180deg, var(--surface-2), var(--surface));
  box-shadow: 0 28px 46px -28px rgba(255,140,30,0.85), inset 0 1px 0 rgba(255,255,255,0.08);
  animation: pn2In 0.55s cubic-bezier(0.22,1,0.36,1) both;
}
.badge-hero-card::before {
  content: ""; position: absolute; left: 50%; top: -60px; width: 300px; height: 300px; margin-left: -150px; border-radius: 50%; pointer-events: none;
  background: conic-gradient(from 0deg, rgba(255,205,100,0) 0deg, rgba(255,205,100,0.24) 35deg, rgba(255,205,100,0) 80deg, rgba(255,205,100,0) 180deg, rgba(255,205,100,0.16) 215deg, rgba(255,205,100,0) 260deg);
  -webkit-mask-image: radial-gradient(circle, #000 15%, transparent 68%); mask-image: radial-gradient(circle, #000 15%, transparent 68%);
  animation: pn2Spin 16s linear infinite;
}
.badge-hero-glow { animation: pn2Breath 3.4s ease-in-out infinite; }
.badge-hero-icon, .badge-hero-icon.streak-hot { box-shadow: none; animation: pn2Flame 1.8s ease-in-out infinite; }
.badge-hero-icon { font-size: 40px; }
.badge-hero-icon.streak-hot { font-size: 52px; }
.badge-hero-value { font-size: 52px; letter-spacing: -1px; text-shadow: 0 0 30px rgba(255,166,48,0.5); }
.badge-hero-label { font-weight: 600; max-width: 280px; margin: 0 auto; line-height: 1.45; }
.badge-grid { gap: 12px; }
.badge-card {
  --acc: var(--amber); position: relative; overflow: hidden; border-radius: 22px; padding: 18px 12px 16px;
  background: radial-gradient(120% 90% at 50% 0%, rgba(255,166,48,0.14), transparent 65%), linear-gradient(180deg, var(--surface-2), var(--surface));
  box-shadow: 0 14px 26px -22px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05);
  animation: pn2In 0.5s cubic-bezier(0.22,1,0.36,1) both;
}
.badge-card:nth-child(1) { animation-delay: 0.08s; }
.badge-card:nth-child(2) { --acc: var(--green); animation-delay: 0.16s; background: radial-gradient(120% 90% at 50% 0%, rgba(110,231,183,0.16), transparent 65%), linear-gradient(180deg, var(--surface-2), var(--surface)); }
.badge-card:nth-child(3) { --acc: #8ab4ff; animation-delay: 0.24s; background: radial-gradient(120% 90% at 50% 0%, rgba(138,180,255,0.16), transparent 65%), linear-gradient(180deg, var(--surface-2), var(--surface)); }
.badge-card:nth-child(4) { animation-delay: 0.32s; }
.badge-card:hover { transform: none; border-color: var(--acc); }
.badge-card:active { transform: scale(0.97); }
.badge-card::after { content: ""; position: absolute; left: 20%; right: 20%; bottom: 0; height: 3px; border-radius: 3px 3px 0 0; background: var(--acc); box-shadow: 0 0 16px var(--acc); }
.badge-card-icon { font-size: 28px; margin-bottom: 8px; filter: drop-shadow(0 6px 10px rgba(0,0,0,0.45)); animation: pn2Bob 3.6s ease-in-out infinite; }
.badge-card:nth-child(2) .badge-card-icon { animation-delay: 0.5s; }
.badge-card:nth-child(3) .badge-card-icon { animation-delay: 1s; }
.badge-card:nth-child(4) .badge-card-icon { animation-delay: 1.5s; }
.badge-card-value { font-size: 21px; letter-spacing: -0.4px; }
.badge-card-label { font-weight: 600; }

/* ---------- ruch ograniczony i jasny motyw ---------- */
@media (prefers-reduced-motion: reduce) {
  .dash-header-row .avatar::before, .dash-hero::before, .debt-hero-card::before, .badge-hero-card::before, .badge-hero-glow,
  .tab-btn.active::after, .tab-dot, .hero-sparkle, .streak-mini-chip::after, .debt-repay-cta::after,
  .bonus-hero-icon, .badge-card-icon, .badge-hero-icon, .empty-state svg { animation: none; }
  .dash-hero, .payout-action-card, .payout-status-card, .ticket, .debt-hero-card, .debt-history-row, .debt-card, .bonus-card,
  .comp-card, .badge-hero-card, .badge-card { animation-duration: 0.01s; animation-delay: 0s; }
}
.app-root.light-theme .dash-hero { background: radial-gradient(120% 90% at 50% 0%, rgba(217,130,15,0.2), transparent 62%), #fff; }
.app-root.light-theme .dash-hero-green { background: radial-gradient(120% 90% at 50% 0%, rgba(31,157,92,0.2), transparent 62%), #fff; }
.app-root.light-theme .dash-hero-red { background: radial-gradient(120% 90% at 50% 0%, rgba(217,69,63,0.2), transparent 62%), #fff; }
.app-root.light-theme .bonus-hero { background: radial-gradient(120% 100% at 50% 0%, rgba(31,157,92,0.2), transparent 62%), #fff; }
.app-root.light-theme .badge-hero-card { background: radial-gradient(120% 100% at 50% 0%, rgba(217,130,15,0.22), transparent 62%), #fff; }
.app-root.light-theme .badge-card, .app-root.light-theme .ticket, .app-root.light-theme .comp-card { background: #fff; }
.app-root.light-theme .stat-chip { background: var(--surface-2); }
.app-root.light-theme .dash-hero::before, .app-root.light-theme .debt-hero-card::before { background: linear-gradient(100deg, transparent, rgba(255,255,255,0.7), transparent); }

/* ---------- jesienne listki ---------- */
@keyframes pn2FallA {
  0% { transform: translate3d(0, -10vh, 0); opacity: 0; }
  7% { opacity: 0.95; }
  90% { opacity: 0.95; }
  100% { transform: translate3d(42px, 108vh, 0); opacity: 0; }
}
@keyframes pn2FallB {
  0% { transform: translate3d(0, -10vh, 0); opacity: 0; }
  7% { opacity: 0.95; }
  90% { opacity: 0.95; }
  100% { transform: translate3d(-42px, 108vh, 0); opacity: 0; }
}
@keyframes pn2SwayA { 0% { transform: translateX(-14px) rotate(-40deg) perspective(240px) rotateY(0deg); } 100% { transform: translateX(14px) rotate(60deg) perspective(240px) rotateY(105deg); } }
@keyframes pn2SwayB { 0% { transform: translateX(-22px) rotate(-40deg) perspective(240px) rotateY(0deg); } 100% { transform: translateX(22px) rotate(60deg) perspective(240px) rotateY(105deg); } }
@keyframes pn2SwayC { 0% { transform: translateX(-30px) rotate(-40deg) perspective(240px) rotateY(0deg); } 100% { transform: translateX(30px) rotate(60deg) perspective(240px) rotateY(105deg); } }
@keyframes pn2LeavesIn { from { opacity: 0; } to { opacity: 1; } }
.leaves { position: fixed; inset: 0; z-index: 40; pointer-events: none; overflow: hidden; animation: pn2LeavesIn 1.6s ease both; }
.leaves::before {
  content: ""; position: absolute; inset: 0;
  background: radial-gradient(120% 38% at 50% 0%, rgba(255,150,40,0.11), transparent 70%);
}
/* Bazowo niewidoczne — gdyby animacja z jakiegoś powodu nie ruszyła, listek nie "wisi" na górze ekranu. */
.leaf {
  position: absolute; top: 0; display: block; opacity: 0; will-change: transform, opacity;
  animation-timing-function: linear; animation-iteration-count: infinite; animation-fill-mode: both;
}
.leaf.d1 { animation-name: pn2FallA; }
.leaf.d2 { animation-name: pn2FallB; }
.leaf i {
  position: absolute; inset: 0; display: block; overflow: hidden; border-radius: 0 100% 0 100%;
  box-shadow: inset -2px -3px 6px rgba(0,0,0,0.22), inset 2px 2px 4px rgba(255,255,255,0.22);
  animation-timing-function: ease-in-out; animation-iteration-count: infinite; animation-direction: alternate;
}
.leaf.s1 i { animation-name: pn2SwayA; }
.leaf.s2 i { animation-name: pn2SwayB; }
.leaf.s3 i { animation-name: pn2SwayC; }
.leaf i::after {
  content: ""; position: absolute; left: 50%; top: -20%; width: 1.5px; height: 141%;
  background: rgba(60,20,0,0.35); transform: translateX(-50%) rotate(-45deg);
}
.leaf-c1 { background: linear-gradient(135deg, #ff8a3d, #c4281c); }
.leaf-c2 { background: linear-gradient(135deg, #ffbf55, #e8761a); }
.leaf-c3 { background: linear-gradient(135deg, #ffe066, #dd9a10); }
.leaf-c4 { background: linear-gradient(135deg, #dd9254, #8f4a1c); }
.leaf-c5 { background: linear-gradient(135deg, #ee5a3e, #8e1b1b); }
@media (prefers-reduced-motion: reduce) { .leaves { display: none; } }

/* =====================================================================
   Czat 3.0 — pełnoekranowy czat (z Szefem, prywatne, grupowy).
   Osobna warstwa NA KOŃCU arkusza (klasy cx-*); usunięcie bloku + powłoki
   ChatShell przywraca poprzedni wygląd. Animacje tylko transform/opacity.
   ===================================================================== */
@keyframes cxIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes cxTop { from { opacity: 0; transform: translateY(-14px); } to { opacity: 1; transform: none; } }
@keyframes cxUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }
@keyframes cxChip { from { opacity: 0; transform: translateY(10px) scale(0.9); } to { opacity: 1; transform: none; } }
@keyframes cxPulse { 0% { transform: scale(1); opacity: 0.55; } 100% { transform: scale(1.9); opacity: 0; } }
@keyframes cxRing { 0%, 70%, 100% { transform: rotate(0deg); } 74% { transform: rotate(-16deg); } 80% { transform: rotate(14deg); } 86% { transform: rotate(-10deg); } 92% { transform: rotate(7deg); } }
@keyframes cxDrift1 { from { transform: translate3d(0, 0, 0); } to { transform: translate3d(70px, 90px, 0); } }
@keyframes cxDrift2 { from { transform: translate3d(0, 0, 0); } to { transform: translate3d(-80px, -70px, 0); } }
@keyframes cxSpin { to { transform: rotate(360deg); } }
@keyframes cxFloat { 0%, 100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-9px) rotate(4deg); } }
@keyframes cxSheetUp { from { opacity: 0; transform: translateY(60px) scale(0.96); } to { opacity: 1; transform: none; } }
@keyframes cxWave { 0% { transform: scale(0.7); opacity: 0.6; } 100% { transform: scale(2.3); opacity: 0; } }

html.cx-lock, html.cx-lock body { overflow: hidden; overscroll-behavior: none; }

/* --- powłoka --- */
.cx {
  position: fixed; top: 0; left: 0; right: 0; width: 100%; max-width: 430px; margin: 0 auto; height: 100dvh;
  z-index: 900; display: flex; flex-direction: column; isolation: isolate; overflow: hidden;
  background: radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1.5px) 0 0/18px 18px, var(--bg);
  animation: cxIn 0.25s ease both;
}
.cx::before, .cx::after { content: ""; position: absolute; z-index: -1; width: 360px; height: 360px; border-radius: 50%; pointer-events: none; }
.cx::before { left: -150px; top: 6%; background: radial-gradient(circle, rgba(255,166,48,0.17), transparent 68%); animation: cxDrift1 20s ease-in-out infinite alternate; }
.cx::after { right: -160px; bottom: 10%; background: radial-gradient(circle, rgba(96,165,250,0.14), transparent 68%); animation: cxDrift2 24s ease-in-out infinite alternate; }
.cx-boss::after { background: radial-gradient(circle, rgba(110,231,183,0.15), transparent 68%); }
.cx-group::before { background: radial-gradient(circle, rgba(192,132,252,0.16), transparent 68%); }
.cx-group::after { background: radial-gradient(circle, rgba(244,114,182,0.12), transparent 68%); }
/* chat-view jest tylko "opakowaniem" — jego dzieci układają się bezpośrednio w kolumnie powłoki */
.cx > .chat-view { display: contents; }

/* --- górny pasek --- */
.cx .chat-header {
  order: -3; flex: none; margin: 0; gap: 8px; align-items: center;
  padding: calc(env(safe-area-inset-top, 0px) + 10px) 10px 11px; border-bottom: 1px solid rgba(255,255,255,0.08);
  background: linear-gradient(180deg, rgba(255,166,48,0.13), rgba(255,166,48,0.02)), var(--surface);
  box-shadow: 0 14px 26px -22px rgba(0,0,0,0.9); animation: cxTop 0.4s cubic-bezier(.22,1,.36,1) both;
}
.cx-boss .chat-header { background: linear-gradient(180deg, rgba(110,231,183,0.13), rgba(110,231,183,0.02)), var(--surface); }
.cx-group .chat-header { background: linear-gradient(180deg, rgba(192,132,252,0.14), rgba(192,132,252,0.02)), var(--surface); }
.cx .chat-header::after { left: 0; right: 0; bottom: -1px; width: auto; height: 1px; background: linear-gradient(90deg, transparent, rgba(255,166,48,0.7), transparent); }
.cx-boss .chat-header::after { background: linear-gradient(90deg, transparent, rgba(110,231,183,0.7), transparent); }
.cx-group .chat-header::after { background: linear-gradient(90deg, transparent, rgba(192,132,252,0.7), transparent); }
.cx-back, .cx .chat-search-toggle {
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  color: var(--text); cursor: pointer; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1);
  transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.cx-back:active, .cx .chat-search-toggle:active { transform: scale(0.88); }
.cx-back:hover, .cx .chat-search-toggle:hover { color: var(--amber); background: rgba(255,166,48,0.12); }
.cx .chat-header-topic { flex: 1; min-width: 0; gap: 9px; }
.cx-who { display: flex; align-items: center; }
.cx-avatar {
  position: relative; isolation: isolate; width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center; font-size: 19px; margin-left: 2px;
  background: linear-gradient(145deg, var(--surface-3), var(--surface-2));
  box-shadow: 0 0 0 2px var(--bg), 0 8px 18px -6px rgba(255,150,30,0.7);
}
.cx-avatar::before {
  content: ""; position: absolute; inset: -4px; border-radius: 50%; z-index: -1; pointer-events: none;
  background: conic-gradient(from 0deg, #ffd27a, #ff9a1f, rgba(255,166,48,0.08) 55%, #ffd27a);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2px));
  animation: cxSpin 7s linear infinite;
}
.cx-avatar-boss { box-shadow: 0 0 0 2px var(--bg), 0 8px 18px -6px rgba(60,210,140,0.75); }
.cx-avatar-boss::before { background: conic-gradient(from 0deg, #a3f2c8, #2fb673, rgba(110,231,183,0.08) 55%, #a3f2c8); }
.cx-avatar-group { box-shadow: 0 0 0 2px var(--bg), 0 8px 18px -6px rgba(192,132,252,0.7); }
.cx-avatar-group::before { background: conic-gradient(from 0deg, #e0c3ff, #a855f7, rgba(192,132,252,0.08) 55%, #e0c3ff); }
.cx .dm-chat-header-avatar { width: 44px; height: 44px; font-size: 15px; box-shadow: 0 0 0 2px var(--bg), 0 0 0 4px rgba(255,166,48,0.6), 0 8px 18px -6px rgba(0,0,0,0.7); }
.cx-who-text { display: flex; flex-direction: column; min-width: 0; line-height: 1.2; }
.cx-who-text b { font-size: 15px; font-weight: 800; letter-spacing: -0.3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cx-who-text small { margin-top: 2px; font-size: 11px; font-weight: 600; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cx-boss .cx-who-text small { color: #6ee7b7; }
.cx .chat-resolve-btn {
  flex-shrink: 0; padding: 7px 10px; border-radius: 999px; font-size: 11px; font-weight: 700;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
}

/* zielona słuchawka */
.cx-call {
  position: relative; width: 40px; height: 40px; border-radius: 50%; border: none; flex-shrink: 0; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #04210f;
  background: linear-gradient(145deg, #7bf0b4, #1fb868);
  box-shadow: 0 10px 20px -6px rgba(31,184,104,0.85), inset 0 1px 0 rgba(255,255,255,0.55);
  transition: transform 0.15s ease;
}
.cx-call:active { transform: scale(0.86); }
.cx-call svg { position: relative; animation: cxRing 3.6s ease-in-out infinite; transform-origin: 50% 50%; }
.cx-call-pulse { position: absolute; inset: 0; border-radius: 50%; border: 2px solid #4be394; animation: cxPulse 2.2s ease-out infinite; pointer-events: none; }

/* arkusz "Zadzwonić do Szefa?" */
.cx-call-overlay {
  position: fixed; inset: 0; z-index: 960; display: flex; align-items: flex-end; justify-content: center;
  background: rgba(4,6,8,0.66); animation: cxIn 0.2s ease both;
}
.cx-call-sheet {
  position: relative; width: 100%; max-width: 430px; margin: 0 12px calc(14px + env(safe-area-inset-bottom, 0px)); padding: 26px 20px 18px;
  border-radius: 30px; text-align: center; overflow: hidden;
  background: radial-gradient(120% 70% at 50% 0%, rgba(110,231,183,0.22), transparent 62%), linear-gradient(180deg, var(--surface-2), var(--surface));
  border: 1px solid rgba(110,231,183,0.4); box-shadow: 0 30px 60px -20px rgba(0,0,0,0.9);
  animation: cxSheetUp 0.4s cubic-bezier(.22,1,.36,1) both;
}
.cx-call-avatar { position: relative; width: 84px; height: 84px; margin: 6px auto 16px; display: flex; align-items: center; justify-content: center; }
.cx-call-avatar span {
  position: relative; width: 84px; height: 84px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 40px;
  background: linear-gradient(145deg, #2a3a33, #17211c); box-shadow: 0 0 0 3px rgba(110,231,183,0.7), 0 14px 30px -8px rgba(31,184,104,0.8);
}
.cx-call-avatar i { position: absolute; inset: 0; border-radius: 50%; border: 2px solid rgba(110,231,183,0.7); animation: cxWave 2.4s ease-out infinite; }
.cx-call-avatar i:nth-child(2) { animation-delay: 0.8s; }
.cx-call-avatar i:nth-child(3) { animation-delay: 1.6s; }
.cx-call-title { font-size: 20px; font-weight: 800; letter-spacing: -0.4px; }
.cx-call-sub { margin: 4px 0 18px; font-size: 12.5px; color: var(--text-muted); }
.cx-call-go {
  display: flex; align-items: center; justify-content: center; gap: 10px; padding: 16px; border-radius: 20px; text-decoration: none;
  font-size: 16px; font-weight: 800; color: #04210f; background: linear-gradient(145deg, #7bf0b4, #1fb868);
  box-shadow: 0 16px 28px -10px rgba(31,184,104,0.85), inset 0 1px 0 rgba(255,255,255,0.55);
  transition: transform 0.15s ease;
}
.cx-call-go:active { transform: scale(0.97); }
.cx-call-cancel {
  width: 100%; margin-top: 10px; padding: 13px; border-radius: 18px; cursor: pointer; font-size: 14px; font-weight: 700;
  color: var(--text-muted); background: transparent; border: 1px solid var(--border);
}

/* --- pasek rozmów prywatnych --- */
.cx .dm-switcher-strip {
  order: -2; flex: none; margin: 0; padding: 10px 12px 8px; gap: 4px; background: var(--surface);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.cx .dm-switcher-chip { width: 62px; opacity: 0.6; }
.cx .dm-switcher-chip.active { opacity: 1; }
.cx .dm-switcher-chip.active .dm-switcher-avatar { box-shadow: 0 0 0 3px rgba(255,166,48,0.25), 0 8px 16px -6px rgba(255,140,30,0.7); }
.cx .chat-search-row { order: -3; flex: none; margin: 0; border-radius: 0; border-left: none; border-right: none; }

/* --- wiadomości --- */
.cx .chat-messages {
  flex: 1; min-height: 0; margin: 0; border: none; border-radius: 0; background: transparent;
  padding: 14px 12px 12px; gap: 4px; animation: none;
}
.cx .chat-date-separator { position: sticky; top: 4px; z-index: 3; margin: 14px 0 8px; pointer-events: none; }
.cx .chat-date-separator span { background: var(--surface-2); border-color: rgba(255,255,255,0.12); }
.cx .chat-bubble { padding: 10px 14px; border-radius: 22px; font-size: 14.5px; line-height: 1.5; }
.cx .msg-with-reply, .cx .chat-bubble-col { max-width: 84%; }
.cx .chat-bubble.mine.msg-group-last { border-bottom-right-radius: 7px; }
.cx .chat-bubble.theirs.msg-group-last { border-bottom-left-radius: 7px; }
.cx-boss .chat-bubble.theirs {
  background: linear-gradient(155deg, rgba(110,231,183,0.24), rgba(110,231,183,0.08));
  border: 1px solid rgba(110,231,183,0.38); box-shadow: 0 10px 24px -14px rgba(31,184,104,0.7);
}
.cx-dm .chat-bubble.theirs { background: linear-gradient(155deg, rgba(96,165,250,0.22), rgba(96,165,250,0.07)); border: 1px solid rgba(96,165,250,0.36); }
.cx .msg-time, .cx .msg-time-group { margin-top: 3px; font-size: 10.5px; opacity: 0.8; }
.cx .empty-state {
  margin: auto 16px; padding: 38px 18px; border: 1px dashed rgba(255,255,255,0.14); border-radius: 28px;
  background: radial-gradient(120% 90% at 50% 0%, rgba(255,166,48,0.12), transparent 70%); color: var(--text-muted); font-size: 14px; font-weight: 600;
}
.cx .empty-state svg { width: 56px; height: 56px; padding: 14px; border-radius: 50%; opacity: 1; color: var(--amber); animation: cxFloat 3.4s ease-in-out infinite; background: rgba(255,166,48,0.14); box-shadow: 0 12px 26px -10px rgba(255,140,30,0.8); }
.cx-boss .empty-state svg { color: #6ee7b7; background: rgba(110,231,183,0.14); box-shadow: 0 12px 26px -10px rgba(40,200,140,0.8); }
.cx .pinned-banner { order: -1; flex: none; margin: 8px 12px 0; }
.cx .chat-scroll-fab { right: 14px; }

/* odpowiedź na wiadomość widoczna też na telefonie (tam nie ma "hover") */
@media (hover: none) {
  .msg-reply-btn { opacity: 0.38; }
}

/* czat grupowy: "Odpowiedz" jako mała ikona przy dymku (mniej wysokie wiadomości) */
.cx-group .chat-bubble-col { position: relative; }
.cx-group .group-reply-link { position: absolute; top: 6px; width: 28px; height: 28px; padding: 0; margin: 0; justify-content: center; font-size: 0; opacity: 0.4; border-radius: 50%; }
.cx-group .group-reply-link svg { width: 15px; height: 15px; }
.cx-group .chat-bubble-col.theirs .group-reply-link { left: 100%; margin-left: 4px; }
.cx-group .chat-bubble-col.mine .group-reply-link { right: 100%; left: auto; margin-right: 4px; }
.cx-group .chat-seen-by { margin-top: 2px; }

/* doczytywanie starszych wiadomości */
.cx-older-btn {
  align-self: center; flex: none; margin: 4px 0 10px; padding: 8px 16px; border-radius: 999px; cursor: pointer;
  font: 700 12px 'IBM Plex Sans', sans-serif; color: var(--amber);
  background: linear-gradient(150deg, rgba(255,184,77,0.16), rgba(255,166,48,0.05)); border: 1px solid rgba(255,166,48,0.4);
  box-shadow: 0 8px 18px -12px rgba(255,140,30,0.9); transition: transform 0.15s ease;
}
.cx-older-btn:active { transform: scale(0.94); }

/* --- szybkie odpowiedzi --- */
.cx-chips { flex: none; display: flex; gap: 8px; overflow-x: auto; padding: 8px 12px 2px; scrollbar-width: none; }
.cx-chips::-webkit-scrollbar { display: none; }
.cx-chip {
  flex: none; padding: 9px 14px; border-radius: 999px; cursor: pointer; white-space: nowrap;
  font: 600 12.5px 'IBM Plex Sans', sans-serif; color: var(--text);
  background: linear-gradient(150deg, rgba(255,184,77,0.18), rgba(255,166,48,0.05)); border: 1px solid rgba(255,166,48,0.4);
  box-shadow: 0 8px 16px -12px rgba(255,140,30,0.9); animation: cxChip 0.4s cubic-bezier(.34,1.56,.64,1) both;
  transition: transform 0.15s ease;
}
.cx-chip:nth-child(2) { animation-delay: 0.05s; }
.cx-chip:nth-child(3) { animation-delay: 0.1s; }
.cx-chip:nth-child(4) { animation-delay: 0.15s; }
.cx-chip:nth-child(5) { animation-delay: 0.2s; }
.cx-chip:active { transform: scale(0.93); }

/* --- pole pisania: kapsuła przyklejona do dołu --- */
.cx .chat-input-row {
  flex: none; margin: 8px 10px calc(10px + env(safe-area-inset-bottom, 0px)); padding: 6px 6px 6px 8px; border-radius: 30px;
  background: linear-gradient(160deg, rgba(255,255,255,0.12), rgba(255,255,255,0.05)), var(--surface);
  box-shadow: 0 18px 36px -16px rgba(0,0,0,0.85), inset 0 1px 0 rgba(255,255,255,0.08);
  animation: cxUp 0.45s cubic-bezier(.22,1,.36,1) 0.1s both;
}
.cx .chat-input { padding: 11px 8px; font-size: 16px; min-width: 0; }
.cx .chat-icon-btn { width: 38px; height: 38px; border-radius: 50%; flex-shrink: 0; }
.cx .chat-send-btn { width: 46px; height: 46px; }
.cx .chat-icon-btn[aria-label="Nagraj głosówkę"] {
  order: 20; width: 46px; height: 46px; color: #1a1206; background: linear-gradient(145deg, #ffc76f, #ff9a2e);
  box-shadow: 0 10px 20px -6px rgba(255,140,30,0.75), inset 0 1px 0 rgba(255,255,255,0.55);
}
.cx .chat-icon-btn[aria-label="Nagraj głosówkę"]:hover:not(:disabled) { color: #1a1206; background: linear-gradient(145deg, #ffc76f, #ff9a2e); }
@supports selector(:has(a)) {
  /* pusty tekst: mikrofon; wpisany tekst: wyślij */
  .cx .chat-input-row:has(.chat-input:placeholder-shown) .chat-send-btn { display: none; }
  .cx .chat-input-row:has(.chat-input:not(:placeholder-shown)) .chat-icon-btn[aria-label="Nagraj głosówkę"] { display: none; }
}
.cx .reply-preview-banner, .cx .upload-progress-banner, .cx .priority-mode-banner, .cx .chat-send-error-banner, .cx .emoji-picker { flex: none; margin-left: 12px; margin-right: 12px; }
.cx .emoji-picker { max-height: 150px; overflow-y: auto; }

/* poprzednie rozmowy */
.cx > .section-label-row { order: 9; flex: none; margin: 0 12px calc(8px + env(safe-area-inset-bottom, 0px)); padding: 9px 14px; border-radius: 16px; background: var(--surface); border: 1px solid var(--border); }
.cx > .ticket-list {
  position: absolute; left: 12px; right: 12px; bottom: 150px; max-height: 50%; overflow-y: auto; z-index: 8; padding: 10px; border-radius: 22px;
  background: var(--surface); border: 1px solid var(--border); box-shadow: 0 24px 50px -16px rgba(0,0,0,0.9); animation: cxSheetUp 0.3s ease both;
}

/* --- jasny motyw --- */
.app-root.light-theme .cx { background: radial-gradient(circle, rgba(0,0,0,0.04) 1px, transparent 1.5px) 0 0/18px 18px, var(--bg); }
.app-root.light-theme .cx .chat-header { background: linear-gradient(180deg, rgba(217,130,15,0.14), rgba(217,130,15,0.02)), #fff; border-bottom-color: rgba(0,0,0,0.08); box-shadow: 0 14px 26px -22px rgba(60,40,10,0.5); }
.app-root.light-theme .cx-boss .chat-header { background: linear-gradient(180deg, rgba(31,157,92,0.14), rgba(31,157,92,0.02)), #fff; }
.app-root.light-theme .cx-group .chat-header { background: linear-gradient(180deg, rgba(147,51,234,0.12), rgba(147,51,234,0.02)), #fff; }
.app-root.light-theme .cx-back, .app-root.light-theme .cx .chat-search-toggle, .app-root.light-theme .cx .chat-resolve-btn { background: rgba(0,0,0,0.05); border-color: rgba(0,0,0,0.1); }
.app-root.light-theme .cx .dm-switcher-strip { background: #fff; border-bottom-color: rgba(0,0,0,0.07); }
.app-root.light-theme .cx-boss .chat-bubble.theirs { background: linear-gradient(155deg, rgba(31,157,92,0.18), rgba(31,157,92,0.06)), #fff; border-color: rgba(31,157,92,0.35); }
.app-root.light-theme .cx-dm .chat-bubble.theirs { background: linear-gradient(155deg, rgba(37,99,235,0.14), rgba(37,99,235,0.04)), #fff; border-color: rgba(37,99,235,0.3); }
.app-root.light-theme .cx .chat-input-row { background: #fff; box-shadow: 0 18px 36px -16px rgba(60,40,10,0.45), inset 0 1px 0 #fff; }
.app-root.light-theme .cx .chat-date-separator span { background: #fff; border-color: rgba(0,0,0,0.1); }
.app-root.light-theme .cx-boss .cx-who-text small { color: #148a55; }
.app-root.light-theme .cx-chip { color: var(--text); background: linear-gradient(150deg, rgba(217,130,15,0.16), rgba(217,130,15,0.04)), #fff; border-color: rgba(217,130,15,0.45); }
.app-root.light-theme .cx .empty-state { border-color: rgba(0,0,0,0.14); }
.app-root.light-theme .cx-call-sheet { background: radial-gradient(120% 70% at 50% 0%, rgba(31,157,92,0.2), transparent 62%), #fff; }
.app-root.light-theme .cx-call-avatar span { background: #eaf7f0; }
.app-root.light-theme .cx > .ticket-list, .app-root.light-theme .cx > .section-label-row { background: #fff; }

@media (prefers-reduced-motion: reduce) {
  .cx::before, .cx::after, .cx-avatar::before, .cx-call svg, .cx-call-pulse, .cx-call-avatar i, .cx .empty-state svg { animation: none; }
  .cx, .cx .chat-header, .cx .chat-input-row, .cx-chip, .cx-call-sheet { animation-duration: 0.01s; animation-delay: 0s; }
}

/* =====================================================================
   Czat 3.1 — spokojniejszy, wyśrodkowany układ + gesty, głosówki i połączenia w aplikacji.
   Dopisane NA KOŃCU arkusza (nadpisuje Czat 3.0); usunięcie bloku przywraca poprzedni wygląd.
   ===================================================================== */
@keyframes cxRise { from { opacity: 0; transform: translateY(18px) scale(0.97); } to { opacity: 1; transform: none; } }
@keyframes cxSpinner { to { transform: rotate(360deg); } }
@keyframes cxFly { 0% { transform: translate(0, 0) rotate(0); opacity: 1; } 45% { transform: translate(7px, -9px) rotate(-12deg); opacity: 0; } 46% { transform: translate(-7px, 9px); opacity: 0; } 100% { transform: translate(0, 0); opacity: 1; } }
@keyframes cxBanner { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: none; } }
@keyframes cxRipple { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2.6); opacity: 0; } }

/* --- tło: spokojne, bez kolorowych plam --- */
.cx {
  background:
    radial-gradient(120% 55% at 50% -12%, rgba(255,255,255,0.055), transparent 62%),
    radial-gradient(circle, rgba(255,255,255,0.02) 1px, transparent 1.6px) 0 0/20px 20px,
    var(--bg);
}
.cx::before, .cx::after { display: none; }
.app-root.light-theme .cx {
  background:
    radial-gradient(120% 55% at 50% -12%, rgba(255,255,255,0.9), transparent 62%),
    radial-gradient(circle, rgba(0,0,0,0.035) 1px, transparent 1.6px) 0 0/20px 20px,
    var(--bg);
}

/* --- nagłówek: neutralny, kolor tylko w cienkiej linii --- */
.cx .chat-header, .cx-boss .chat-header, .cx-group .chat-header,
.app-root.light-theme .cx .chat-header, .app-root.light-theme .cx-boss .chat-header, .app-root.light-theme .cx-group .chat-header {
  background: var(--surface); box-shadow: 0 10px 24px -20px rgba(0,0,0,0.8);
}
.cx-offline {
  order: -4; flex: none; padding: 7px 14px; text-align: center; font-size: 11.5px; font-weight: 700; color: #fff;
  background: linear-gradient(90deg, #d94b4b, #c23a3a); animation: cxBanner 0.3s ease both;
}
.cx-hint {
  position: absolute; z-index: 20; left: 50%; top: calc(env(safe-area-inset-top, 0px) + 74px); transform: translateX(-50%);
  max-width: calc(100% - 32px); padding: 9px 16px; border-radius: 999px; font-size: 12px; font-weight: 700; text-align: center;
  color: var(--text); background: var(--surface-2); border: 1px solid var(--border); box-shadow: 0 14px 28px -12px rgba(0,0,0,0.7);
  pointer-events: none; animation: cxBanner 0.35s ease both;
}

/* --- wiadomości: równe marginesy, spokojniejsze kolory --- */
.cx .chat-messages { padding: 14px 16px 12px; gap: 4px; }
.cx .msg-with-reply, .cx .chat-bubble-col { max-width: 80%; }
.cx .chat-bubble { font-size: 14.5px; padding: 10px 14px; }
.cx .chat-bubble.mine {
  background: linear-gradient(160deg, #f7b552, #ee9a2c); color: #1d1305;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 16px -10px rgba(0,0,0,0.7);
}
.cx .chat-bubble.theirs {
  background: var(--surface-2); border: 1px solid rgba(255,255,255,0.07);
  box-shadow: 0 8px 16px -12px rgba(0,0,0,0.8);
}
.cx-boss .chat-bubble.theirs, .cx-dm .chat-bubble.theirs { background: var(--surface-2); border: 1px solid rgba(255,255,255,0.07); }
.cx-boss .chat-bubble.theirs { box-shadow: inset 3px 0 0 #4fd591, 0 8px 16px -12px rgba(0,0,0,0.8); padding-left: 16px; }
.cx-dm .chat-bubble.theirs { box-shadow: inset 3px 0 0 #60a5fa, 0 8px 16px -12px rgba(0,0,0,0.8); padding-left: 16px; }
.cx .chat-bubble.admin-msg { box-shadow: inset 3px 0 0 rgba(255,255,255,0.35), 0 8px 16px -12px rgba(0,0,0,0.7) !important; }
.app-root.light-theme .cx .chat-bubble.theirs, .app-root.light-theme .cx-boss .chat-bubble.theirs, .app-root.light-theme .cx-dm .chat-bubble.theirs {
  background: #fff; border-color: rgba(0,0,0,0.07);
}
.app-root.light-theme .cx-boss .chat-bubble.theirs { box-shadow: inset 3px 0 0 #1f9d5c, 0 8px 16px -12px rgba(60,40,10,0.35); }
.app-root.light-theme .cx-dm .chat-bubble.theirs { box-shadow: inset 3px 0 0 #2563eb, 0 8px 16px -12px rgba(60,40,10,0.35); }
.cx .msg-time, .cx .msg-time-group { color: var(--text-dim); }
.cx .chat-date-separator span { font-size: 10.5px; letter-spacing: 1px; }

/* przycisk "odpowiedz" na telefonie znika (jest gest przesunięcia); na komputerze nadal pojawia się po najechaniu */
@media (hover: none) {
  .cx .msg-reply-btn, .cx .group-reply-link { display: none; }
}

/* gest "przesuń, aby odpowiedzieć" */
.cx-swiping { position: relative; will-change: transform; }
.cx-swiping::before {
  content: "↩"; position: absolute; top: 50%; left: -34px; width: 28px; height: 28px; margin-top: -14px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 800; color: #1a1206;
  background: var(--amber); opacity: var(--sw, 0); transform: scale(calc(0.5 + var(--sw, 0) * 0.5));
}
.cx-swiping.mine::before { left: auto; right: -34px; }

/* wejście: ostatnie wiadomości "wjeżdżają" po kolei przy otwarciu rozmowy */
.cx .chat-messages:not([data-ready]) > :nth-last-child(-n+8) { animation: cxRise 0.5s cubic-bezier(.22,1,.36,1) both; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(8) { animation-delay: 0s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(7) { animation-delay: 0.04s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(6) { animation-delay: 0.08s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(5) { animation-delay: 0.12s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(4) { animation-delay: 0.16s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(3) { animation-delay: 0.2s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(2) { animation-delay: 0.24s; }
.cx .chat-messages:not([data-ready]) > :nth-last-child(1) { animation-delay: 0.28s; }

/* wysyłanie: samolot "odlatuje" */
.cx .chat-send-btn:active svg { animation: cxFly 0.42s ease both; }

/* --- głosówki --- */
.rec-wave.live i { animation: none; transform: scaleY(calc(0.14 + var(--lv, 0.25) * var(--k, 1) * 0.86)); transition: transform 0.09s linear; }
.cx-voice-banner {
  flex: none; display: flex; align-items: center; gap: 8px; margin: 6px 12px 0; padding: 9px 10px 9px 14px; border-radius: 16px;
  font-size: 12.5px; font-weight: 600; color: var(--text); background: rgba(255,166,48,0.14); border: 1px solid rgba(255,166,48,0.45);
  animation: cxBanner 0.3s ease both;
}
.cx-voice-banner span { flex: 1; min-width: 0; }
.cx-voice-banner button {
  flex: none; padding: 7px 13px; border-radius: 999px; border: none; cursor: pointer; font: 800 12px 'IBM Plex Sans', sans-serif;
  color: #1a1206; background: linear-gradient(145deg, #ffc76f, #ff9a2e);
}
.cx-voice-banner button.ghost { background: transparent; color: var(--text-muted); border: 1px solid var(--border); }
.cx-voice-banner.sending { background: rgba(255,255,255,0.06); border-color: var(--border); }
.cx-spinner { flex: none !important; width: 14px; height: 14px; border-radius: 50%; border: 2px solid var(--border); border-top-color: var(--amber); animation: cxSpinner 0.8s linear infinite; }

/* --- połączenia --- */
.cx-call-overlay { z-index: 1100; }
.cx-call-full { align-items: center; background: rgba(4,6,8,0.82); }
.cx-call-full .cx-call-sheet { margin: 0 16px; }
.cx-call-alt {
  display: block; margin-top: 10px; padding: 14px; border-radius: 20px; text-decoration: none; text-align: center;
  font-size: 14px; font-weight: 800; color: #6ee7b7; border: 1.5px solid rgba(110,231,183,0.45); background: rgba(110,231,183,0.08);
}
.cx-call-go { width: 100%; border: none; cursor: pointer; font-family: inherit; }
.cx-call-ctl { display: flex; justify-content: center; gap: 28px; margin-top: 8px; }
.cx-call-round {
  width: 64px; height: 64px; border-radius: 50%; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: var(--text); background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.14); transition: transform 0.15s ease, background 0.15s ease;
}
.cx-call-round:disabled { opacity: 0.4; }
.cx-call-round:active:not(:disabled) { transform: scale(0.9); }
.cx-call-round.on { background: #fff; color: #111; }
.cx-call-hang { color: #fff; background: linear-gradient(145deg, #ff6b6b, #d12f2f); border: none; box-shadow: 0 14px 26px -10px rgba(220,50,50,0.9); }
.cx-call-pick { color: #04210f; background: linear-gradient(145deg, #7bf0b4, #1fb868); border: none; box-shadow: 0 14px 26px -10px rgba(31,184,104,0.9); animation: cxRing 1.4s ease-in-out infinite; }
.cx-call-live .cx-call-sub { font-variant-numeric: tabular-nums; }
.cx-ph-failed .cx-call-avatar i, .cx-ph-ended .cx-call-avatar i, .cx-ph-noanswer .cx-call-avatar i, .cx-ph-declined .cx-call-avatar i, .cx-ph-nomic .cx-call-avatar i { animation: none; opacity: 0; }
.cx-ph-live .cx-call-avatar i { animation-duration: 3.2s; border-color: rgba(110,231,183,0.5); }
.cx-missed-toast {
  position: fixed; z-index: 1050; left: 50%; bottom: 22px; transform: translateX(-50%); width: min(92vw, 380px);
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 18px; color: var(--text);
  background: var(--surface-2); border: 1px solid rgba(255,107,107,0.5); box-shadow: 0 20px 40px -14px rgba(0,0,0,0.85); animation: cxBanner 0.35s ease both;
}
.cx-missed-toast svg { flex: none; color: #ff6b6b; }
.cx-missed-toast div { flex: 1; min-width: 0; display: flex; flex-direction: column; font-size: 13px; }
.cx-missed-toast span { font-size: 11.5px; color: var(--text-muted); margin-top: 1px; }
.cx-missed-toast button { flex: none; width: 30px; height: 30px; border-radius: 50%; border: none; cursor: pointer; color: var(--text-muted); background: rgba(255,255,255,0.08); }

.cx .chat-date-separator { position: static; }
/* okna połączeń żyją też poza .app-root (panel Szefa) — własna czcionka i kolor tekstu */
.cx-call-sheet { font-family: 'IBM Plex Sans', sans-serif; color: var(--text, #edeff3); }
/* reakcje i znaczniki pod wiadomością: mniejsze i bliżej dymka */
.cx .msg-reaction-add { width: 22px; height: 22px; padding: 0; font-size: 11px; opacity: 0.5; }
.cx .msg-reactions:not(.has-reactions) { margin-top: 2px; }
.cx .chat-seen-by, .cx .msg-time-group { margin-top: 1px; line-height: 1.25; }

@media (prefers-reduced-motion: reduce) {
  .cx .chat-messages:not([data-ready]) > :nth-last-child(-n+8), .cx-call-pick, .cx-offline, .cx-hint, .cx-voice-banner, .cx-missed-toast { animation: none; }
}

/* =====================================================================
   Rekompensaty 2.0 — aktualny tydzień + efektowne karty + zwijane archiwum (klasy cm2-*).
   Dopisane NA KOŃCU arkusza; usunięcie bloku przywraca poprzedni wygląd kart.
   ===================================================================== */
@keyframes cm2In { 0% { opacity: 0; transform: translateY(20px) scale(0.97); } 100% { opacity: 1; transform: none; } }
@keyframes cm2Dot { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-3px); } }
@keyframes cm2Sweep { 0% { transform: translateX(-160%) skewX(-18deg); } 45%, 100% { transform: translateX(430%) skewX(-18deg); } }
@keyframes cm2Glow { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

/* --- podsumowanie tygodnia --- */
.cm2-hero {
  position: relative; overflow: hidden; display: flex; align-items: center; gap: 16px; padding: 18px 16px; margin-bottom: 14px; border-radius: 24px;
  background: radial-gradient(120% 130% at 0% 0%, rgba(255,166,48,0.2), transparent 62%), var(--surface);
  border: 1px solid rgba(255,166,48,0.45); box-shadow: 0 22px 40px -26px rgba(255,140,30,0.8), inset 0 1px 0 rgba(255,255,255,0.06);
  animation: cm2In 0.6s cubic-bezier(.22,1,.36,1) both;
}
.cm2-hero-green { background: radial-gradient(120% 130% at 0% 0%, rgba(110,231,183,0.22), transparent 62%), var(--surface); border-color: rgba(110,231,183,0.5); box-shadow: 0 22px 40px -26px rgba(40,200,140,0.8), inset 0 1px 0 rgba(255,255,255,0.06); }
.cm2-hero-red { background: radial-gradient(120% 130% at 0% 0%, rgba(255,107,107,0.22), transparent 62%), var(--surface); border-color: rgba(255,107,107,0.5); box-shadow: 0 22px 40px -26px rgba(255,80,80,0.75), inset 0 1px 0 rgba(255,255,255,0.06); }
.cm2-hero::before {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 34%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.1), transparent); animation: cm2Sweep 6s ease-in-out infinite 1.4s;
}
.cm2-ring-num { font-family: 'Space Mono', monospace; font-weight: 700; font-size: 22px; line-height: 1; color: var(--text); }
.cm2-ring-num small { font-size: 12px; color: var(--text-dim); }
.cm2-hero-info { position: relative; min-width: 0; flex: 1; }
.cm2-hero-label { font-size: 10.5px; font-weight: 800; letter-spacing: 1.3px; text-transform: uppercase; color: var(--text-dim); }
.cm2-hero-amount { margin: 4px 0 8px; font-family: 'Space Mono', monospace; font-size: 27px; font-weight: 700; letter-spacing: -0.8px; line-height: 1.1; color: var(--amber); text-shadow: 0 0 22px rgba(255,166,48,0.4); }
.cm2-hero-amount.pos { color: var(--green); text-shadow: 0 0 22px rgba(110,231,183,0.4); }
.cm2-hero-amount.neg { color: var(--red); text-shadow: 0 0 22px rgba(255,107,107,0.4); }
.cm2-hero-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.cm2-chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 999px; font-size: 10.5px; font-weight: 800; }
.cm2-chip-pending { background: rgba(255,166,48,0.16); color: var(--amber); }
.cm2-chip-ok { background: rgba(110,231,183,0.16); color: var(--green); }
.cm2-chip-no { background: rgba(255,107,107,0.16); color: var(--red); }

/* zapasowe kolory dla starszych przeglądarek bez color-mix() */
.cm2-badge { background: rgba(110,231,183,0.16); }
.cm2-sub .cm2-badge { background: rgba(255,107,107,0.16); }
.cm2-note { border-left: 3px solid rgba(110,231,183,0.6); }
.cm2-sub .cm2-note { border-left-color: rgba(255,107,107,0.6); }
/* --- karta zgłoszenia --- */
.cm2-card {
  --acc: var(--green); position: relative; overflow: hidden; margin-bottom: 12px; padding: 14px 16px 13px 20px; border-radius: 22px;
  background: linear-gradient(160deg, var(--surface-2) -40%, var(--surface) 55%); border: 1px solid var(--border);
  box-shadow: 0 16px 28px -22px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05);
  animation: cm2In 0.5s cubic-bezier(.22,1,.36,1) both; transition: transform 0.15s ease, border-color 0.2s ease;
}
.cm2-card:active { transform: scale(0.985); }
.cm2-sub { --acc: var(--red); }
.cm2-card::before {
  content: ""; position: absolute; left: 0; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, var(--acc), transparent 130%); box-shadow: 0 0 14px var(--acc);
}
.cm2-st-approved { border-color: color-mix(in srgb, var(--acc) 38%, var(--border)); }
.cm2-st-approved::after { content: ""; position: absolute; right: -40px; top: -40px; width: 130px; height: 130px; border-radius: 50%; pointer-events: none; background: radial-gradient(circle, color-mix(in srgb, var(--acc) 22%, transparent), transparent 70%); }
.cm2-st-pending { border-color: rgba(255,166,48,0.5); }
.cm2-st-pending::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 30%; pointer-events: none;
  background: linear-gradient(100deg, transparent, rgba(255,166,48,0.14), transparent); animation: cm2Sweep 3.6s ease-in-out infinite;
}
.cm2-st-rejected { opacity: 0.78; }
.cm2-st-rejected .cm2-amount { text-decoration: line-through; text-decoration-thickness: 2px; opacity: 0.75; }
.cm2-card-top { position: relative; z-index: 1; display: flex; align-items: center; gap: 12px; }
.cm2-badge {
  width: 44px; height: 44px; border-radius: 15px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: var(--acc);
  background: color-mix(in srgb, var(--acc) 16%, transparent); box-shadow: 0 10px 20px -10px var(--acc), inset 0 1px 0 rgba(255,255,255,0.12);
}
.cm2-main { flex: 1; min-width: 0; }
.cm2-kind { font-size: 14.5px; font-weight: 800; letter-spacing: -0.2px; }
.cm2-when { margin-top: 2px; font-size: 11px; color: var(--text-dim); }
.cm2-amount { flex-shrink: 0; font-family: 'Space Mono', monospace; font-size: 19px; font-weight: 700; color: var(--acc); text-shadow: 0 0 18px color-mix(in srgb, var(--acc) 45%, transparent); }
.cm2-note {
  position: relative; z-index: 1; margin-top: 11px; padding: 10px 13px; border-radius: 14px; font-size: 13.5px; line-height: 1.45; overflow-wrap: anywhere;
  background: rgba(255,255,255,0.045); border-left: 3px solid color-mix(in srgb, var(--acc) 70%, transparent);
}
.cm2-footer { position: relative; z-index: 1; display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin-top: 11px; }
.cm2-status { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 800; }
.cm2-status-pending { background: rgba(255,166,48,0.16); color: var(--amber); }
.cm2-status-approved { background: rgba(110,231,183,0.16); color: var(--green); }
.cm2-status-rejected { background: rgba(255,107,107,0.16); color: var(--red); }
.cm2-dots { display: inline-flex; gap: 2px; margin-left: 2px; }
.cm2-dots b { width: 4px; height: 4px; border-radius: 50%; background: currentColor; animation: cm2Dot 1.2s ease-in-out infinite; }
.cm2-dots b:nth-child(2) { animation-delay: 0.15s; }
.cm2-dots b:nth-child(3) { animation-delay: 0.3s; }
.cm2-period, .cm2-decided { display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; color: var(--text-dim); font-family: 'Space Mono', monospace; }
.cm2-decided { margin-left: auto; }

.cm2-allclear {
  display: flex; align-items: center; gap: 12px; padding: 18px 16px; margin-bottom: 12px; border-radius: 22px; color: var(--text-muted); font-size: 13px;
  border: 1px dashed var(--border); background: linear-gradient(160deg, var(--surface), transparent);
}
.cm2-allclear svg { flex-shrink: 0; color: var(--green); }
.cm2-allclear b { display: block; color: var(--text); font-size: 14px; margin-bottom: 2px; }

/* --- archiwum (używa wspólnych klas tk2-archive-*) --- */
.cm2-awk { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 12px 0 4px; font: 800 10.5px 'Space Mono', monospace; letter-spacing: 0.8px; color: var(--text-dim); text-transform: uppercase; }
.cm2-awk:first-child { padding-top: 2px; }
.cm2-awk span:last-child { padding: 2px 8px; border-radius: 999px; background: var(--surface-3); color: var(--text-muted); }
.cm2-awk span.pos { color: var(--green); background: rgba(110,231,183,0.12); }
.cm2-awk span.neg { color: var(--red); background: rgba(255,107,107,0.12); }
.cm2-arow { display: flex; align-items: center; gap: 10px; padding: 9px 0; border-bottom: 1px solid var(--border); }
.cm2-arow-ic { width: 26px; height: 26px; border-radius: 9px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: var(--surface-3); }
.cm2-arow-pos .cm2-arow-ic { color: var(--green); background: rgba(110,231,183,0.14); }
.cm2-arow-neg .cm2-arow-ic { color: var(--red); background: rgba(255,107,107,0.14); }
.cm2-arow-main { flex: 1; min-width: 0; }
.cm2-arow-note { font-size: 12.5px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cm2-arow-sub { display: flex; align-items: center; gap: 4px; margin-top: 2px; font-size: 10px; color: var(--text-dim); }
.cm2-arow-amount { flex-shrink: 0; font-family: 'Space Mono', monospace; font-size: 13px; font-weight: 700; }
.cm2-arow-pos .cm2-arow-amount { color: var(--green); }
.cm2-arow-neg .cm2-arow-amount { color: var(--red); }
.cm2-arow-rejected { opacity: 0.6; }
.cm2-arow-rejected .cm2-arow-amount { text-decoration: line-through; }

/* --- jasny motyw --- */
.app-root.light-theme .cm2-hero { background: radial-gradient(120% 130% at 0% 0%, rgba(217,130,15,0.18), transparent 62%), #fff; }
.app-root.light-theme .cm2-hero-green { background: radial-gradient(120% 130% at 0% 0%, rgba(31,157,92,0.18), transparent 62%), #fff; }
.app-root.light-theme .cm2-hero-red { background: radial-gradient(120% 130% at 0% 0%, rgba(217,69,63,0.16), transparent 62%), #fff; }
.app-root.light-theme .cm2-card { background: #fff; }
.app-root.light-theme .cm2-note { background: rgba(0,0,0,0.04); }

@media (prefers-reduced-motion: reduce) {
  .cm2-hero::before, .cm2-st-pending::after, .cm2-dots b { animation: none; }
  .cm2-hero, .cm2-card { animation-duration: 0.01s; animation-delay: 0s; }
}

/* =====================================================================
   Czat 3.2 — dymki z efektem WOW, wyśrodkowana kolumna wiadomości, nowe okienka odpowiedzi,
   pasek polskich znaków, czaty w panelu admina w tym samym stylu (pełny ekran).
   Dopisane NA KOŃCU arkusza (nadpisuje Czat 3.0/3.1); usunięcie bloku przywraca poprzedni wygląd.
   ===================================================================== */
@keyframes cxPopR { 0% { opacity: 0; transform: translate(26px, 12px) scale(0.84); } 60% { opacity: 1; transform: translate(-2px, -1px) scale(1.02); } 100% { opacity: 1; transform: none; } }
@keyframes cxPopL { 0% { opacity: 0; transform: translate(-26px, 12px) scale(0.84); } 60% { opacity: 1; transform: translate(2px, -1px) scale(1.02); } 100% { opacity: 1; transform: none; } }

/* --- układ: wyśrodkowana kolumna; czaty admina szerzej, na dużym ekranie jako panel na środku --- */
.cx-adminchat, .cx-admingroup { max-width: 760px; }
.cx .chat-messages { padding: 18px max(14px, calc((100% - 660px) / 2)) 14px; gap: 5px; }
@media (min-width: 820px) {
  /* na szerokim ekranie czat jest panelem na środku, a reszta strony jest przyciemniona (cień o ogromnym rozmiarze) */
  .cx { box-shadow: 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 100vmax rgba(4,6,9,0.84); }
}
.cx .chat-back-btn {
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; cursor: pointer;
  color: var(--text); background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.1);
}
.app-root.light-theme .cx .chat-back-btn { background: rgba(0,0,0,0.05); border-color: rgba(0,0,0,0.1); }
.cx .chat-input-row { position: relative; backdrop-filter: none; -webkit-backdrop-filter: none; }

/* --- dymki --- */
.cx .chat-bubble { position: relative; padding: 10px 15px; border-radius: 22px; font-size: 14.5px; line-height: 1.5; }
.cx .msg-with-reply.mine .chat-bubble, .cx .msg-with-delete.mine .chat-bubble, .cx .chat-bubble-row.mine .chat-bubble { animation: cxPopR 0.5s cubic-bezier(.22,1,.36,1) both; transform-origin: 100% 100%; }
.cx .msg-with-reply.theirs .chat-bubble, .cx .msg-with-delete.theirs .chat-bubble, .cx .chat-bubble-row.theirs .chat-bubble { animation: cxPopL 0.5s cubic-bezier(.22,1,.36,1) both; transform-origin: 0 100%; }
.cx .chat-messages:not([data-ready]) .chat-bubble { animation-duration: 0s !important; animation-delay: 0s !important; }
.cx .chat-bubble.mine {
  color: #1d1305; border: none;
  background: linear-gradient(155deg, #ffd584 0%, #f9ae3f 52%, #ee8c20 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -3px 7px rgba(150,65,0,0.2), 0 12px 24px -14px rgba(238,140,32,0.6), 0 2px 5px rgba(0,0,0,0.22);
}
.cx .chat-bubble.theirs {
  border: 1px solid transparent;
  background: linear-gradient(160deg, var(--surface-2), var(--surface)) padding-box, linear-gradient(160deg, rgba(255,255,255,0.32), rgba(255,255,255,0.05) 58%, rgba(255,255,255,0.14)) border-box;
  box-shadow: 0 12px 24px -16px rgba(0,0,0,0.9);
}
.cx-boss .chat-bubble.theirs { box-shadow: inset 3px 0 0 #4fd591, 0 12px 24px -16px rgba(0,0,0,0.9); padding-left: 17px; }
.cx-dm .chat-bubble.theirs { box-shadow: inset 3px 0 0 #60a5fa, 0 12px 24px -16px rgba(0,0,0,0.9); padding-left: 17px; }
.cx .chat-bubble.admin-msg { box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 12px 24px -16px rgba(0,0,0,0.8) !important; }
.cx-admingroup .chat-bubble.mine.admin-msg {
  background: linear-gradient(155deg, #ffd584 0%, #f9ae3f 52%, #ee8c20 100%) !important;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -3px 7px rgba(150,65,0,0.2), 0 12px 24px -14px rgba(238,140,32,0.6), 0 2px 5px rgba(0,0,0,0.22) !important;
}
.cx .chat-bubble.mine.msg-group-last { border-bottom-right-radius: 7px; }
.cx .chat-bubble.theirs.msg-group-last { border-bottom-left-radius: 7px; }
.app-root.light-theme .cx .chat-bubble.theirs, .app-root.light-theme .cx-boss .chat-bubble.theirs, .app-root.light-theme .cx-dm .chat-bubble.theirs {
  background: linear-gradient(160deg, #fff, #f6f4ee) padding-box, linear-gradient(160deg, rgba(0,0,0,0.16), rgba(0,0,0,0.03) 58%, rgba(0,0,0,0.09)) border-box;
}
.cx .msg-with-reply, .cx .msg-with-delete, .cx .chat-bubble-col { max-width: 80%; }
.cx .msg-time, .cx .msg-time-group, .cx .chat-msg-time { margin-top: 4px; padding: 0 6px; font-size: 10.5px; letter-spacing: 0.3px; color: var(--text-dim); }

/* --- data: linia z "pastylką" pośrodku --- */
.cx .chat-date-separator { display: flex; align-items: center; justify-content: center; gap: 12px; width: 100%; margin: 18px 0 10px; }
.cx .chat-date-separator::before, .cx .chat-date-separator::after { content: ""; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15)); }
.cx .chat-date-separator::after { background: linear-gradient(270deg, transparent, rgba(255,255,255,0.15)); }
.cx .chat-date-separator span { flex: none; }
.app-root.light-theme .cx .chat-date-separator::before { background: linear-gradient(90deg, transparent, rgba(0,0,0,0.14)); }
.app-root.light-theme .cx .chat-date-separator::after { background: linear-gradient(270deg, transparent, rgba(0,0,0,0.14)); }

/* --- okienka odpowiedzi: pasek nad polem pisania i cytat w dymku --- */
.cx .reply-preview-banner {
  position: relative; flex: none; display: flex; align-items: center; gap: 12px; margin: 8px 12px 0; padding: 10px 12px 10px 18px; border-radius: 18px; overflow: hidden;
  background: linear-gradient(160deg, var(--surface-2), var(--surface)); border: 1px solid var(--border);
  box-shadow: 0 16px 30px -18px rgba(0,0,0,0.85); animation: cxBanner 0.28s cubic-bezier(.22,1,.36,1) both;
}
.cx .reply-preview-banner::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: linear-gradient(180deg, #ffc76f, #ff8a3d); box-shadow: 0 0 12px rgba(255,150,40,0.7); }
.cx .reply-preview-banner > svg:first-child { flex: none; color: var(--amber); }
.cx .reply-preview-author { font-size: 11px; font-weight: 800; letter-spacing: 0.4px; text-transform: uppercase; color: var(--amber); }
.cx .reply-preview-text { margin-top: 2px; font-size: 13px; line-height: 1.35; color: var(--text); white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.cx .reply-preview-cancel { flex: none; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.08); border: none; color: var(--text-muted); cursor: pointer; }
.app-root.light-theme .cx .reply-preview-banner { background: #fff; }
.app-root.light-theme .cx .reply-preview-cancel { background: rgba(0,0,0,0.06); }
.cx .chat-quote { margin: 0 0 8px; padding: 7px 12px 7px 13px; border-radius: 13px; border-left-width: 3px; background: rgba(0,0,0,0.2); }
.cx .chat-quote-author { margin-bottom: 2px; font-size: 10.5px; font-weight: 800; letter-spacing: 0.4px; text-transform: uppercase; }
.cx .chat-quote-text { white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-size: 12.5px; line-height: 1.35; }
.cx .chat-bubble.mine .chat-quote { background: rgba(80,35,0,0.16); }

/* --- panel admina: wiadomości w układzie jak u kierowcy, akcje po dotknięciu dymka --- */
.cx .msg-with-delete { position: relative; display: flex; flex-direction: column; gap: 0; }
.cx .msg-with-delete.mine { align-self: flex-end; align-items: flex-end; }
.cx .msg-with-delete.theirs { align-self: flex-start; align-items: flex-start; }
.cx .msg-with-delete .chat-seen-by { align-self: flex-end; }
.cx .msg-with-delete .msg-reply-btn, .cx .msg-with-delete .msg-delete-btn,
.cx .chat-bubble-row > .chat-delete-btn { display: none; }
.cx .msg-with-delete.cx-actions, .cx .chat-bubble-row.cx-actions { margin-top: 40px; }
.cx .msg-with-delete.cx-actions .msg-reply-btn, .cx .msg-with-delete.cx-actions .msg-delete-btn,
.cx .chat-bubble-row.cx-actions > .chat-delete-btn {
  display: flex; position: absolute; top: -38px; width: 34px; height: 34px; border-radius: 50%; align-items: center; justify-content: center; cursor: pointer; opacity: 1;
  color: var(--text); background: var(--surface-2); border: 1px solid var(--border); box-shadow: 0 12px 22px -10px rgba(0,0,0,0.85); animation: cxBanner 0.22s ease both;
}
.cx .msg-with-delete.cx-actions .msg-reply-btn { right: 40px; }
.cx .msg-with-delete.cx-actions .msg-delete-btn { right: 0; color: var(--red); }
.cx .chat-bubble-row { position: relative; width: 100%; }
.cx .chat-bubble-row.cx-actions > .chat-delete-btn:last-child { right: 0; color: var(--red); }
.cx .chat-bubble-row.cx-actions > .chat-delete-btn:nth-last-child(2) { right: 40px; }
.cx .chat-bubble-row.cx-actions > .chat-delete-btn:nth-last-child(3) { right: 80px; }
.cx .chat-bubble-row.cx-actions > .chat-delete-btn:nth-last-child(4) { right: 120px; }

/* --- pasek polskich znaków nad polem pisania (ekrany dotykowe) --- */
@keyframes cxPl { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.cx-pl {
  position: absolute; left: 4px; right: 4px; bottom: calc(100% + 8px); z-index: 15; display: flex; gap: 5px; padding: 6px; border-radius: 18px;
  background: var(--surface-2); border: 1px solid var(--border); box-shadow: 0 18px 32px -14px rgba(0,0,0,0.85); animation: cxPl 0.2s ease both;
}
.cx-pl-key {
  flex: 1; min-width: 0; height: 42px; border-radius: 12px; border: none; cursor: pointer; padding: 0;
  font: 800 19px 'IBM Plex Sans', sans-serif; color: var(--text); background: var(--surface-3); transition: transform 0.1s ease, background 0.15s ease;
}
.cx-pl-key:active { transform: scale(0.88); background: var(--amber); color: #1a1206; }
.cx-pl-shift { flex: 0 0 44px; font-size: 17px; color: var(--text-muted); }
.cx-pl-shift.on { background: var(--amber); color: #1a1206; }

@media (prefers-reduced-motion: reduce) {
  .cx .chat-bubble, .cx-pl, .cx .reply-preview-banner { animation: none !important; }
}

/* =====================================================================
   Czat 3.3 — dopracowane dymki: godzina WEWNĄTRZ dymka (styl komunikatorów), równe rogi i marginesy,
   czytelna plakietka priorytetu, nowy wygląd cytatu, spokojniejsze kolory i cienie.
   Dopisane NA KOŃCU arkusza (nadpisuje Czat 3.1/3.2); usunięcie bloku przywraca poprzedni wygląd.
   ===================================================================== */
@keyframes cxTickPop { 0% { transform: scale(0.4); opacity: 0; } 70% { transform: scale(1.25); } 100% { transform: scale(1); opacity: 1; } }

/* godzina jest teraz w dymku (znacznik .cx-stamp) — zewnętrzne etykiety czasu chowamy */
.cx .msg-time, .cx .msg-time-group, .cx .chat-msg-time { display: none; }

/* --- dymek: spójny rytm odstępów, tekst zawsze po lewej, godzina w prawym dolnym rogu --- */
.cx .chat-bubble {
  display: flow-root; min-width: 74px; padding: 9px 13px 7px 14px; border-radius: 20px;
  font-size: 15px; line-height: 1.42; letter-spacing: 0; text-align: left; overflow-wrap: anywhere;
}
.cx .chat-bubble.mine.msg-group-last { border-bottom-right-radius: 6px; }
.cx .chat-bubble.theirs.msg-group-last { border-bottom-left-radius: 6px; }
.cx .chat-bubble.mine.msg-group-notfirst { border-top-right-radius: 8px; }
.cx .chat-bubble.theirs.msg-group-notfirst { border-top-left-radius: 8px; }
.cx .chat-bubble:not(.mine):not(.theirs) { border-radius: 20px; }
.cx .chat-bubble:active { filter: brightness(0.96); }
.cx-stamp {
  float: right; display: inline-flex; align-items: center; gap: 3px; margin: 8px 0 -3px 14px;
  font: 600 10.5px/1 'IBM Plex Sans', sans-serif; letter-spacing: 0.2px; color: var(--text-dim); user-select: none; -webkit-user-select: none;
}
.cx .chat-bubble.mine .cx-stamp { color: rgba(48,26,4,0.72); }
.cx-stamp .msg-ticks { margin-left: 2px; color: rgba(48,26,4,0.5); }
.cx-stamp .msg-ticks.read { color: #0a7c69; }
.cx-stamp .msg-ticks.read svg { animation: cxTickPop 0.4s cubic-bezier(.34,1.56,.64,1) both; }
.cx-stamp .msg-ticks-label { display: none; }
.cx .chat-bubble:has(> .emoji-jumbo) .cx-stamp { margin-top: 2px; }

/* --- kolory: bursztyn z lekkim połyskiem, bez jaskrawej poświaty --- */
.cx .chat-bubble.mine {
  color: #22150a;
  background: linear-gradient(160deg, #ffd176 0%, #f8a835 55%, #ec8a1f 100%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.55), 0 1px 2px rgba(0,0,0,0.3), 0 10px 20px -13px rgba(236,138,31,0.5);
}
.cx .chat-bubble.theirs { box-shadow: 0 1px 2px rgba(0,0,0,0.3), 0 10px 20px -15px rgba(0,0,0,0.9); }
.cx-boss .chat-bubble.theirs { box-shadow: inset 3px 0 0 #4fd591, 0 1px 2px rgba(0,0,0,0.3), 0 10px 20px -15px rgba(0,0,0,0.9); }
.cx-dm .chat-bubble.theirs { box-shadow: inset 3px 0 0 #60a5fa, 0 1px 2px rgba(0,0,0,0.3), 0 10px 20px -15px rgba(0,0,0,0.9); }

/* --- plakietka priorytetu: czytelna, bez migającej poświaty (animacja nieskończona obciążała telefony) --- */
.cx .chat-bubble.priority-msg {
  border: none !important; animation: none !important;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.55), 0 0 0 2px rgba(229,56,74,0.7), 0 10px 22px -12px rgba(229,56,74,0.6) !important;
}
.cx .priority-badge, .cx .important-badge {
  display: flex; width: fit-content; align-items: center; gap: 4px; margin: 0 0 6px; padding: 3px 10px 3px 8px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.6px; color: #fff !important;
  background: linear-gradient(135deg, #e5384a, #c2212f); box-shadow: 0 6px 12px -6px rgba(200,30,40,0.7);
}
.cx .important-badge { background: linear-gradient(135deg, #f2554a, #d2312a); }

/* --- cytat (odpowiedź na wiadomość): prosty pasek z lewej, równe zaokrąglenia --- */
.cx .chat-quote {
  position: relative; display: block; margin: 0 0 8px; padding: 6px 11px 6px 14px; border: 0; border-radius: 12px; overflow: hidden;
  background: rgba(0,0,0,0.2);
}
.cx .chat-quote::before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--amber); }
.cx .chat-quote-author { margin-bottom: 1px; font-size: 11px; font-weight: 800; letter-spacing: 0.2px; text-transform: none; color: var(--amber); }
.cx .chat-quote-text { font-size: 13px; line-height: 1.35; opacity: 0.92; }
.cx .chat-bubble.mine .chat-quote { background: rgba(80,40,0,0.14); }
.cx .chat-bubble.mine .chat-quote::before { background: #5b3000; }
.cx .chat-bubble.mine .chat-quote-author { color: #5b3000; }
.cx .chat-bubble.mine .chat-quote-text { color: rgba(34,21,10,0.78); }
.cx .chat-bubble:has(> .chat-quote) { min-width: min(220px, 100%); }
.cx .chat-bubble-author { margin-bottom: 3px; font-size: 11.5px; font-weight: 800; letter-spacing: 0.1px; }

/* --- nowa wiadomość: krótki "błysk" ramki (tylko dla wiadomości, które przychodzą po otwarciu rozmowy) --- */
@keyframes cxFlashM { 0% { opacity: 1; box-shadow: 0 0 0 0 rgba(255,196,90,0.9); } 100% { opacity: 0; box-shadow: 0 0 0 11px rgba(255,196,90,0); } }
@keyframes cxFlashT { 0% { opacity: 1; box-shadow: 0 0 0 0 rgba(120,230,170,0.85); } 100% { opacity: 0; box-shadow: 0 0 0 11px rgba(120,230,170,0); } }
.cx .chat-bubble::before { content: ""; position: absolute; inset: -1px; border-radius: inherit; pointer-events: none; opacity: 0; }
.cx .chat-bubble.mine::before { animation: cxFlashM 0.9s ease-out 0.2s both; }
.cx .chat-bubble.theirs::before { animation: cxFlashT 0.9s ease-out 0.2s both; }
.cx .chat-messages:not([data-ready]) .chat-bubble::before { animation-duration: 0s !important; animation-delay: 0s !important; }
.cx .chat-bubble:has(> .emoji-jumbo)::before { display: none; }

/* --- jasny motyw --- */
.app-root.light-theme .cx .chat-bubble.mine { color: #2a1a06; box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(120,70,0,0.25), 0 10px 20px -13px rgba(236,138,31,0.55); }
.app-root.light-theme .cx .chat-bubble.theirs { box-shadow: 0 1px 2px rgba(60,40,10,0.14), 0 10px 20px -15px rgba(60,40,10,0.4); }
.app-root.light-theme .cx-boss .chat-bubble.theirs { box-shadow: inset 3px 0 0 #1f9d5c, 0 1px 2px rgba(60,40,10,0.14), 0 10px 20px -15px rgba(60,40,10,0.4); }
.app-root.light-theme .cx-dm .chat-bubble.theirs { box-shadow: inset 3px 0 0 #2563eb, 0 1px 2px rgba(60,40,10,0.14), 0 10px 20px -15px rgba(60,40,10,0.4); }
.app-root.light-theme .cx .chat-quote { background: rgba(0,0,0,0.05); }
.app-root.light-theme .cx .chat-bubble.mine .chat-quote { background: rgba(110,60,0,0.12); }

@media (prefers-reduced-motion: reduce) {
  .cx-stamp .msg-ticks.read svg, .cx .chat-bubble::before { animation: none; }
}

/* =====================================================================
   Czat 3.4 — reakcje na wiadomości w stylu Messengera (pasek reakcji, odznaki na rogu dymka,
   lista "kto zareagował", serduszko po dwukrotnym dotknięciu). Wszystkie czaty (Szef, prywatne, grupa, panel admina).
   Dopisane NA KOŃCU arkusza; usunięcie bloku wyłącza wygląd (dane reakcji zostają w wiadomościach).
   ===================================================================== */
@keyframes cxBarIn { 0% { opacity: 0; transform: translateY(14px) scale(0.7); } 100% { opacity: 1; transform: none; } }
@keyframes cxEmoIn { 0% { opacity: 0; transform: translateY(18px) scale(0.3); } 100% { opacity: 1; transform: none; } }
@keyframes cxSheetIn { 0% { transform: translateY(100%); } 100% { transform: none; } }
@keyframes cxBurstMain { 0% { opacity: 0; transform: translate(-50%, -50%) scale(0.2); } 22% { opacity: 1; transform: translate(-50%, -54%) scale(1.5); } 55% { opacity: 1; transform: translate(-50%, -58%) scale(1.15); } 100% { opacity: 0; transform: translate(-50%, -110%) scale(1); } }
@keyframes cxBurstBit { 0% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); } 25% { opacity: 1; } 100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.7); } }
@keyframes cxToast { 0% { opacity: 0; transform: translate(-50%, 10px); } 12%, 85% { opacity: 1; transform: translate(-50%, 0); } 100% { opacity: 0; transform: translate(-50%, -6px); } }

/* --- wiadomość w układzie kolumnowym (odznaki reakcji pod dymkiem, jak w Messengerze) --- */
.cx .msg-with-reply { flex-direction: column; align-items: flex-start; }
.cx .msg-with-reply.mine { flex-direction: column; align-items: flex-end; }
.cx .msg-reply-btn { display: none; }
.cx .chat-bubble { touch-action: manipulation; }
@media (hover: none) {
  .cx .chat-bubble { -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; }
  .cx .chat-bubble a { -webkit-touch-callout: default; }
}
.cx .msg-reaction-add, .cx .msg-reaction-picker { display: none; }
.cx .msg-reactions:not(.has-reactions) { display: none; }

/* --- odznaki reakcji: zachodzą na dolny róg dymka --- */
.cx .msg-reactions.has-reactions { position: relative; z-index: 2; display: flex; gap: 4px; margin: -12px 10px 4px; padding: 0; width: fit-content; }
.cx .chat-bubble-row.mine .msg-reactions, .cx .msg-with-reply.mine > .msg-reactions, .cx .msg-with-delete.mine > .msg-reactions { align-self: flex-end; }
.cx .chat-bubble-row.theirs .msg-reactions, .cx .msg-with-reply.theirs > .msg-reactions, .cx .msg-with-delete.theirs > .msg-reactions { align-self: flex-start; }
.cx .msg-reaction-pill {
  display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px 3px 7px; border-radius: 999px; cursor: pointer;
  font: 700 12px/1.1 'IBM Plex Sans', sans-serif; color: var(--text-muted);
  background: var(--surface-2); border: 2px solid var(--bg); box-shadow: 0 6px 12px -6px rgba(0,0,0,0.75);
  transition: transform 0.15s ease;
}
.cx .msg-reaction-pill:active { transform: scale(0.92); }
.cx .msg-reaction-pill.mine { color: #2a1a06; background: linear-gradient(150deg, #ffd176, #f8a835); border-color: var(--bg); }
.app-root.light-theme .cx .msg-reaction-pill { background: #fff; border-color: #f5f3ee; box-shadow: 0 6px 12px -6px rgba(60,40,10,0.35); }
.app-root.light-theme .cx .msg-reaction-pill.mine { background: linear-gradient(150deg, #ffd176, #f8a835); }

/* --- pasek reakcji + menu --- */
.cx-react-overlay { position: absolute; inset: 0; z-index: 20; background: rgba(4,6,9,0.55); animation: cxIn 0.16s ease both; }
.cx-react-target { position: relative; z-index: 21; }
.cx-react-pop { position: absolute; z-index: 22; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.cx-react-pop.mine { align-items: flex-end; }
.cx-react-bar {
  display: flex; gap: 0; padding: 5px 6px; border-radius: 999px; transform-origin: 50% 100%;
  background: var(--surface-2); border: 1px solid var(--border); box-shadow: 0 22px 44px -14px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.08);
  animation: cxBarIn 0.3s cubic-bezier(.22,1.3,.36,1) both;
}
.cx-react-emo {
  width: 46px; height: 46px; padding: 0; border: none; border-radius: 50%; cursor: pointer; background: none; font-size: 28px; line-height: 1;
  animation: cxEmoIn 0.4s cubic-bezier(.34,1.7,.5,1) both; animation-delay: calc(var(--i) * 40ms + 60ms);
  transition: transform 0.14s cubic-bezier(.34,1.7,.5,1), background 0.15s ease;
}
.cx-react-emo:hover, .cx-react-emo:active { transform: scale(1.35) translateY(-7px); }
.cx-react-emo.sel { background: rgba(255,166,48,0.25); box-shadow: inset 0 0 0 2px rgba(255,166,48,0.7); }
.cx-react-actions { display: flex; gap: 8px; animation: cxBarIn 0.3s cubic-bezier(.22,1.3,.36,1) 0.08s both; }
.cx-react-actions button {
  padding: 9px 15px; border-radius: 999px; cursor: pointer; font: 700 13px 'IBM Plex Sans', sans-serif; color: var(--text);
  background: var(--surface-2); border: 1px solid var(--border); box-shadow: 0 12px 24px -10px rgba(0,0,0,0.85);
}
.cx-react-actions button:active { transform: scale(0.95); }
.app-root.light-theme .cx-react-overlay { background: rgba(30,20,5,0.4); }
.app-root.light-theme .cx-react-bar, .app-root.light-theme .cx-react-actions button { background: #fff; }

/* --- lista "kto zareagował" --- */
.cx-who-sheet {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 23; max-height: 62%; overflow-y: auto; padding: 8px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  border-radius: 26px 26px 0 0; background: var(--surface); border: 1px solid var(--border); border-bottom: none;
  box-shadow: 0 -24px 50px -16px rgba(0,0,0,0.85); animation: cxSheetIn 0.32s cubic-bezier(.22,1,.36,1) both;
}
.cx-who-grip { width: 42px; height: 4px; margin: 4px auto 12px; border-radius: 4px; background: var(--border); }
.cx-who-title { margin-bottom: 8px; font-size: 13px; font-weight: 800; letter-spacing: 0.6px; text-transform: uppercase; color: var(--text-dim); }
.cx-who-empty { padding: 18px 0; text-align: center; color: var(--text-dim); font-size: 13px; }
.cx-who-row { width: 100%; display: flex; align-items: center; gap: 14px; padding: 10px 6px; border: none; background: none; text-align: left; cursor: pointer; color: var(--text); border-bottom: 1px solid var(--border); }
.cx-who-row:last-child { border-bottom: none; }
.cx-who-emo { font-size: 26px; line-height: 1; }
.cx-who-name { flex: 1; font-size: 15px; font-weight: 700; }
.cx-who-row small { font-size: 11px; color: var(--text-dim); }
.app-root.light-theme .cx-who-sheet { background: #fff; }

/* --- serduszko po dwukrotnym dotknięciu --- */
.cx-burst { position: absolute; z-index: 30; width: 0; height: 0; pointer-events: none; }
.cx-burst-main { position: absolute; left: 0; top: 0; font-size: 72px; line-height: 1; filter: drop-shadow(0 8px 14px rgba(0,0,0,0.5)); animation: cxBurstMain 1s cubic-bezier(.2,.9,.3,1) both; }
.cx-burst i { position: absolute; left: 0; top: 0; font-style: normal; font-size: 20px; animation: cxBurstBit 0.9s ease-out both; }
.cx-burst i:nth-child(2) { --dx: -62px; --dy: -52px; }
.cx-burst i:nth-child(3) { --dx: 58px; --dy: -62px; animation-delay: 0.05s; }
.cx-burst i:nth-child(4) { --dx: -80px; --dy: 6px; animation-delay: 0.08s; }
.cx-burst i:nth-child(5) { --dx: 78px; --dy: 4px; animation-delay: 0.03s; }
.cx-burst i:nth-child(6) { --dx: -30px; --dy: -86px; animation-delay: 0.1s; }
.cx-burst i:nth-child(7) { --dx: 34px; --dy: -90px; animation-delay: 0.06s; }
.cx-toast {
  position: absolute; z-index: 40; left: 50%; top: calc(env(safe-area-inset-top, 0px) + 78px); padding: 8px 16px; border-radius: 999px; font-size: 12.5px; font-weight: 800; color: var(--text);
  background: var(--surface-2); border: 1px solid var(--border); box-shadow: 0 14px 28px -12px rgba(0,0,0,0.8); animation: cxToast 1.5s ease both; pointer-events: none;
}

/* --- czat na CAŁĄ wysokość ekranu (wysokość z klawiatury ustawia skrypt tylko podczas pisania) --- */
.cx { top: 0; bottom: 0; height: auto; }
@supports (height: 100dvh) { .cx { height: 100dvh; bottom: auto; } }

/* --- dodatki: pasek reakcji reaguje na palec, mocniejsze "wlatywanie" własnych wiadomości, skaczące emotki --- */
.cx-react-emo.hov { transform: scale(1.5) translateY(-10px); background: rgba(255,255,255,0.1); }
.cx-react-bar { touch-action: none; }
@keyframes cxFlyIn { 0% { opacity: 0; transform: translate(14px, 54px) scale(0.62); } 55% { opacity: 1; transform: translate(-2px, -3px) scale(1.04); } 100% { opacity: 1; transform: none; } }
@keyframes cxJumbo { 0% { transform: scale(0.2) rotate(-14deg); opacity: 0; } 55% { transform: scale(1.3) rotate(6deg); opacity: 1; } 100% { transform: none; } }
.cx .msg-with-reply.mine .chat-bubble, .cx .msg-with-delete.mine .chat-bubble, .cx .chat-bubble-row.mine .chat-bubble { animation-name: cxFlyIn; animation-duration: 0.55s; }
.cx .emoji-jumbo { animation: cxJumbo 0.6s cubic-bezier(.34,1.56,.64,1) both; transform-origin: 50% 80%; }
.cx .chat-messages:not([data-ready]) .emoji-jumbo { animation-duration: 0s !important; }

@media (prefers-reduced-motion: reduce) {
  .cx-react-bar, .cx-react-emo, .cx-react-actions, .cx-who-sheet, .cx-burst-main, .cx-burst i, .cx-toast { animation-duration: 0.01s !important; animation-delay: 0s !important; }
}

/* =====================================================================
   Obecność online — zielona kropka z falą na awatarach w rankingu, pigułka "N online" nad rankingiem
   i karta "Na żywo w aplikacji" na pulpicie admina. Dopisane NA KOŃCU arkusza; usunięcie bloku wyłącza wygląd.
   ===================================================================== */
@keyframes prPing { 0% { transform: scale(1); opacity: 0.75; } 80%, 100% { transform: scale(2.6); opacity: 0; } }
@keyframes prHalo { 0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.5), 0 10px 24px -8px var(--glow, rgba(52,211,153,0.5)); } 50% { box-shadow: 0 0 0 7px rgba(52,211,153,0), 0 10px 24px -8px var(--glow, rgba(52,211,153,0.5)); } }
@keyframes prPop { 0% { opacity: 0; transform: translateY(12px) scale(0.85); } 100% { opacity: 1; transform: none; } }
@keyframes prPillIn { 0% { opacity: 0; transform: translateY(-8px) scale(0.9); } 100% { opacity: 1; transform: none; } }
@keyframes prFace { 0% { opacity: 0; transform: translateX(-10px) scale(0.5); } 100% { opacity: 1; transform: none; } }
@keyframes prCount { 0% { transform: scale(1.5); color: #6ee7b7; } 100% { transform: none; } }
@keyframes prRipple { 0% { transform: scale(0.82); opacity: 0.65; } 100% { transform: scale(1.7); opacity: 0; } }
@keyframes prSheen { 0% { transform: translateX(-120%); } 60%, 100% { transform: translateX(260%); } }

/* kropka statusu na awatarze */
.pr-dot {
  position: absolute; right: -1px; top: -1px; z-index: 3; width: 13px; height: 13px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #a7f3d0, #10b981 70%); border: 2.5px solid var(--bg); box-sizing: border-box;
}
.pr-dot::after { content: ""; position: absolute; inset: -2.5px; border-radius: 50%; border: 2px solid #34d399; animation: prPing 2.2s ease-out infinite; }
.rk2-avatar-ring.is-online { animation: rk2Drop .85s cubic-bezier(.34,1.56,.64,1) both, prHalo 2.6s ease-in-out 1.2s infinite; animation-delay: var(--d), 1.2s; }
.rk2-place-1 .pr-dot { width: 16px; height: 16px; right: 1px; top: 3px; }
.rk2-row-avatar { position: relative; }
.rk2-row-avatar.is-online { box-shadow: inset 0 0 0 2px rgba(52,211,153,0.85), 0 0 14px -2px rgba(52,211,153,0.55); }
.rk2-row-avatar .pr-dot { width: 12px; height: 12px; right: -3px; top: -3px; }
.pr-chip { display: inline-flex; align-items: center; gap: 5px; font-weight: 800; font-size: 10px; letter-spacing: 0.3px; text-transform: uppercase; color: #34d399; }
.pr-chip i { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 8px #34d399; animation: prHalo 2s ease-in-out infinite; }

/* żywa kropka (nagłówek karty i pigułka) */
.pr-live { position: relative; flex-shrink: 0; width: 9px; height: 9px; border-radius: 50%; background: #34d399; box-shadow: 0 0 10px rgba(52,211,153,0.9); }
.pr-live::after { content: ""; position: absolute; inset: 0; border-radius: 50%; background: #34d399; animation: prPing 1.8s ease-out infinite; }
.pr-live-lg { width: 12px; height: 12px; }

/* pigułka nad rankingiem */
.pr-pill {
  position: relative; overflow: hidden; display: inline-flex; align-items: center; gap: 10px; margin-top: 12px; padding: 6px 14px 6px 12px; border-radius: 999px;
  background: linear-gradient(120deg, rgba(16,185,129,0.2), rgba(52,211,153,0.07)); border: 1px solid rgba(52,211,153,0.45);
  box-shadow: 0 10px 26px -12px rgba(16,185,129,0.75); animation: prPillIn 0.5s cubic-bezier(.22,1.3,.36,1) both;
}
.pr-pill::after { content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 40%; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.28), transparent); transform: translateX(-120%); animation: prSheen 4.2s ease-in-out 1s infinite; pointer-events: none; }
.pr-pill-faces { display: inline-flex; align-items: center; }
.pr-pill-face {
  position: relative; width: 24px; height: 24px; margin-left: -7px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 800; text-transform: uppercase; color: var(--text); background: var(--surface-3); border: 2px solid var(--bg); box-sizing: border-box;
  animation: prFace 0.45s cubic-bezier(.34,1.6,.5,1) both;
}
.pr-pill-face:first-child { margin-left: 0; }
.pr-pill-more { background: rgba(52,211,153,0.25); color: #34d399; }
.pr-pill-text { font-size: 12.5px; color: var(--text-muted); font-weight: 600; }
.pr-pill-text b { color: #34d399; font-family: 'Space Mono', monospace; font-size: 14px; margin-right: 2px; }

/* karta na pulpicie admina */
.pr-card {
  position: relative; overflow: hidden; margin-bottom: 20px; padding: 16px 16px 14px; border-radius: 20px;
  background: linear-gradient(160deg, var(--surface-2), var(--surface)); border: 1px solid rgba(52,211,153,0.28);
  box-shadow: 0 18px 40px -26px rgba(16,185,129,0.65);
}
.pr-card-glow { position: absolute; top: -70px; right: -50px; width: 230px; height: 230px; border-radius: 50%; background: radial-gradient(circle, rgba(52,211,153,0.2), transparent 68%); pointer-events: none; }
.pr-card-head { position: relative; display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.pr-card-title { flex: 1; min-width: 0; font-size: 15px; font-weight: 800; color: var(--text); }
.pr-card-title small { display: block; margin-top: 2px; font-size: 11.5px; font-weight: 500; color: var(--text-dim); }
.pr-card-count { font-family: 'Space Mono', monospace; font-size: 30px; font-weight: 700; line-height: 1; color: #34d399; animation: prCount 0.5s cubic-bezier(.34,1.56,.64,1) both; }
.pr-card-count span { margin-left: 3px; font-size: 13px; color: var(--text-dim); }
.pr-tiles { position: relative; display: grid; grid-template-columns: repeat(auto-fill, minmax(104px, 1fr)); gap: 10px; }
.pr-tile { display: flex; flex-direction: column; align-items: center; text-align: center; padding: 12px 6px 10px; border-radius: 16px; background: var(--surface-3); border: 1px solid var(--border); animation: prPop 0.5s cubic-bezier(.22,1.3,.36,1) both; min-width: 0; }
.pr-tile.on { background: linear-gradient(170deg, rgba(16,185,129,0.16), var(--surface-3)); border-color: rgba(52,211,153,0.4); }
.pr-tile-av { position: relative; width: 46px; height: 46px; margin-bottom: 8px; }
.pr-tile-face { position: relative; z-index: 1; width: 100%; height: 100%; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 15px; text-transform: uppercase; background: var(--surface-2); color: var(--text); box-shadow: inset 0 0 0 2px var(--border); }
.pr-tile.on .pr-tile-face { box-shadow: inset 0 0 0 2px rgba(52,211,153,0.9); }
.pr-tile.off .pr-tile-face { color: var(--text-dim); }
.pr-tile.off { background: transparent; }
.pr-tile.off .pr-tile-av { opacity: 0.6; filter: grayscale(0.6); }
.pr-ripple { position: absolute; inset: 0; border-radius: 50%; border: 2px solid #34d399; animation: prRipple 2.4s ease-out infinite; }
.pr-ripple-2 { animation-delay: 1.2s; }
.pr-tile-av .pr-dot { right: -2px; top: -2px; width: 14px; height: 14px; }
.pr-tile-name { max-width: 100%; font-size: 12.5px; font-weight: 700; line-height: 1.2; color: var(--text); overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; word-break: break-word; }
.pr-tile.off .pr-tile-name { color: var(--text-muted); font-weight: 600; }
.pr-tile-sub { margin-top: 2px; font-size: 10.5px; color: var(--text-dim); }
.pr-tile-sub.on { color: #34d399; font-weight: 800; text-transform: uppercase; letter-spacing: 0.3px; font-size: 9.5px; }
.pr-sep { position: relative; margin: 14px 0 10px; font-size: 10.5px; font-weight: 800; letter-spacing: 0.7px; text-transform: uppercase; color: var(--text-dim); }
.pr-tiles-off { margin-top: 0; }
.pr-more { position: relative; display: block; margin: 12px auto 0; padding: 7px 16px; border-radius: 999px; cursor: pointer; font: 700 12px 'IBM Plex Sans', sans-serif; color: var(--text-muted); background: var(--surface-3); border: 1px solid var(--border); }
.app-root.light-theme .pr-card { border-color: rgba(16,185,129,0.4); }
.app-root.light-theme .pr-pill-face { background: #fff; }

@media (prefers-reduced-motion: reduce) {
  .pr-dot::after, .pr-live::after, .pr-ripple, .pr-pill::after, .pr-chip i, .rk2-avatar-ring.is-online { animation: none !important; }
}

/* =====================================================================
   Załączniki 2.0 — album zdjęć, galeria na pełnym ekranie, miniatury z leniwym ładowaniem, tacka wysyłania.
   Dopisane NA KOŃCU arkusza; usunięcie bloku wyłącza wygląd.
   ===================================================================== */
@keyframes mxShimmer { 0% { background-position: 120% 0; } 100% { background-position: -120% 0; } }
@keyframes mxFade { 0% { opacity: 0; transform: scale(1.04); } 100% { opacity: 1; transform: none; } }
@keyframes utIn { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: none; } }
@keyframes utWait { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.7; } }
@keyframes gxIn { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes gxMedia { 0% { opacity: 0; transform: scale(0.94); } 100% { opacity: 1; transform: none; } }

/* kafelek ze zdjęciem / plakatem filmu */
.mx-tile {
  position: relative; display: block; padding: 0; margin: 0; border: none; overflow: hidden; cursor: zoom-in; color: #fff;
  background: linear-gradient(100deg, var(--surface-3) 30%, var(--surface-2) 50%, var(--surface-3) 70%); background-size: 240% 100%; animation: mxShimmer 1.4s linear infinite;
  -webkit-tap-highlight-color: transparent;
}
.mx-tile.ld { animation: none; background: var(--surface-3); }
.mx-tile img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block; opacity: 0; }
.mx-tile.ld img { animation: mxFade 0.35s ease both; opacity: 1; }
.mx-ph { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: var(--text-dim); }
.mx-tile:active { filter: brightness(0.9); }
.mx-play {
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 46px; height: 46px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: rgba(10,12,16,0.62); backdrop-filter: blur(6px); border: 1.5px solid rgba(255,255,255,0.55); box-shadow: 0 8px 20px -6px rgba(0,0,0,0.7);
}
.mx-play svg { margin-left: 2px; }
.mx-play em { position: absolute; top: calc(100% + 6px); left: 50%; transform: translateX(-50%); font: 700 10.5px 'Space Mono', monospace; font-style: normal; padding: 1px 6px; border-radius: 8px; background: rgba(10,12,16,0.65); white-space: nowrap; }
.mx-more { position: absolute; inset: 0; z-index: 2; display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: 800; background: rgba(8,10,14,0.58); backdrop-filter: blur(2px); }

/* pojedyncze zdjęcie / film o znanych wymiarach */
.mx-one { width: 240px; max-width: 100%; border-radius: 16px; margin-bottom: 6px; box-shadow: 0 12px 26px -14px rgba(0,0,0,0.75); border: 1px solid rgba(255,255,255,0.1); }
.mx-one.mx-video { position: relative; background: #000; overflow: hidden; width: 260px; max-width: 100%; }
.mx-one.mx-video video { position: absolute; inset: 0; width: 100%; height: 100%; max-width: none; max-height: none; object-fit: contain; margin: 0; border-radius: 0; box-shadow: none; }

/* album */
.mx-album { display: grid; gap: 2px; width: 264px; max-width: 100%; margin-bottom: 6px; border-radius: 16px; overflow: hidden; box-shadow: 0 12px 26px -14px rgba(0,0,0,0.75); border: 1px solid rgba(255,255,255,0.1); }
.mx-album .mx-tile { width: 100%; height: 100%; min-height: 0; }
.mx-n2 { grid-template-columns: 1fr 1fr; aspect-ratio: 2 / 1.15; }
.mx-n3 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; aspect-ratio: 1 / 1; }
.mx-n3 .mx-tile:first-child { grid-row: 1 / span 2; }
.mx-n4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; aspect-ratio: 1 / 1; }
.mx-n5 { grid-template-columns: repeat(6, 1fr); grid-template-rows: 1.15fr 1fr; aspect-ratio: 1 / 0.95; }
.mx-n5 .mx-tile:nth-child(-n+2) { grid-column: span 3; }
.mx-n5 .mx-tile:nth-child(n+3) { grid-column: span 2; }
.mx-n6 { grid-template-columns: repeat(3, 1fr); grid-template-rows: 1fr 1fr; aspect-ratio: 3 / 2; }
.mx-album .mx-play { width: 34px; height: 34px; }
.mx-album .mx-play em { display: none; }
.cx .chat-attachment-img { aspect-ratio: auto; background: var(--surface-3); }

/* galeria na pełnym ekranie */
.gx { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center; background: rgba(3,4,6,0.99); animation: gxIn 0.18s ease both; touch-action: pan-y; }
.gx-stage { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; padding: calc(env(safe-area-inset-top, 0px) + 56px) 0 calc(env(safe-area-inset-bottom, 0px) + 24px); box-sizing: border-box; }
.gx-media { max-width: 100%; max-height: 100%; object-fit: contain; background-position: center; background-size: contain; background-repeat: no-repeat; animation: gxMedia 0.25s cubic-bezier(.22,1,.36,1) both; -webkit-user-select: none; user-select: none; }
.gx-close, .gx-nav {
  position: absolute; z-index: 3; width: 42px; height: 42px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.22); cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #fff; background: rgba(30,32,38,0.7); backdrop-filter: blur(8px);
}
.gx-close { top: calc(env(safe-area-inset-top, 0px) + 12px); right: 14px; }
.gx-nav { top: 50%; margin-top: -21px; }
.gx-prev { left: 12px; }
.gx-next { right: 12px; }
.gx-count { position: absolute; z-index: 3; top: calc(env(safe-area-inset-top, 0px) + 20px); left: 50%; transform: translateX(-50%); padding: 3px 12px; border-radius: 999px; font: 700 12.5px 'Space Mono', monospace; color: #fff; background: rgba(30,32,38,0.7); }
@media (hover: none) { .gx-nav { display: none; } }

/* tacka wysyłania (nie blokuje pisania) */
.ut { margin: 0 12px 8px; padding: 10px 12px 10px; border-radius: 16px; background: linear-gradient(155deg, rgba(255,166,48,0.12), var(--surface-2)); border: 1px solid var(--amber-dim); animation: utIn 0.25s ease both; }
.cx .ut { flex: none; }
.ut.ok { border-color: rgba(52,211,153,0.5); background: linear-gradient(155deg, rgba(16,185,129,0.14), var(--surface-2)); }
.ut.bad { border-color: rgba(255,107,107,0.55); background: linear-gradient(155deg, rgba(255,107,107,0.12), var(--surface-2)); }
.ut-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
.ut-title { font-size: 12px; font-weight: 800; color: var(--amber); }
.ut.ok .ut-title { color: #34d399; }
.ut.bad .ut-title { color: var(--red); }
.ut-cancel { padding: 4px 11px; border-radius: 999px; cursor: pointer; font: 700 11.5px 'IBM Plex Sans', sans-serif; color: var(--text-muted); background: var(--surface-3); border: 1px solid var(--border); }
.ut-row { display: flex; gap: 7px; overflow-x: auto; padding-bottom: 2px; scrollbar-width: none; }
.ut-row::-webkit-scrollbar { display: none; }
.ut-item { position: relative; flex: none; width: 46px; height: 46px; border-radius: 12px; overflow: hidden; background: var(--surface-3); border: 1px solid var(--border); }
.ut-item img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ut-ph { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; color: var(--text-dim); }
.ut-over { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(8,10,14,0.5); color: #fff; }
.ut-wait { animation: utWait 1.1s ease-in-out infinite; }
.ut-done { background: rgba(16,185,129,0.55); }
.ut-err { background: rgba(220,60,60,0.6); }
.ut-ring { width: 32px; height: 32px; }
.ut-bar { height: 4px; margin-top: 9px; border-radius: 3px; background: var(--surface-3); overflow: hidden; }
.ut-bar i { display: block; height: 100%; border-radius: 3px; background: linear-gradient(90deg, var(--amber), #ffb84d); transition: width 0.25s ease; }
.ut-msg { margin-top: 7px; font-size: 11.5px; color: var(--text-muted); }

@media (prefers-reduced-motion: reduce) {
  .mx-tile, .mx-tile.ld img, .gx, .gx-media, .ut, .ut-wait { animation: none !important; }
  .mx-tile.ld img, .mx-tile img { opacity: 1; }
}

/* Głosówki: przycisk awaryjnego odtwarzania */
.voice-alt-btn { display: block; margin: 6px 2px 0; padding: 0; border: none; background: none; cursor: pointer; font: 600 11.5px 'IBM Plex Sans', sans-serif; color: var(--text-dim); text-decoration: underline; text-underline-offset: 2px; text-align: left; }
.voice-alt-btn:active { color: var(--amber); }

/* "Zawołaj wszystkich": komunikat u kierowcy i okno u admina */
@keyframes caPulse { 0% { transform: scale(0.6); opacity: 0.8; } 100% { transform: scale(2.4); opacity: 0; } }
@keyframes caShake { 0%, 100% { transform: rotate(0); } 15% { transform: rotate(-14deg); } 30% { transform: rotate(12deg); } 45% { transform: rotate(-10deg); } 60% { transform: rotate(8deg); } 75% { transform: rotate(-4deg); } }
@keyframes caIn { 0% { opacity: 0; transform: translateY(24px) scale(0.92); } 100% { opacity: 1; transform: none; } }
.ca-overlay { position: fixed; inset: 0; z-index: 3000; display: flex; align-items: center; justify-content: center; padding: 22px; background: rgba(4,6,9,0.82); backdrop-filter: blur(6px); animation: gxIn 0.2s ease both; }
.ca-card { position: relative; overflow: hidden; width: min(360px, 100%); padding: 30px 22px 22px; border-radius: 28px; text-align: center; background: linear-gradient(165deg, #2a1a08, var(--surface)); border: 1px solid rgba(255,166,48,0.55); box-shadow: 0 30px 70px -20px rgba(255,140,30,0.55); animation: caIn 0.4s cubic-bezier(.22,1.3,.36,1) both; }
.ca-rings i { position: absolute; left: 50%; top: 74px; width: 70px; height: 70px; margin: -35px 0 0 -35px; border-radius: 50%; border: 2px solid rgba(255,166,48,0.7); animation: caPulse 2.2s ease-out infinite; }
.ca-rings i:nth-child(2) { animation-delay: 0.7s; }
.ca-rings i:nth-child(3) { animation-delay: 1.4s; }
.ca-icon { position: relative; font-size: 64px; line-height: 1; display: inline-block; animation: caShake 1.1s ease-in-out 0.3s infinite; transform-origin: 70% 80%; }
.ca-title { position: relative; margin-top: 16px; font-size: 22px; font-weight: 800; color: var(--amber); }
.ca-text { position: relative; margin: 10px 0 20px; font-size: 16px; line-height: 1.4; color: var(--text); white-space: pre-wrap; word-break: break-word; }
.ca-go { position: relative; display: block; width: 100%; padding: 14px; border: none; border-radius: 16px; cursor: pointer; font: 800 16px 'IBM Plex Sans', sans-serif; color: #1a1206; background: linear-gradient(135deg, #ffd176, #ff9d2e); box-shadow: 0 14px 30px -12px rgba(255,140,30,0.8); }
.ca-go:active { transform: scale(0.97); }
.ca-close { position: relative; display: block; margin: 12px auto 0; padding: 6px 14px; border: none; background: none; cursor: pointer; font: 600 13px 'IBM Plex Sans', sans-serif; color: var(--text-dim); }
.callall-btn { white-space: nowrap; }
@media (max-width: 520px) { .cx-hide-sm { display: none; } }
.callall-result { margin-top: 10px; font-size: 13px; font-weight: 700; color: #34d399; }
.callall-result.err { color: var(--red); }
@media (prefers-reduced-motion: reduce) { .ca-rings i, .ca-icon { animation: none !important; } }

.debt-card-paid::before { background: linear-gradient(180deg, #34d399, transparent 130%) !important; box-shadow: 0 0 12px #34d399 !important; }
.debt-card-paid { background: linear-gradient(160deg, rgba(52,211,153,0.1), var(--surface) 62%) !important; border-color: rgba(52,211,153,0.35) !important; }

/* ===== Kamienie milowe: poziomy za zaliczone zadania ===== */

/* zadanie-kamień milowy u kierowcy */
.ms-chip { display: inline-flex; align-items: center; gap: 5px; margin-top: 6px; padding: 3px 10px 3px 7px; border-radius: 999px; font: 800 10.5px 'IBM Plex Sans', sans-serif; letter-spacing: 0.3px; color: #1a1206; background: linear-gradient(120deg, #ffe08a, #ff9a2e); box-shadow: 0 0 14px -3px #ff9a2e; }
.ms-chip.done { color: #052e1a; background: linear-gradient(120deg, #a7f3d0, #34d399); box-shadow: 0 0 14px -3px #34d399; }
.ms-chip-ic { font-size: 12px; }
.ms-chip { max-width: 100%; white-space: nowrap; }
.task-title .ms-chip { margin: 0 0 0 6px; vertical-align: middle; }

/* tablica w Osiągnięciach */

/* nowy poziom (pełny ekran, kilka sekund) */
@media (prefers-reduced-motion: reduce) { .tk2-ms, .tk2-ms .tk2-sheen, .ms-ladder-line i, .ms-step, .ms-hero-disc, .ms-ov-rays, .ms-cf, .ms-ov-medal { animation: none !important; } .ms-ov-rays { opacity: 0.3; } }


/* Głosówka w trakcie wysyłania: dymek w rozmowie + informacja po wysłaniu */
@keyframes vpbIn { 0% { opacity: 0; transform: translateY(10px) scale(0.96); } 100% { opacity: 1; transform: none; } }
.vpb-row { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; margin: 6px 0; padding: 0 4px; animation: vpbIn 0.3s ease both; }
.vpb { display: inline-flex; align-items: center; gap: 9px; padding: 10px 14px; border-radius: 20px 20px 6px 20px; background: linear-gradient(150deg, #ffd176, #f8a835); color: #2a1a06; box-shadow: 0 8px 18px -10px rgba(0,0,0,0.6); }
.vpb.wait { opacity: 0.85; }
.vpb-mic { font-size: 16px; }
.vpb-bars { display: inline-flex; align-items: center; gap: 2px; height: 22px; }
.vpb-bars i { display: block; width: 3px; border-radius: 2px; background: rgba(42,26,6,0.55); }
.vpb-time { font: 700 12px 'Space Mono', monospace; }
.vpb-state { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 6px; font-size: 11px; color: var(--text-dim); }
.vpb-state button { padding: 3px 9px; border: 1px solid var(--amber-dim); border-radius: 999px; cursor: pointer; font: 700 11px 'IBM Plex Sans', sans-serif; color: var(--amber); background: none; }
.vpb-state button.ghost { color: var(--text-muted); border-color: var(--border); }
.cx-voice-banner.notice { background: rgba(255,166,48,0.12); border: 1px solid var(--amber-dim); color: var(--text); font-size: 12px; }
/* ===== Tickety: zaproszenie w Osiągnięciach (samo koło ma własne style w doładowywanym pliku) ===== */
.task-priority-toggle.tk-on { border-color: rgba(255,166,48,0.7); background: rgba(255,166,48,0.15); color: var(--amber); }
/* ===== Złote bilety (zadania z ticketem), pasek ticketów, baner koła, zakładka Fortuna ===== */
.gt { position: relative; display: flex; align-items: stretch; margin-bottom: 10px; border-radius: 16px; overflow: hidden; color: var(--text); background: linear-gradient(135deg, rgba(255,196,70,0.22), rgba(255,140,30,0.05) 58%), var(--surface); border: 1.5px solid rgba(255,190,70,0.6); box-shadow: 0 18px 34px -20px rgba(255,150,30,0.9), inset 0 1px 0 rgba(255,255,255,0.1); transform-origin: 50% 100%; animation: gtIn 0.75s cubic-bezier(.2,.9,.25,1.08) both; -webkit-tap-highlight-color: transparent; }
@keyframes gtIn { from { opacity: 0; transform: perspective(700px) rotateX(46deg) translateY(28px) scale(0.94); } to { opacity: 1; transform: none; } }
.gt:active { transform: scale(0.985); }
.gt-body { position: relative; flex: 1; min-width: 0; padding: 9px 9px 9px 14px; background-image: repeating-linear-gradient(135deg, rgba(255,220,140,0.055) 0 2px, transparent 2px 10px); }
.gt-body::before { content: ""; position: absolute; left: 0; top: 12px; bottom: 12px; width: 4px; border-radius: 0 4px 4px 0; background: linear-gradient(180deg, #fff1b8, #ff9a2e); box-shadow: 0 0 14px rgba(255,154,46,0.9); }
.gt-eyebrow { font-size: 8.5px; font-weight: 800; letter-spacing: 1.6px; text-transform: uppercase; color: #ffcf6b; }
.gt-eyebrow em { font-style: normal; color: #ff8a80; }
.gt-star { display: inline-block; animation: gtTw 2.4s ease-in-out 3 both; }
.gt-title { margin-top: 2px; font-size: 14.5px; font-weight: 800; line-height: 1.25; word-break: break-word; }
.gt-dates { margin-top: 2px; font-size: 10px; color: var(--text-dim); font-family: 'Space Mono', monospace; }
.gt-time { height: 4px; margin: 7px 0 0; border-radius: 3px; background: rgba(255,255,255,0.08); overflow: hidden; }
.gt-time i { display: block; height: 100%; border-radius: 3px; background: linear-gradient(90deg, #ff9a2e, #ffe08a); transform-origin: left; animation: rk2Grow 1.2s cubic-bezier(.22,1,.36,1) both 0.3s; }
.gt-time.late i { background: linear-gradient(90deg, #ff9a6b, #ff5e5e); }
.gt-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 5px; margin-top: 7px; }
.gt-pill { font-size: 10.5px; font-weight: 700; padding: 4px 8px; border-radius: 8px; background: rgba(255,255,255,0.07); color: var(--text-muted); }
.gt-pill.done { background: rgba(110,231,183,0.16); color: var(--green); }
.gt-pill.missed { background: rgba(255,107,107,0.14); color: var(--red); }
.gt-chip { font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 999px; background: rgba(255,166,48,0.16); color: var(--amber); }
.gt-chip.urgent { background: rgba(255,107,107,0.2); color: #ff8a80; }
.gt-pts { margin-left: auto; font: 800 10.5px 'Space Mono', monospace; padding: 3px 8px; border-radius: 999px; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); }
.gt-pts.done { background: linear-gradient(150deg, #a7f3d0, #34d399); }
.gt-pts.missed { background: var(--surface-3); color: var(--text-dim); }
.gt-perf { position: relative; flex: none; width: 0; margin: 8px 0; border-left: 2px dashed rgba(255,200,100,0.6); }
.gt-perf b { position: absolute; left: -9px; width: 16px; height: 16px; border-radius: 50%; background: var(--bg); border: 1.5px solid rgba(255,190,70,0.6); }
.gt-perf b.t { top: -16px; }
.gt-perf b.b { bottom: -16px; }
.gt-stub { position: relative; flex: none; width: 66px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; padding: 8px 4px; color: #2a1703; text-align: center; background: linear-gradient(160deg, #fff0b8 0%, #ffc247 38%, #ff9a1f 72%, #ffd36b 100%); box-shadow: inset 0 0 0 1px rgba(255,255,255,0.35), inset 6px 0 14px -8px rgba(120,60,0,0.45); }
.gt-stub-top { font-size: 7.5px; font-weight: 900; letter-spacing: 2px; text-indent: 2px; opacity: 0.75; }
.gt-stub-n { font: 800 25px 'Space Mono', monospace; line-height: 1.05; text-shadow: 0 1px 0 rgba(255,255,255,0.7), 0 -1px 0 rgba(120,60,0,0.25); animation: gtPop 0.65s cubic-bezier(.3,1.7,.4,1) 0.5s both; }
.gt-stub-sub { display: none; font-size: 9.5px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; opacity: 0.75; }
.gt-mini { position: relative; width: 24px; height: 15px; margin: 3px 0 0; border-radius: 4px; background: radial-gradient(circle at 0 50%, transparent 4px, #2a1703 4.6px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 50%, transparent 4px, #2a1703 4.6px) right / 51% 100% no-repeat; }
.gt-mini::after { content: ""; position: absolute; left: 50%; top: 3px; bottom: 3px; border-left: 1.5px dashed rgba(255,214,120,0.9); }
.gt-waiting .gt-mini { animation: gtWig 0.9s ease-in-out 1.7s 2; }
.gt-done .gt-stub { background: linear-gradient(160deg, #d6f9e8 0%, #6ee7b7 42%, #2fbf86 78%, #8fe9c5 100%); }
.gt-missed .gt-stub { background: linear-gradient(160deg, #d5d9de, #98a0ab); filter: saturate(0.4); }
.gt-missed { border-color: rgba(255,255,255,0.14); box-shadow: none; }
.gt-urgent { border-color: rgba(255,120,100,0.75); }
.gt-stamp { position: absolute; left: 50%; bottom: 9px; margin: 0 0 0 -29px; width: 58px; padding: 2px 0; border: 2px solid #0b6a42; border-radius: 6px; color: #0b6a42; background: rgba(255,255,255,0.45); font: 900 8.5px 'IBM Plex Sans', sans-serif; letter-spacing: 1px; text-align: center; transform: rotate(-16deg); animation: gtStamp 0.55s cubic-bezier(.3,1.4,.4,1) 0.7s both; }
.gt-done .gt-mini, .gt-done .gt-stub-sub, .gt-missed .gt-mini, .gt-missed .gt-stub-sub { visibility: hidden; }
.gt-stamp { bottom: 11px; }
.gt-stamp.miss { border-color: #a02a2a; color: #a02a2a; }
.gt-sheen { position: absolute; top: 0; bottom: 0; left: 0; width: 34%; pointer-events: none; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.24), transparent); transform: translateX(-130%) skewX(-20deg); animation: gtSheen 4.2s ease-out 0.6s 2 both; }
.gt-sp { position: absolute; font-style: normal; font-size: 12px; color: #ffe08a; opacity: 0; pointer-events: none; animation: gtTw 2.4s ease-in-out 3 both; }
.gt-sp1 { right: 76px; top: 5px; animation-delay: 0.8s; }
.gt-sp2 { left: 10px; bottom: 9px; animation-delay: 1.4s; font-size: 10px; }
.gt-sp3 { right: 78px; bottom: 10px; animation-delay: 2s; }
@keyframes gtTw { 0%, 100% { opacity: 0; transform: scale(0.3) rotate(0deg); } 50% { opacity: 1; transform: scale(1.15) rotate(25deg); } }
@keyframes gtPop { from { opacity: 0; transform: scale(0.2) rotate(-14deg); } to { opacity: 1; transform: none; } }
@keyframes gtStamp { from { opacity: 0; transform: scale(2.7) rotate(-42deg); } to { opacity: 0.96; transform: scale(1) rotate(-16deg); } }
@keyframes gtSheen { 0% { transform: translateX(-130%) skewX(-20deg); } 30%, 100% { transform: translateX(430%) skewX(-20deg); } }
@keyframes gtWig { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-14deg); } 75% { transform: rotate(14deg); } }
.app-root.light-theme .gt { background: linear-gradient(135deg, rgba(255,190,60,0.3), #fffaf0 62%); border-color: rgba(217,130,15,0.6); }
.app-root.light-theme .gt-eyebrow { color: #b56d0c; }
.app-root.light-theme .gt-pill { background: rgba(0,0,0,0.06); }
.app-root.light-theme .gt-time { background: rgba(0,0,0,0.08); }
.gt-mini-tag { display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 6px; font-size: 10px; font-weight: 800; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); vertical-align: middle; }
.gts { position: relative; overflow: hidden; display: flex; align-items: center; gap: 12px; width: 100%; margin: 4px 0 14px; padding: 12px 14px; border-radius: 16px; cursor: pointer; text-align: left; color: var(--text); font-family: inherit; background: linear-gradient(120deg, rgba(255,190,60,0.26), rgba(255,120,30,0.06)), var(--surface); border: 1px solid rgba(255,190,70,0.55); box-shadow: 0 12px 26px -16px rgba(255,150,30,0.9); -webkit-tap-highlight-color: transparent; animation: gtIn 0.6s cubic-bezier(.2,.9,.25,1.08) both; }
.gts:active { transform: scale(0.985); }
.gts::after { content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 30%; pointer-events: none; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.22), transparent); transform: translateX(-130%) skewX(-20deg); animation: gtSheen 4.2s ease-out 0.9s 2 both; }
.gts-ic { flex: none; width: 42px; height: 42px; border-radius: 12px; display: grid; place-items: center; font-size: 21px; background: linear-gradient(150deg, #ffd166, #ff9a1f); box-shadow: 0 8px 16px -8px rgba(255,140,30,1); animation: gtWig 0.9s ease-in-out 1.2s 2; }
.gts-tx { min-width: 0; flex: 1; }
.gts-tx b { display: block; font-size: 14.5px; font-weight: 800; }
.gts-tx small { display: block; margin-top: 2px; font-size: 11.5px; color: var(--text-muted); line-height: 1.3; }
.gts-go { flex: none; font-size: 12px; font-weight: 800; color: #2a1703; padding: 7px 11px; border-radius: 999px; background: linear-gradient(150deg, #ffd166, #ff9a1f); }
.tkp2 { position: relative; overflow: hidden; display: flex; align-items: center; gap: 14px; width: 100%; margin: 0 0 14px; padding: 14px; border-radius: 20px; cursor: pointer; text-align: left; color: var(--text); font-family: inherit; background: radial-gradient(120% 140% at 0% 0%, rgba(255,190,60,0.28), transparent 60%), linear-gradient(160deg, var(--surface-2), var(--surface)); border: 1.5px solid rgba(255,190,70,0.6); box-shadow: 0 18px 34px -20px rgba(255,150,30,0.95); -webkit-tap-highlight-color: transparent; animation: gtIn 0.7s cubic-bezier(.2,.9,.25,1.08) both; }
.tkp2:active { transform: scale(0.985); }
.tkp2::after { content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 30%; pointer-events: none; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.22), transparent); transform: translateX(-130%) skewX(-20deg); animation: gtSheen 4.2s ease-out 1s 2 both; }
.tkp2-wheel { position: relative; flex: none; width: 58px; height: 58px; border-radius: 50%; background: radial-gradient(circle, #fff3c4 0 9px, transparent 10px), conic-gradient(#ffa630 0 45deg, #6ee7b7 45deg 90deg, #60a5fa 90deg 135deg, #f472b6 135deg 180deg, #a78bfa 180deg 225deg, #fb7185 225deg 270deg, #facc15 270deg 315deg, #34d399 315deg 360deg); box-shadow: 0 0 0 4px #ffcf6b, 0 10px 18px -6px rgba(0,0,0,0.65); animation: tkpSpin 2s cubic-bezier(.12,.75,.2,1) 0.2s both; }
.tkp2-wheel::before { content: ""; position: absolute; left: 50%; top: -9px; margin-left: -6px; border: 6px solid transparent; border-top: 12px solid #ff4d5a; filter: drop-shadow(0 2px 2px rgba(0,0,0,0.5)); }
@keyframes tkpSpin { from { transform: rotate(-720deg); } to { transform: none; } }
.tkp2-tx { min-width: 0; flex: 1; }
.tkp2-tx em { display: block; font-style: normal; font-size: 10px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: #ffcf6b; }
.tkp2-tx b { display: block; margin-top: 2px; font-size: 18px; font-weight: 800; }
.tkp2-tx small { display: block; margin-top: 3px; font-size: 11.5px; color: var(--text-muted); line-height: 1.35; }
.tkp2-go { flex: none; font-size: 12px; font-weight: 800; color: #2a1703; padding: 8px 12px; border-radius: 999px; background: linear-gradient(150deg, #ffd166, #ff9a1f); box-shadow: 0 8px 16px -8px rgba(255,140,30,1); }
.app-root.light-theme .tkp2-tx em { color: #b56d0c; }
.tab-btn.tab-fz:not(.active) { color: var(--amber); border-color: rgba(255,190,70,0.65); background: linear-gradient(160deg, rgba(255,190,60,0.2), var(--surface)); }
@media (prefers-reduced-motion: reduce) { .gt, .gts, .tkp2, .gt-sheen, .gt-sp, .gt-star, .gt-stub-n, .gt-stamp, .gt-mini, .gts-ic, .gts::after, .tkp2::after, .tkp2-wheel { animation: none !important; } .gt-stamp { opacity: 0.96; } }
/* ===== v38: pomoc z powiadomieniami, oś czasu wypłaty, koło spłaty, kupony bonusów, ranking, puste stany ===== */
@keyframes ptIn { from { opacity: 0; transform: translateY(14px) scale(0.98); } }
@keyframes ptPop { from { transform: scale(0.3) rotate(-30deg); } }
.bonus-card.bn-coupon { overflow: hidden; padding: 0 0 0 20px; min-height: 78px; gap: 12px; }
.bonus-card.bn-coupon .bonus-info { padding: 14px 0; }
.bn-perf { position: relative; flex: none; align-self: stretch; width: 0; margin: 10px 0; border-left: 2px dashed rgba(255,200,100,0.55); }
.bn-perf b { position: absolute; left: -9px; width: 16px; height: 16px; border-radius: 50%; background: var(--bg); border: 1px solid rgba(255,190,70,0.5); }
.bn-perf b.t { top: -18px; }
.bn-perf b.b { bottom: -18px; }
.bonus-card.bn-coupon .bonus-amount { position: relative; overflow: hidden; align-self: stretch; flex: none; width: 104px; display: flex; align-items: center; justify-content: center; text-align: center; padding: 0 6px; font: 800 13.5px 'Space Mono', monospace; color: #2a1703; text-shadow: 0 1px 0 rgba(255,255,255,0.5); text-decoration: none; background: linear-gradient(160deg, #fff0b8 0%, #ffc247 45%, #ff9a1f 80%, #ffd36b 100%); }
.bonus-card.bn-coupon .bonus-amount::after { content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 40%; pointer-events: none; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.5), transparent); transform: translateX(-130%) skewX(-20deg); animation: gtSheen 4.2s ease-out 0.9s 2 both; }
.bonus-card-completed.bn-coupon .bonus-amount { background: linear-gradient(160deg, #d6f9e8 0%, #6ee7b7 42%, #2fbf86 78%, #8fe9c5 100%); color: #05361f; }
.bonus-card-completed.bn-coupon .bn-perf { border-left-color: rgba(110,231,183,0.6); }
.bonus-card-expired.bn-coupon .bonus-amount { background: linear-gradient(160deg, #d5d9de, #98a0ab); color: #3a4048; text-shadow: none; }
.bonus-card-expired.bn-coupon .bn-perf { border-left-color: rgba(255,255,255,0.18); }
.bonus-card-upcoming.bn-coupon .bonus-amount { background: linear-gradient(160deg, rgba(255,214,120,0.55), rgba(255,154,31,0.55)); }
.bn-stamp { position: absolute; left: 50%; bottom: 7px; margin-left: -29px; width: 58px; padding: 2px 0; border: 2px solid #0b6a42; border-radius: 5px; color: #0b6a42; background: rgba(255,255,255,0.5); font: 900 8px 'IBM Plex Sans', sans-serif; letter-spacing: 1px; text-align: center; text-shadow: none; transform: rotate(-14deg); animation: gtStamp 0.55s cubic-bezier(.3,1.4,.4,1) 0.5s both; }
.rk2-mv { display: inline-flex; align-items: center; margin-left: 6px; padding: 1px 6px; border-radius: 999px; font: 800 10px 'Space Mono', monospace; vertical-align: middle; }
.rk2-mv.up { color: #052e1a; background: linear-gradient(120deg, #a7f3d0, #34d399); animation: ptPop 0.6s cubic-bezier(.3,1.7,.4,1) 0.4s both; }
.rk2-mv.down { color: #fff; background: linear-gradient(120deg, #ff8a80, #e5484d); }
.rk2-mv.eq { color: var(--text-dim); background: var(--surface-3); }
.rk2-wk { display: inline-block; margin-top: 3px; padding: 2px 7px; border-radius: 999px; font-size: 9px; font-weight: 900; letter-spacing: 0.6px; text-transform: uppercase; color: #2a1703; background: linear-gradient(120deg, #ffd166, #ff9a1f); }
.rk2-slot-mv { margin-top: 2px; line-height: 1.2; }
.ec { position: relative; overflow: hidden; display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; padding: 30px 18px 24px; border-radius: 22px; border: 1px dashed var(--border); background: radial-gradient(120% 90% at 50% 0%, rgba(255,190,60,0.1), transparent 60%), var(--surface); animation: ptIn 0.6s cubic-bezier(.2,.9,.25,1) both; }
.ec-ic { font-size: 42px; line-height: 1; animation: ecPop 0.8s cubic-bezier(.3,1.7,.4,1) 0.15s both; }
@keyframes ecPop { from { transform: scale(0) rotate(-25deg); opacity: 0; } }
.ec b { font-size: 15px; }
.ec span { max-width: 290px; font-size: 12.5px; color: var(--text-muted); line-height: 1.45; }
.ec button { margin-top: 10px; border: 0; border-radius: 12px; padding: 10px 16px; font: 800 13px 'IBM Plex Sans', sans-serif; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); cursor: pointer; -webkit-tap-highlight-color: transparent; }
.app-root.light-theme .pt-step small { color: var(--text-muted); }
@media (prefers-reduced-motion: reduce) { .pt, .pt-line i, .pt-step.cur .pt-dot, .pt-step.cur .pt-dot::after, .dr-ring .fg, .bn-stamp, .bonus-card.bn-coupon .bonus-amount::after, .rk2-mv, .rk2-starbar, .rk2-starbar::after, .rk2-starbar-ic, .ec, .ec-ic { animation: none !important; transition: none !important; } .pt-line.on i { transform: scaleX(1); } }
/* ===== v41: czytelne rozliczenia (tk3), pasek utargu (wp) ===== */
.tk3 { position: relative; overflow: hidden; padding: 14px 16px 14px 20px; border-radius: 20px; background: linear-gradient(160deg, var(--surface-2), var(--surface)); border: 1px solid var(--border); --acc: var(--amber); box-shadow: 0 14px 26px -22px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.04); animation: pn2In 0.45s cubic-bezier(0.22,1,0.36,1) both; }
.tk3[data-tone="green"] { --acc: var(--green); }
.tk3[data-tone="red"] { --acc: var(--red); }
.tk3::before { content: ""; position: absolute; left: 0; top: 14px; bottom: 14px; width: 4px; border-radius: 0 4px 4px 0; background: var(--acc); box-shadow: 0 0 12px var(--acc); }
.tk3-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.tk3-period { font-size: 14px; font-weight: 800; letter-spacing: -0.1px; }
.tk3-pay { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin: 11px 0 12px; padding: 10px 13px; border-radius: 14px; background: rgba(255,255,255,0.04); border: 1px solid var(--border); }
.tk3-pay small { font-size: 11px; font-weight: 800; letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-muted); }
.tk3-pay b { font: 800 22px 'Space Mono', monospace; color: var(--acc); text-shadow: 0 0 18px rgba(255,166,48,0.18); }
.tk3-pay.neg b { color: var(--red); }
.tk3-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px 14px; }
.tk3-c small { display: block; font-size: 11px; color: var(--text-muted); margin-bottom: 1px; }
.tk3-c b { font: 700 14px 'Space Mono', monospace; color: var(--text); }
.tk3-c b.neg { color: var(--red); }
.tk3-note { display: flex; align-items: flex-start; gap: 7px; margin-top: 11px; padding: 8px 10px; border-radius: 12px; border-left: 2px solid var(--acc); background: var(--surface-3); font-size: 12px; font-style: italic; color: var(--text-muted); line-height: 1.4; }
.tk3-note svg { flex: none; margin-top: 2px; opacity: 0.6; }
.app-root.light-theme .tk3-pay { background: rgba(0,0,0,0.04); }
@media (prefers-reduced-motion: reduce) { .tk3, .wp, .wp-bar i { animation: none !important; } }
/* ===== v42: oś etapów rozliczenia (tl) ===== */
.tl { position: relative; overflow: hidden; margin: 12px 0 12px; padding: 18px 12px 14px; border-radius: 22px; background: linear-gradient(165deg, var(--surface-2), var(--surface)); border: 1px solid var(--border); box-shadow: 0 16px 30px -24px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.05); animation: ptIn 0.6s cubic-bezier(.2,.9,.25,1) both; }
.tl-glow { position: absolute; left: 50%; top: -40px; width: 260px; height: 140px; margin-left: -130px; pointer-events: none; background: radial-gradient(closest-side, rgba(255,190,60,0.2), transparent); }
.tl-s2 .tl-glow { background: radial-gradient(closest-side, rgba(110,231,183,0.22), transparent); }
.tl-debt .tl-glow { background: radial-gradient(closest-side, rgba(255,107,107,0.22), transparent); }
.tl-track { position: relative; height: 84px; margin: 0 6px; }
.tl-line { position: absolute; left: 30px; right: 30px; top: 21px; height: 6px; margin-top: -3px; border-radius: 4px; background: var(--surface-3); box-shadow: inset 0 1px 2px rgba(0,0,0,0.4); }
.tl-line i { position: absolute; top: 0; right: 0; bottom: 0; left: 0; border-radius: 4px; background: linear-gradient(90deg, #ff9a2e, #ffe08a, #fff3c4); transform-origin: left; transform: scaleX(0); }
.tl.on .tl-line i { transform: scaleX(var(--p)); transition: transform 1.5s cubic-bezier(.2,.8,.2,1) 0.2s; }
.tl-s2 .tl-line i { background: linear-gradient(90deg, #ff9a2e, #ffe08a 55%, #6ee7b7); }
.tl-debt .tl-line i { background: linear-gradient(90deg, #ff9a2e, #ff6b6b); }
.tl-car { position: absolute; top: -19px; left: 0; margin-left: -11px; line-height: 0; color: #ffd166; opacity: 0; will-change: transform; transition: transform 1.5s cubic-bezier(.2,.8,.2,1) 0.2s, opacity 0.3s ease 0.2s; }
.tl.on .tl-car { opacity: 1; }
.tl-s1.on .tl-car, .tl-s2.on .tl-car { animation: tlArrive 2.1s ease 0.2s forwards; }
@keyframes tlArrive { 0%, 78% { opacity: 1; } 100% { opacity: 0; } }
.tl-st { position: absolute; top: 0; width: 84px; margin-left: -42px; text-align: center; left: calc(30px + (100% - 60px) * var(--k) / 2); }
.tl-node { position: relative; display: grid; place-items: center; width: 44px; height: 44px; margin: 0 auto 7px; border-radius: 50%; font-size: 20px; background-color: var(--surface-3); border: 2px solid var(--border); }
.tl-node b { opacity: 0.55; }
.tl-st.done .tl-node b, .tl-st.cur .tl-node b { opacity: 1; }
.tl-node b { font-weight: 800; font-size: 20px; line-height: 1; }
.tl-st small { display: block; font-size: 11px; font-weight: 700; line-height: 1.25; color: var(--text-dim); }
.tl-st.done .tl-node { opacity: 1; border-color: #ffd166; color: #2a1703; background: radial-gradient(circle at 32% 26%, #fff3c4, #ffb02e); box-shadow: 0 0 16px -2px rgba(255,176,46,0.7); }
.tl-st.done .tl-node b { font-size: 19px; }
.tl-st.cur .tl-node { opacity: 1; border-color: var(--amber); background-color: var(--surface-3); background-image: linear-gradient(rgba(255,166,48,0.16), rgba(255,166,48,0.16)); box-shadow: 0 0 20px -2px rgba(255,166,48,0.65); animation: tlPop 0.7s cubic-bezier(.3,1.7,.4,1) 0.5s both; }
.tl-st.cur .tl-node::after { content: ""; position: absolute; top: -3px; right: -3px; bottom: -3px; left: -3px; border-radius: 50%; border: 2px solid var(--amber); opacity: 0; animation: tlRing 1.8s ease-out 0.9s 3; }
.tl-st.done small, .tl-st.cur small { color: var(--text); }
.tl-s2 .tl-st.cur .tl-node { border-color: #ffd166; color: #2a1703; background: radial-gradient(circle at 32% 26%, #fff3c4, #ffb02e); box-shadow: 0 0 24px 0 rgba(255,176,46,0.8); }
.tl-s2 .tl-st.cur .tl-node::after { border-color: #ffd166; }
.tl-debt.tl-s2 .tl-st.cur .tl-node { border-color: var(--red); color: #fff; background: var(--red); box-shadow: 0 0 22px 0 rgba(255,107,107,0.7); }
@keyframes tlPop { from { transform: scale(0.3) rotate(-35deg); } }
@keyframes tlRing { 0% { opacity: 0.9; transform: scale(1); } 100% { opacity: 0; transform: scale(1.8); } }
.tl-days { display: flex; justify-content: center; gap: 6px; margin: 4px 0 2px; }
.tl-days span { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 10px; font-size: 12px; font-weight: 800; color: var(--text-dim); background: var(--surface-3); border: 1px solid var(--border); }
.tl-days span.past { color: #2a1703; background: linear-gradient(150deg, #ffe9a8, #ffb02e); border-color: transparent; }
.tl-days span.now { color: var(--amber); border-color: var(--amber); background: rgba(255,166,48,0.14); animation: tlNow 1.6s ease-out 0.8s 3; }
@keyframes tlNow { 0%, 100% { transform: scale(1); } 40% { transform: scale(1.18); } }
.tl-hint { margin-top: 12px; padding-top: 11px; border-top: 1px dashed var(--border); text-align: center; }
.tl-hint b { display: block; font-size: 15px; font-weight: 800; color: var(--amber); }
.tl-hint span { display: block; margin-top: 3px; font-size: 12.5px; line-height: 1.45; color: var(--text-muted); }
.tl-s2 .tl-hint b { color: var(--green); }
.tl-debt .tl-hint b { color: var(--red); }
@media (prefers-reduced-motion: reduce) { .tl, .tl-st.cur .tl-node, .tl-st.cur .tl-node::after, .tl-days span.now, .tl-car { animation: none !important; } .tl .tl-line i, .tl .tl-car { transition: none !important; } }
.rk2-wkp { position: relative; display: inline-flex; align-items: center; gap: 6px; overflow: hidden; padding: 3px 19px 3px 3px; border-radius: 13px; color: #2a1703; background: linear-gradient(135deg, #fff3c4, #ffc83a 48%, #ff9a2e); border: 1px solid rgba(255,236,170,0.8); box-shadow: 0 0 16px -2px rgba(255,176,46,0.7), inset 0 1px 0 rgba(255,255,255,0.7); text-align: left; line-height: 1; }
.rk2-wkp-ic { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 10px; color: #ffd166; background: linear-gradient(160deg, #3a2208, #1b1004); box-shadow: inset 0 0 0 1px rgba(255,209,102,0.45); flex-shrink: 0; }
.rk2-wkp-tx { display: grid; gap: 1px; }
.rk2-wkp-tx b { font-size: 8px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; opacity: 0.75; }
.rk2-wkp-tx em { font-style: normal; font-size: 12px; font-weight: 800; letter-spacing: 0.01em; }
.rk2-wkp::after { content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 38%; pointer-events: none; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.75), transparent); transform: translateX(-130%) skewX(-18deg); animation: rkwShine 2.2s ease 0.7s 2 forwards; }
.rk2-wkp-sp { position: absolute; top: 4px; right: 6px; width: 7px; height: 7px; color: #fff; opacity: 0.9; animation: rkwTw 1.5s ease 0.9s 3; }
@keyframes rkwShine { to { transform: translateX(380%) skewX(-18deg); } }
@keyframes rkwTw { 0%, 100% { transform: scale(0.6); opacity: 0.4; } 50% { transform: scale(1.25); opacity: 1; } }
.rk2-slot-wk { margin-top: 5px; display: flex; justify-content: center; }
.rk2-row-wk { margin: 4px 0 1px; }
.rk2-row.rk2-row-star { border-color: rgba(255,200,80,0.55); background-image: linear-gradient(90deg, rgba(255,190,60,0.13), transparent 65%); box-shadow: 0 10px 26px -14px rgba(255,176,46,0.6); }
@media (prefers-reduced-motion: reduce) { .rk2-wkp::after, .rk2-wkp-sp { animation: none !important; } }
.sp4 { display:inline-block; width:1em; height:1em; background:currentColor; clip-path:polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%); -webkit-clip-path:polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%) }
/* ======================= Panel 3.0: orbita, dolny pasek, oś czasu (usuń ten blok, aby wrócić do starego wyglądu CSS) ======================= */
.screen.dash3 { padding-bottom: 132px; }
html.ui3 .chat-fab { display: none !important; }

/* ---- nagłówek: pierścień tygodnia wokół awatara ---- */
.hd3 { display: flex; align-items: center; gap: 12px; margin: 2px 0 4px; min-height: 62px; }
.hd3-av { position: relative; flex: 0 0 auto; width: 58px; height: 58px; padding: 0; border: 0; background: none; cursor: pointer; -webkit-tap-highlight-color: transparent; }
.hd3-av svg { position: absolute; left: 0; top: 0; width: 58px; height: 58px; transform: rotate(-90deg); filter: drop-shadow(0 0 8px rgba(255,190,80,0.45)); }
.hd3-av b { position: absolute; left: 6px; top: 6px; right: 6px; bottom: 6px; border-radius: 50%; display: grid; place-items: center; font-size: 21px; font-weight: 800; color: var(--text); background: radial-gradient(circle at 35% 30%, var(--surface-3), var(--surface)); }
.hd3-av i { position: absolute; right: -8px; bottom: -4px; padding: 2px 7px; border-radius: 99px; font-style: normal; font-size: 10px; font-weight: 800; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); border: 2px solid var(--bg); }
.hd3 .t { flex: 1; min-width: 0; }
.hd3 .t b { display: block; font-size: 19px; font-weight: 800; letter-spacing: -0.2px; line-height: 1.2; }
.hd3 .t small { display: block; margin-top: 2px; font-size: 12px; color: var(--text-muted); line-height: 1.3; }
.hd3-btn { flex: 0 0 auto; width: 42px; height: 42px; border-radius: 15px; display: grid; place-items: center; cursor: pointer; color: var(--text); background: var(--surface); border: 1px solid var(--border); -webkit-tap-highlight-color: transparent; }

/* ---- orbita: pierścień z kwotą w środku ---- */
.orb3 { position: relative; width: 250px; height: 250px; margin: 10px auto 0; }
.orb3-rg { position: absolute; left: 0; top: 0; width: 250px; height: 250px; transform: rotate(-90deg); overflow: visible; }
.orb3-rg .val { transition: stroke-dashoffset 1.4s cubic-bezier(0.2, 0.8, 0.2, 1); filter: drop-shadow(0 0 10px rgba(255,200,100,0.75)); }
.orb3-orbit { position: absolute; left: 0; top: 0; width: 250px; height: 250px; animation: p3Rot 10s linear infinite; }
.orb3-orbit i { position: absolute; left: 50%; top: -4px; width: 9px; height: 9px; margin-left: -4.5px; border-radius: 50%; background: #fff; box-shadow: 0 0 14px 4px rgba(255,220,140,0.9); }
@keyframes p3Rot { to { transform: rotate(360deg); } }
.orb3-core { position: absolute; left: 30px; top: 30px; right: 30px; bottom: 30px; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; background: radial-gradient(circle at 50% 28%, rgba(52,28,38,0.84), rgba(20,10,16,0.8)); border: 1px solid rgba(255,255,255,0.16); box-shadow: inset 0 1px 0 rgba(255,255,255,0.2), 0 20px 40px -22px rgba(0,0,0,0.8); }
.light-theme .orb3-core { background: radial-gradient(circle at 50% 28%, rgba(255,255,255,0.95), rgba(240,238,246,0.92)); border-color: rgba(0,0,0,0.1); }
.light-theme .am3 .i { background: linear-gradient(180deg, #0b5a3a, #1fa56f); -webkit-background-clip: text; background-clip: text; filter: none; }
.light-theme .am3 .d, .light-theme .am3 .m { color: #1fa56f; }
.light-theme .am3.t-red .i { background: linear-gradient(180deg, #8a1230, #e11d48); -webkit-background-clip: text; background-clip: text; }
.light-theme .am3.t-gold .i { background: linear-gradient(180deg, #8a4b00, #e08a00); -webkit-background-clip: text; background-clip: text; }
.orb3-lbl { font-size: 10px; font-weight: 800; letter-spacing: 2.5px; color: var(--text-muted); }
.orb3-sub { margin-top: 7px; font-size: 10.5px; color: var(--text-muted); max-width: 150px; line-height: 1.3; }
.orb3-pill { margin-top: 2px; }
.orb3-sp { position: absolute; width: 13px; height: 13px; background: #fff; clip-path: polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%); -webkit-clip-path: polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%); filter: drop-shadow(0 0 6px #fff); opacity: 0; animation: p3Tw 2.8s ease-in-out infinite; }
.orb3-sp.a { left: 6%; top: 20%; }
.orb3-sp.b { left: 90%; top: 30%; width: 11px; height: 11px; animation-delay: 1s; }
.orb3-sp.c { left: 82%; top: 84%; width: 9px; height: 9px; animation-delay: 2s; }
@keyframes p3Tw { 0%, 100% { opacity: 0; transform: scale(0.2) rotate(0); } 50% { opacity: 1; transform: scale(1) rotate(45deg); } }

/* ---- kwota: grube cyfry (IBM Plex Sans), grosze mniejsze ---- */
.am3 { display: flex; align-items: baseline; justify-content: center; margin: 7px 0 7px; font-family: 'IBM Plex Sans', sans-serif; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: -1.8px; line-height: 1; white-space: nowrap; }
.am3 .i { font-size: 44px; background: linear-gradient(180deg, #ffffff 25%, var(--am3c, #a6f0cd)); -webkit-background-clip: text; background-clip: text; color: transparent; filter: drop-shadow(0 6px 16px var(--am3g, rgba(110,231,183,0.4))); }
.am3 .m { font-size: 36px; margin-right: 3px; color: var(--am3c, #a6f0cd); }
.am3 .d { margin-left: 2px; font-size: 21px; font-weight: 600; letter-spacing: -0.4px; color: var(--am3c, #a6f0cd); opacity: 0.9; }
.am3 .c { margin-left: 6px; font-size: 17px; font-weight: 600; letter-spacing: 0; color: var(--text-muted); }
.am3.t-red { --am3c: #fda4af; --am3g: rgba(251,113,133,0.4); }
.am3.t-gold { --am3c: #ffd78a; --am3g: rgba(255,190,80,0.45); }
.am3.t-amber { --am3c: #ffd78a; --am3g: rgba(255,190,80,0.45); }
.chips3 { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin: 12px 0 4px; }
.chips3 span { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 99px; font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; background: rgba(26,16,24,0.78); border: 1px solid var(--border); animation: p3Pop 0.7s cubic-bezier(0.2, 1.4, 0.3, 1) both; }
.light-theme .chips3 span { background: rgba(255,255,255,0.92); border-color: rgba(0,0,0,0.1); }
.chips3 span:nth-child(2) { animation-delay: 0.1s; } .chips3 span:nth-child(3) { animation-delay: 0.2s; }
@keyframes p3Pop { from { opacity: 0; transform: translateY(12px) scale(0.9); } to { opacity: 1; transform: none; } }

/* ---- dolny pasek ---- */
.dock3 { position: fixed; left: 50%; transform: translateX(-50%); bottom: calc(12px + env(safe-area-inset-bottom, 0px)); width: min(406px, calc(100vw - 24px)); height: 68px; border-radius: 26px; display: flex; align-items: center; justify-content: space-around; padding: 0 6px; z-index: 90; background: linear-gradient(180deg, rgba(34,36,46,0.92), rgba(14,15,22,0.95)); border: 1px solid rgba(255,255,255,0.14); box-shadow: 0 24px 40px -14px rgba(0,0,0,0.9), inset 0 1px 0 rgba(255,255,255,0.16); -webkit-backdrop-filter: blur(20px) saturate(150%); backdrop-filter: blur(20px) saturate(150%); }
html.lite .dock3 { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(18,19,26,0.97); }
.dk3 { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; width: 66px; height: 60px; border: 0; background: none; cursor: pointer; font: 700 10px 'IBM Plex Sans', sans-serif; color: #9aa3b2; -webkit-tap-highlight-color: transparent; }
.dk3.on { color: #ffd166; }
.dk3.on:before { content: ""; position: absolute; top: -3px; left: 50%; width: 28px; height: 3px; margin-left: -14px; border-radius: 3px; background: #ffb02e; box-shadow: 0 0 14px 2px rgba(255,170,40,0.9); }
.dk3.on:after { content: ""; position: absolute; top: 2px; left: 50%; width: 54px; height: 54px; margin-left: -27px; border-radius: 50%; background: radial-gradient(circle, rgba(255,170,40,0.26), transparent 70%); z-index: -1; }
.dk3 .dot { position: absolute; top: 6px; right: 15px; width: 10px; height: 10px; border-radius: 50%; background: #f43f5e; box-shadow: 0 0 0 2px #14161d, 0 0 10px #f43f5e; }
.coin3 { position: relative; flex: 0 0 auto; margin-top: -30px; width: 62px; height: 62px; border-radius: 50%; border: 0; display: grid; place-items: center; cursor: pointer; color: #2a1703; background: conic-gradient(from 210deg, #ffe08a, #ff9a1f, #ffd166, #ff7a1f, #ffe08a); box-shadow: 0 14px 26px -6px rgba(255,150,30,0.9), 0 0 0 6px rgba(14,15,22,0.95), inset 0 2px 0 rgba(255,255,255,0.6); -webkit-tap-highlight-color: transparent; }
.coin3:after { content: ""; position: absolute; left: -9px; top: -9px; right: -9px; bottom: -9px; border-radius: 50%; border: 2px solid rgba(255,190,70,0.5); pointer-events: none; }
.coin3.on { box-shadow: 0 0 0 6px rgba(14,15,22,0.95), 0 0 34px 8px rgba(255,170,40,0.75), 0 14px 26px -6px rgba(255,150,30,0.9), inset 0 2px 0 rgba(255,255,255,0.6); }
.coin3:active { transform: scale(0.94); }

/* ---- arkusze: Więcej i Profil ---- */
.sh3-dim { position: fixed; left: 0; top: 0; right: 0; bottom: 0; z-index: 2400; background: rgba(5,5,10,0.55); animation: p3Fade 0.25s ease both; }
.sh3 { position: fixed; left: 50%; transform: translateX(-50%); bottom: 0; width: min(430px, 100vw); max-height: 86vh; overflow-y: auto; z-index: 2410; padding: 12px 16px calc(100px + env(safe-area-inset-bottom, 0px)); border-radius: 30px 30px 0 0; background: linear-gradient(180deg, rgba(40,42,54,0.97), rgba(16,17,24,0.99)); border-top: 1px solid rgba(255,255,255,0.2); box-shadow: 0 -30px 60px -10px rgba(0,0,0,0.8); animation: p3Up 0.32s cubic-bezier(0.2, 0.9, 0.3, 1) both; color: #f2f0ec; font-family: 'IBM Plex Sans', sans-serif; }
.light-theme .sh3 { background: linear-gradient(180deg, #ffffff, #f1f2f5); color: #1a1d24; border-top-color: rgba(0,0,0,0.1); }
@keyframes p3Fade { from { opacity: 0; } }
@keyframes p3Up { from { transform: translate(-50%, 40px); opacity: 0; } to { transform: translateX(-50%); opacity: 1; } }
.sh3-grab { width: 42px; height: 5px; margin: 0 auto 14px; border-radius: 3px; background: rgba(255,255,255,0.28); }
.sh3-k { font-size: 11px; font-weight: 800; letter-spacing: 2px; color: #9aa3b2; margin: 0 2px 2px; }
.sh3-row { position: relative; display: flex; align-items: center; gap: 13px; width: 100%; margin-top: 9px; padding: 11px 12px; border-radius: 18px; text-align: left; cursor: pointer; font-family: inherit; color: inherit; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); -webkit-tap-highlight-color: transparent; }
.light-theme .sh3-row { background: rgba(0,0,0,0.04); border-color: rgba(0,0,0,0.08); }
.sh3-row.static { cursor: default; }
.sh3-row .ic { flex: 0 0 auto; width: 42px; height: 42px; border-radius: 14px; display: grid; place-items: center; color: #fff; }
.sh3-row .tx { flex: 1; min-width: 0; }
.sh3-row .tx b { display: block; font-size: 14.5px; }
.sh3-row .tx small { display: block; margin-top: 1px; font-size: 11.5px; color: #9aa3b2; }
.sh3-row .bd { flex: 0 0 auto; width: 10px; height: 10px; border-radius: 50%; background: #f43f5e; box-shadow: 0 0 10px #f43f5e; }
.sh3-me { display: flex; align-items: center; gap: 14px; margin-bottom: 4px; }
.sh3-me .av { width: 56px; height: 56px; border-radius: 50%; display: grid; place-items: center; font-size: 24px; font-weight: 800; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); box-shadow: 0 0 22px rgba(255,150,40,0.55); }
.sh3-me b { display: block; font-size: 19px; font-weight: 800; }
.sh3-me small { font-size: 12px; color: #9aa3b2; }
.sh3-av { display: flex; gap: 8px; overflow-x: auto; padding: 8px 2px 10px; -webkit-overflow-scrolling: touch; }
.sh3-av button { flex: 0 0 auto; width: 44px; height: 44px; border-radius: 14px; font-size: 22px; cursor: pointer; background: rgba(255,255,255,0.07); border: 2px solid transparent; }
.sh3-av button.on { border-color: #ffb82e; background: rgba(255,184,46,0.18); }
.sh3-av button.rst { font-size: 13px; font-weight: 800; color: #9aa3b2; }

/* ---- rozliczenia jako oś czasu z rozwijaniem szczegółów ---- */
.tl3 { position: relative; padding-left: 26px; display: block; }
.tl3:before { content: ""; position: absolute; left: 8px; top: 12px; bottom: 12px; width: 2px; border-radius: 2px; background: linear-gradient(180deg, rgba(255,255,255,0.3), rgba(255,255,255,0.05)); }
.tl3 .tk4:before { content: ""; position: absolute; left: -26px; top: 19px; width: 16px; height: 16px; border-radius: 50%; background: var(--nc, #4fdc92); border: 3px solid var(--bg); box-shadow: 0 0 12px var(--nc, #4fdc92); }
.tk4 { position: relative; margin-top: 10px; border-radius: 20px; overflow: hidden; background: linear-gradient(160deg, rgba(46,28,38,0.88), rgba(26,16,24,0.86)); border: 1px solid var(--border); animation: p3Pop 0.6s cubic-bezier(0.2, 1.2, 0.3, 1) both; }
.light-theme .tk4 { background: rgba(255,255,255,0.92); }
.tk4[data-tone="green"] { --nc: #4fdc92; } .tk4[data-tone="amber"] { --nc: #ffb02e; } .tk4[data-tone="red"] { --nc: #fb7185; }
.tk4.open { border-color: rgba(255,190,70,0.55); box-shadow: 0 0 0 1px rgba(255,190,70,0.2), 0 18px 34px -18px rgba(255,150,30,0.7); }
.tk4.open:before { background: #ffb02e; box-shadow: 0 0 0 4px rgba(255,255,255,0.14), 0 0 18px #ffb02e; }
.tk4-h { display: flex; align-items: center; gap: 10px; width: 100%; padding: 12px 14px; border: 0; background: none; text-align: left; cursor: pointer; color: var(--text); font-family: inherit; -webkit-tap-highlight-color: transparent; }
.tk4-d { flex: 1; min-width: 0; display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.tk4-d b { font-size: 14px; font-weight: 600; white-space: nowrap; }
.tk4-amt { flex: 0 0 auto; display: inline-flex; align-items: baseline; font: 700 18px 'IBM Plex Sans', sans-serif; letter-spacing: -0.4px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.tk4-amt i { font-style: normal; font-size: 13px; font-weight: 600; opacity: 0.8; }
.tk4-amt em { font-style: normal; font-size: 13px; font-weight: 600; margin-left: 4px; color: var(--text-muted); }
.tk4-amt.neg { color: #fda4af; }
.tk4-chev { flex: 0 0 auto; color: var(--text-muted); transition: transform 0.3s ease; }
.tk4.open .tk4-chev { transform: rotate(180deg); color: #ffd166; }
.tk4-body { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.38s cubic-bezier(0.2, 0.9, 0.3, 1); }
.tk4.open .tk4-body { grid-template-rows: 1fr; }
.tk4-in { overflow: hidden; min-height: 0; padding: 0 14px; }
.tk4.open .tk4-in { padding-bottom: 14px; }
.tk4-bar { display: flex; gap: 3px; height: 8px; margin: 2px 0 12px; }
.tk4-bar i { display: block; height: 100%; border-radius: 99px; }

/* ---- istniejąca oś "Tydzień trwa → Rozliczanie → Wypłata" dostosowana do nowego stylu ---- */
html.ui3 .tl { border-radius: 24px; }
html.ui3 .tl { background: rgba(26,16,24,0.7); }
.light-theme.ui3 .tl, html.ui3 .light-theme .tl { background: rgba(255,255,255,0.9); }
html.ui3 .tl-node { box-shadow: 0 0 0 4px var(--bg), 0 0 14px rgba(110,231,183,0.5); }

/* ---- pozostałe zakładki: miękkie, szklane karty, zaokrąglenia i poświata ---- */
html.ui3 .payout-action-card, html.ui3 .payout-status-card, html.ui3 .debt-hero-card, html.ui3 .debt-repay-form, html.ui3 .badge-card, html.ui3 .badge-hero-card, html.ui3 .comp-action-row .comp-btn, html.ui3 .ranking-subnav { border-radius: 22px; box-shadow: 0 18px 34px -22px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.1); }
html.ui3 .section-label { letter-spacing: 2px; }
html.ui3 .empty-state, html.ui3 .ec { background: rgba(26,16,24,0.8); }
html.ui3 .light-theme .empty-state, html.ui3 .light-theme .ec { background: rgba(255,255,255,0.92); }
html.ui3 .refresh-btn { background: var(--surface); }
html.ui3 .ranking-subnav { background: rgba(255,255,255,0.05); }

html.lite .orb3-orbit, html.lite .orb3-sp { animation: none; }
html.lite .orb3-sp { display: none; }
@media (prefers-reduced-motion: reduce) { .orb3-orbit, .orb3-sp, .chips3 span, .tk4, .sh3, .sh3-dim { animation: none !important; } .orb3-sp { display: none; } .orb3-rg .val, .tk4-body { transition: none; } }
/* ======================= koniec Panelu 3.0 ======================= */

/* ======================= Panel 3.0 etap 2: droga z samochodem, osie czasu, orbity w zakładkach ======================= */
html.ui3 .section-label { display: flex; align-items: center; gap: 8px; width: fit-content; margin: 16px 2px 8px; padding: 5px 12px 5px 10px; border-radius: 99px; font-size: 11px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: #e4e7ee; background: rgba(22,9,15,0.66); }
html.ui3 .section-label:after { display: none; }
html.ui3 .section-label:before { content: ""; flex: 0 0 auto; width: 3px; height: 14px; border-radius: 2px; background: #ff9a1f; }
.light-theme.ui3 .section-label, html.ui3 .light-theme .section-label { color: #3b4150; background: rgba(255,255,255,0.85); }
.big3 { display: flex; align-items: baseline; justify-content: center; gap: 2px; margin: 6px 0 7px; font: 700 54px/1 'IBM Plex Sans', sans-serif; letter-spacing: -2px; color: #fff; font-variant-numeric: tabular-nums; text-shadow: 0 6px 20px rgba(255,255,255,0.18); }
.big3 small { font-size: 24px; font-weight: 600; letter-spacing: 0; color: var(--text-muted); }
.big3.mint { color: #c9ffd9; text-shadow: 0 6px 22px rgba(110,231,183,0.5); }
.light-theme .big3 { color: #14161c; text-shadow: none; }
.light-theme .big3.mint { color: #0b7a4e; }
.chips3 span.up { color: #6ee7b7; border-color: rgba(110,231,183,0.45); }
.chips3 span.dn { color: #fb7185; border-color: rgba(251,113,133,0.45); }

/* ---- droga: Tydzień → Rozliczenie → Wypłata z samochodzikiem ---- */
.rd3 { margin: 16px 0 0; padding: 10px 0 8px; border-radius: 24px; background: linear-gradient(160deg, rgba(44,14,26,0.66), rgba(20,8,14,0.6)); border: 1px solid rgba(255,255,255,0.12); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); }
html.lite .rd3 { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(30,10,20,0.9); }
.light-theme .rd3 { background: rgba(255,255,255,0.92); border-color: rgba(0,0,0,0.1); }
.rd3-road { position: relative; margin: 0 46px; height: 80px; }
.rd3-ln { position: absolute; left: 0; right: 0; top: 41px; height: 4px; border-radius: 4px; background: repeating-linear-gradient(90deg, rgba(255,255,255,0.42) 0 10px, transparent 10px 18px); }
.rd3-dn { position: absolute; left: 0; top: 41px; height: 4px; border-radius: 4px; background: linear-gradient(90deg, #6ee7b7, #ffd166); box-shadow: 0 0 12px rgba(255,200,100,0.85); transition: width 1.5s cubic-bezier(0.2, 0.8, 0.2, 1); }
.rd3-st { position: absolute; top:34px; width: 18px; height: 18px; margin-left: -9px; border-radius: 50%; display: grid; place-items: center; box-sizing: border-box; color: #06140c; background: #1a0f14; border: 3px solid rgba(255,255,255,0.45); }
.rd3-st.done { background: #6ee7b7; border-color: #6ee7b7; box-shadow: 0 0 12px #6ee7b7; }
.rd3-st.cur { border-color: #ffb02e; box-shadow: 0 0 0 5px rgba(255,170,40,0.28), 0 0 16px #ffb02e; }
.rd3-car { position: absolute; top: 4px; width: 0; height: 0; transition: left 1.5s cubic-bezier(0.2, 0.8, 0.2, 1); z-index: 2; }
.rd3-car i { position: absolute; left: 0; top: 0; font-style: normal; font-size: 30px; line-height: 1; transform: translateX(-50%) scaleX(-1); filter: drop-shadow(0 0 12px rgba(255,220,140,0.95)); animation: rd3Drive 3.2s ease-in-out infinite alternate; }
@keyframes rd3Drive { from { margin-left: -7px; } to { margin-left: 7px; } }
.rd3-lb { position: absolute; top: 60px; transform: translateX(-50%); font-size: 10.5px; font-weight: 700; color: #aeb6c4; white-space: nowrap; }
.rd3-lb.cur { color: #ffd166; }
.rd3.s2 .rd3-car i { animation: none; }
.rd3-hint { margin: 2px 8px 0; text-align: center; font-size: 12px; line-height: 1.4; color: var(--text-muted); }
.rd3-hint b { color: var(--text); }
.light-theme .rd3-ln { background: repeating-linear-gradient(90deg, rgba(0,0,0,0.3) 0 10px, transparent 10px 18px); }
.light-theme .rd3-st { background: #fff; border-color: rgba(0,0,0,0.3); }
.light-theme .rd3-st.done { background: #2fbf86; border-color: #2fbf86; }
.light-theme .rd3-lb { color: #5a6272; }
.light-theme .rd3-lb.cur { color: #b86e00; }

/* ---- historia rozliczeń: kompaktowe karty na osi czasu ---- */
.tk4 { overflow: visible; border-radius: 20px; background: linear-gradient(160deg, rgba(70,28,40,0.7), rgba(30,12,20,0.64)); -webkit-backdrop-filter: blur(14px) saturate(140%); backdrop-filter: blur(14px) saturate(140%); border-color: rgba(255,255,255,0.15); }
html.lite .tk4 { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(38,16,26,0.92); }
.light-theme .tk4 { background: rgba(255,255,255,0.94); border-color: rgba(0,0,0,0.1); }
.tl3 .tk4:before { top: 22px; }
.tk4-h { padding: 12px 14px; gap: 12px; }
.tk4-d { gap: 3px; }
.tk4-d b { font-size: 14.5px; font-weight: 600; }
.tk4-st { font-size: 11.5px; font-weight: 600; color: #aeb6c4; }
.tk4-st.amber { color: #ffd166; }
.tk4-st.red { color: #fda4af; }
.tk4-amt { color: #fff; font-size: 19px; }
.tk4-amt i { font-size: 13px; opacity: 0.85; }
.tk4-amt em { color: #aeb6c4; }
.light-theme .tk4-amt { color: #14161c; }
.tk4-body { overflow: hidden; border-radius: 0 0 20px 20px; }
.tk4-chev { width: 16px; height: 16px; opacity: 0.6; }
.tk5-rows { margin-top: 2px; }
.tk5-r { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 9px 0; border-bottom: 1px dashed rgba(255,255,255,0.18); font-size: 13px; color: #d8dce4; }
.tk5-r b { font: 600 13.5px 'IBM Plex Sans', sans-serif; font-variant-numeric: tabular-nums; color: #fff; white-space: nowrap; }
.tk5-r.neg b { color: #ff9aa8; }
.tk5-r.tot { border: 0; padding-top: 11px; font-size: 14.5px; font-weight: 800; color: #fff; }
.tk5-r.tot b { font-size: 18px; font-weight: 700; color: #6ee7b7; }
.tk5-r.tot b.neg { color: #fda4af; }
.light-theme .tk5-r { color: #3b4150; border-bottom-color: rgba(0,0,0,0.14); }
.light-theme .tk5-r b { color: #14161c; }
.light-theme .tk5-r.neg b { color: #c2263f; }
.light-theme .tk5-r.tot { color: #14161c; }
.light-theme .tk5-r.tot b { color: #0b7a4e; }
.tk5-ch { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 4px; }
.tk5-ch span { padding: 4px 10px; border-radius: 99px; font-size: 10.5px; font-weight: 800; }
.tk5-ch span.up { color: #6ee7b7; background: rgba(110,231,183,0.14); border: 1px solid rgba(110,231,183,0.4); }
.tk5-ch span.dn { color: #fda4af; background: rgba(251,113,133,0.14); border: 1px solid rgba(251,113,133,0.4); }
.tk5-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; margin-top: 12px; padding: 12px; border-radius: 16px; font: 700 13px 'IBM Plex Sans', sans-serif; color: var(--text); cursor: pointer; background: rgba(255,255,255,0.09); border: 1px solid rgba(255,255,255,0.18); -webkit-tap-highlight-color: transparent; }
.tk5-btn:active { transform: scale(0.98); }

/* ---- wiersze osi czasu: ranking, kupony, zadłużenie; opakowanie kart zadań i rekompensat ---- */
.tn3 { position: relative; display: flex; align-items: center; gap: 10px; width: 100%; margin-top: 9px; padding: 12px 14px; border-radius: 20px; text-align: left; font-family: inherit; color: var(--text); box-sizing: border-box; background: linear-gradient(160deg, rgba(70,28,40,0.7), rgba(30,12,20,0.64)); border: 1px solid rgba(255,255,255,0.15); -webkit-backdrop-filter: blur(14px) saturate(140%); backdrop-filter: blur(14px) saturate(140%); animation: p3Pop 0.6s cubic-bezier(0.2, 1.2, 0.3, 1) both; -webkit-tap-highlight-color: transparent; }
html.lite .tn3 { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(38,16,26,0.92); }
.light-theme .tn3 { background: rgba(255,255,255,0.94); border-color: rgba(0,0,0,0.1); }
.tl3 > .tn3:before, .tl3 > .tlr:before { content: ""; position: absolute; left: -26px; top: 19px; width: 16px; height: 16px; border-radius: 50%; background: var(--nc, #4fdc92); border: 3px solid var(--bg); box-shadow: 0 0 12px var(--nc, #4fdc92); z-index: 1; }
.tl3 > .tlr:before { top: 21px; }
.tlr { position: relative; margin-top: 10px; }
.tlr > * { margin-top: 0 !important; }
.tn3-t { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.tn3-t b { font-size: 14px; font-weight: 600; }
.tn3-t small { font-size: 11.5px; color: #aeb6c4; }
.light-theme .tn3-t small { color: #5a6272; }
.tn3.me { border-color: rgba(255,190,70,0.65); box-shadow: 0 0 0 1px rgba(255,190,70,0.22), 0 14px 28px -16px rgba(255,150,30,0.75); }
.tn3.me:before { box-shadow: 0 0 0 4px rgba(255,255,255,0.14), 0 0 16px var(--nc); }
.mv { flex: 0 0 auto; font-size: 11.5px; font-weight: 800; }
.mv.up { color: #6ee7b7; }
.mv.dn { color: #fb7185; }
.rk3-on { display: inline-block; width: 7px; height: 7px; margin-left: 7px; border-radius: 50%; background: #4fdc92; box-shadow: 0 0 8px #4fdc92; }
.bn3-a { flex: 0 0 auto; font: 700 15px 'IBM Plex Sans', sans-serif; color: #ffd166; white-space: nowrap; }
.bn3.done .bn3-a { color: #6ee7b7; }
.bn3-n { font-size: 11px !important; opacity: 0.85; }

/* ---- ranking: orbita kierowcy tygodnia ---- */
html.ui3 .rk3-hd, html.ui3 .rk3-hide, html.ui3 .tk2-hero, html.ui3 .cm2-hero, html.ui3 .bh-old, html.ui3 .bg-old, html.ui3 .dh-old { display: none; }
html.ui3 .ranking-subnav { background: none; border: 0; box-shadow: none; padding: 0 2px 4px; margin: 4px 0 2px; gap: 8px; overflow-x: auto; }
html.ui3 .ranking-subnav-btn { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 7px; height: 38px; padding: 0 14px; border-radius: 99px; font-size: 13px; font-weight: 700; color: #d8dce4; background: rgba(26,16,24,0.74); border: 1px solid rgba(255,255,255,0.16); box-shadow: none; }
html.ui3 .ranking-subnav-btn.active { color: #2a1703; border-color: transparent; background: linear-gradient(150deg, #ffe08a, #ff9a1f); box-shadow: 0 10px 20px -8px rgba(255,150,30,0.95); }
.light-theme.ui3 .ranking-subnav-btn, html.ui3 .light-theme .ranking-subnav-btn { color: #3b4150; background: rgba(255,255,255,0.92); border-color: rgba(0,0,0,0.12); }
html.ui3 .ranking-subnav-btn.active { color: #2a1703; }
.rk3-crown { font-size: 26px; line-height: 1; filter: drop-shadow(0 4px 10px rgba(255,190,60,0.85)); animation: rk3Bob 2.4s ease-in-out 3; }
@keyframes rk3Bob { 0%, 100% { transform: translateY(0) rotate(-4deg); } 50% { transform: translateY(-5px) rotate(4deg); } }
.rk3-av { width: 54px; height: 54px; margin-top: 2px; border-radius: 50%; display: grid; place-items: center; font-size: 25px; font-weight: 800; color: #fff; background: radial-gradient(circle at 35% 30%, #4a2e16, #170e08); border: 3px solid #ffd166; box-shadow: 0 0 18px rgba(255,190,60,0.65); }
.rk3-nm { margin-top: 5px; font-size: 18px; font-weight: 800; line-height: 1.1; }
.rk3-k { margin-top: 2px; font-size: 8.5px; font-weight: 800; letter-spacing: 1.8px; color: #aeb6c4; }
.rk3-pt { margin-top: 3px; font: 700 18px 'IBM Plex Sans', sans-serif; color: #ffd78a; font-variant-numeric: tabular-nums; }
.light-theme .rk3-pt { color: #b86e00; }
.rk3-prog { margin: 12px 0 0; padding: 12px 14px 13px; border-radius: 18px; font-size: 13px; font-weight: 600; color: #f2e9e4; background: linear-gradient(135deg, rgba(120,40,24,0.7), rgba(50,16,16,0.68)); border: 1px solid rgba(255,150,60,0.5); }
.rk3-prog b { color: #ffd166; }
.rk3-prog > div:first-child { display: flex; justify-content: space-between; gap: 8px; }
.rk3-prog.lead { display: flex; align-items: center; gap: 8px; color: #ffd166; font-weight: 800; }
.rk3-bar { height: 7px; margin-top: 9px; border-radius: 99px; background: rgba(255,255,255,0.16); overflow: hidden; }
.rk3-bar i { display: block; height: 100%; border-radius: 99px; background: linear-gradient(90deg, #ffd166, #ff7a1f); box-shadow: 0 0 12px rgba(255,170,40,0.9); }
.light-theme .rk3-prog { color: #3b2a22; background: rgba(255,240,225,0.95); }
.rk3-list { margin-top: 10px; }
.rk3-row { cursor: pointer; }

/* ---- osiągnięcia: poziom jako orbita + odznaki ---- */
html.ui3 .lv { background: none; border: 0; box-shadow: none; padding: 0; margin: 8px 0 0; }
html.ui3 .lv-k { display: none; }
html.ui3 .lv-orb { width: 250px; height: 250px; margin: 0 auto; }
html.ui3 .lv-orb svg, html.ui3 .lv-orbit { width: 250px; height: 250px; }
html.ui3 .lv-core { left: 30px; top: 30px; right: 30px; bottom: 30px; background: radial-gradient(circle at 50% 28%, rgba(52,28,38,0.84), rgba(20,10,16,0.8)); border-color: rgba(255,255,255,0.16); }
.light-theme.ui3 .lv-core, html.ui3 .light-theme .lv-core { background: radial-gradient(circle at 50% 28%, rgba(255,255,255,0.95), rgba(240,238,246,0.92)); }
html.ui3 .lv-nx { width: fit-content; margin: 12px auto 0; padding: 7px 14px; border-radius: 99px; background: rgba(22,9,15,0.8); color: #d8dce4; }
html.ui3 .lv-bar { height: 10px; margin: 10px 4px 0; background: rgba(22,9,15,0.8); border: 1px solid rgba(255,255,255,0.14); }
html.ui3 .lv-lad span { background: rgba(22,9,15,0.8); color: #aeb6c4; }
html.ui3 .lv-lad span.past { color: #ffd166; background: rgba(60,34,10,0.85); }
html.ui3 .lv-lad span.on { color: #2a1703; background: linear-gradient(150deg, #ffe08a, #ff9a1f); }
html.ui3 .lv-wk span { background: rgba(22,9,15,0.8); }
html.ui3 .lv-how { background: rgba(22,9,15,0.8); color: #d8dce4; }
html.ui3 .lv-ic { font-size: 40px; }
html.ui3 .lv-nm { font-size: 26px; }
.streak-chip { display: none; }
.lv ~ .streak-orb { display: none; }
.lv ~ .streak-chip { display: flex; }
.bw3 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px 8px; margin-top: 8px; }
.bd3 { text-align: center; font-size: 10.5px; font-weight: 700; line-height: 1.25; color: #eef0f5; text-shadow: 0 1px 6px rgba(0,0,0,0.85); animation: p3Pop 0.6s cubic-bezier(0.2, 1.3, 0.3, 1) both; }
.bd3 span { position: relative; display: grid; place-items: center; width: 56px; height: 56px; margin: 0 auto 5px; border-radius: 50%; font-size: 26px; overflow: hidden; }
.bd3.on span { background: linear-gradient(150deg, #ffe08a, #ff9a1f); box-shadow: 0 10px 18px -6px rgba(255,150,30,0.9), inset 0 2px 0 rgba(255,255,255,0.6); }
.bd3.on span:after { content: ""; position: absolute; top: -30%; bottom: -30%; left: -60%; width: 40%; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.75), transparent); transform: skewX(-20deg); animation: p3Shn 3s ease-in-out 1s 2; }
@keyframes p3Shn { 0% { left: -60%; } 45%, 100% { left: 140%; } }
.bd3.off span { background: rgba(255,255,255,0.06); border: 2px dashed rgba(255,255,255,0.24); color: #6b7383; filter: grayscale(1); }
.bd3.off { color: #cfd4de; }
.light-theme .bd3 { color: #3b4150; text-shadow: none; }

/* ---- rekompensaty: duże przyciski Dodaj / Odejmij ---- */
html.ui3 .comp-action-row { gap: 10px; margin-top: 14px; }
html.ui3 .comp-action-row .comp-btn { height: 54px; border-radius: 18px; font-size: 15px; font-weight: 800; -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px); }
html.ui3 .comp-btn-add { color: #c9ffd9; background: linear-gradient(135deg, rgba(110,231,183,0.32), rgba(110,231,183,0.1)); border: 1px solid rgba(110,231,183,0.55); }
html.ui3 .comp-btn-subtract { color: #ffc2cb; background: linear-gradient(135deg, rgba(251,113,133,0.32), rgba(251,113,133,0.1)); border: 1px solid rgba(251,113,133,0.55); }
.light-theme.ui3 .comp-btn-add, html.ui3 .light-theme .comp-btn-add { color: #0b6a43; }
.light-theme.ui3 .comp-btn-subtract, html.ui3 .light-theme .comp-btn-subtract { color: #a3152f; }

/* ---- grupa: konstelacja osób online w nagłówku ---- */
.con3 { position: relative; flex: 0 0 auto; width: 56px; height: 56px; }
.con3-core { position: absolute; left: 50%; top: 50%; width: 26px; height: 26px; margin: -13px; border-radius: 50%; display: grid; place-items: center; font-size: 15px; background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.22); box-shadow: 0 0 18px rgba(255,170,60,0.35); }
.con3 i { position: absolute; width: 18px; height: 18px; margin: -9px; border-radius: 50%; display: grid; place-items: center; font: 800 10px 'IBM Plex Sans', sans-serif; font-style: normal; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); box-shadow: 0 4px 10px -3px rgba(255,150,30,0.9); animation: p3Pop 0.6s cubic-bezier(0.2, 1.4, 0.3, 1) both; }
.con3 i:after { content: ""; position: absolute; right: -2px; bottom: -2px; width: 8px; height: 8px; border-radius: 50%; background: #4fdc92; border: 2px solid #1a0f14; box-sizing: border-box; }

html.ui3 .screen.dash3, html.ui3 .fz { overflow-x: clip; }
/* ---- fortuna: ticketów w jednej smukłej pigułce nad kołem ---- */
html.ui3 .fz-hero { justify-content: center; margin: 8px 0; padding: 9px 18px; border-radius: 99px; background: rgba(22,9,15,0.74); border-color: rgba(255,190,70,0.5); }
html.ui3 .fz-hero:before, html.ui3 .fz-hero:after, html.ui3 .fz-fan, html.ui3 .fz-hero-sub { display: none; }
html.ui3 .fz-hero-main { flex: 0 1 auto; display: flex; align-items: baseline; gap: 10px; }
html.ui3 .fz-hero-num { order: 1; font: 700 26px 'IBM Plex Sans', sans-serif; letter-spacing: -0.6px; }
html.ui3 .fz-hero-num small { font-size: 13px; }
html.ui3 .fz-hero-label { order: 2; font-size: 10.5px; letter-spacing: 1.6px; }

/* ---- lista zadań/rekompensat na osi: karty bez własnego marginesu ---- */
.tl3r .tlr > .tk2-card, .tl3r .tlr > .cm2-card { margin-top: 0; }
.tl3d > .debt-card { position: relative; margin-top: 10px; overflow: visible; }
.tl3d > .debt-card:after { content: ""; position: absolute; left: -26px; top: 22px; width: 16px; height: 16px; border-radius: 50%; background: #fb7185; border: 3px solid var(--bg); box-shadow: 0 0 12px #fb7185; box-sizing: content-box; }
.tl3d > .debt-card-paid:after { background: #4fdc92; box-shadow: 0 0 12px #4fdc92; }

/* motyw jasny: ciemniejsze akcenty na białych kartach */
.light-theme .tk4-st { color: #5a6272; }
.light-theme .tk4-st.amber { color: #b86e00; }
.light-theme .tk4-st.red { color: #c2263f; }
.light-theme .tk5-ch span.up { color: #0b7a4e; background: rgba(15,150,100,0.1); border-color: rgba(15,150,100,0.35); }
.light-theme .tk5-ch span.dn { color: #c2263f; background: rgba(225,29,72,0.08); border-color: rgba(225,29,72,0.3); }
.light-theme .tk5-btn { background: rgba(0,0,0,0.05); border-color: rgba(0,0,0,0.14); }
.light-theme .bn3-a { color: #b86e00; }
.light-theme .bn3.done .bn3-a { color: #0b7a4e; }
.light-theme .rk3-prog b { color: #b86e00; }
.light-theme .mv.up { color: #0b7a4e; }
.light-theme .mv.dn { color: #c2263f; }
.light-theme.ui3 .lv-nx, html.ui3 .light-theme .lv-nx { background: rgba(255,255,255,0.92); color: #3b4150; }
.light-theme.ui3 .lv-nx b, html.ui3 .light-theme .lv-nx b { color: #14161c; }
.light-theme.ui3 .lv-nm, html.ui3 .light-theme .lv-nm { color: #b86e00; }
.light-theme.ui3 .lv-bar, html.ui3 .light-theme .lv-bar { background: rgba(0,0,0,0.1); border-color: rgba(0,0,0,0.1); }
@media (prefers-reduced-motion: reduce) { .rd3-car i, .rk3-crown, .bd3, .bd3.on span:after, .tn3, .con3 i { animation: none !important; } .rd3-dn, .rd3-car { transition: none; } }
html.lite .rd3-car i, html.lite .rk3-crown { animation: none; }
/* ======================= koniec etapu 2 ======================= */

/* ======================= etap 3: ciemniejsze tło, okno decyzji o wypłacie, wykres tygodni ======================= */
html.ui3 .app-root.hw-moon .road-scene-bg::after { background: radial-gradient(ellipse 85% 85% at 50% 52%, rgba(20,2,8,0.1) 40%, rgba(20,2,8,0.72) 100%), linear-gradient(180deg, rgba(20,2,8,0.6) 0%, rgba(20,2,8,0.46) 30%, rgba(20,2,8,0.5) 70%, rgba(20,2,8,0.76) 100%); }

.pa3 { position: relative; margin: 8px 0 12px; padding: 11px 12px 12px; border-radius: 22px; overflow: hidden; background: linear-gradient(160deg, rgba(52,16,32,0.88), rgba(22,8,16,0.88)); border: 1px solid rgba(255,190,80,0.45); box-shadow: 0 18px 34px -18px rgba(255,140,30,0.6), inset 0 1px 0 rgba(255,255,255,0.14); animation: pa3In 0.7s cubic-bezier(0.2, 1.3, 0.3, 1) both; }
@keyframes pa3In { from { opacity: 0; transform: translateY(16px) scale(0.94); } to { opacity: 1; transform: none; } }
.pa3-glow { position: absolute; left: -30%; top: -70%; width: 90%; height: 150%; background: radial-gradient(circle, rgba(255,180,60,0.3), transparent 65%); pointer-events: none; }
.pa3:before { content: ""; position: absolute; top: -20%; bottom: -20%; left: -40%; width: 26%; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.14), transparent); transform: skewX(-20deg); animation: p3Shn 4.4s ease-in-out 1s infinite; pointer-events: none; }
.pa3-h { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 9px; font-size: 12.5px; font-weight: 700; color: #f2e9e4; }
.pa3-h span { display: inline-flex; align-items: center; gap: 6px; }
.pa3-h svg { color: #ffd166; }
.pa3-h b { font: 700 15px 'IBM Plex Sans', sans-serif; color: #a6f0cd; letter-spacing: -0.4px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.pa3-h b i { font-style: normal; font-size: 11.5px; opacity: 0.85; }
.pa3-b { position: relative; display: grid; grid-template-columns: 1.25fr 1fr 1fr; gap: 8px; }
.pa3-t { position: relative; overflow: visible; height: 58px; padding: 0 6px; border-radius: 16px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; font: 800 11.5px/1.15 'IBM Plex Sans', sans-serif; text-align: center; cursor: pointer; border: 1px solid transparent; -webkit-tap-highlight-color: transparent; transition: transform 0.15s ease; }
.pa3-t:active { transform: scale(0.95); }
.pa3-t.ok { color: #04170e; background: linear-gradient(150deg, #8bf5c6, #2fbf86); box-shadow: 0 10px 20px -8px rgba(60,220,150,0.95), inset 0 1px 0 rgba(255,255,255,0.6); animation: pa3Pulse 2.4s ease-in-out 1s 5; }
@keyframes pa3Pulse { 0%, 100% { box-shadow: 0 10px 20px -8px rgba(60,220,150,0.95), 0 0 0 0 rgba(110,231,183,0.55), inset 0 1px 0 rgba(255,255,255,0.6); } 50% { box-shadow: 0 10px 20px -8px rgba(60,220,150,0.95), 0 0 0 9px rgba(110,231,183,0), inset 0 1px 0 rgba(255,255,255,0.6); } }
.pa3-t.ok .pa3-sh { position: absolute; left: 0; top: 0; right: 0; bottom: 0; overflow: hidden; border-radius: 15px; pointer-events: none; }
.pa3-t.ok .pa3-sh:after { content: ""; position: absolute; top: -20%; bottom: -20%; left: -50%; width: 34%; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.65), transparent); transform: skewX(-20deg); animation: p3Shn 3.2s ease-in-out 0.6s infinite; }
.pa3-t.no { color: #ffc2cb; background: linear-gradient(160deg, rgba(251,113,133,0.26), rgba(251,113,133,0.08)); border-color: rgba(251,113,133,0.55); }
.pa3-t.nx { color: #ffe08a; background: linear-gradient(160deg, rgba(255,190,70,0.26), rgba(255,190,70,0.08)); border-color: rgba(255,190,70,0.55); }
.pa3-cf { position: absolute; left: 50%; top: 40%; width: 0; height: 0; pointer-events: none; z-index: 5; }
.pa3-cf i { position: absolute; left: 0; top: 0; width: 7px; height: 11px; border-radius: 2px; opacity: 0; animation: pa3Cf 0.95s cubic-bezier(0.1, 0.8, 0.3, 1) both; }
@keyframes pa3Cf { 0% { opacity: 1; transform: translate(0, 0) rotate(0); } 70% { opacity: 1; } 100% { opacity: 0; transform: translate(var(--x), calc(var(--y) + 70px)) rotate(var(--r)); } }
.light-theme .pa3 { background: linear-gradient(160deg, #fff7ea, #ffffff); border-color: rgba(230,150,30,0.5); }
.light-theme .pa3-h { color: #3b2a22; }
.light-theme .pa3-h b { color: #0b7a4e; }
.light-theme .pa3-t.no { color: #a3152f; }
.light-theme .pa3-t.nx { color: #8a5200; }

.wk3 { margin: 12px 0 0; padding: 12px 14px 10px; border-radius: 22px; background: linear-gradient(160deg, rgba(44,14,26,0.7), rgba(20,8,14,0.66)); border: 1px solid rgba(255,255,255,0.13); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); animation: p3Pop 0.7s cubic-bezier(0.2, 1.2, 0.3, 1) both; }
html.lite .wk3 { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(30,10,20,0.9); }
.light-theme .wk3 { background: rgba(255,255,255,0.94); border-color: rgba(0,0,0,0.1); }
.wk3-h { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 10.5px; font-weight: 800; letter-spacing: 1.8px; color: #c4c9d3; }
.wk3-h b { font: 700 13px 'IBM Plex Sans', sans-serif; letter-spacing: 0; color: #fff; white-space: nowrap; }
.wk3-h em { font-style: normal; font-size: 11px; }
.wk3-h em.up { color: #6ee7b7; }
.wk3-h em.dn { color: #fb7185; }
.light-theme .wk3-h { color: #5a6272; }
.light-theme .wk3-h b { color: #14161c; }
.wk3-bars { display: flex; align-items: flex-end; gap: 7px; height: 84px; margin-top: 8px; }
.wk3-c { flex: 1; min-width: 0; height: 100%; display: flex; flex-direction: column; justify-content: flex-end; align-items: center; gap: 3px; }
.wk3-c i { display: block; width: 100%; border-radius: 7px 7px 3px 3px; background: linear-gradient(180deg, #ffd166, #ff9a1f); opacity: 0.8; transition: height 1s cubic-bezier(0.2, 0.8, 0.2, 1); }
.wk3-c.best i { opacity: 1; box-shadow: 0 0 14px rgba(255,190,60,0.75); }
.wk3-c.now i { opacity: 1; background: linear-gradient(180deg, #c9ffd9, #4fdc92); box-shadow: 0 0 16px rgba(80,220,150,0.65); }
.wk3-v { height: 12px; font-size: 11px; line-height: 1; }
.wk3-c small { font-size: 9px; font-variant-numeric: tabular-nums; color: #aeb6c4; }
.light-theme .wk3-c small { color: #5a6272; }
@media (prefers-reduced-motion: reduce) { .pa3, .pa3:before, .pa3-t.ok, .pa3-t.ok .pa3-sh:after, .wk3 { animation: none !important; } .wk3-c i { transition: none; } }
html.lite .pa3:before, html.lite .pa3-t.ok .pa3-sh:after { animation: none; }
/* ======================= koniec etapu 3 ======================= */

/* ======================= etap 4: lekka trasa, duże okno wypłaty, zadłużenie, fortuna na żywo, czat na tle ======================= */
/* ---- trasa: bez karty, smukła, jak na makiecie ---- */
.rd3 { margin: 12px 0 0; padding: 0; background: none; border: 0; -webkit-backdrop-filter: none; backdrop-filter: none; }
html.lite .rd3, .light-theme .rd3 { background: none; border: 0; }
.rd3-road { margin: 0 46px; height: 64px; }
.rd3-ln { top: 29px; }
.rd3-dn { top: 29px; }
.rd3-st { top: 22px; width: 16px; height: 16px; margin-left: -8px; background: #1a0f14; border-width: 3px; }
.rd3-st.done { background: #1a0f14; border-color: #6ee7b7; box-shadow: 0 0 10px rgba(110,231,183,0.85); }
.rd3-st.cur { background: #1a0f14; border-color: #ffb02e; box-shadow: 0 0 0 5px rgba(255,170,40,0.28), 0 0 14px #ffb02e; }
.rd3-car { top: 0; }
.rd3-car i { font-size: 26px; }
.rd3-lb { top: 44px; text-shadow: 0 1px 6px rgba(0,0,0,0.9); color: #d0d5df; }
.light-theme .rd3-st { background: #fff; }
.light-theme .rd3-st.done { background: #fff; border-color: #2fbf86; }
.light-theme .rd3-lb { text-shadow: none; color: #5a6272; }

/* ---- okno "Co robimy z tą wypłatą?": większe, z animowaną ramką ---- */
@property --pa3a { syntax: '<angle>'; inherits: false; initial-value: 0deg; }
@keyframes pa3Spin { to { --pa3a: 360deg; } }
.pa3 { padding: 15px 14px 14px; border-radius: 26px; border: 1.5px solid transparent; background: linear-gradient(160deg, rgba(54,16,34,0.95), rgba(24,8,18,0.95)) padding-box, conic-gradient(from var(--pa3a), #ffd166, #ff7ab6, #7aa7ff, #6ee7b7, #ffd166) border-box; box-shadow: 0 24px 44px -20px rgba(255,150,40,0.65); animation: pa3In 0.7s cubic-bezier(0.2, 1.3, 0.3, 1) both, pa3Spin 7s linear infinite; }
html.lite .pa3 { animation: pa3In 0.7s cubic-bezier(0.2, 1.3, 0.3, 1) both; }
.pa3-h { margin-bottom: 13px; font-size: 14px; }
.pa3-h b { font-size: 17px; }
.pa3-b { display: flex; flex-direction: column; gap: 10px; grid-template-columns: none; }
.pa3-t.ok { flex-direction: row; justify-content: flex-start; gap: 12px; height: 64px; padding: 0 16px; text-align: left; border-radius: 20px; }
.pa3-ic { flex: 0 0 auto; width: 40px; height: 40px; border-radius: 50%; display: grid; place-items: center; background: rgba(4,23,14,0.16); }
.pa3-tx { display: flex; flex-direction: column; min-width: 0; text-align: left; }
.pa3-tx b { display: block; font-size: 14.5px; font-weight: 800; line-height: 1.2; }
.pa3-tx small { display: block; margin-top: 2px; font-size: 11px; font-weight: 700; opacity: 0.78; line-height: 1.2; }
.pa3-t.ok .pa3-tx b { font-size: 16px; }
.pa3-r { display: grid; grid-template-columns: 1fr 1.2fr; gap: 10px; }
.pa3-t.no, .pa3-t.nx { flex-direction: row; justify-content: flex-start; gap: 10px; height: 58px; padding: 0 12px; text-align: left; border-radius: 18px; }
.pa3-t.no svg, .pa3-t.nx svg { flex: 0 0 auto; }
.pa3-t.no .pa3-tx small, .pa3-t.nx .pa3-tx small { font-size: 10.5px; white-space: nowrap; }

/* ---- zadłużenie / zadania: dni do wolności, karta postępu, spłaty ---- */
html.ui3 .debt-label { display: none; }
html.ui3 .debt-repay-cta { margin-top: 12px; }
.free3 { display: flex; align-items: center; gap: 14px; margin-top: 12px; padding: 12px 16px; border-radius: 22px; background: linear-gradient(135deg, rgba(60,180,130,0.3), rgba(22,10,16,0.82)); border: 1px solid rgba(110,231,183,0.5); animation: p3Pop 0.7s cubic-bezier(0.2, 1.3, 0.3, 1) both; }
.free3 b { font: 700 44px/1 'IBM Plex Sans', sans-serif; letter-spacing: -2px; color: #c9ffd9; filter: drop-shadow(0 0 14px rgba(110,231,183,0.6)); font-variant-numeric: tabular-nums; }
.free3 small { font-size: 10px; font-weight: 800; letter-spacing: 2px; color: #c4c9d3; }
.dots3 { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.dots3 i { display: block; width: 14px; height: 14px; border-radius: 50%; background: rgba(255,255,255,0.2); }
.dots3 i.on { background: #6ee7b7; box-shadow: 0 0 10px #6ee7b7; }
.dots3 i.nx { background: #ffb02e; box-shadow: 0 0 0 4px rgba(255,170,40,0.25), 0 0 12px #ffb02e; }
.light-theme .free3 { background: linear-gradient(135deg, rgba(60,180,130,0.2), #fff); }
.light-theme .free3 b { color: #0b7a4e; filter: none; }
.light-theme .free3 small { color: #5a6272; }
.light-theme .dots3 i { background: rgba(0,0,0,0.14); }
.goal3 { margin-top: 12px; padding: 12px 14px 13px; border-radius: 20px; background: linear-gradient(135deg, rgba(120,40,24,0.72), rgba(40,14,16,0.76)); border: 1px solid rgba(255,150,60,0.5); animation: p3Pop 0.7s cubic-bezier(0.2, 1.3, 0.3, 1) both; }
.goal3-h { display: flex; justify-content: space-between; align-items: center; font-size: 13px; font-weight: 700; color: #f2e9e4; }
.goal3-h em { font-style: normal; font-weight: 800; color: #ffd166; }
.dots3.g { margin-top: 10px; }
.light-theme .goal3 { background: rgba(255,240,225,0.95); }
.light-theme .goal3-h { color: #3b2a22; }
.light-theme .goal3-h em { color: #b86e00; }
.stt3 { flex: 0 0 auto; padding: 3px 9px; border-radius: 99px; font-size: 10.5px; font-weight: 800; }
.stt3.approved { color: #6ee7b7; background: rgba(110,231,183,0.18); }
.stt3.rejected { color: #fda4af; background: rgba(251,113,133,0.18); }
.stt3.pending { color: #2a1703; background: linear-gradient(150deg, #ffe08a, #ff9a1f); animation: pls3 2s ease-in-out 6; }
@keyframes pls3 { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,170,40,0.55); } 50% { box-shadow: 0 0 0 8px rgba(255,170,40,0); } }
.cm2-status-pending { animation: pls3 2.2s ease-in-out 6; }

/* ---- fortuna: chipy nad kołem i "na żywo w ekipie" ---- */
.fz-chips3 { display: none; }
html.ui3 .fz-chips3 { position: relative; display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; margin: 8px 0 4px; }
html.ui3 .fz-chips3 span, html.ui3 .fz-chips3 button { display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 99px; font: 700 13px 'IBM Plex Sans', sans-serif; color: var(--text); cursor: pointer; background: rgba(26,16,24,0.78); border: 1px solid rgba(255,255,255,0.16); animation: p3Pop 0.7s cubic-bezier(0.2, 1.4, 0.3, 1) both; }
html.ui3 .fz-hero { display: none; }
html.ui3 .fz-chips3 button { position: absolute; right: 2px; top: calc(100% + 8px); width: 36px; height: 36px; padding: 0; justify-content: center; z-index: 3; }
.light-theme.ui3 .fz-chips3 span, html.ui3 .light-theme .fz-chips3 span, html.ui3 .light-theme .fz-chips3 button { background: rgba(255,255,255,0.92); border-color: rgba(0,0,0,0.12); }
.fz-live { margin-top: 14px; }
.fz-live-h { display: flex; align-items: center; gap: 8px; width: fit-content; padding: 5px 12px 5px 10px; border-radius: 99px; background: rgba(22,9,15,0.68); font-size: 11px; font-weight: 800; letter-spacing: 2px; color: #e4e7ee; }
.fz-live-h i { display: block; width: 7px; height: 7px; border-radius: 50%; background: #f43f5e; box-shadow: 0 0 10px #f43f5e; animation: pls3 1.6s ease-in-out 8; }
.fz-feed { margin-top: 8px; border-radius: 20px; overflow: hidden; background: linear-gradient(160deg, rgba(150,30,40,0.55), rgba(40,10,18,0.78)); border: 1px solid rgba(255,120,120,0.38); }
.fz-fr { display: flex; align-items: center; gap: 10px; padding: 10px 13px; font-size: 12.5px; color: #f2e9e4; border-bottom: 1px solid rgba(255,255,255,0.08); animation: p3Pop 0.6s cubic-bezier(0.2, 1.2, 0.3, 1) both; }
.fz-fr:last-child { border-bottom: 0; }
.fz-fr b { font-weight: 800; }
.fz-fr small { margin-left: auto; padding-left: 8px; font-size: 11px; white-space: nowrap; color: #aeb6c4; }
.fz-av { flex: 0 0 auto; width: 26px; height: 26px; border-radius: 50%; display: grid; place-items: center; font: 800 11px 'IBM Plex Sans', sans-serif; color: #2a1703; background: linear-gradient(150deg, #ffd166, #ff9a1f); }
.light-theme .fz-feed { background: rgba(255,235,235,0.95); }
.light-theme .fz-fr { color: #3b2a22; }
.light-theme .fz-live-h { background: rgba(255,255,255,0.9); color: #3b4150; }

/* ---- poziom: etykieta w orbicie ---- */
html.ui3 .lv-xp { font-size: 12.5px; }

/* ---- czat: tło sceny widoczne, szklany nagłówek i dymki ---- */
html.ui3 .app-root.hw-moon .cx { background: linear-gradient(180deg, rgba(20,2,8,0.6) 0%, rgba(20,2,8,0.4) 35%, rgba(20,2,8,0.5) 70%, rgba(20,2,8,0.78) 100%), var(--hw-img2) 50% 100% / cover no-repeat, #2a0610; }
html.ui3 .app-root.hw .cx .chat-header { background: rgba(20,8,14,0.55); -webkit-backdrop-filter: blur(14px); backdrop-filter: blur(14px); border-bottom: 1px solid rgba(255,255,255,0.12); }
html.ui3 .app-root.hw .cx .chat-bubble.theirs { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.16); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); }
html.lite.ui3 .app-root.hw .cx .chat-header, html.lite.ui3 .app-root.hw .cx .chat-bubble.theirs { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(24,10,18,0.88); }
@media (prefers-reduced-motion: reduce) { .pa3, .free3, .goal3, .fz-fr, .stt3.pending, .cm2-status-pending, .fz-live-h i { animation: none !important; } }
/* ======================= koniec etapu 4 ======================= */

/* ======================= etap 5: zadania 3.0, podium TOP 3, następna odznaka, zapowiedź poziomów ======================= */
/* ---- zadania: zwarte, wyraźne karty ---- */
.tf3 { display: flex; gap: 8px; margin: 12px 0 2px; overflow-x: auto; padding-bottom: 2px; }
.tf3 button { flex: 0 0 auto; height: 34px; padding: 0 13px; border-radius: 99px; font: 700 12.5px 'IBM Plex Sans', sans-serif; color: #d8dce4; cursor: pointer; background: rgba(26,16,24,0.76); border: 1px solid rgba(255,255,255,0.16); -webkit-tap-highlight-color: transparent; }
.tf3 button.on { color: #2a1703; border-color: transparent; background: linear-gradient(150deg, #ffe08a, #ff9a1f); box-shadow: 0 8px 16px -8px rgba(255,150,30,0.95); }
.light-theme .tf3 button { color: #3b4150; background: rgba(255,255,255,0.92); border-color: rgba(0,0,0,0.12); }
.light-theme .tf3 button.on { color: #2a1703; }
.tf3-empty { margin-top: 10px; padding: 14px; text-align: center; border-radius: 16px; font-size: 12.5px; color: #c4c9d3; background: rgba(22,9,15,0.7); }
.tc3 { position: relative; display: flex; align-items: center; gap: 11px; padding: 11px 12px 11px 15px; border-radius: 18px; overflow: hidden; box-sizing: border-box; background: linear-gradient(160deg, rgba(70,28,40,0.78), rgba(30,12,20,0.74)); border: 1px solid rgba(255,255,255,0.16); -webkit-backdrop-filter: blur(12px); backdrop-filter: blur(12px); animation: p3Pop 0.6s cubic-bezier(0.2, 1.2, 0.3, 1) both; --ac: #ffb02e; }
html.lite .tc3 { -webkit-backdrop-filter: none; backdrop-filter: none; background: rgba(38,16,26,0.92); }
.tc3:before { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--ac); box-shadow: 0 0 14px var(--ac); }
.tc3:after { content: ""; position: absolute; top: -20%; bottom: -20%; left: -40%; width: 24%; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.14), transparent); transform: skewX(-20deg); animation: p3Shn 2.6s ease-in-out 0.8s 1 both; pointer-events: none; }
.tc3.done { --ac: #4fdc92; }
.tc3.missed { --ac: #fb7185; opacity: 0.82; }
.tc3.urgent { --ac: #f43f5e; border-color: rgba(244,63,94,0.6); animation: p3Pop 0.6s cubic-bezier(0.2, 1.2, 0.3, 1) both, tc3Urg 1.8s ease-in-out 1s 6; }
@keyframes tc3Urg { 0%, 100% { box-shadow: 0 0 0 0 rgba(244,63,94,0.5); } 50% { box-shadow: 0 0 0 7px rgba(244,63,94,0); } }
.tc3.tk { border-color: rgba(255,200,80,0.6); background: linear-gradient(160deg, rgba(120,72,18,0.66), rgba(40,16,18,0.76)); }
.tc3-ic { flex: 0 0 auto; width: 38px; height: 38px; border-radius: 13px; display: grid; place-items: center; color: #2a1703; background: linear-gradient(150deg, #ffe08a, #ff9a1f); box-shadow: 0 8px 14px -6px rgba(255,150,30,0.9), inset 0 1px 0 rgba(255,255,255,0.55); }
.tc3.done .tc3-ic { background: linear-gradient(150deg, #8bf5c6, #2fbf86); box-shadow: 0 8px 14px -6px rgba(60,220,150,0.9), inset 0 1px 0 rgba(255,255,255,0.55); animation: tc3Pop 0.7s cubic-bezier(0.2, 1.7, 0.3, 1) 0.3s both; }
.tc3.missed .tc3-ic { background: linear-gradient(150deg, #fda4af, #e11d48); color: #fff; box-shadow: none; }
@keyframes tc3Pop { from { transform: scale(0.4) rotate(-20deg); } to { transform: none; } }
.tc3-m { flex: 1; min-width: 0; }
.tc3-t { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-size: 14px; font-weight: 700; line-height: 1.25; color: #fff; }
.tc3-fl { display: inline-block; margin-right: 4px; vertical-align: -1px; color: #ff9a1f; }
.tc3-s { display: block; margin-top: 3px; font-size: 11.5px; color: #c9cedb; }
.tc3.urgent .tc3-s { color: #ffb3bf; font-weight: 700; }
.tc3.done .tc3-s { color: #7ff0bd; font-weight: 700; }
.tc3-bar { height: 5px; margin-top: 8px; border-radius: 99px; background: rgba(255,255,255,0.15); overflow: hidden; }
.tc3-bar i { display: block; width: 100%; height: 100%; border-radius: 99px; transform-origin: left; background: linear-gradient(90deg, #6ee7b7, #ffd166); box-shadow: 0 0 10px rgba(255,200,80,0.6); animation: tc3Grow 1.1s cubic-bezier(0.2, 0.8, 0.2, 1) 0.3s both; }
.tc3-bar.late i { background: linear-gradient(90deg, #ffd166, #f43f5e); }
@keyframes tc3Grow { from { transform: scaleX(0); } }
.tc3-r { flex: 0 0 auto; display: flex; flex-direction: column; align-items: flex-end; gap: 6px; }
.tc3.tk .tc3-r { align-self: stretch; justify-content: center; padding-left: 11px; border-left: 2px dashed rgba(255,255,255,0.28); }
.tc3-pts { font: 700 16px 'IBM Plex Sans', sans-serif; color: #ffd166; white-space: nowrap; font-variant-numeric: tabular-nums; }
.tc3-pts small { font-size: 10.5px; font-weight: 700; color: #c9cedb; }
.tc3.done .tc3-pts { color: #7ff0bd; }
.tc3-tk { padding: 2px 8px; border-radius: 99px; font-size: 10.5px; font-weight: 800; white-space: nowrap; color: #2a1703; background: linear-gradient(150deg, #ffe08a, #ff9a1f); box-shadow: 0 4px 10px -4px rgba(255,150,30,0.9); }
.light-theme .tc3 { background: rgba(255,255,255,0.95); border-color: rgba(0,0,0,0.1); }
.light-theme .tc3.tk { background: rgba(255,244,222,0.97); }
.light-theme .tc3-t { color: #14161c; }
.light-theme .tc3-s { color: #5a6272; }
.light-theme .tc3-pts { color: #b86e00; }
.light-theme .tc3-bar { background: rgba(0,0,0,0.1); }
.tl3r .tlr { margin-top: 9px; }

/* ---- ranking: podium TOP 3 ---- */
.pd3 { position: relative; margin-top: 4px; padding: 14px 4px 0; }
.pd3-spot { position: absolute; left: 50%; top: -6px; width: 240px; height: 240px; margin-left: -120px; background: radial-gradient(ellipse 50% 60% at 50% 30%, rgba(255,210,100,0.34), transparent 72%); pointer-events: none; animation: pd3Spot 3s ease-in-out 1s 4 alternate; }
@keyframes pd3Spot { from { opacity: 0.5; transform: scale(0.94); } to { opacity: 1; transform: scale(1.06); } }
.pd3-sp { position: absolute; width: 11px; height: 11px; background: #fff; clip-path: polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%); -webkit-clip-path: polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%); filter: drop-shadow(0 0 6px #ffe08a); opacity: 0; animation: p3Tw 2.8s ease-in-out 0s 6; pointer-events: none; z-index: 2; }
.pd3-sp.a { left: 22%; top: 8%; } .pd3-sp.b { left: 74%; top: 14%; animation-delay: 0.7s; } .pd3-sp.c { left: 42%; top: 2%; width: 8px; height: 8px; animation-delay: 1.4s; } .pd3-sp.d { left: 60%; top: 30%; width: 9px; height: 9px; animation-delay: 2.1s; }
.pd3-row { position: relative; display: flex; align-items: flex-end; justify-content: center; gap: 8px; }
.pd3-s { flex: 1 1 0; min-width: 0; display: flex; flex-direction: column; align-items: center; padding: 0; border: 0; background: none; font-family: inherit; color: var(--text); cursor: pointer; -webkit-tap-highlight-color: transparent; animation: pd3Rise 0.9s cubic-bezier(0.2, 1.25, 0.3, 1) both; }
@keyframes pd3Rise { from { opacity: 0; transform: translateY(46px) scale(0.9); } to { opacity: 1; transform: none; } }
.pd3-crown { font-size: 26px; line-height: 1; margin-bottom: -2px; filter: drop-shadow(0 4px 10px rgba(255,190,60,0.9)); animation: rk3Bob 2.4s ease-in-out 1s 4; }
.pd3-av { position: relative; width: 54px; height: 54px; border-radius: 50%; display: grid; place-items: center; font-size: 24px; font-weight: 800; color: #fff; background: radial-gradient(circle at 35% 30%, #4a2e16, #170e08); border: 3px solid var(--rc, #ffd166); box-shadow: 0 0 18px var(--rg, rgba(255,190,60,0.6)); }
.pd3-s.g { --rc: #ffd166; --rg: rgba(255,190,60,0.75); } .pd3-s.g .pd3-av { width: 66px; height: 66px; font-size: 29px; }
.pd3-s.s { --rc: #e5e7eb; --rg: rgba(220,226,240,0.5); }
.pd3-s.b { --rc: #fdba74; --rg: rgba(253,150,80,0.55); }
.pd3-on { position: absolute; right: -1px; bottom: 1px; width: 11px; height: 11px; border-radius: 50%; background: #4fdc92; border: 2px solid #1a0f14; box-sizing: border-box; box-shadow: 0 0 8px #4fdc92; }
.pd3-n { margin-top: 6px; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 800; text-shadow: 0 1px 6px rgba(0,0,0,0.85); }
.pd3-p { margin-top: 1px; font: 700 13px 'IBM Plex Sans', sans-serif; color: #ffd78a; font-variant-numeric: tabular-nums; text-shadow: 0 1px 6px rgba(0,0,0,0.85); }
.pd3-base { position: relative; overflow: hidden; width: 100%; margin-top: 7px; display: grid; place-items: start center; padding-top: 8px; box-sizing: border-box; border-radius: 14px 14px 4px 4px; background: linear-gradient(180deg, var(--rc), rgba(60,24,30,0.92)); box-shadow: 0 14px 22px -12px var(--rg), inset 0 1px 0 rgba(255,255,255,0.5); }
.pd3-s.g .pd3-base { height: 86px; } .pd3-s.s .pd3-base { height: 64px; } .pd3-s.b .pd3-base { height: 50px; }
.pd3-base em { font: 800 24px 'IBM Plex Sans', sans-serif; font-style: normal; color: rgba(42,23,3,0.78); }
.pd3-sh { position: absolute; top: -20%; bottom: -20%; left: -60%; width: 36%; background: linear-gradient(100deg, transparent, rgba(255,255,255,0.65), transparent); transform: skewX(-20deg); animation: p3Shn 3s ease-in-out 1.2s 3; }
.pd3-s.me .pd3-av { box-shadow: 0 0 0 4px rgba(255,255,255,0.18), 0 0 22px var(--rg); }
.pd3-me { margin: 12px auto 0; width: fit-content; padding: 7px 16px; border-radius: 99px; font-size: 13px; font-weight: 800; color: #2a1703; background: linear-gradient(150deg, #ffe08a, #ff9a1f); box-shadow: 0 10px 22px -8px rgba(255,150,30,0.95); animation: p3Pop 0.8s cubic-bezier(0.2, 1.5, 0.3, 1) 1.1s both; }
.light-theme .pd3-n { color: #14161c; text-shadow: none; }
.light-theme .pd3-p { color: #b86e00; text-shadow: none; }
.light-theme .pd3-spot { opacity: 0.6; }

/* ---- osiągnięcia: następna odznaka + zapowiedź systemu poziomów ---- */
.nb3 { display: flex; align-items: center; gap: 12px; margin-top: 12px; padding: 12px 14px; border-radius: 20px; background: linear-gradient(135deg, rgba(120,60,200,0.34), rgba(22,10,20,0.82)); border: 1px solid rgba(192,132,252,0.55); animation: p3Pop 0.7s cubic-bezier(0.2, 1.3, 0.3, 1) both; }
.nb3-ic { flex: 0 0 auto; width: 44px; height: 44px; border-radius: 50%; display: grid; place-items: center; font-size: 22px; filter: grayscale(0.4); background: rgba(255,255,255,0.08); border: 2px dashed rgba(255,255,255,0.32); }
.nb3-m { flex: 1; min-width: 0; }
.nb3-m small { display: block; font-size: 9.5px; font-weight: 800; letter-spacing: 1.8px; color: #d9c2ff; }
.nb3-m b { display: block; margin-top: 2px; font-size: 14px; font-weight: 700; color: #fff; }
.nb3-bar { height: 6px; margin-top: 7px; border-radius: 99px; background: rgba(255,255,255,0.16); overflow: hidden; }
.nb3-bar i { display: block; width: 100%; height: 100%; border-radius: 99px; transform-origin: left; background: linear-gradient(90deg, #c084fc, #ffd166); box-shadow: 0 0 10px rgba(192,132,252,0.7); animation: tc3Grow 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) 0.3s both; }
.nb3-n { flex: 0 0 auto; font: 700 16px 'IBM Plex Sans', sans-serif; color: #ffd166; font-variant-numeric: tabular-nums; white-space: nowrap; }
.nb3-n small { font-size: 11px; font-weight: 600; color: #c9cedb; }
.light-theme .nb3 { background: rgba(245,236,255,0.96); border-color: rgba(150,90,230,0.45); }
.light-theme .nb3-m b { color: #14161c; }
.light-theme .nb3-m small { color: #6a3fb0; }
.light-theme .nb3-n { color: #b86e00; }
.lv-teaser .lv-orb svg .trk { stroke: rgba(255,255,255,0.32); }
.lv-teaser .lv-core { background: radial-gradient(circle at 50% 28%, rgba(52,28,38,0.84), rgba(20,10,16,0.8)); }
.lv-teaser .lv-ic { filter: grayscale(0.6) drop-shadow(0 6px 12px rgba(255,170,40,0.5)); }
html.ui3 .lv-teaser .lv-tb { color: #d8dce4; padding: 9px 12px; border-radius: 16px; background: rgba(22,9,15,0.8); }
.light-theme.ui3 .lv-teaser .lv-tb, html.ui3 .light-theme .lv-teaser .lv-tb { color: #3b4150; background: rgba(255,255,255,0.92); }
@media (prefers-reduced-motion: reduce) { .tc3, .tc3:after, .tc3-bar i, .tc3-ic, .nb3, .nb3-bar i, .pd3-s, .pd3-crown, .pd3-spot, .pd3-sp, .pd3-sh, .pd3-me { animation: none !important; } }
/* ======================= koniec etapu 5 ======================= */

.save-err { margin: 10px 0 2px; padding: 10px 12px; border-radius: 14px; font-size: 12.5px; font-weight: 700; line-height: 1.35; color: #ffc2cb; background: rgba(244,63,94,0.16); border: 1px solid rgba(244,63,94,0.55); }
.light-theme .save-err { color: #a3152f; background: rgba(225,29,72,0.08); }
.btn:disabled { opacity: 0.6; cursor: default; }

`;
